import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useInactivityTimeout } from '../hooks/useInactivityTimeout';
import { SessionExpiredModal, SessionWarningBanner } from '../components/SessionExpiredModal';
import axios from 'axios';
import { toast } from 'sonner';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { usePlatformSettings } from '../hooks/usePlatformSettings';
import { usePlaidLink } from 'react-plaid-link';
import { LeadershipHelpGuide } from '../components/HelpGuide';
import '../components/HelpGuide.css';
import './LeadershipDashboard.css';
import { t } from '../utils/groupFormTranslations';
import { computeLoanSchedule, describeLoanRate } from '../utils/loanMath';
import { LoanInterestFields, LoanSchedulePreview } from '../components/LoanInterestFields';


// Currency symbol mapping
const CURRENCY_SYMBOLS = {
  USD: '$', EUR: '€', GBP: '£', KES: 'KSh', NGN: '₦', ZAR: 'R',
  GHS: '₵', UGX: 'USh', TZS: 'TSh', RWF: 'FRw', ETB: 'Br', EGP: 'E£',
  MAD: 'DH', INR: '₹', PHP: '₱', IDR: 'Rp', MYR: 'RM', SGD: 'S$',
  CAD: 'C$', AUD: 'A$', NZD: 'NZ$', BRL: 'R$', MXN: 'MX$'
};

const getCurrencySymbol = (currencyCode) => {
  return CURRENCY_SYMBOLS[currencyCode] || currencyCode || '$';
};

const formatCurrency = (amount, currencyCode) => {
  const symbol = getCurrencySymbol(currencyCode);
  const formattedAmount = (amount || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
  return `${symbol}${formattedAmount}`;
};

import { API } from '../config';

export default function LeadershipDashboard({ user }) {
  const { settings: platformSettings } = usePlatformSettings();
  const { sessionExpired, showWarning, handleSessionLogout, dismissWarning } = useInactivityTimeout();
  const [activeTab, setActiveTab] = useState('overview');
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [members, setMembers] = useState([]);
  const [payments, setPayments] = useState([]);
  const [caseNumbers, setCaseNumbers] = useState([]);
  const [currentPaymentFilters, setCurrentPaymentFilters] = useState({});
  const [disbursements, setDisbursements] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [showHelpGuide, setShowHelpGuide] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('');
  const [paymentSetup, setPaymentSetup] = useState(null); // {groupId, clientSecret} after group creation
  const [loading, setLoading] = useState(true);
  const [myMemberRecord, setMyMemberRecord] = useState(null);
  const [formData, setFormData] = useState({});
  // Support Ticket state
  const [supportTickets, setSupportTickets] = useState([]);
  const [myTickets, setMyTickets] = useState([]);
  const [overviewClaims, setOverviewClaims] = useState([]);
  const [overviewLoanStats, setOverviewLoanStats] = useState(null);
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [ticketSubject, setTicketSubject] = useState('');
  const [ticketMessage, setTicketMessage] = useState('');
  const [ticketCategory, setTicketCategory] = useState('general');
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketReply, setTicketReply] = useState('');
  const [ticketStats, setTicketStats] = useState({ total: 0, open: 0, in_progress: 0, resolved: 0 });
  const [myAccess, setMyAccess] = useState(null); // { permissions: {...}, is_founder: bool }
  const navigate = useNavigate();

  useEffect(() => {
    loadGroups();
  }, []);

  useEffect(() => {
    if (selectedGroup) {
      loadGroupData();
      loadMyAccess();
      // Reset group-specific state for clean slate
      setSupportTickets([]);
      setMyTickets([]);
      setTicketStats({ total: 0, open: 0, in_progress: 0, resolved: 0 });
      setOverviewClaims([]);
      setOverviewLoanStats(null);
      setSelectedTicket(null);
    }
  }, [selectedGroup]);

  // Auto-refresh overview stats every 30 seconds when on the overview tab
  useEffect(() => {
    if (!selectedGroup || activeTab !== 'overview') return;
    const refreshOverviewStats = () => {
      Promise.all([
        axios.get(`${API}/groups/${selectedGroup.id}/claims`),
        axios.get(`${API}/groups/${selectedGroup.id}/loan-stats`),
        axios.get(`${API}/support/tickets`, { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } })
      ]).then(([claimsRes, loanStatsRes, ticketsRes]) => {
        setOverviewClaims(claimsRes.data);
        setOverviewLoanStats(loanStatsRes.data);
        const memberTickets = ticketsRes.data.filter(t => t.to_role === 'leadership' && t.group_id === selectedGroup.id);
        setSupportTickets(memberTickets);
      }).catch(() => {});
    };
    const interval = setInterval(refreshOverviewStats, 30000);
    return () => clearInterval(interval);
  }, [selectedGroup, activeTab]);

  const loadGroups = async () => {
    try {
      const response = await axios.get(`${API}/groups`);
      setGroups(response.data);
      if (response.data.length > 0) {
        setSelectedGroup(response.data[0]);
      }
    } catch (error) {
      toast.error('Failed to load groups');
    } finally {
      setLoading(false);
    }
  };

  const loadGroupData = async () => {
    if (!selectedGroup) return;

    try {
      const [membersRes, paymentsRes, disbursementsRes, meetingsRes, analyticsRes, subscriptionRes, caseNumbersRes] = await Promise.all([
        axios.get(`${API}/groups/${selectedGroup.id}/members`),
        axios.get(`${API}/groups/${selectedGroup.id}/payments`),
        axios.get(`${API}/groups/${selectedGroup.id}/disbursements`),
        axios.get(`${API}/groups/${selectedGroup.id}/meetings`),
        axios.get(`${API}/groups/${selectedGroup.id}/analytics`),
        axios.get(`${API}/subscriptions/group/${selectedGroup.id}`),
        axios.get(`${API}/groups/${selectedGroup.id}/case-numbers`)
      ]);

      setMembers(membersRes.data);
      setPayments(paymentsRes.data);
      setDisbursements(disbursementsRes.data);
      setMeetings(meetingsRes.data);
      setAnalytics(analyticsRes.data);
      setSubscription(subscriptionRes.data);
      setCaseNumbers(caseNumbersRes.data.case_numbers || []);
      
      // Load overview stats in background (non-blocking)
      Promise.all([
        axios.get(`${API}/groups/${selectedGroup.id}/claims`),
        axios.get(`${API}/groups/${selectedGroup.id}/loan-stats`)
      ]).then(([claimsRes, loanStatsRes]) => {
        setOverviewClaims(claimsRes.data);
        setOverviewLoanStats(loanStatsRes.data);
      }).catch(() => {});
      
      // Check if the leadership user is also a member of this group
      const myMember = membersRes.data.find(m => m.user_email === user.email);
      setMyMemberRecord(myMember);
    } catch (error) {
      toast.error('Failed to load group data');
    }
  };

  const loadMyAccess = async () => {
    if (!selectedGroup) return;
    try {
      const response = await axios.get(`${API}/groups/${selectedGroup.id}/my-access`);
      setMyAccess(response.data);
    } catch (error) {
      console.error('Failed to load access permissions');
    }
  };

  const canView = (feature) => {
    if (!myAccess) return true; // Default allow while loading
    const level = myAccess.permissions?.[feature];
    return level === 'read-only' || level === 'read-write';
  };

  const canWrite = (feature) => {
    if (!myAccess) return true;
    return myAccess.permissions?.[feature] === 'read-write';
  };

  const openModal = async (type, data = null) => {
    setModalType(type);
    if (data) {
      // Pre-fill form data for edit
      setFormData({
        name: data.user_name || '',
        email: data.user_email || '',
        phone: data.phone_number || '',
        membership_number: data.membership_number || '',
        member_id: data.id || ''
      });
    } else {
      // Reset form data for new entries
      const newFormData = {};
      
      // Auto-fetch next membership number for add-member
      if (type === 'add-member' && selectedGroup) {
        try {
          const resp = await axios.get(`${API}/groups/${selectedGroup.id}/next-membership-number`);
          if (resp.data.next_membership_number) {
            newFormData._nextMembershipNumber = resp.data.next_membership_number;
            newFormData.membership_number = resp.data.next_membership_number;
          }
        } catch (err) {
          // Silently fail - manual entry still available
        }
      }
      setFormData(newFormData);
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setModalType('');
    setFormData({});
  };

  // Support ticket functions
  const loadSupportTickets = async () => {
    if (!selectedGroup) return;
    const token = localStorage.getItem('token');
    try {
      // Load tickets from members (to leadership)
      const ticketsRes = await axios.get(`${API}/support/tickets`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      // Filter tickets to current group only
      const memberTickets = ticketsRes.data.filter(t => t.to_role === 'leadership' && t.group_id === selectedGroup.id);
      const leadershipTickets = ticketsRes.data.filter(t => t.from_user_role === 'leadership' && t.group_id === selectedGroup.id);
      
      setSupportTickets(memberTickets);
      setMyTickets(leadershipTickets);
      
      // Calculate stats for current group only
      const allGroupTickets = ticketsRes.data.filter(t => t.group_id === selectedGroup.id);
      setTicketStats({
        total: allGroupTickets.length,
        open: allGroupTickets.filter(t => t.status === 'open').length,
        in_progress: allGroupTickets.filter(t => t.status === 'in_progress').length,
        resolved: allGroupTickets.filter(t => t.status === 'resolved' || t.status === 'closed').length
      });
    } catch (error) {
      console.error('Failed to load support tickets:', error);
    }
  };

  const handleSubmitTicket = async () => {
    if (!ticketSubject.trim() || !ticketMessage.trim()) {
      toast.error('Please fill in subject and message');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/support/tickets`, {
        subject: ticketSubject,
        message: ticketMessage,
        category: ticketCategory,
        group_id: selectedGroup?.id
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      toast.success(`Ticket ${response.data.ticket_number} created!`);
      setShowTicketModal(false);
      setTicketSubject('');
      setTicketMessage('');
      setTicketCategory('general');
      loadSupportTickets();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create ticket');
    }
  };

  const handleTicketReply = async () => {
    if (!ticketReply.trim() || !selectedTicket) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/support/tickets/${selectedTicket.id}/reply`, {
        message: ticketReply
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      toast.success('Reply sent!');
      setTicketReply('');
      loadSupportTickets();
      
      // Refresh selected ticket
      const updatedTicket = await axios.get(`${API}/support/tickets/${selectedTicket.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setSelectedTicket(updatedTicket.data);
    } catch (error) {
      toast.error('Failed to send reply');
    }
  };

  const updateTicketStatus = async (ticketId, newStatus) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API}/support/tickets/${ticketId}/status?status=${newStatus}`, null, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      toast.success(`Ticket marked as ${newStatus}`);
      loadSupportTickets();
      if (selectedTicket?.id === ticketId) {
        setSelectedTicket({ ...selectedTicket, status: newStatus });
      }
    } catch (error) {
      toast.error('Failed to update ticket status');
    }
  };

  // Load support tickets when tab changes or group changes
  useEffect(() => {
    if (activeTab === 'support' && selectedGroup) {
      loadSupportTickets();
    }
  }, [activeTab, selectedGroup?.id]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/');
    window.location.reload();
  };

  const exportMembers = async (format = 'csv') => {
    try {
      let endpoint = `${API}/groups/${selectedGroup.id}/members/export`;
      let filename = `members_${selectedGroup.name}.csv`;
      
      if (format === 'excel') {
        endpoint = `${API}/groups/${selectedGroup.id}/members/export-excel`;
        filename = `members_${selectedGroup.name}.xlsx`;
      } else if (format === 'pdf') {
        endpoint = `${API}/groups/${selectedGroup.id}/members/export-pdf`;
        filename = `members_${selectedGroup.name}.pdf`;
      }
      
      const token = localStorage.getItem('token');
      const response = await axios.get(endpoint, {
        responseType: 'blob',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`Members exported as ${format.toUpperCase()} successfully`);
    } catch (error) {
      toast.error('Failed to export members');
      console.error('Export error:', error);
    }
  };

  const handlePaymentFilterChange = useCallback(async (caseNumber, timeframe, startDate = '', endDate = '', search = '') => {
    try {
      const params = new URLSearchParams();
      const filterState = {};
      if (caseNumber) { params.append('case_number', caseNumber); filterState.case_number = caseNumber; }
      if (search) { params.append('search', search); filterState.search = search; }
      
      if (timeframe === 'custom') {
        if (startDate) { params.append('start_date', startDate); filterState.start_date = startDate; }
        if (endDate) { params.append('end_date', endDate); filterState.end_date = endDate; }
      } else if (timeframe && timeframe !== 'alltime') {
        params.append('timeframe', timeframe);
      }
      
      setCurrentPaymentFilters(filterState);
      const response = await axios.get(`${API}/groups/${selectedGroup.id}/payments?${params.toString()}`);
      setPayments(response.data);
    } catch (error) {
      toast.error('Failed to filter payments');
    }
  }, [selectedGroup?.id]);

  const exportPayments = async (format = 'csv') => {
    try {
      let endpoint = `${API}/groups/${selectedGroup.id}/payments/export`;
      let filename = `payments_${selectedGroup.name}.csv`;
      
      if (format === 'excel') {
        endpoint = `${API}/groups/${selectedGroup.id}/payments/export-excel`;
        filename = `payments_${selectedGroup.name}.xlsx`;
      } else if (format === 'pdf') {
        endpoint = `${API}/groups/${selectedGroup.id}/payments/export-pdf`;
        filename = `payments_${selectedGroup.name}.pdf`;
      }
      
      // Append current filters as query params
      const params = new URLSearchParams();
      if (currentPaymentFilters.case_number) params.append('case_number', currentPaymentFilters.case_number);
      if (currentPaymentFilters.search) params.append('search', currentPaymentFilters.search);
      if (currentPaymentFilters.start_date) params.append('start_date', currentPaymentFilters.start_date);
      if (currentPaymentFilters.end_date) params.append('end_date', currentPaymentFilters.end_date);
      const queryStr = params.toString();
      if (queryStr) endpoint += `?${queryStr}`;

      const token = localStorage.getItem('token');
      const response = await axios.get(endpoint, {
        responseType: 'blob',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`Payments exported as ${format.toUpperCase()} successfully`);
    } catch (error) {
      toast.error('Failed to export payments');
      console.error('Export error:', error);
    }
  };

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="leadership-dashboard" data-testid="leadership-dashboard">
      {sessionExpired && <SessionExpiredModal onLogin={handleSessionLogout} />}
      {showWarning && !sessionExpired && <SessionWarningBanner onDismiss={dismissWarning} />}
      <nav className="dashboard-nav">
        <div className="nav-left">
          <div className="logo">
            {platformSettings.platform_logo_url ? (
              <img 
                src={`${platformSettings.platform_logo_url}?v=${Date.now()}`} 
                alt="Logo" 
                className="logo-image"
              />
            ) : (
              <span className="logo-icon">{platformSettings.platform_logo_text || '💰'}</span>
            )}
            <div className="logo-text-container">
              <span className="group-name">{selectedGroup?.name || platformSettings.platform_name}</span>
              <span className="powered-by">Powered by {platformSettings.platform_name}</span>
            </div>
          </div>
        </div>
        <div className="nav-right">
          {selectedGroup && (
            <button 
              onClick={() => navigate(`/member-dashboard/${selectedGroup.id}`)} 
              className="btn-switch-view"
              data-testid="switch-to-member-btn"
            >
              👤 Switch to Member View
            </button>
          )}
          <span className="user-name">👤 {user.name}</span>
          <button onClick={handleLogout} className="btn-logout" data-testid="logout-btn">Logout</button>
        </div>
      </nav>

      <div className="dashboard-container">
        <aside className="sidebar">
          <div className="sidebar-section">
            <h3>Groups</h3>
            <div className="group-selector">
              {groups.map((group) => (
                <div
                  key={group.id}
                  className={`group-option ${selectedGroup?.id === group.id ? 'active' : ''}`}
                  onClick={() => setSelectedGroup(group)}
                  data-testid={`group-option-${group.id}`}
                >
                  <div className="group-option-name">{group.name}</div>
                </div>
              ))}
            </div>
            <button 
              onClick={() => openModal('create-group')} 
              className="btn btn-primary full-width mt-2"
              data-testid="create-group-btn"
            >
              + Create Group
            </button>
          </div>

          <div className="sidebar-section">
            <h3>Navigation</h3>
            <div className="nav-menu">
              <button
                className={`nav-item ${activeTab === 'overview' ? 'active' : ''}`}
                onClick={() => setActiveTab('overview')}
                data-testid="tab-overview"
              >
                📊 Overview
              </button>
              {canView('members') && <button
                className={`nav-item ${activeTab === 'members' ? 'active' : ''}`}
                onClick={() => setActiveTab('members')}
                data-testid="tab-members"
              >
                👥 Members
              </button>}
              {canView('payments') && <button
                className={`nav-item ${activeTab === 'payments' ? 'active' : ''}`}
                onClick={() => setActiveTab('payments')}
                data-testid="tab-payments"
              >
                💵 Payments
              </button>}
              {canView('manual-verification') && <button
                className={`nav-item ${activeTab === 'manual-verification' ? 'active' : ''}`}
                onClick={() => setActiveTab('manual-verification')}
                data-testid="tab-manual-verification"
              >
                ✅ Manual Verification
              </button>}
              {canView('disbursements') && <button
                className={`nav-item ${activeTab === 'disbursements' ? 'active' : ''}`}
                onClick={() => setActiveTab('disbursements')}
                data-testid="tab-disbursements"
              >
                💸 Disbursements
              </button>}
              {canView('meetings') && <button
                className={`nav-item ${activeTab === 'meetings' ? 'active' : ''}`}
                onClick={() => setActiveTab('meetings')}
                data-testid="tab-meetings"
              >
                📅 Meetings
              </button>}
              {canView('claims') && <button
                className={`nav-item ${activeTab === 'claims' ? 'active' : ''}`}
                onClick={() => setActiveTab('claims')}
                data-testid="tab-claims"
              >
                🛡️ Claims
              </button>}
              {canView('notifications') && <button
                className={`nav-item ${activeTab === 'notifications' ? 'active' : ''}`}
                onClick={() => setActiveTab('notifications')}
                data-testid="tab-notifications"
              >
                🔔 Notifications
              </button>}
              {canView('accounts') && <button
                className={`nav-item ${activeTab === 'accounts' ? 'active' : ''}`}
                onClick={() => setActiveTab('accounts')}
                data-testid="tab-accounts"
              >
                🏦 Bank Account
              </button>}
              {canView('analytics') && <button
                className={`nav-item ${activeTab === 'analytics' ? 'active' : ''}`}
                onClick={() => setActiveTab('analytics')}
                data-testid="tab-analytics"
              >
                📊 Analytics
              </button>}
              {canView('support') && <button
                className={`nav-item ${activeTab === 'support' ? 'active' : ''}`}
                onClick={() => setActiveTab('support')}
                data-testid="tab-support"
              >
                🎫 Support {ticketStats.open > 0 && <span className="badge">{ticketStats.open}</span>}
              </button>}
              {canView('group-settings') && <button
                className={`nav-item ${activeTab === 'group-settings' ? 'active' : ''}`}
                onClick={() => setActiveTab('group-settings')}
                data-testid="tab-group-settings"
              >
                ⚙️ Group Settings
              </button>}
              {canView('subscription') && <button
                className={`nav-item ${activeTab === 'subscription' ? 'active' : ''}`}
                onClick={() => setActiveTab('subscription')}
                data-testid="tab-subscription"
              >
                💳 Subscription
              </button>}
              {canView('access-control') && <button
                className={`nav-item ${activeTab === 'access-control' ? 'active' : ''}`}
                onClick={() => setActiveTab('access-control')}
                data-testid="tab-access-control"
              >
                🔐 Access Control
              </button>}
            </div>
          </div>
        </aside>

        <main className="main-content">
          {!selectedGroup ? (
            <div className="empty-state card">
              <h2>No Groups Yet</h2>
              <p>Create your first group to get started</p>
              <button onClick={() => openModal('create-group')} className="btn btn-primary">
                Create Group
              </button>
            </div>
          ) : (
            <>
              {activeTab === 'overview' && (
                <OverviewTab 
                  analytics={analytics} 
                  group={selectedGroup}
                  claims={overviewClaims}
                  loanStats={overviewLoanStats}
                  disbursements={disbursements}
                  supportTickets={supportTickets}
                  setActiveTab={setActiveTab}
                />
              )}
              {activeTab === 'members' && (
                <MembersTab 
                  members={members} 
                  groupId={selectedGroup.id}
                  group={selectedGroup}
                  currentUserId={user?.id}
                  onAddMember={() => openModal('add-member')}
                  onImportMembers={() => openModal('import-members')}
                  onEditMember={(member) => openModal('edit-member', member)}
                  onExport={exportMembers}
                  onRefresh={loadGroupData}
                />
              )}
              {activeTab === 'payments' && (
                <PaymentsTab 
                  payments={payments}
                  groupId={selectedGroup.id}
                  group={selectedGroup}
                  caseNumbers={caseNumbers}
                  analytics={analytics}
                  members={members}
                  onExport={exportPayments}
                  onFilterChange={handlePaymentFilterChange}
                />
              )}
              {activeTab === 'manual-verification' && (
                <ManualPaymentVerificationTab 
                  groupId={selectedGroup.id}
                  group={selectedGroup}
                  onRefresh={loadGroupData}
                />
              )}
              {activeTab === 'disbursements' && (
                <DisbursementsTab 
                  disbursements={disbursements}
                  groupId={selectedGroup.id}
                  group={selectedGroup}
                  members={members}
                  onCreateDisbursement={() => openModal('create-disbursement')}
                  onRefresh={loadGroupData}
                />
              )}
              {activeTab === 'meetings' && (
                <MeetingsTab 
                  meetings={meetings}
                  groupId={selectedGroup.id}
                  onCreateMeeting={() => openModal('create-meeting')}
                  onRefresh={loadGroupData}
                />
              )}
              {activeTab === 'notifications' && (
                <NotificationsTab 
                  groupId={selectedGroup.id}
                />
              )}
              {activeTab === 'accounts' && (
                <AccountsTab 
                  groupId={selectedGroup.id}
                  group={selectedGroup}
                  onRefresh={loadGroupData}
                />
              )}
              {activeTab === 'analytics' && (
                <AnalyticsTab 
                  groupId={selectedGroup.id}
                  groupName={selectedGroup.name}
                  group={selectedGroup}
                />
              )}
              {activeTab === 'group-settings' && (
                <GroupSettingsTab 
                  group={selectedGroup}
                  onUpdate={loadGroupData}
                />
              )}
              {activeTab === 'subscription' && (
                <SubscriptionTab 
                  groupId={selectedGroup.id}
                  subscription={subscription}
                  onRefresh={loadGroupData}
                />
              )}
              
              {/* Claims Tab */}
              {activeTab === 'claims' && (
                <LeadershipClaimsTab 
                  groupId={selectedGroup.id}
                  members={members}
                  group={selectedGroup}
                  user={user}
                  currencyCode={selectedGroup?.currency}
                />
              )}
              
              {/* Support Tab */}
              {activeTab === 'support' && (
                <div className="support-tab card" data-testid="support-tab">
                  <div className="support-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                    <div>
                      <h2 style={{ margin: 0 }}>🎫 Support Center</h2>
                      <p style={{ margin: '4px 0 0 0', color: '#6B7280', fontSize: '14px' }}>
                        Manage member support requests and contact super admin
                      </p>
                    </div>
                    <button 
                      className="btn btn-primary"
                      onClick={() => setShowTicketModal(true)}
                    >
                      + Contact Super Admin
                    </button>
                  </div>
                  
                  {/* Stats */}
                  <div className="support-stats" style={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(4, 1fr)', 
                    gap: '16px', 
                    marginBottom: '24px' 
                  }}>
                    <div style={{ background: '#F3F4F6', padding: '16px', borderRadius: '12px', textAlign: 'center' }}>
                      <div style={{ fontSize: '28px', fontWeight: '700', color: '#1F2937' }}>{ticketStats.total}</div>
                      <div style={{ color: '#6B7280', fontSize: '14px' }}>Total Tickets</div>
                    </div>
                    <div style={{ background: '#FEF3C7', padding: '16px', borderRadius: '12px', textAlign: 'center' }}>
                      <div style={{ fontSize: '28px', fontWeight: '700', color: '#92400E' }}>{ticketStats.open}</div>
                      <div style={{ color: '#92400E', fontSize: '14px' }}>Open</div>
                    </div>
                    <div style={{ background: '#DBEAFE', padding: '16px', borderRadius: '12px', textAlign: 'center' }}>
                      <div style={{ fontSize: '28px', fontWeight: '700', color: '#1E40AF' }}>{ticketStats.in_progress}</div>
                      <div style={{ color: '#1E40AF', fontSize: '14px' }}>In Progress</div>
                    </div>
                    <div style={{ background: '#D1FAE5', padding: '16px', borderRadius: '12px', textAlign: 'center' }}>
                      <div style={{ fontSize: '28px', fontWeight: '700', color: '#065F46' }}>{ticketStats.resolved}</div>
                      <div style={{ color: '#065F46', fontSize: '14px' }}>Resolved</div>
                    </div>
                  </div>
                  
                  {/* Email Support Info */}
                  <div style={{
                    background: '#EFF6FF',
                    border: '1px solid #BFDBFE',
                    borderRadius: '10px',
                    padding: '16px',
                    marginBottom: '24px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px'
                  }}>
                    <span style={{ fontSize: '24px' }}>📧</span>
                    <div>
                      <p style={{ margin: 0, fontWeight: '600', color: '#1E40AF' }}>Email Support</p>
                      <p style={{ margin: 0, color: '#3B82F6', fontSize: '14px' }}>
                        For urgent matters, contact us at: <a href="mailto:support@chamalink.com" target="_blank" rel="noopener noreferrer" style={{ color: '#2563EB', fontWeight: '600' }} onClick={(e) => e.stopPropagation()}>support@chamalink.com</a>
                      </p>
                    </div>
                  </div>
                  
                  {/* Two-column layout for tickets */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
                    {/* Member Tickets (to Leadership) */}
                    <div>
                      <h3 style={{ margin: '0 0 16px 0', color: '#1F2937' }}>📩 Member Requests</h3>
                      {supportTickets.length === 0 ? (
                        <div style={{ background: '#F9FAFB', padding: '24px', borderRadius: '12px', textAlign: 'center', color: '#6B7280' }}>
                          No support requests from members
                        </div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', maxHeight: '400px', overflowY: 'auto' }}>
                          {supportTickets.map((ticket) => (
                            <div 
                              key={ticket.id}
                              onClick={() => setSelectedTicket(ticket)}
                              style={{
                                background: 'white',
                                border: ticket.status === 'open' ? '2px solid #F59E0B' : '1px solid #E5E7EB',
                                borderRadius: '10px',
                                padding: '14px',
                                cursor: 'pointer',
                                transition: 'all 0.2s'
                              }}
                            >
                              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                                <span style={{ fontSize: '12px', color: '#6B7280' }}>{ticket.ticket_number}</span>
                                <span style={{
                                  background: ticket.status === 'open' ? '#FEF3C7' : 
                                              ticket.status === 'in_progress' ? '#DBEAFE' : '#D1FAE5',
                                  color: ticket.status === 'open' ? '#92400E' : 
                                         ticket.status === 'in_progress' ? '#1E40AF' : '#065F46',
                                  padding: '2px 8px',
                                  borderRadius: '4px',
                                  fontSize: '11px',
                                  textTransform: 'capitalize'
                                }}>
                                  {ticket.status.replace('_', ' ')}
                                </span>
                              </div>
                              <h4 style={{ margin: '0 0 4px 0', fontSize: '14px' }}>{ticket.subject}</h4>
                              <p style={{ margin: 0, color: '#6B7280', fontSize: '13px' }}>
                                From: {ticket.from_user_name}
                              </p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    
                    {/* My Tickets (to Super Admin) */}
                    <div>
                      <h3 style={{ margin: '0 0 16px 0', color: '#1F2937' }}>📤 My Requests to Admin</h3>
                      {myTickets.length === 0 ? (
                        <div style={{ background: '#F9FAFB', padding: '24px', borderRadius: '12px', textAlign: 'center', color: '#6B7280' }}>
                          No tickets to super admin
                        </div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', maxHeight: '400px', overflowY: 'auto' }}>
                          {myTickets.map((ticket) => (
                            <div 
                              key={ticket.id}
                              onClick={() => setSelectedTicket(ticket)}
                              style={{
                                background: 'white',
                                border: '1px solid #E5E7EB',
                                borderRadius: '10px',
                                padding: '14px',
                                cursor: 'pointer'
                              }}
                            >
                              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                                <span style={{ fontSize: '12px', color: '#6B7280' }}>{ticket.ticket_number}</span>
                                <span style={{
                                  background: ticket.status === 'open' ? '#FEF3C7' : 
                                              ticket.status === 'in_progress' ? '#DBEAFE' : '#D1FAE5',
                                  color: ticket.status === 'open' ? '#92400E' : 
                                         ticket.status === 'in_progress' ? '#1E40AF' : '#065F46',
                                  padding: '2px 8px',
                                  borderRadius: '4px',
                                  fontSize: '11px',
                                  textTransform: 'capitalize'
                                }}>
                                  {ticket.status.replace('_', ' ')}
                                </span>
                              </div>
                              <h4 style={{ margin: '0 0 4px 0', fontSize: '14px' }}>{ticket.subject}</h4>
                              <p style={{ margin: 0, color: '#9CA3AF', fontSize: '12px' }}>
                                {new Date(ticket.created_at).toLocaleDateString()}
                              </p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Access Control Tab */}
              {activeTab === 'access-control' && canView('access-control') && (
                <AccessControlTab
                  groupId={selectedGroup.id}
                  group={selectedGroup}
                  currentUserId={user?.id}
                  canWrite={canWrite('access-control')}
                  onRefresh={loadMyAccess}
                />
              )}
            </>
          )}
        </main>
      </div>

      {/* Ticket Modal - Create ticket to Super Admin */}
      {showTicketModal && (
        <div className="modal-overlay" onClick={() => setShowTicketModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h2>📤 Contact Super Admin</h2>
              <button className="modal-close" onClick={() => setShowTicketModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Category</label>
                <select value={ticketCategory} onChange={(e) => setTicketCategory(e.target.value)}>
                  <option value="general">General Question</option>
                  <option value="payment">Billing/Payment Issue</option>
                  <option value="account">Account Problem</option>
                  <option value="technical">Technical Issue</option>
                  <option value="feature">Feature Request</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div className="form-group">
                <label>Subject *</label>
                <input
                  type="text"
                  value={ticketSubject}
                  onChange={(e) => setTicketSubject(e.target.value)}
                  placeholder="Brief description of your issue"
                />
              </div>
              <div className="form-group">
                <label>Message *</label>
                <textarea
                  value={ticketMessage}
                  onChange={(e) => setTicketMessage(e.target.value)}
                  placeholder="Please describe your issue in detail..."
                  rows="5"
                />
              </div>
              <div className="modal-actions">
                <button className="btn btn-secondary" onClick={() => setShowTicketModal(false)}>Cancel</button>
                <button 
                  className="btn btn-primary"
                  onClick={handleSubmitTicket}
                  disabled={!ticketSubject.trim() || !ticketMessage.trim()}
                >
                  Submit Ticket
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Ticket Details Modal */}
      {selectedTicket && (
        <div className="modal-overlay" onClick={() => setSelectedTicket(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <h2>🎫 {selectedTicket.ticket_number}</h2>
              <button className="modal-close" onClick={() => setSelectedTicket(null)}>×</button>
            </div>
            <div className="modal-body">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <div>
                  <span style={{
                    background: selectedTicket.status === 'open' ? '#FEF3C7' : 
                                selectedTicket.status === 'in_progress' ? '#DBEAFE' : 
                                selectedTicket.status === 'resolved' ? '#D1FAE5' : '#F3F4F6',
                    color: selectedTicket.status === 'open' ? '#92400E' : 
                           selectedTicket.status === 'in_progress' ? '#1E40AF' : 
                           selectedTicket.status === 'resolved' ? '#065F46' : '#374151',
                    padding: '4px 12px',
                    borderRadius: '6px',
                    fontSize: '13px',
                    textTransform: 'capitalize'
                  }}>
                    {selectedTicket.status.replace('_', ' ')}
                  </span>
                </div>
                {selectedTicket.to_role === 'leadership' && selectedTicket.status !== 'resolved' && (
                  <select 
                    value={selectedTicket.status}
                    onChange={(e) => updateTicketStatus(selectedTicket.id, e.target.value)}
                    style={{ padding: '6px 12px', borderRadius: '6px' }}
                  >
                    <option value="open">Open</option>
                    <option value="in_progress">In Progress</option>
                    <option value="resolved">Resolved</option>
                  </select>
                )}
              </div>
              
              <p style={{ color: '#6B7280', fontSize: '13px', margin: '0 0 8px 0' }}>
                From: <strong>{selectedTicket.from_user_name}</strong> ({selectedTicket.from_user_email})
              </p>
              <p style={{ color: '#9CA3AF', fontSize: '12px', margin: '0 0 16px 0' }}>
                Created: {new Date(selectedTicket.created_at).toLocaleString()}
              </p>
              
              <h3 style={{ margin: '0 0 8px 0' }}>{selectedTicket.subject}</h3>
              <p style={{ 
                background: '#F9FAFB', 
                padding: '12px', 
                borderRadius: '8px', 
                margin: '0 0 20px 0',
                color: '#374151',
                whiteSpace: 'pre-wrap'
              }}>
                {selectedTicket.message}
              </p>
              
              {/* Replies */}
              {selectedTicket.replies?.length > 0 && (
                <div style={{ marginBottom: '20px' }}>
                  <h4 style={{ margin: '0 0 12px 0' }}>💬 Conversation</h4>
                  {selectedTicket.replies.map((reply, idx) => (
                    <div key={idx} style={{
                      background: reply.user_role === 'member' ? '#EFF6FF' : 
                                  reply.user_role === 'leadership' ? '#F0FDF4' : '#FEF3C7',
                      borderLeft: `4px solid ${reply.user_role === 'member' ? '#3B82F6' : 
                                  reply.user_role === 'leadership' ? '#10B981' : '#F59E0B'}`,
                      padding: '12px',
                      borderRadius: '0 8px 8px 0',
                      marginBottom: '12px'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                        <strong style={{ fontSize: '14px' }}>{reply.user_name}</strong>
                        <span style={{ color: '#9CA3AF', fontSize: '12px' }}>
                          {new Date(reply.created_at).toLocaleString()}
                        </span>
                      </div>
                      <p style={{ margin: 0, fontSize: '14px', color: '#374151' }}>{reply.message}</p>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Reply Form */}
              {selectedTicket.status !== 'resolved' && selectedTicket.status !== 'closed' && (
                <div>
                  <div className="form-group">
                    <label>Reply</label>
                    <textarea
                      value={ticketReply}
                      onChange={(e) => setTicketReply(e.target.value)}
                      placeholder="Type your reply..."
                      rows="3"
                    />
                  </div>
                  <button 
                    className="btn btn-primary"
                    onClick={handleTicketReply}
                    disabled={!ticketReply.trim()}
                  >
                    Send Reply
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showModal && (
        <Modal 
          type={modalType} 
          onClose={closeModal} 
          onSuccess={(data) => {
            closeModal();
            if (modalType === 'create-group') {
              loadGroups();
            } else {
              loadGroupData();
            }
          }}
          groupId={selectedGroup?.id}
          members={members}
          group={selectedGroup}
          user={user}
          initialFormData={formData}
          onPaymentSetup={setPaymentSetup}
        />
      )}

      {/* Payment Setup After Group Creation */}
      {paymentSetup && paymentSetup.clientSecret && (
        <div className="modal-overlay" data-testid="payment-setup-overlay">
          <div className="modal-content" style={{ maxWidth: '520px' }}>
            <div style={{ padding: '24px 24px 0' }}>
              <h2 style={{ margin: '0 0 6px', color: '#1E293B', fontSize: '18px' }}>Add Payment Method</h2>
              <p style={{ margin: '0 0 4px', color: '#6B7280', fontSize: '14px' }}>
                Group <strong>{paymentSetup.groupName}</strong> created successfully!
              </p>
              <p style={{ margin: '0', color: '#6B7280', fontSize: '13px' }}>
                Add a card or bank account to keep your group active after the 14-day trial.
              </p>
            </div>
            <div style={{ padding: '0 24px 24px' }}>
              <StripePaymentForm
                clientSecret={paymentSetup.clientSecret}
                groupId={paymentSetup.groupId}
                onSuccess={() => {
                  toast.success('Payment method added successfully!');
                  setPaymentSetup(null);
                  loadGroups();
                }}
                onCancel={() => {
                  setPaymentSetup(null);
                  toast.info('You can add a payment method later from the Subscription tab.');
                }}
                hasExisting={false}
              />
            </div>
          </div>
        </div>
      )}

      {/* Help Button */}
      <button 
        className="help-button"
        onClick={() => setShowHelpGuide(true)}
        data-testid="help-button"
        title="Help Guide"
      >
        <span>?</span> Help Guide
      </button>

      {/* Help Guide Modal */}
      {showHelpGuide && (
        <LeadershipHelpGuide onClose={() => setShowHelpGuide(false)} />
      )}
    </div>
  );
}


function LeadershipClaimsTab({ groupId, members, group, user, currencyCode }) {
  const [claims, setClaims] = useState([]);
  const [loadingClaims, setLoadingClaims] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');
  const [showTriggerModal, setShowTriggerModal] = useState(false);
  const [claimForm, setClaimForm] = useState({ member_id: '', reason: 'member_death', description: '' });
  const [selectedBeneficiaryIds, setSelectedBeneficiaryIds] = useState([]);
  const [memberBeneficiaries, setMemberBeneficiaries] = useState([]);
  const [loadingBeneficiaries, setLoadingBeneficiaries] = useState(false);
  const [triggering, setTriggering] = useState(false);
  // Beneficiary Registry state
  const [claimsSubTab, setClaimsSubTab] = useState('claims');
  const [registry, setRegistry] = useState([]);
  const [registrySearch, setRegistrySearch] = useState('');
  const [expandedMember, setExpandedMember] = useState(null);
  const [changeRequests, setChangeRequests] = useState([]);
  const [loadingRegistry, setLoadingRegistry] = useState(false);
  const [relationships, setRelationships] = useState([]);
  const [setupWindowHours, setSetupWindowHours] = useState(72);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showInfoRequestModal, setShowInfoRequestModal] = useState(false);
  const [infoRequestTarget, setInfoRequestTarget] = useState(null);
  const [infoRequestNote, setInfoRequestNote] = useState('');
  const [infoRequestDocs, setInfoRequestDocs] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);

  useEffect(() => {
    loadClaims();
  }, [groupId]);

  const loadClaims = async () => {
    setLoadingClaims(true);
    try {
      const res = await axios.get(`${API}/groups/${groupId}/claims`);
      setClaims(res.data);
    } catch (error) {
      toast.error('Failed to load claims');
    } finally {
      setLoadingClaims(false);
    }
  };

  const loadRegistry = async () => {
    setLoadingRegistry(true);
    try {
      const [regRes, reqRes, relRes, docRes] = await Promise.all([
        axios.get(`${API}/groups/${groupId}/beneficiary-registry?search=${registrySearch}`),
        axios.get(`${API}/groups/${groupId}/beneficiary-requests`),
        axios.get(`${API}/groups/${groupId}/beneficiary-relationships`),
        axios.get(`${API}/beneficiary/document-types`)
      ]);
      setRegistry(regRes.data);
      setChangeRequests(reqRes.data);
      setRelationships(relRes.data.relationships || []);
      setSetupWindowHours(relRes.data.setup_window_hours || 72);
      setDocumentTypes(docRes.data.document_types || []);
    } catch (error) {
      console.error('Failed to load registry', error);
    } finally {
      setLoadingRegistry(false);
    }
  };

  useEffect(() => {
    if (claimsSubTab === 'registry' || claimsSubTab === 'requests') loadRegistry();
    // eslint-disable-next-line
  }, [claimsSubTab, registrySearch]);

  const handleReviewRequest = async (requestId, action, note = '', docTypes = []) => {
    try {
      await axios.patch(`${API}/groups/${groupId}/beneficiary-requests/${requestId}`, {
        action, note, document_types: docTypes
      });
      toast.success(action === 'approve' ? 'Request approved' : action === 'reject' ? 'Request rejected' : 'Information requested');
      loadRegistry();
    } catch (error) {
      toast.error('Failed to process request');
    }
  };

  // Load beneficiaries when member is selected
  const handleMemberSelect = async (memberId) => {
    setClaimForm(prev => ({ ...prev, member_id: memberId }));
    setSelectedBeneficiaryIds([]);
    setMemberBeneficiaries([]);
    if (!memberId) return;
    
    setLoadingBeneficiaries(true);
    try {
      const res = await axios.get(`${API}/members/${memberId}/beneficiaries`);
      setMemberBeneficiaries(res.data.filter(b => b.is_active !== false));
    } catch (error) {
      toast.error('Failed to load member beneficiaries');
    } finally {
      setLoadingBeneficiaries(false);
    }
  };

  const toggleBeneficiarySelection = (benId) => {
    setSelectedBeneficiaryIds(prev => 
      prev.includes(benId) ? prev.filter(id => id !== benId) : [...prev, benId]
    );
  };

  const handleTriggerClaim = async () => {
    if (!claimForm.member_id) { toast.error('Please select a member'); return; }
    if (selectedBeneficiaryIds.length === 0) { toast.error('Please select at least one beneficiary to compensate'); return; }
    
    setTriggering(true);
    try {
      await axios.post(`${API}/groups/${groupId}/claims`, {
        member_id: claimForm.member_id,
        claim_type: 'member_death',
        reason: claimForm.reason,
        selected_beneficiary_ids: selectedBeneficiaryIds,
        description: claimForm.description
      });
      toast.success('Member death claim triggered. Selected beneficiaries will be notified.');
      setShowTriggerModal(false);
      setClaimForm({ member_id: '', reason: 'member_death', description: '' });
      setSelectedBeneficiaryIds([]);
      setMemberBeneficiaries([]);
      loadClaims();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to trigger claim');
    } finally {
      setTriggering(false);
    }
  };

  const handleUpdateStatus = async (claimId, status) => {
    try {
      await axios.put(`${API}/claims/${claimId}/status`, { status });
      toast.success(`Claim ${status}`);
      loadClaims();
    } catch (error) {
      toast.error('Failed to update claim status');
    }
  };

  const statusColors = { pending: '#F59E0B', approved: '#3B82F6', disbursed: '#10B981', rejected: '#EF4444' };
  const claimTypeLabels = { beneficiary_loss: 'Beneficiary Loss', member_death: 'Member Death' };

  return (
    <div className="claims-tab card" data-testid="leadership-claims-tab" style={{ padding: '24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '20px', color: '#1E293B' }}>Claims Management</h2>
          <p style={{ margin: '4px 0 0', color: '#6B7280', fontSize: '14px' }}>Manage claims and beneficiary registry</p>
        </div>
        {claimsSubTab === 'claims' && (
          <button className="btn btn-primary" onClick={() => { setShowTriggerModal(true); setClaimForm({ member_id: '', reason: 'member_death', description: '' }); setSelectedBeneficiaryIds([]); setMemberBeneficiaries([]); }} data-testid="trigger-claim-btn" style={{ padding: '10px 20px' }}>
            + Trigger Member Death Claim
          </button>
        )}
        {claimsSubTab === 'registry' && (
          <button className="btn btn-secondary" onClick={() => setShowSettingsModal(true)} data-testid="registry-settings-btn" style={{ padding: '8px 16px', fontSize: '13px' }}>
            Registry Settings
          </button>
        )}
      </div>

      {/* Sub-tabs */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '20px', background: '#F3F4F6', borderRadius: '8px', padding: '4px' }}>
        {[
          { key: 'claims', label: 'Claims', count: claims.length },
          { key: 'registry', label: 'Beneficiary Registry', count: registry.length },
          { key: 'requests', label: 'Change Requests', count: changeRequests.filter(r => r.status === 'pending' || r.status === 'info_requested').length }
        ].map(tab => (
          <button key={tab.key} onClick={() => setClaimsSubTab(tab.key)}
            data-testid={`claims-subtab-${tab.key}`}
            style={{
              flex: 1, padding: '8px 16px', borderRadius: '6px', border: 'none', cursor: 'pointer',
              fontSize: '13px', fontWeight: claimsSubTab === tab.key ? '600' : '400',
              background: claimsSubTab === tab.key ? 'white' : 'transparent',
              color: claimsSubTab === tab.key ? '#1E293B' : '#6B7280',
              boxShadow: claimsSubTab === tab.key ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'
            }}>
            {tab.label} {tab.count > 0 && <span style={{ marginLeft: '4px', background: claimsSubTab === tab.key ? '#3B82F6' : '#D1D5DB', color: claimsSubTab === tab.key ? 'white' : '#4B5563', padding: '1px 6px', borderRadius: '10px', fontSize: '11px' }}>{tab.count}</span>}
          </button>
        ))}
      </div>

      {/* BENEFICIARY REGISTRY SUB-TAB */}
      {claimsSubTab === 'registry' && (
        <div data-testid="beneficiary-registry">
          <div style={{ marginBottom: '16px' }}>
            <input type="text" placeholder="Search by member name or number..."
              value={registrySearch} onChange={(e) => setRegistrySearch(e.target.value)}
              data-testid="registry-search-input"
              style={{ width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #D1D5DB', fontSize: '14px' }}
            />
          </div>
          {loadingRegistry ? <p>Loading registry...</p> : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {registry.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#9CA3AF' }}>No members found</div>
              ) : registry.map(entry => (
                <div key={entry.member_id} style={{ border: '1px solid #E5E7EB', borderRadius: '10px', overflow: 'hidden' }}>
                  <div onClick={() => setExpandedMember(expandedMember === entry.member_id ? null : entry.member_id)}
                    style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 18px',
                      cursor: 'pointer', background: expandedMember === entry.member_id ? '#F8FAFC' : 'white'
                    }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: '#E0E7FF', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '600', color: '#4338CA', fontSize: '14px' }}>
                        {(entry.member_name || '?')[0]}
                      </div>
                      <div>
                        <div style={{ fontWeight: '600', color: '#1E293B', fontSize: '14px' }}>{entry.member_name}</div>
                        <div style={{ fontSize: '12px', color: '#6B7280' }}>{entry.member_email} {entry.membership_number && `| ${entry.membership_number}`}</div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <span style={{ fontSize: '13px', color: '#6B7280' }}>{entry.beneficiary_count} beneficiar{entry.beneficiary_count !== 1 ? 'ies' : 'y'}</span>
                      {!entry.initial_setup_complete && (
                        <span style={{ background: '#FEF3C7', color: '#92400E', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '600' }}>Setup Pending</span>
                      )}
                      <span style={{ transform: expandedMember === entry.member_id ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.2s', fontSize: '18px', color: '#9CA3AF' }}>&#9660;</span>
                    </div>
                  </div>
                  {expandedMember === entry.member_id && (
                    <div style={{ borderTop: '1px solid #E5E7EB', padding: '14px 18px', background: '#FAFAFA' }}>
                      {entry.beneficiaries.length === 0 ? (
                        <p style={{ color: '#9CA3AF', fontSize: '13px', margin: 0 }}>No beneficiaries registered</p>
                      ) : (
                        <div style={{ display: 'grid', gap: '8px' }}>
                          {entry.beneficiaries.map(ben => (
                            <div key={ben.id} style={{
                              display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px',
                              background: 'white', borderRadius: '8px', border: `1px solid ${ben.is_covered ? '#D1D5DB' : '#E5E7EB'}`,
                              opacity: ben.is_covered ? 0.5 : 1
                            }}>
                              <div>
                                <div style={{ fontWeight: '500', color: '#1E293B', fontSize: '14px' }}>
                                  {ben.full_name}
                                  {ben.is_covered && <span style={{ marginLeft: '8px', background: '#E5E7EB', color: '#6B7280', padding: '1px 6px', borderRadius: '4px', fontSize: '10px' }}>COVERED</span>}
                                </div>
                                <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '2px' }}>
                                  {ben.relationship} {ben.phone_number && `| ${ben.phone_number}`} {ben.email && `| ${ben.email}`}
                                </div>
                              </div>
                              <div style={{ textAlign: 'right' }}>
                                <div style={{ fontSize: '14px', fontWeight: '600', color: '#3B82F6' }}>{ben.percentage}%</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* CHANGE REQUESTS SUB-TAB */}
      {claimsSubTab === 'requests' && (
        <div data-testid="beneficiary-change-requests">
          {changeRequests.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#9CA3AF' }}>No change requests</div>
          ) : changeRequests.map(req => (
            <div key={req.id} style={{ border: '1px solid #E5E7EB', borderRadius: '10px', padding: '16px', marginBottom: '12px', background: 'white' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                <div>
                  <div style={{ fontWeight: '600', color: '#1E293B', fontSize: '14px' }}>{req.member_name}</div>
                  <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '2px' }}>
                    Action: <strong style={{ textTransform: 'capitalize' }}>{req.action}</strong>
                    {req.beneficiary_data?.full_name && ` — ${req.beneficiary_data.full_name} (${req.beneficiary_data.relationship})`}
                  </div>
                  {req.reason && <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '2px' }}>Reason: {req.reason}</div>}
                </div>
                <span style={{
                  padding: '3px 10px', borderRadius: '4px', fontSize: '11px', fontWeight: '600',
                  background: req.status === 'pending' ? '#FEF3C7' : req.status === 'approved' ? '#DCFCE7' : req.status === 'rejected' ? '#FEE2E2' : '#DBEAFE',
                  color: req.status === 'pending' ? '#92400E' : req.status === 'approved' ? '#166534' : req.status === 'rejected' ? '#991B1B' : '#1E40AF'
                }}>
                  {req.status === 'info_requested' ? 'Info Requested' : req.status}
                </span>
              </div>

              {/* Member responses */}
              {req.member_responses?.length > 0 && (
                <div style={{ background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: '6px', padding: '10px', marginBottom: '10px', fontSize: '13px' }}>
                  <strong style={{ color: '#166534' }}>Member Response:</strong>
                  {req.member_responses.map((resp, i) => (
                    <div key={i} style={{ marginTop: '4px', color: '#374151' }}>
                      {resp.message}
                      {resp.document_urls?.length > 0 && <div style={{ marginTop: '4px' }}>{resp.document_urls.length} document(s) attached</div>}
                    </div>
                  ))}
                </div>
              )}

              {(req.status === 'pending' || req.status === 'info_requested') && (
                <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                  <button onClick={() => handleReviewRequest(req.id, 'approve')}
                    style={{ padding: '6px 14px', borderRadius: '6px', border: 'none', background: '#22C55E', color: 'white', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}>
                    Approve
                  </button>
                  <button onClick={() => handleReviewRequest(req.id, 'reject', 'Rejected by leadership')}
                    style={{ padding: '6px 14px', borderRadius: '6px', border: '1px solid #EF4444', background: 'white', color: '#EF4444', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}>
                    Reject
                  </button>
                  <button onClick={() => { setInfoRequestTarget(req); setShowInfoRequestModal(true); setInfoRequestNote(''); setInfoRequestDocs([]); }}
                    data-testid="request-info-btn"
                    style={{ padding: '6px 14px', borderRadius: '6px', border: '1px solid #3B82F6', background: 'white', color: '#3B82F6', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}>
                    Request Info
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* REGISTRY SETTINGS MODAL */}
      {showSettingsModal && (
        <div className="modal-overlay" onClick={() => setShowSettingsModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h2>Beneficiary Registry Settings</h2>
              <button className="close-button" onClick={() => setShowSettingsModal(false)}>&times;</button>
            </div>
            <div className="modal-body">
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontWeight: '600', marginBottom: '6px', fontSize: '14px' }}>Setup Window (Hours)</label>
                <input type="number" value={setupWindowHours} onChange={e => setSetupWindowHours(parseInt(e.target.value) || 72)} min="1"
                  style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #D1D5DB' }}
                />
                <small style={{ color: '#6B7280' }}>New members have {setupWindowHours} hours to complete initial beneficiary setup</small>
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontWeight: '600', marginBottom: '6px', fontSize: '14px' }}>Allowed Relationships</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '8px' }}>
                  {relationships.map((rel, i) => (
                    <span key={i} style={{ background: '#E0E7FF', color: '#3730A3', padding: '4px 10px', borderRadius: '14px', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                      {rel}
                      <button onClick={() => setRelationships(relationships.filter((_, idx) => idx !== i))}
                        style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#6366F1', fontSize: '14px', padding: 0 }}>&times;</button>
                    </span>
                  ))}
                </div>
                <input type="text" placeholder="Add relationship and press Enter"
                  onKeyDown={e => { if (e.key === 'Enter' && e.target.value.trim()) { setRelationships([...relationships, e.target.value.trim()]); e.target.value = ''; } }}
                  style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1px solid #D1D5DB', fontSize: '13px' }}
                />
              </div>
              <button onClick={async () => {
                try {
                  await axios.put(`${API}/groups/${groupId}/beneficiary-relationships`, { relationships, setup_window_hours: setupWindowHours });
                  toast.success('Settings saved');
                  setShowSettingsModal(false);
                } catch { toast.error('Failed to save'); }
              }} className="btn btn-primary" style={{ width: '100%' }}>Save Settings</button>
            </div>
          </div>
        </div>
      )}

      {/* INFO REQUEST MODAL */}
      {showInfoRequestModal && infoRequestTarget && (
        <div className="modal-overlay" onClick={() => setShowInfoRequestModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '480px' }}>
            <div className="modal-header">
              <h2>Request More Information</h2>
              <button className="close-button" onClick={() => setShowInfoRequestModal(false)}>&times;</button>
            </div>
            <div className="modal-body">
              <p style={{ color: '#6B7280', fontSize: '14px', marginBottom: '16px' }}>
                Request additional documents/info from <strong>{infoRequestTarget.member_name}</strong>
              </p>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontWeight: '600', marginBottom: '6px', fontSize: '14px' }}>Message to Member</label>
                <textarea value={infoRequestNote} onChange={e => setInfoRequestNote(e.target.value)} rows={3} placeholder="Please provide..."
                  style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #D1D5DB', fontSize: '14px', resize: 'vertical' }}
                />
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontWeight: '600', marginBottom: '6px', fontSize: '14px' }}>Request Documents</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {documentTypes.map(dt => (
                    <label key={dt} style={{
                      display: 'flex', alignItems: 'center', gap: '4px', padding: '5px 10px', borderRadius: '6px', fontSize: '12px', cursor: 'pointer',
                      border: `1px solid ${infoRequestDocs.includes(dt) ? '#3B82F6' : '#D1D5DB'}`,
                      background: infoRequestDocs.includes(dt) ? '#EFF6FF' : 'white',
                      color: infoRequestDocs.includes(dt) ? '#1D4ED8' : '#374151'
                    }}>
                      <input type="checkbox" checked={infoRequestDocs.includes(dt)}
                        onChange={() => setInfoRequestDocs(prev => prev.includes(dt) ? prev.filter(d => d !== dt) : [...prev, dt])}
                        style={{ width: '14px', height: '14px' }}
                      /> {dt}
                    </label>
                  ))}
                </div>
              </div>
              <button onClick={() => { handleReviewRequest(infoRequestTarget.id, 'request_info', infoRequestNote, infoRequestDocs); setShowInfoRequestModal(false); }}
                className="btn btn-primary" style={{ width: '100%' }}
                disabled={!infoRequestNote.trim()}>
                Send Info Request
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CLAIMS SUB-TAB (existing) */}
      {claimsSubTab === 'claims' && (<>

      {/* Stats - Clickable filters */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '24px' }}>
        {[
          { label: 'Total Claims', count: claims.length, bg: statusFilter === 'all' ? '#1F2937' : '#F3F4F6', color: statusFilter === 'all' ? '#fff' : '#1F2937', filter: 'all' },
          { label: 'Pending', count: claims.filter(c => c.status === 'pending').length, bg: statusFilter === 'pending' ? '#F59E0B' : '#FEF3C7', color: statusFilter === 'pending' ? '#fff' : '#92400E', filter: 'pending' },
          { label: 'Approved', count: claims.filter(c => c.status === 'approved').length, bg: statusFilter === 'approved' ? '#3B82F6' : '#DBEAFE', color: statusFilter === 'approved' ? '#fff' : '#1E40AF', filter: 'approved' },
          { label: 'Disbursed', count: claims.filter(c => c.status === 'disbursed').length, bg: statusFilter === 'disbursed' ? '#10B981' : '#D1FAE5', color: statusFilter === 'disbursed' ? '#fff' : '#065F46', filter: 'disbursed' },
        ].map((s, i) => (
          <div key={i} onClick={() => setStatusFilter(s.filter)} data-testid={`claims-filter-${s.filter}`} style={{ background: s.bg, padding: '14px', borderRadius: '10px', textAlign: 'center', cursor: 'pointer', transition: 'all 0.2s ease', border: statusFilter === s.filter ? '2px solid currentColor' : '2px solid transparent' }}>
            <div style={{ fontSize: '24px', fontWeight: '700', color: s.color }}>{s.count}</div>
            <div style={{ color: s.color, fontSize: '13px' }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Active filter indicator */}
      {statusFilter !== 'all' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
          <span style={{ fontSize: '13px', color: '#6B7280' }}>Showing: <strong style={{ textTransform: 'capitalize' }}>{statusFilter}</strong> claims</span>
          <button onClick={() => setStatusFilter('all')} style={{ background: 'none', border: 'none', color: '#3B82F6', cursor: 'pointer', fontSize: '13px', textDecoration: 'underline' }} data-testid="clear-claims-filter">Show All</button>
        </div>
      )}

      {/* Search & Time Frame Filters */}
      <div data-testid="claims-search-filters" style={{ display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div style={{ flex: '1 1 220px' }}>
          <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', color: '#6B7280', marginBottom: '4px' }}>Search by claim # or member name</label>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Type to search..."
            data-testid="claims-search-input"
            style={{ width: '100%', padding: '8px 12px', border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '14px' }}
          />
        </div>
        <div style={{ flex: '0 0 160px' }}>
          <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', color: '#6B7280', marginBottom: '4px' }}>From</label>
          <input
            type="date"
            value={filterStartDate}
            onChange={(e) => setFilterStartDate(e.target.value)}
            data-testid="claims-start-date"
            style={{ width: '100%', padding: '8px 12px', border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '14px' }}
          />
        </div>
        <div style={{ flex: '0 0 160px' }}>
          <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', color: '#6B7280', marginBottom: '4px' }}>To</label>
          <input
            type="date"
            value={filterEndDate}
            onChange={(e) => setFilterEndDate(e.target.value)}
            data-testid="claims-end-date"
            style={{ width: '100%', padding: '8px 12px', border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '14px' }}
          />
        </div>
        {(searchQuery || filterStartDate || filterEndDate) && (
          <button
            onClick={() => { setSearchQuery(''); setFilterStartDate(''); setFilterEndDate(''); }}
            data-testid="claims-clear-search"
            style={{ padding: '8px 16px', border: '1px solid #D1D5DB', borderRadius: '8px', background: '#F9FAFB', cursor: 'pointer', fontSize: '13px', color: '#374151', whiteSpace: 'nowrap' }}
          >
            Clear Filters
          </button>
        )}
      </div>

      {/* Claims List */}
      {loadingClaims ? (
        <p style={{ textAlign: 'center', color: '#9CA3AF' }}>Loading claims...</p>
      ) : claims.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#9CA3AF' }}>
          <p style={{ fontSize: '16px' }}>No claims yet</p>
          <p style={{ fontSize: '13px' }}>Beneficiary loss claims are filed by members. Member death claims are triggered by leadership.</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: '12px' }}>
          {(() => {
            const query = searchQuery.toLowerCase().trim();
            const filtered = claims.filter(c => {
              if (statusFilter !== 'all' && c.status !== statusFilter) return false;
              if (query && !(
                (c.claim_number || c.id || '').toLowerCase().includes(query) ||
                (c.member_name || '').toLowerCase().includes(query)
              )) return false;
              if (filterStartDate && new Date(c.created_at) < new Date(filterStartDate)) return false;
              if (filterEndDate && new Date(c.created_at) > new Date(filterEndDate + 'T23:59:59')) return false;
              return true;
            });
            if (filtered.length === 0) return (
              <p style={{ textAlign: 'center', color: '#9CA3AF', padding: '20px' }}>No matching claims</p>
            );
            return filtered.map(c => (
            <div key={c.id} data-testid={`claim-card-${c.id}`} style={{ border: '1px solid #E5E7EB', borderRadius: '12px', padding: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                    <span style={{ fontSize: '11px', background: c.claim_type === 'beneficiary_loss' ? '#EDE9FE' : '#FEE2E2', color: c.claim_type === 'beneficiary_loss' ? '#6D28D9' : '#DC2626', padding: '2px 8px', borderRadius: '4px', fontWeight: '600' }}>
                      {claimTypeLabels[c.claim_type] || c.claim_type || 'Legacy'}
                    </span>
                    <p style={{ margin: 0, fontWeight: '600', fontSize: '16px', color: '#1E293B' }}>{c.member_name}</p>
                  </div>
                  <p style={{ margin: '2px 0', fontSize: '13px', color: '#6B7280' }}>
                    {c.claim_type === 'beneficiary_loss' 
                      ? `Lost beneficiary: ${c.deceased_beneficiary?.full_name || 'N/A'} (${c.deceased_beneficiary?.relationship || ''})` 
                      : `Reason: ${c.reason?.replace('_',' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Death'}`
                    }
                    &nbsp;&middot; Triggered by {c.triggered_by_name} &middot; {new Date(c.created_at).toLocaleDateString()}
                  </p>
                  {c.description && <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#6B7280' }}>{c.description}</p>}
                  {c.claim_type === 'member_death' && c.beneficiaries?.length > 0 && (
                    <div style={{ marginTop: '8px', fontSize: '13px', color: '#374151' }}>
                      <strong>Compensate:</strong> {c.beneficiaries.map(b => `${b.full_name} (${b.percentage}%)`).join(', ')}
                    </div>
                  )}
                  {c.supporting_documents?.length > 0 && (
                    <div style={{ marginTop: '6px', fontSize: '13px', color: '#374151' }}>
                      <strong>Documents:</strong> {c.supporting_documents.length} file(s) attached
                      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '4px' }}>
                        {c.supporting_documents.map((doc, idx) => (
                          <a key={idx} href={`${API.replace('/api', '')}/uploads/${doc}`} target="_blank" rel="noopener noreferrer" style={{ fontSize: '12px', color: '#3B82F6', textDecoration: 'underline' }} data-testid={`claim-doc-${c.id}-${idx}`}>
                            Doc {idx + 1}
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <span style={{ background: statusColors[c.status] || '#6B7280', color: '#fff', padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: '600', whiteSpace: 'nowrap' }}>
                  {c.status?.toUpperCase()}
                </span>
              </div>
              {c.status === 'pending' && (
                <div style={{ display: 'flex', gap: '8px', marginTop: '12px', justifyContent: 'flex-end' }}>
                  <button onClick={() => handleUpdateStatus(c.id, 'approved')} className="btn btn-primary" style={{ padding: '6px 14px', fontSize: '13px' }} data-testid={`approve-claim-${c.id}`}>Approve</button>
                  <button onClick={() => handleUpdateStatus(c.id, 'rejected')} className="btn btn-secondary" style={{ padding: '6px 14px', fontSize: '13px', background: '#FEE2E2', color: '#DC2626', border: 'none' }} data-testid={`reject-claim-${c.id}`}>Reject</button>
                </div>
              )}
              {c.status === 'approved' && (
                <div style={{ display: 'flex', gap: '8px', marginTop: '12px', justifyContent: 'flex-end' }}>
                  <button onClick={() => handleUpdateStatus(c.id, 'disbursed')} className="btn btn-primary" style={{ padding: '6px 14px', fontSize: '13px', background: '#10B981' }} data-testid={`disburse-claim-${c.id}`}>Mark as Disbursed</button>
                </div>
              )}
            </div>
          ));
          })()}
        </div>
      )}

      {/* Trigger Member Death Claim Modal */}
      {showTriggerModal && (
        <div className="modal-overlay" onClick={() => setShowTriggerModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '540px' }}>
            <div className="modal-header">
              <h2>Trigger Member Death Claim</h2>
              <button className="modal-close" onClick={() => setShowTriggerModal(false)}>×</button>
            </div>
            <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
              <div style={{ background: '#FEE2E2', border: '1px solid #EF4444', borderRadius: '8px', padding: '12px', marginBottom: '16px', fontSize: '13px', color: '#991B1B' }}>
                This will mark the selected member as deceased and notify the selected beneficiaries for compensation per your group's constitution.
              </div>
              <div className="form-group">
                <label>Select Deceased Member *</label>
                <select value={claimForm.member_id} onChange={(e) => handleMemberSelect(e.target.value)} data-testid="claim-member-select">
                  <option value="">-- Select Member --</option>
                  {members.filter(m => m.status !== 'deceased').map(m => (
                    <option key={m.id} value={m.id}>{m.user_name} ({m.user_email})</option>
                  ))}
                </select>
              </div>
              
              {/* Beneficiary selection */}
              {claimForm.member_id && (
                <div className="form-group">
                  <label>Select Beneficiary(ies) to Compensate *</label>
                  <p style={{ fontSize: '12px', color: '#6B7280', margin: '0 0 8px' }}>
                    Select which beneficiaries should receive compensation per your group's constitution
                  </p>
                  {loadingBeneficiaries ? (
                    <p style={{ color: '#9CA3AF', fontSize: '13px' }}>Loading beneficiaries...</p>
                  ) : memberBeneficiaries.length === 0 ? (
                    <div style={{ background: '#FEF3C7', borderRadius: '8px', padding: '12px', fontSize: '13px', color: '#92400E' }}>
                      This member has no active beneficiaries registered. The claim can still be filed and beneficiaries can be designated later.
                    </div>
                  ) : (
                    <div style={{ display: 'grid', gap: '8px' }}>
                      {memberBeneficiaries.map(b => {
                        const isSelected = selectedBeneficiaryIds.includes(b.id);
                        return (
                          <div 
                            key={b.id} 
                            onClick={() => toggleBeneficiarySelection(b.id)}
                            data-testid={`select-beneficiary-${b.id}`}
                            style={{
                              display: 'flex', alignItems: 'center', gap: '12px',
                              padding: '10px 14px', borderRadius: '8px', cursor: 'pointer',
                              border: isSelected ? '2px solid #3B82F6' : '1px solid #E5E7EB',
                              background: isSelected ? '#EFF6FF' : '#FAFAFA',
                              transition: 'all 0.15s ease'
                            }}
                          >
                            <div style={{ width: '20px', height: '20px', borderRadius: '4px', border: isSelected ? '2px solid #3B82F6' : '2px solid #D1D5DB', background: isSelected ? '#3B82F6' : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                              {isSelected && <span style={{ color: '#fff', fontSize: '12px', fontWeight: '700' }}>✓</span>}
                            </div>
                            <div>
                              <p style={{ margin: 0, fontWeight: '600', fontSize: '14px', color: '#1E293B' }}>{b.full_name}</p>
                              <p style={{ margin: 0, fontSize: '12px', color: '#6B7280' }}>
                                {b.relationship} &middot; {b.percentage}% allocation
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
              
              <div className="form-group">
                <label>Description / Notes</label>
                <textarea value={claimForm.description} onChange={(e) => setClaimForm({...claimForm, description: e.target.value})} placeholder="Additional details..." rows="3" data-testid="claim-description-input" />
              </div>
              <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
                <button className="btn btn-secondary" onClick={() => setShowTriggerModal(false)} style={{ flex: 1 }}>Cancel</button>
                <button className="btn btn-primary" onClick={handleTriggerClaim} disabled={triggering} style={{ flex: 1 }} data-testid="confirm-trigger-claim-btn">
                  {triggering ? 'Processing...' : 'Trigger Claim'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>)}
    </div>
  );
}


function OverviewTab({ analytics, group, claims, loanStats, disbursements, supportTickets, setActiveTab }) {
  if (!analytics) return <div>Loading...</div>;

  // Helper to format payment frequency
  const formatFrequency = (freq) => {
    const labels = {
      'weekly': 'Weekly',
      'monthly': 'Monthly',
      'quarterly': 'Quarterly',
      'on_demand': 'On Demand (As Needed)',
      'custom': 'Custom'
    };
    return labels[freq] || freq;
  };

  const pendingClaims = claims?.filter(c => c.status === 'pending').length || 0;
  const approvedClaims = claims?.filter(c => c.status === 'approved').length || 0;
  const totalClaims = claims?.length || 0;

  const openTickets = supportTickets?.filter(t => t.status === 'open' || t.status === 'in_progress').length || 0;
  const resolvedTickets = supportTickets?.filter(t => t.status === 'resolved' || t.status === 'closed').length || 0;
  const totalTickets = supportTickets?.length || 0;

  const activeLoans = loanStats?.active_loans || 0;
  const pendingLoans = loanStats?.pending_loans || 0;
  const totalDisbursed = loanStats?.total_disbursed || 0;
  const totalDisbursements = disbursements?.length || 0;

  const statSections = [
    {
      title: 'Claims',
      tab: 'claims',
      icon: '🛡️',
      accent: '#8B5CF6',
      hasPending: pendingClaims > 0,
      stats: [
        { label: 'Pending', value: pendingClaims, highlight: pendingClaims > 0 },
        { label: 'Approved', value: approvedClaims },
        { label: 'Total', value: totalClaims }
      ]
    },
    {
      title: 'Disbursements',
      tab: 'disbursements',
      icon: '💸',
      accent: '#3B82F6',
      hasPending: pendingLoans > 0,
      stats: [
        { label: 'Pending Loans', value: pendingLoans, highlight: pendingLoans > 0 },
        { label: 'Active Loans', value: activeLoans },
        { label: 'Bank Transfers', value: totalDisbursements },
        { label: 'Total Disbursed', value: formatCurrency(totalDisbursed, group?.currency), isFormatted: true }
      ]
    },
    {
      title: 'Support',
      tab: 'support',
      icon: '🎫',
      accent: '#F59E0B',
      hasPending: openTickets > 0,
      stats: [
        { label: 'Open', value: openTickets, highlight: openTickets > 0 },
        { label: 'Resolved', value: resolvedTickets },
        { label: 'Total', value: totalTickets }
      ]
    }
  ];

  return (
    <div className="overview-tab" data-testid="overview-tab">
      <h1>Group Overview</h1>
      <div className="stats-grid">
        <div className="stat-card card">
          <div className="stat-icon">👥</div>
          <div className="stat-value">{analytics.total_members}</div>
          <div className="stat-label">Total Members</div>
        </div>
        <div className="stat-card card">
          <div className="stat-icon">✅</div>
          <div className="stat-value">{analytics.active_members}</div>
          <div className="stat-label">Active Members</div>
        </div>
        <div className="stat-card card">
          <div className="stat-icon">💵</div>
          <div className="stat-value">{formatCurrency(analytics.total_amount, group?.currency)}</div>
          <div className="stat-label">Total Collected</div>
        </div>
        <div className="stat-card card">
          <div className="stat-icon">📝</div>
          <div className="stat-value">{analytics.total_payments}</div>
          <div className="stat-label">Total Payments</div>
        </div>
      </div>

      {/* Interactive Stat Cards for Claims, Disbursements, Support */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '16px', marginTop: '24px' }}>
        {statSections.map((section) => (
          <div
            key={section.tab}
            onClick={() => setActiveTab(section.tab)}
            data-testid={`overview-card-${section.tab}`}
            style={{
              background: 'white',
              border: `1px solid #E5E7EB`,
              borderTop: `4px solid ${section.accent}`,
              borderRadius: '12px',
              padding: '20px',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              boxShadow: '0 1px 3px rgba(0,0,0,0.06)'
            }}
            onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.06)'; e.currentTarget.style.transform = 'translateY(0)'; }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', position: 'relative' }}>
                <span style={{ fontSize: '24px' }}>{section.icon}</span>
                <h3 style={{ margin: 0, fontSize: '17px', fontWeight: '600', color: '#1E293B' }}>{section.title}</h3>
                {section.hasPending && <span className="pulse-dot" data-testid={`pulse-dot-${section.tab}`} />}
              </div>
              <span style={{ fontSize: '13px', color: section.accent, fontWeight: '500' }}>View →</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(section.stats.length, 4)}, 1fr)`, gap: '10px' }}>
              {section.stats.map((stat, idx) => (
                <div key={idx} style={{ textAlign: 'center', padding: '8px 4px', background: stat.highlight ? '#FEF3C7' : '#F9FAFB', borderRadius: '8px' }}>
                  <div style={{ fontSize: stat.isFormatted ? '14px' : '20px', fontWeight: '700', color: stat.highlight ? '#92400E' : '#1F2937' }}>
                    {stat.value}
                  </div>
                  <div style={{ fontSize: '11px', color: '#6B7280', marginTop: '2px' }}>{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="info-section card mt-3">
        <h2>Group Information</h2>
        <div className="info-grid">
          <div className="info-item">
            <span className="info-label">Group Name:</span>
            <span className="info-value">{group.name}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Payment Amount:</span>
            <span className="info-value">{formatCurrency(group.payment_amount, group?.currency)}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Payment Frequency:</span>
            <span className="info-value">{formatFrequency(group.payment_frequency)}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Criteria Days:</span>
            <span className="info-value">{group.payment_criteria_days} days</span>
          </div>
        </div>
      </div>
    </div>
  );
}



function MyContributionsTab({ member, group, payments, onRefresh }) {
  const [loading, setLoading] = useState(false);

  // Helper to format payment frequency
  const formatFrequency = (freq) => {
    const labels = {
      'weekly': 'Weekly',
      'monthly': 'Monthly',
      'quarterly': 'Quarterly',
      'on_demand': 'On Demand (As Needed)',
      'custom': 'Custom'
    };
    return labels[freq] || freq;
  };

  const handleMakePayment = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Use Connect checkout if group has connected account
      const endpoint = group.connected_account_id && group.connected_account_status === 'active'
        ? `${API}/payments/checkout-connect`
        : `${API}/payments/checkout`;
      
      const response = await axios.post(endpoint, {
        member_id: member.id,
        group_id: group.id
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'origin': window.location.origin
        }
      });

      // Redirect to Stripe checkout
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Payment error:', error);
      toast.error(error.response?.data?.detail || 'Failed to initiate payment');
      setLoading(false);
    }
  };

  return (
    <div className="my-contributions-tab" data-testid="my-contributions-tab">
      <div className="tab-header">
        <h1>My Contributions</h1>
      </div>

      <div className="member-info-card card">
        <h2>Member Information</h2>
        <div className="info-grid">
          <div className="info-item">
            <label>Name:</label>
            <span>{member.user_name}</span>
          </div>
          <div className="info-item">
            <label>Email:</label>
            <span>{member.user_email}</span>
          </div>
          <div className="info-item">
            <label>Phone:</label>
            <span>{member.phone_number}</span>
          </div>
          <div className="info-item">
            <label>Status:</label>
            <span className={`status-badge status-${member.status}`}>
              {member.status}
            </span>
          </div>
          <div className="info-item">
            <label>Join Date:</label>
            <span>{new Date(member.join_date).toLocaleDateString()}</span>
          </div>
          <div className="info-item">
            <label>Last Payment:</label>
            <span>{member.last_payment_date ? new Date(member.last_payment_date).toLocaleDateString() : 'Never'}</span>
          </div>
          <div className="info-item">
            <label>Total Paid:</label>
            <span className="amount-highlight">{formatCurrency(member.total_paid || 0, group?.currency)}</span>
          </div>
          <div className="info-item">
            <label>Payment Count:</label>
            <span>{member.payment_count || 0}</span>
          </div>
        </div>

        <div className="payment-action">
          <div className="payment-info">
            <h3>Contribution Amount: {formatCurrency(group.payment_amount, group?.currency)}</h3>
            <p>Frequency: {formatFrequency(group.payment_frequency)}</p>
            {group.platform_fee_percentage > 0 && (
              <p className="fee-info">+ {formatCurrency(group.payment_amount * group.platform_fee_percentage / 100, group?.currency)} platform fee ({group.platform_fee_percentage}%)</p>
            )}
            <p className="total-amount">Total: {formatCurrency(group.payment_amount + (group.payment_amount * group.platform_fee_percentage / 100), group?.currency)}</p>
          </div>
          <button 
            onClick={handleMakePayment}
            className="btn btn-primary btn-large"
            disabled={loading || member.status !== 'active'}
            data-testid="make-payment-btn"
          >
            {loading ? 'Processing...' : '💳 Make Payment'}
          </button>
        </div>
      </div>

      <div className="payment-history card">
        <h2>My Payment History</h2>
        {payments.length === 0 ? (
          <div className="empty-state">
            <p>No payments yet</p>
          </div>
        ) : (
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Amount</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {payments.map((payment) => (
                  <tr key={payment.id}>
                    <td>{new Date(payment.date).toLocaleDateString()}</td>
                    <td>{formatCurrency(payment.amount, group?.currency)}</td>
                    <td>
                      <span className="status-badge status-approved">
                        {payment.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function MembersTab({ members, groupId, group, currentUserId, onAddMember, onExport, onRefresh, onImportMembers, onEditMember }) {
  const [electing, setElecting] = useState(false);
  const [pendingElections, setPendingElections] = useState([]);
  const [showProbationModal, setShowProbationModal] = useState(false);
  const [probationTarget, setProbationTarget] = useState(null);
  const [probationDays, setProbationDays] = useState(30);
  const [probationReason, setProbationReason] = useState('');
  
  // SMS state for MembersTab
  const [showSmsCompose, setShowSmsCompose] = useState(false);
  const [smsMessage, setSmsMessage] = useState('');
  const [smsRecipientFilter, setSmsRecipientFilter] = useState('all');
  const [sendingSms, setSendingSms] = useState(false);
  
  // SMS send handler for MembersTab
  const handleSendSms = async () => {
    if (!smsMessage.trim()) { toast.error('Please enter a message'); return; }
    setSendingSms(true);
    try {
      const response = await axios.post(`${API}/groups/${groupId}/sms/send`, {
        message: smsMessage.trim(),
        recipient_filter: smsRecipientFilter
      }, { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } });
      toast.success(response.data.message || 'SMS sent successfully');
      setSmsMessage('');
      setShowSmsCompose(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send SMS');
    } finally {
      setSendingSms(false);
    }
  };
  
  // Check if current user is the founding leader
  const isFoundingLeader = group?.founding_leader_id === currentUserId || 
    (!group?.founding_leader_id && group?.leadership_ids?.[0] === currentUserId);

  useEffect(() => {
    if (isFoundingLeader && groupId) {
      loadPendingElections();
    }
  }, [isFoundingLeader, groupId]);

  const loadPendingElections = async () => {
    try {
      const response = await axios.get(`${API}/groups/${groupId}/leader-elections`);
      setPendingElections(response.data.filter(e => e.status === 'pending'));
    } catch (error) {
      console.error('Failed to load elections:', error);
    }
  };

  const handleElectToLeadership = async (member) => {
    if (!window.confirm(`Nominate ${member.user_name} to leadership? This requires super admin approval.`)) {
      return;
    }

    setElecting(true);
    try {
      await axios.post(`${API}/groups/${groupId}/elect-leader`, {
        member_id: member.id
      });
      toast.success(`${member.user_name} has been nominated for leadership. Awaiting admin approval.`);
      loadPendingElections();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to nominate member');
    } finally {
      setElecting(false);
    }
  };

  const isPendingElection = (memberId) => {
    return pendingElections.some(e => e.member_id === memberId);
  };

  const updateStatus = async (memberId, newStatus, probationData = null) => {
    try {
      const payload = { status: newStatus };
      if (newStatus === 'probation' && probationData) {
        payload.probation_days = probationData.days;
        payload.probation_reason = probationData.reason;
      }
      await axios.patch(`${API}/members/${memberId}/status`, payload);
      toast.success('Member status updated');
      onRefresh();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleProbationSelect = (member) => {
    setProbationTarget(member);
    setProbationDays(30);
    setProbationReason('');
    setShowProbationModal(true);
  };

  const confirmProbation = async () => {
    if (!probationDays || probationDays < 1) {
      toast.error('Please enter a valid number of days');
      return;
    }
    await updateStatus(probationTarget.id, 'probation', {
      days: parseInt(probationDays),
      reason: probationReason || 'No reason provided'
    });
    setShowProbationModal(false);
    setProbationTarget(null);
  };

  const removeMember = async (memberId) => {
    try {
      await axios.delete(`${API}/members/${memberId}`);
      toast.success('Member removed from group');
      onRefresh();
    } catch (error) {
      toast.error('Failed to remove member');
    }
  };

  return (
    <>
    <div className="members-tab" data-testid="members-tab">
      <div className="tab-header">
        <h1>Members</h1>
        <div className="tab-actions">
          <div className="export-buttons">
            <button onClick={() => onExport('csv')} className="btn btn-secondary btn-sm" data-testid="export-members-csv">
              📄 CSV
            </button>
            <button onClick={() => onExport('excel')} className="btn btn-secondary btn-sm" data-testid="export-members-excel">
              📊 Excel
            </button>
            <button onClick={() => onExport('pdf')} className="btn btn-secondary btn-sm" data-testid="export-members-pdf">
              📑 PDF
            </button>
          </div>
          <button onClick={onImportMembers} className="btn btn-secondary" data-testid="import-members-btn">
            Import Members
          </button>
          <button 
            onClick={async () => {
              try {
                const token = localStorage.getItem('token');
                const res = await axios.post(`${API}/groups/${groupId}/invite-link`, {}, { headers: { Authorization: `Bearer ${token}` } });
                const link = res.data.invite_url;
                await navigator.clipboard.writeText(link);
                toast.success('Invite link copied to clipboard!');
              } catch (err) {
                toast.error(err.response?.data?.detail || 'Failed to create invite link');
              }
            }}
            className="btn btn-secondary"
            data-testid="create-invite-link-btn"
            style={{ background: '#8B5CF6', color: '#fff', border: 'none' }}
          >
            Share Invite Link
          </button>
          <button
            onClick={() => { setSmsRecipientFilter('all'); setShowSmsCompose(true); }}
            className="btn btn-sm"
            data-testid="sms-all-members-btn"
            style={{ background: '#DBEAFE', color: '#1E40AF', border: '1px solid #3B82F6', padding: '8px 16px' }}
          >
            SMS Members
          </button>
          <button onClick={onAddMember} className="btn btn-primary" data-testid="add-member-btn">
            + Add Member
          </button>
        </div>
      </div>

      {/* SMS Compose Panel for Members */}
      {showSmsCompose && (
        <div className="card" style={{ marginBottom: '20px', padding: '20px', background: '#F0FDF4', border: '1px solid #86EFAC' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <h4 style={{ margin: 0, color: '#166534' }}>📱 Send SMS to Members</h4>
            <button onClick={() => setShowSmsCompose(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6B7280', fontSize: '18px' }}>&times;</button>
          </div>
          <div style={{ marginBottom: '12px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>Recipients</label>
            <select
              value={smsRecipientFilter}
              onChange={(e) => setSmsRecipientFilter(e.target.value)}
              style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #D1D5DB' }}
            >
              <option value="all">All Members</option>
              <option value="active">Active Members Only</option>
            </select>
          </div>
          <div style={{ marginBottom: '12px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>Message</label>
            <textarea
              value={smsMessage}
              onChange={(e) => setSmsMessage(e.target.value)}
              placeholder="Type your message here..."
              rows={3}
              style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #D1D5DB', resize: 'vertical' }}
            />
            <small style={{ color: '#9CA3AF', fontSize: '12px' }}>{smsMessage.length}/320 characters (SMS limit: 160 per part)</small>
          </div>
          <button
            onClick={handleSendSms}
            disabled={sendingSms || !smsMessage.trim()}
            style={{ background: '#16A34A', color: '#fff', padding: '10px 20px', borderRadius: '6px', border: 'none', cursor: 'pointer', fontWeight: 600 }}
          >
            {sendingSms ? 'Sending...' : `Send SMS to ${smsRecipientFilter === 'all' ? 'All' : 'Active'} Members`}
          </button>
        </div>
      )}

      {members.length === 0 ? (
        <div className="empty-state card">
          <h3>No members yet</h3>
          <p>Add your first member to get started</p>
        </div>
      ) : (
        <div className="table-container card">
          <table className="data-table members-detailed-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Membership #</th>
                <th>Email</th>
                <th>Phone Number</th>
                <th>Status</th>
                <th>Join Date</th>
                <th>Last Payment</th>
                <th>Total Paid</th>
                <th>Payments Count</th>
                <th>Payment Completed</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {members.map((member) => (
                <tr key={member.id}>
                  <td>{member.user_name}</td>
                  <td>{member.membership_number || <span className="text-muted">N/A</span>}</td>
                  <td>{member.user_email}</td>
                  <td>{member.phone_number || 'N/A'}</td>
                  <td>
                    <span className={`status-badge status-${member.status}`}>
                      {member.status}
                    </span>
                    {member.status === 'probation' && member.probation_days && (
                      <div style={{ fontSize: '11px', color: '#92400E', marginTop: '3px' }}>
                        {member.probation_days}d
                        {member.probation_end_date && (() => {
                          const remaining = Math.max(0, Math.ceil((new Date(member.probation_end_date) - new Date()) / 86400000));
                          return <span style={{ fontWeight: '600' }}> ({remaining}d left)</span>;
                        })()}
                      </div>
                    )}
                  </td>
                  <td>{new Date(member.join_date).toLocaleDateString()}</td>
                  <td>{member.last_payment_date ? new Date(member.last_payment_date).toLocaleDateString() : 'Never'}</td>
                  <td>{formatCurrency(member.total_paid || 0, group?.currency)}</td>
                  <td>{member.payment_count || 0}</td>
                  <td>
                    <span className={`status-badge ${member.payment_completed ? 'status-approved' : 'status-pending'}`}>
                      {member.payment_completed ? 'Yes' : 'No'}
                    </span>
                  </td>
                  <td className="actions-cell" style={{ 
                    minWidth: '200px', 
                    whiteSpace: 'nowrap',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '12px 8px'
                  }}>
                    <select
                      value={member.status}
                      onChange={(e) => {
                        if (e.target.value === 'remove') {
                          if (window.confirm(`Are you sure you want to remove ${member.name} from this group? This action cannot be undone.`)) {
                            removeMember(member.id);
                          }
                        } else if (e.target.value === 'probation') {
                          handleProbationSelect(member);
                        } else {
                          updateStatus(member.id, e.target.value);
                        }
                      }}
                      className="status-select"
                      data-testid={`status-select-${member.id}`}
                      style={{ 
                        width: 'auto', 
                        minWidth: '90px',
                        padding: '6px 8px',
                        borderRadius: '6px',
                        border: '1px solid #D1D5DB',
                        fontSize: '13px'
                      }}
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                      <option value="probation">Probation</option>
                      <option value="suspended">Suspended</option>
                      <option value="remove" style={{color: 'red'}}>🗑️ Remove</option>
                    </select>
                    <select
                      value=""
                      onChange={(e) => {
                        const action = e.target.value;
                        if (action === 'edit') {
                          onEditMember(member);
                        } else if (action === 'nominate') {
                          handleElectToLeadership(member);
                        }
                      }}
                      className="action-select"
                      data-testid={`action-select-${member.id}`}
                      style={{
                        background: '#3B82F6',
                        color: 'white',
                        border: 'none',
                        padding: '6px 12px',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '13px',
                        fontWeight: '500',
                        minWidth: '90px'
                      }}
                    >
                      <option value="" disabled style={{ display: 'none' }}>Actions</option>
                      <option value="edit" style={{ background: 'white', color: '#374151' }}>✏️ Edit</option>
                      {isFoundingLeader && member.status === 'active' && !member.is_leader && !isPendingElection(member.id) ? (
                        <option value="nominate" style={{ background: 'white', color: '#8B5CF6' }}>👑 Nominate</option>
                      ) : null}
                    </select>
                    {isPendingElection(member.id) && (
                      <span style={{
                        padding: '6px 10px',
                        background: '#FEF3C7',
                        color: '#92400E',
                        borderRadius: '6px',
                        fontSize: '12px',
                        fontWeight: '500'
                      }}>
                        ⏳ Pending
                      </span>
                    )}
                    {member.is_leader && (
                      <span style={{
                        padding: '6px 10px',
                        background: '#10B981',
                        color: 'white',
                        borderRadius: '6px',
                        fontSize: '12px',
                        fontWeight: '500'
                      }}>
                        👑 Leader
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>

      {/* Probation Period Modal */}
      {showProbationModal && probationTarget && (
        <div className="modal-overlay" onClick={() => setShowProbationModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '480px' }}>
            <div className="modal-header">
              <h2 data-testid="probation-modal-title">Set Probation Period</h2>
              <button className="close-button" onClick={() => setShowProbationModal(false)}>&times;</button>
            </div>
            <div className="modal-body">
              <div style={{ background: '#FEF3C7', border: '1px solid #FCD34D', borderRadius: '8px', padding: '14px', marginBottom: '20px' }}>
                <p style={{ margin: 0, color: '#92400E', fontSize: '14px' }}>
                  Placing <strong>{probationTarget.user_name || probationTarget.name}</strong> on probation. They will see a countdown and the member will automatically be reverted to active status when the period ends.
                </p>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#374151', fontSize: '14px' }}>
                  Probation Duration (days) *
                </label>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '8px' }}>
                  {[7, 14, 30, 60, 90].map(d => (
                    <button key={d} type="button" onClick={() => setProbationDays(d)}
                      data-testid={`probation-quick-${d}`}
                      style={{
                        padding: '6px 14px', borderRadius: '6px', fontSize: '13px', fontWeight: '500', cursor: 'pointer',
                        border: probationDays === d ? '2px solid #3B82F6' : '1px solid #D1D5DB',
                        background: probationDays === d ? '#EFF6FF' : 'white',
                        color: probationDays === d ? '#1D4ED8' : '#374151'
                      }}>
                      {d} days
                    </button>
                  ))}
                </div>
                <input
                  type="number" min="1" max="365" value={probationDays}
                  onChange={(e) => setProbationDays(parseInt(e.target.value) || '')}
                  data-testid="probation-days-input"
                  style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #D1D5DB', fontSize: '14px' }}
                  placeholder="Enter number of days"
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#374151', fontSize: '14px' }}>
                  Reason for Probation
                </label>
                <textarea
                  value={probationReason}
                  onChange={(e) => setProbationReason(e.target.value)}
                  data-testid="probation-reason-input"
                  rows={3}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #D1D5DB', fontSize: '14px', resize: 'vertical' }}
                  placeholder="e.g., New member onboarding period, missed payments..."
                />
              </div>

              {probationDays > 0 && (
                <div style={{ background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: '8px', padding: '12px', marginBottom: '16px' }}>
                  <p style={{ margin: 0, color: '#166534', fontSize: '13px' }}>
                    Probation will end on <strong>{new Date(Date.now() + probationDays * 86400000).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</strong>.
                    The member will be automatically reverted to Active status and leadership will be notified.
                  </p>
                </div>
              )}

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button onClick={() => setShowProbationModal(false)}
                  style={{ padding: '10px 20px', borderRadius: '8px', border: '1px solid #D1D5DB', background: 'white', cursor: 'pointer', fontSize: '14px' }}>
                  Cancel
                </button>
                <button onClick={confirmProbation}
                  data-testid="confirm-probation-btn"
                  disabled={!probationDays || probationDays < 1}
                  style={{
                    padding: '10px 20px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: '14px', fontWeight: '600',
                    background: probationDays > 0 ? '#F59E0B' : '#D1D5DB', color: probationDays > 0 ? 'white' : '#9CA3AF'
                  }}>
                  Confirm Probation
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}


function MpesaTransactionsPanel({ groupId, group, members }) {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (groupId) fetchTransactions();
  }, [groupId]);

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/mpesa/transactions/${groupId}`, { headers });
      setTransactions(res.data);
    } catch (err) {
      console.error('Failed to load M-Pesa transactions');
    } finally {
      setLoading(false);
    }
  };

  const getMemberName = (memberId) => {
    const m = members?.find(mem => mem.id === memberId || mem.member_id === memberId);
    return m ? m.name || m.full_name || m.email : memberId;
  };

  const filtered = filter === 'all' ? transactions : transactions.filter(t => t.status === filter);
  const stats = {
    total: transactions.length,
    completed: transactions.filter(t => t.status === 'completed').length,
    pending: transactions.filter(t => t.status === 'pending').length,
    failed: transactions.filter(t => t.status === 'failed' || t.status === 'cancelled').length,
    totalAmount: transactions.filter(t => t.status === 'completed').reduce((sum, t) => sum + (t.amount_paid || t.amount || 0), 0)
  };

  const statusStyle = (status) => {
    const map = {
      completed: { bg: '#DEF7EC', color: '#03543F' },
      pending: { bg: '#FEF3C7', color: '#92400E' },
      failed: { bg: '#FDE8E8', color: '#9B1C1C' },
      cancelled: { bg: '#F3F4F6', color: '#6B7280' }
    };
    return map[status] || map.pending;
  };

  return (
    <div data-testid="mpesa-transactions-panel" style={{ padding: '0' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <h3 style={{ margin: 0, fontSize: '18px', color: '#1F2937' }}>M-Pesa Transactions</h3>
        <button onClick={fetchTransactions} className="btn btn-secondary" data-testid="refresh-mpesa-btn" style={{ fontSize: '13px', padding: '6px 14px' }}>
          Refresh
        </button>
      </div>

      {/* Stats tiles */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px', marginBottom: '16px' }}>
        <div style={{ background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '12px', cursor: 'pointer', borderBottom: filter === 'all' ? '3px solid #4CAF50' : 'none' }} onClick={() => setFilter('all')}>
          <div style={{ fontSize: '11px', color: '#6B7280' }}>Total</div>
          <div style={{ fontSize: '22px', fontWeight: 700, color: '#1F2937' }}>{stats.total}</div>
        </div>
        <div style={{ background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: '8px', padding: '12px', cursor: 'pointer', borderBottom: filter === 'completed' ? '3px solid #16A34A' : 'none' }} onClick={() => setFilter('completed')}>
          <div style={{ fontSize: '11px', color: '#6B7280' }}>Completed</div>
          <div style={{ fontSize: '22px', fontWeight: 700, color: '#16A34A' }}>{stats.completed}</div>
          <div style={{ fontSize: '11px', color: '#059669', fontWeight: 600 }}>{formatCurrency(stats.totalAmount, group?.currency)}</div>
        </div>
        <div style={{ background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: '8px', padding: '12px', cursor: 'pointer', borderBottom: filter === 'pending' ? '3px solid #D97706' : 'none' }} onClick={() => setFilter('pending')}>
          <div style={{ fontSize: '11px', color: '#6B7280' }}>Pending</div>
          <div style={{ fontSize: '22px', fontWeight: 700, color: '#D97706' }}>{stats.pending}</div>
        </div>
        <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '8px', padding: '12px', cursor: 'pointer', borderBottom: filter === 'failed' ? '3px solid #DC2626' : 'none' }} onClick={() => setFilter('failed')}>
          <div style={{ fontSize: '11px', color: '#6B7280' }}>Failed</div>
          <div style={{ fontSize: '22px', fontWeight: 700, color: '#DC2626' }}>{stats.failed}</div>
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#9CA3AF' }}>Loading M-Pesa transactions...</div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#9CA3AF' }}>
          {transactions.length === 0 ? 'No M-Pesa transactions yet' : 'No transactions match this filter'}
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table data-testid="mpesa-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ background: '#F9FAFB', borderBottom: '2px solid #E5E7EB' }}>
                <th style={{ textAlign: 'left', padding: '10px 12px', color: '#6B7280', fontWeight: 600 }}>Date</th>
                <th style={{ textAlign: 'left', padding: '10px 12px', color: '#6B7280', fontWeight: 600 }}>Member</th>
                <th style={{ textAlign: 'left', padding: '10px 12px', color: '#6B7280', fontWeight: 600 }}>Phone</th>
                <th style={{ textAlign: 'left', padding: '10px 12px', color: '#6B7280', fontWeight: 600 }}>Case</th>
                <th style={{ textAlign: 'right', padding: '10px 12px', color: '#6B7280', fontWeight: 600 }}>Amount</th>
                <th style={{ textAlign: 'left', padding: '10px 12px', color: '#6B7280', fontWeight: 600 }}>Receipt</th>
                <th style={{ textAlign: 'left', padding: '10px 12px', color: '#6B7280', fontWeight: 600 }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((txn, i) => {
                const st = statusStyle(txn.status);
                return (
                  <tr key={txn.id || i} style={{ borderBottom: '1px solid #E5E7EB' }} data-testid={`mpesa-row-${i}`}>
                    <td style={{ padding: '10px 12px', color: '#6B7280', whiteSpace: 'nowrap' }}>{txn.created_at ? new Date(txn.created_at).toLocaleString() : '-'}</td>
                    <td style={{ padding: '10px 12px', fontWeight: 500 }}>{getMemberName(txn.member_id)}</td>
                    <td style={{ padding: '10px 12px', fontFamily: 'monospace', fontSize: '12px', color: '#6B7280' }}>{txn.phone_number}</td>
                    <td style={{ padding: '10px 12px' }}>{txn.case_id ? `#${txn.case_id}` : '-'}</td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600, color: '#059669' }}>{formatCurrency(txn.amount_paid || txn.amount || 0, group?.currency)}</td>
                    <td style={{ padding: '10px 12px', fontFamily: 'monospace', fontSize: '12px', color: '#374151' }}>{txn.mpesa_receipt_number || '-'}</td>
                    <td style={{ padding: '10px 12px' }}>
                      <span style={{ padding: '3px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: 600, background: st.bg, color: st.color }}>{txn.status}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


function PaymentsTab({ payments, groupId, group, caseNumbers, analytics, members, onExport, onFilterChange }) {
  const [activeSubTab, setActiveSubTab] = useState('current');
  const [selectedCase, setSelectedCase] = useState('');
  const [selectedTimeframe, setSelectedTimeframe] = useState('alltime');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Bank transaction filters
  const [bankFilterCase, setBankFilterCase] = useState('');
  const [bankFilterMember, setBankFilterMember] = useState('');
  const [bankFilterTime, setBankFilterTime] = useState('alltime');
  
  // Unified payment filters
  const [unifiedFilterMember, setUnifiedFilterMember] = useState('');
  const [unifiedFilterTime, setUnifiedFilterTime] = useState('alltime');
  
  // Compliance
  const [complianceCaseFilter, setComplianceCaseFilter] = useState('');
  const [expandedComplianceCase, setExpandedComplianceCase] = useState(null);
  
  // Import state
  const [importedPayments, setImportedPayments] = useState([]);
  const [importBatches, setImportBatches] = useState([]);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importing, setImporting] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [loadingImported, setLoadingImported] = useState(false);
  const [selectedBatchFilter, setSelectedBatchFilter] = useState('');

  // Bank/Plaid state
  const [bankConnection, setBankConnection] = useState(null);
  const [bankTransactions, setBankTransactions] = useState([]);
  const [bankTxFilter, setBankTxFilter] = useState('all');
  const [txSummary, setTxSummary] = useState({});
  const [syncing, setSyncing] = useState(false);
  const [loadingBankTx, setLoadingBankTx] = useState(false);
  const [showPaymentInstructionsModal, setShowPaymentInstructionsModal] = useState(false);
  const [paymentInstructions, setPaymentInstructions] = useState({});
  const [matchModalTx, setMatchModalTx] = useState(null);
  const [matchCaseId, setMatchCaseId] = useState('');
  const [linkToken, setLinkToken] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [bankClaims, setBankClaims] = useState([]);
  const [prepaidSettings, setPrepaidSettings] = useState({ prepaid_disabled: false });
  const [parsingFees, setParsingFees] = useState({ fees: [], total_fees_charged: 0, total_amount_parsed: 0 });
  const [unifiedPayments, setUnifiedPayments] = useState([]);
  const [unifiedCaseFilter, setUnifiedCaseFilter] = useState('');
  const [complianceData, setComplianceData] = useState({ cases: [], members: [] });

  useEffect(() => {
    if (selectedTimeframe === 'custom') {
      onFilterChange(selectedCase, 'custom', customStartDate, customEndDate, searchTerm);
    } else {
      onFilterChange(selectedCase, selectedTimeframe, '', '', searchTerm);
    }
  }, [selectedCase, selectedTimeframe, customStartDate, customEndDate, searchTerm, onFilterChange]);

  // Check bank connection on mount for tab visibility
  useEffect(() => {
    if (groupId) {
      axios.get(`${API}/groups/${groupId}/bank-connection`).then(res => {
        setBankConnection(res.data?.id ? res.data : null);
      }).catch(() => setBankConnection(null));
    }
  }, [groupId]);

  // Time filter helper
  const filterByTime = (dateStr, timeframe) => {
    if (!timeframe || timeframe === 'alltime') return true;
    const d = new Date(dateStr);
    const now = new Date();
    if (timeframe === 'month') {
      return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
    }
    if (timeframe === 'quarter') {
      const q = Math.floor(now.getMonth() / 3);
      const qStart = new Date(now.getFullYear(), q * 3, 1);
      return d >= qStart;
    }
    if (timeframe === 'annual') {
      return d.getFullYear() === now.getFullYear();
    }
    return true;
  };

  const hasBankConnection = bankConnection !== null;

  useEffect(() => {
    if (activeSubTab === 'imported' && groupId) {
      loadImportedPayments();
      loadImportBatches();
    }
    if (activeSubTab === 'bank' && groupId) {
      loadBankData();
    }
    if (activeSubTab === 'unified' && groupId) {
      loadUnifiedPayments();
    }
    if (activeSubTab === 'compliance' && groupId) {
      loadComplianceData();
    }
  }, [activeSubTab, groupId]);

  const loadBankData = async () => {
    try {
      const [connRes, instrRes, summRes, claimsRes, prepaidRes, feesRes] = await Promise.all([
        axios.get(`${API}/groups/${groupId}/bank-connection`),
        axios.get(`${API}/groups/${groupId}/payment-instructions`),
        axios.get(`${API}/groups/${groupId}/bank-transactions/summary`),
        axios.get(`${API}/groups/${groupId}/claims`).catch(() => ({ data: [] })),
        axios.get(`${API}/groups/${groupId}/prepaid-settings`).catch(() => ({ data: { prepaid_disabled: false } })),
        axios.get(`${API}/groups/${groupId}/plaid-parsing-fees`).catch(() => ({ data: { fees: [], total_fees_charged: 0, total_amount_parsed: 0 } }))
      ]);
      setBankConnection(connRes.data?.id ? connRes.data : null);
      setPaymentInstructions(instrRes.data || {});
      setTxSummary(summRes.data || {});
      setBankClaims(claimsRes.data || []);
      setPrepaidSettings(prepaidRes.data || { prepaid_disabled: false });
      setParsingFees(feesRes.data || { fees: [], total_fees_charged: 0, total_amount_parsed: 0 });
      if (connRes.data?.id) loadBankTransactions();
    } catch (e) { console.error('Bank data load error:', e); }
  };

  const loadBankTransactions = async () => {
    setLoadingBankTx(true);
    try {
      const res = await axios.get(`${API}/groups/${groupId}/bank-transactions?status=${bankTxFilter}&limit=100`);
      setBankTransactions(res.data);
    } catch (e) { toast.error('Failed to load transactions'); }
    finally { setLoadingBankTx(false); }
  };

  useEffect(() => {
    if (activeSubTab === 'bank' && groupId && bankConnection) loadBankTransactions();
  }, [bankTxFilter]);

  const loadUnifiedPayments = async () => {
    try {
      // Combine platform payments and confirmed bank transactions
      const [bankRes] = await Promise.all([
        axios.get(`${API}/groups/${groupId}/bank-transactions?status=confirmed&limit=200`).catch(() => ({ data: [] }))
      ]);
      
      // Merge: platform payments from props + confirmed bank transactions
      const platformEntries = (payments || []).map(p => ({
        id: p.id,
        type: 'platform',
        member_name: p.member_name || p.user_name || 'Unknown',
        member_id: p.member_id,
        amount: p.amount,
        case_number: p.case_number,
        date: p.date || p.created_at,
        method: p.payment_method === 'wallet_autopay' ? 'Wallet Autopay' : 'Stripe',
        status: p.status
      }));
      
      const bankEntries = (bankRes.data || []).map(t => ({
        id: t.id,
        type: 'bank',
        member_name: t.matched_member_name || t.name || 'Unknown',
        member_id: t.matched_member_id,
        amount: t.amount,
        case_number: t.matched_case_id,
        date: t.date,
        method: 'Bank Transfer',
        status: 'confirmed'
      }));
      
      const combined = [...platformEntries, ...bankEntries].sort((a, b) => 
        new Date(b.date) - new Date(a.date)
      );
      setUnifiedPayments(combined);
    } catch (e) { console.error('Unified payments load error:', e); }
  };

  const loadComplianceData = async () => {
    try {
      const [membersRes, casesRes] = await Promise.all([
        axios.get(`${API}/groups/${groupId}/members`),
        axios.get(`${API}/groups/${groupId}/cases`).catch(() => ({ data: [] }))
      ]);
      
      const members = membersRes.data || [];
      const cases = (casesRes.data || []).filter(c => c.status === 'active' || c.status === 'approved');
      
      // For each active case, check which members have paid
      const complianceCases = cases.map(c => {
        const paidMembers = (payments || [])
          .filter(p => p.case_number === c.case_number && p.status === 'completed')
          .map(p => p.member_id);
        
        const totalMembers = members.filter(m => m.status === 'active').length;
        const paidCount = new Set(paidMembers).size;
        const unpaidCount = totalMembers - paidCount;
        const compliance = totalMembers > 0 ? (paidCount / totalMembers * 100) : 0;
        
        return {
          ...c,
          total_members: totalMembers,
          paid_count: paidCount,
          unpaid_count: unpaidCount,
          compliance_pct: compliance,
          unpaid_members: members
            .filter(m => m.status === 'active' && !paidMembers.includes(m.id))
            .map(m => ({ id: m.id, name: m.user_name || m.user_email }))
        };
      });
      
      setComplianceData({ cases: complianceCases, members });
    } catch (e) { console.error('Compliance data load error:', e); }
  };

  const handleSyncTransactions = async () => {
    setSyncing(true);
    try {
      const res = await axios.post(`${API}/groups/${groupId}/sync-transactions`);
      toast.success(res.data.message);
      loadBankTransactions();
      const summRes = await axios.get(`${API}/groups/${groupId}/bank-transactions/summary`);
      setTxSummary(summRes.data || {});
    } catch (e) { toast.error('Failed to sync: ' + (e.response?.data?.detail || 'Unknown error')); }
    finally { setSyncing(false); }
  };

  const handleConfirmTx = async (txId) => {
    try {
      await axios.post(`${API}/groups/${groupId}/bank-transactions/${txId}/confirm`);
      toast.success('Transaction confirmed and payment recorded');
      loadBankTransactions();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to confirm'); }
  };

  const handleDismissTx = async (txId) => {
    if (!window.confirm('Dismiss this transaction?')) return;
    try {
      await axios.post(`${API}/groups/${groupId}/bank-transactions/${txId}/dismiss`, { reason: 'Not related' });
      toast.success('Transaction dismissed');
      loadBankTransactions();
    } catch (e) { toast.error('Failed to dismiss'); }
  };

  const handleManualMatch = async () => {
    if (!matchModalTx || !matchCaseId) return;
    try {
      await axios.post(`${API}/groups/${groupId}/bank-transactions/${matchModalTx.id}/match`, { case_id: matchCaseId });
      toast.success('Transaction matched');
      setMatchModalTx(null);
      setMatchCaseId('');
      loadBankTransactions();
    } catch (e) { toast.error('Failed to match'); }
  };

  const handleSavePaymentInstructions = async () => {
    try {
      await axios.put(`${API}/groups/${groupId}/payment-instructions`, paymentInstructions);
      toast.success('Payment instructions saved');
      setShowPaymentInstructionsModal(false);
    } catch (e) { toast.error('Failed to save'); }
  };

  const handleTogglePrepaid = async () => {
    const newDisabled = !prepaidSettings.prepaid_disabled;
    try {
      await axios.put(`${API}/groups/${groupId}/prepaid-settings`, { prepaid_disabled: newDisabled });
      setPrepaidSettings(prev => ({ ...prev, prepaid_disabled: newDisabled }));
      toast.success(`Prepaid services ${newDisabled ? 'disabled' : 'enabled'}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update prepaid settings'); }
  };

  const initPlaidLink = async () => {
    try {
      const res = await axios.post(`${API}/plaid/create-link-token`);
      setLinkToken(res.data.link_token);
      if (res.data.oauth_limited) {
        toast.info('Some banks using OAuth may not be available. Ask your admin to configure the Plaid redirect URI.');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to initialize bank connection');
    }
  };

  const handlePlaidSuccess = async (publicToken, metadata) => {
    setConnecting(true);
    try {
      const res = await axios.post(`${API}/groups/${groupId}/connect-bank`, {
        public_token: publicToken,
        institution_name: metadata?.institution?.name || ''
      });
      toast.success('Bank account connected!');
      setBankConnection(res.data.connection);
      setLinkToken(null);
    } catch (e) {
      console.error('[Plaid] Connect failed:', e);
      toast.error(e.response?.data?.detail || 'Failed to connect bank');
    }
    finally { setConnecting(false); }
  };

  const handleDisconnectBank = async () => {
    if (!window.confirm('Disconnect bank account? Transaction history will be preserved.')) return;
    try {
      await axios.delete(`${API}/groups/${groupId}/bank-connection`);
      toast.success('Bank disconnected');
      setBankConnection(null);
    } catch (e) { toast.error('Failed to disconnect'); }
  };

  const loadImportedPayments = async () => {
    setLoadingImported(true);
    try {
      let url = `${API}/groups/${groupId}/imported-payments`;
      if (selectedBatchFilter) {
        url += `?batch_id=${selectedBatchFilter}`;
      }
      const response = await axios.get(url);
      setImportedPayments(response.data);
    } catch (error) {
      toast.error('Failed to load imported payments');
    } finally {
      setLoadingImported(false);
    }
  };

  const loadImportBatches = async () => {
    try {
      const response = await axios.get(`${API}/groups/${groupId}/import-batches`);
      setImportBatches(response.data);
    } catch (error) {
      console.error('Failed to load import batches:', error);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validTypes = ['.csv', '.xlsx', '.xls', '.pdf'];
      const ext = '.' + file.name.split('.').pop().toLowerCase();
      if (!validTypes.includes(ext)) {
        toast.error('Please select a CSV, Excel (.xlsx, .xls), or PDF file');
        return;
      }
      setSelectedFile(file);
      setImportResult(null);
    }
  };

  const handleImport = async () => {
    if (!selectedFile) {
      toast.error('Please select a file first');
      return;
    }

    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await axios.post(
        `${API}/groups/${groupId}/import-payments`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      setImportResult(response.data);
      if (response.data.records_imported > 0) {
        toast.success(`Successfully imported ${response.data.records_imported} payment records!`);
        loadImportedPayments();
        loadImportBatches();
      } else {
        toast.error('No valid records found in the file');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import file');
    } finally {
      setImporting(false);
    }
  };

  const handleDeleteBatch = async (batchId, filename) => {
    if (!window.confirm(`Are you sure you want to delete all imported records from "${filename}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await axios.delete(`${API}/groups/${groupId}/import-batches/${batchId}`);
      toast.success('Import batch deleted successfully');
      loadImportedPayments();
      loadImportBatches();
    } catch (error) {
      toast.error('Failed to delete import batch');
    }
  };

  const closeImportModal = () => {
    setShowImportModal(false);
    setSelectedFile(null);
    setImportResult(null);
  };

  const PlaidLinkButton = ({ token, onSuccess }) => {
    // Check if returning from OAuth redirect
    const receivedRedirectUri = window.location.href.includes('oauth_state_id') ? window.location.href : undefined;
    const { open, ready } = usePlaidLink({
      token,
      onSuccess: (public_token, metadata) => onSuccess(public_token, metadata),
      onExit: (err, metadata) => {
        if (err) {
          console.error('[PlaidLink] Exit error:', err);
          toast.error(`Bank connection error: ${err.display_message || err.error_message || 'Please try again'}`);
        }
      },
      receivedRedirectUri
    });
    return (
      <button onClick={() => open()} disabled={!ready || connecting} data-testid="payments-plaid-connect-btn"
        style={{ padding: '10px 24px', borderRadius: '10px', border: 'none', background: '#059669', color: '#fff', fontSize: '14px', fontWeight: '700', cursor: 'pointer', whiteSpace: 'nowrap' }}>
        {connecting ? 'Connecting...' : 'Connect Bank Account'}
      </button>
    );
  };

  return (
    <div className="payments-tab" data-testid="payments-tab">
      <div className="tab-header">
        <h1>Payments</h1>
        <div className="export-buttons">
          <button onClick={() => onExport('csv')} className="btn btn-secondary btn-sm" data-testid="export-payments-csv">
            📄 CSV
          </button>
          <button onClick={() => onExport('excel')} className="btn btn-secondary btn-sm" data-testid="export-payments-excel">
            📊 Excel
          </button>
          <button onClick={() => onExport('pdf')} className="btn btn-secondary btn-sm" data-testid="export-payments-pdf">
            📑 PDF
          </button>
        </div>
      </div>

      {/* Sub-tabs */}
      {!hasBankConnection && (
        <div data-testid="plaid-connect-banner" style={{
          background: 'linear-gradient(135deg, #F0FDF4, #ECFDF5)',
          border: '1px solid #A7F3D0',
          borderRadius: '12px',
          padding: '20px',
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '16px',
          flexWrap: 'wrap'
        }}>
          <div style={{ flex: 1, minWidth: '200px' }}>
            <h3 style={{ margin: '0 0 6px', fontSize: '15px', color: '#166534', fontWeight: '700' }}>
              Connect Bank Account
            </h3>
            <p style={{ margin: 0, fontSize: '13px', color: '#15803D', lineHeight: '1.5' }}>
              Link your group's bank to automatically detect Zelle, Cash App & other incoming payments. This unlocks Bank Transactions and Unified Payments tabs.
            </p>
          </div>
          <div>
            {linkToken ? (
              <PlaidLinkButton token={linkToken} onSuccess={handlePlaidSuccess} />
            ) : (
              <button
                className="btn btn-primary"
                onClick={initPlaidLink}
                disabled={connecting}
                data-testid="payments-init-plaid-btn"
                style={{
                  padding: '10px 24px',
                  borderRadius: '10px',
                  border: 'none',
                  background: '#059669',
                  color: '#fff',
                  fontSize: '14px',
                  fontWeight: '700',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap'
                }}
              >
                {connecting ? 'Connecting...' : 'Set Up Bank Connection'}
              </button>
            )}
          </div>
        </div>
      )}
      <div className="sub-tabs" style={{ 
        display: 'flex', 
        gap: '4px', 
        marginBottom: '20px',
        borderBottom: '2px solid #E5E7EB',
        paddingBottom: '0',
        overflowX: 'auto'
      }}>
        <button
          onClick={() => setActiveSubTab('current')}
          className={`sub-tab-btn ${activeSubTab === 'current' ? 'active' : ''}`}
          data-testid="subtab-platform-payments"
          style={{
            padding: '12px 20px',
            border: 'none',
            background: activeSubTab === 'current' ? '#3B82F6' : 'transparent',
            color: activeSubTab === 'current' ? 'white' : '#6B7280',
            borderRadius: '8px 8px 0 0',
            cursor: 'pointer',
            fontWeight: '600',
            fontSize: '13px',
            transition: 'all 0.2s',
            whiteSpace: 'nowrap'
          }}
        >
          Platform Payments
        </button>
        {hasBankConnection && (
        <button
          onClick={() => setActiveSubTab('bank')}
          className={`sub-tab-btn ${activeSubTab === 'bank' ? 'active' : ''}`}
          data-testid="subtab-bank-transactions"
          style={{
            padding: '12px 20px',
            border: 'none',
            background: activeSubTab === 'bank' ? '#059669' : 'transparent',
            color: activeSubTab === 'bank' ? 'white' : '#6B7280',
            borderRadius: '8px 8px 0 0',
            cursor: 'pointer',
            fontWeight: '600',
            fontSize: '13px',
            transition: 'all 0.2s',
            whiteSpace: 'nowrap'
          }}
        >
          Bank Transactions
        </button>
        )}
        {hasBankConnection && (
        <button
          onClick={() => setActiveSubTab('unified')}
          className={`sub-tab-btn ${activeSubTab === 'unified' ? 'active' : ''}`}
          data-testid="subtab-unified-payments"
          style={{
            padding: '12px 20px',
            border: 'none',
            background: activeSubTab === 'unified' ? '#7C3AED' : 'transparent',
            color: activeSubTab === 'unified' ? 'white' : '#6B7280',
            borderRadius: '8px 8px 0 0',
            cursor: 'pointer',
            fontWeight: '600',
            fontSize: '13px',
            transition: 'all 0.2s',
            whiteSpace: 'nowrap'
          }}
        >
          Unified Payments
        </button>
        )}
        <button
          onClick={() => setActiveSubTab('compliance')}
          className={`sub-tab-btn ${activeSubTab === 'compliance' ? 'active' : ''}`}
          data-testid="subtab-compliance"
          style={{
            padding: '12px 20px',
            border: 'none',
            background: activeSubTab === 'compliance' ? '#DC2626' : 'transparent',
            color: activeSubTab === 'compliance' ? 'white' : '#6B7280',
            borderRadius: '8px 8px 0 0',
            cursor: 'pointer',
            fontWeight: '600',
            fontSize: '13px',
            transition: 'all 0.2s',
            whiteSpace: 'nowrap'
          }}
        >
          Compliance
        </button>
        <button
          onClick={() => setActiveSubTab('imported')}
          className={`sub-tab-btn ${activeSubTab === 'imported' ? 'active' : ''}`}
          data-testid="subtab-imported-history"
          style={{
            padding: '12px 20px',
            border: 'none',
            background: activeSubTab === 'imported' ? '#8B5CF6' : 'transparent',
            color: activeSubTab === 'imported' ? 'white' : '#6B7280',
            borderRadius: '8px 8px 0 0',
            cursor: 'pointer',
            fontWeight: '600',
            fontSize: '13px',
            transition: 'all 0.2s',
            whiteSpace: 'nowrap'
          }}
        >
          Imported History
        </button>
        {group?.currency === 'KES' && (
          <button
            onClick={() => setActiveSubTab('mpesa')}
            className={`sub-tab-btn ${activeSubTab === 'mpesa' ? 'active' : ''}`}
            data-testid="subtab-mpesa"
            style={{
              padding: '12px 20px',
              border: 'none',
              background: activeSubTab === 'mpesa' ? '#4CAF50' : 'transparent',
              color: activeSubTab === 'mpesa' ? 'white' : '#6B7280',
              borderRadius: '8px 8px 0 0',
              cursor: 'pointer',
              fontWeight: '600',
              fontSize: '13px',
              transition: 'all 0.2s',
              whiteSpace: 'nowrap'
            }}
          >
            M-Pesa Transactions
          </button>
        )}
      </div>

      {activeSubTab === 'current' && (
        <>
          <div className="filters-section card">
            <h3>🔍 Filter Payments</h3>
            <div className="form-row">
              <div className="form-group" style={{ flex: 2 }}>
                <label>Search by Member Name or Membership #</label>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Type to search..."
                  data-testid="payments-search-input"
                />
              </div>
              <div className="form-group">
                <label>Case Number</label>
                <select value={selectedCase} onChange={(e) => setSelectedCase(e.target.value)} data-testid="platform-filter-case">
                  <option value="">All Cases</option>
                  {caseNumbers.map((caseNum) => (
                    <option key={caseNum} value={caseNum}>{caseNum}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Member</label>
                <select value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} data-testid="platform-filter-member">
                  <option value="">All Members</option>
                  {(members || []).filter(m => m.status === 'active').map(m => (
                    <option key={m.id} value={m.user_name || m.user_email}>{m.user_name || m.user_email}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Timeframe</label>
                <select value={selectedTimeframe} onChange={(e) => setSelectedTimeframe(e.target.value)}>
                  <option value="alltime">All Time</option>
                  <option value="annual">This Year</option>
                  <option value="quarter">This Quarter</option>
                  <option value="month">This Month</option>
                  <option value="custom">Custom Date Range</option>
                </select>
              </div>
            </div>
            
            {selectedTimeframe === 'custom' && (
              <div className="form-row custom-date-range">
                <div className="form-group">
                  <label>Start Date</label>
                  <input
                    type="date"
                    value={customStartDate}
                    onChange={(e) => setCustomStartDate(e.target.value)}
                    className="date-input"
                  />
                </div>
                <div className="form-group">
                  <label>End Date</label>
                  <input
                    type="date"
                    value={customEndDate}
                    onChange={(e) => setCustomEndDate(e.target.value)}
                    className="date-input"
                  />
                </div>
              </div>
            )}
          </div>

          {payments.length === 0 ? (
            <div className="empty-state card">
              <h3>No payments yet</h3>
              <p>Payments will appear here once members start paying</p>
            </div>
          ) : (
            <>
              {(() => {
                const activeMembers = analytics?.active_members || 0;
                const uniquePaidMembers = new Set(payments.map(p => p.member_email || p.member_name)).size;
                const paymentRate = activeMembers > 0 ? Math.round((uniquePaidMembers / activeMembers) * 100) : 0;
                const rateColor = paymentRate <= 30 ? '#EF4444' : paymentRate <= 70 ? '#F59E0B' : '#22C55E';
                const rateBg = paymentRate <= 30 ? 'rgba(239,68,68,0.15)' : paymentRate <= 70 ? 'rgba(245,158,11,0.15)' : 'rgba(34,197,94,0.15)';
                const rateLabel = paymentRate <= 30 ? 'Low' : paymentRate <= 70 ? 'Moderate' : 'High';

                return (
              <div data-testid="payments-total-bar" style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px',
                background: 'linear-gradient(135deg, #1E3A5F, #1E3A5F)',
                borderRadius: '10px', padding: '16px 24px', marginBottom: '16px', color: 'white'
              }}>
                <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', opacity: 0.7 }}>Total Amount</div>
                    <div data-testid="payments-total-amount" style={{ fontSize: '26px', fontWeight: 700 }}>
                      {formatCurrency(payments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0), group?.currency)}
                    </div>
                  </div>
                  <div style={{ borderLeft: '1px solid rgba(255,255,255,0.2)', paddingLeft: '24px' }}>
                    <div style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', opacity: 0.7 }}>Records</div>
                    <div data-testid="payments-total-count" style={{ fontSize: '20px', fontWeight: 600 }}>{payments.length}</div>
                  </div>
                  {selectedCase && (
                    <div style={{ borderLeft: '1px solid rgba(255,255,255,0.2)', paddingLeft: '24px' }}>
                      <div style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', opacity: 0.7 }}>Case</div>
                      <div style={{ fontSize: '16px', fontWeight: 600 }}>{selectedCase}</div>
                    </div>
                  )}
                </div>

                {/* Payment Rate Indicator */}
                <div data-testid="payment-rate-indicator" style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', opacity: 0.7, marginBottom: '4px' }}>
                      Payment Rate
                    </div>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px', justifyContent: 'flex-end' }}>
                      <span data-testid="payment-rate-value" style={{ fontSize: '22px', fontWeight: 700, color: rateColor }}>{paymentRate}%</span>
                      <span style={{
                        fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px',
                        background: rateBg, color: rateColor, padding: '2px 8px', borderRadius: '4px'
                      }}>
                        {rateLabel}
                      </span>
                    </div>
                    <div style={{ fontSize: '11px', opacity: 0.6, marginTop: '2px' }}>
                      {uniquePaidMembers} of {activeMembers} active member{activeMembers !== 1 ? 's' : ''}
                    </div>
                  </div>
                  {/* Progress Ring */}
                  <div style={{ position: 'relative', width: '52px', height: '52px', flexShrink: 0 }}>
                    <svg width="52" height="52" viewBox="0 0 52 52" style={{ transform: 'rotate(-90deg)' }}>
                      <circle cx="26" cy="26" r="22" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="5" />
                      <circle cx="26" cy="26" r="22" fill="none" stroke={rateColor} strokeWidth="5"
                        strokeDasharray={`${(paymentRate / 100) * 138.2} 138.2`}
                        strokeLinecap="round"
                        style={{ transition: 'stroke-dasharray 0.6s ease' }}
                      />
                    </svg>
                    <div style={{
                      position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                      fontSize: '11px', fontWeight: 700, color: rateColor
                    }}>
                      {paymentRate}%
                    </div>
                  </div>
                </div>
              </div>
                );
              })()}

              <div className="table-container card">
              <table className="data-table payments-detailed-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Case #</th>
                    <th>Member Name</th>
                    <th>Email</th>
                    <th>Phone Number</th>
                    <th>Amount</th>
                    <th>Member Status</th>
                    <th>Payment Status</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((payment) => (
                    <tr key={payment.id}>
                      <td>{new Date(payment.date).toLocaleDateString()}</td>
                      <td>{payment.case_number || 'N/A'}</td>
                      <td>{payment.member_name || 'Unknown'}</td>
                      <td>{payment.member_email || 'Unknown'}</td>
                      <td>{payment.member_phone || 'N/A'}</td>
                      <td>{formatCurrency(payment.amount, group?.currency)}</td>
                      <td>
                        <span className={`status-badge status-${payment.member_status || 'unknown'}`}>
                          {payment.member_status || 'unknown'}
                        </span>
                      </td>
                      <td>
                        <span className="status-badge status-approved">
                          {payment.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            </>
          )}
        </>
      )}

      {/* Unified Payments Tab */}
      {activeSubTab === 'unified' && (
        <div data-testid="unified-payments-panel">
          <div className="card" style={{ padding: '20px', marginBottom: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <div>
                <h3 style={{ margin: 0, color: '#1E293B', fontSize: '16px' }}>Unified Payment View</h3>
                <p style={{ margin: '4px 0 0', color: '#64748B', fontSize: '13px' }}>
                  All payment sources combined — platform, bank transfers, and wallet autopay
                </p>
              </div>
              <select
                value={unifiedCaseFilter}
                onChange={(e) => setUnifiedCaseFilter(e.target.value)}
                data-testid="unified-case-filter"
                style={{ padding: '8px 12px', border: '1px solid #CBD5E1', borderRadius: '6px', fontSize: '13px' }}
              >
                <option value="">All Cases</option>
                {caseNumbers.map(cn => (
                  <option key={cn} value={cn}>Case #{cn}</option>
                ))}
              </select>
            </div>

            {/* Unified Filters */}
            <div style={{ display: 'flex', gap: '8px', marginBottom: '14px', flexWrap: 'wrap' }} data-testid="unified-filters">
              <select value={unifiedFilterMember} onChange={(e) => setUnifiedFilterMember(e.target.value)} data-testid="unified-filter-member"
                style={{ padding: '7px 10px', border: '1px solid #CBD5E1', borderRadius: '6px', fontSize: '12px', minWidth: '140px' }}>
                <option value="">All Members</option>
                {(members || []).filter(m => m.status === 'active').map(m => <option key={m.id} value={m.user_name || m.user_email}>{m.user_name || m.user_email}</option>)}
              </select>
              <select value={unifiedFilterTime} onChange={(e) => setUnifiedFilterTime(e.target.value)} data-testid="unified-filter-time"
                style={{ padding: '7px 10px', border: '1px solid #CBD5E1', borderRadius: '6px', fontSize: '12px' }}>
                <option value="alltime">All Time</option>
                <option value="month">This Month</option>
                <option value="quarter">This Quarter</option>
                <option value="annual">This Year</option>
              </select>
              {(unifiedCaseFilter || unifiedFilterMember || unifiedFilterTime !== 'alltime') && (
                <button onClick={() => { setUnifiedCaseFilter(''); setUnifiedFilterMember(''); setUnifiedFilterTime('alltime'); }}
                  style={{ padding: '7px 10px', border: 'none', background: '#FEE2E2', color: '#DC2626', borderRadius: '6px', fontSize: '12px', cursor: 'pointer', fontWeight: '600' }}>
                  Clear
                </button>
              )}
            </div>

            {/* Stats row */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', marginBottom: '16px' }}>
              <div style={{ padding: '14px', background: '#EFF6FF', borderRadius: '10px', textAlign: 'center', border: '1px solid #BFDBFE' }}>
                <p style={{ margin: 0, fontSize: '20px', fontWeight: '700', color: '#1D4ED8' }}>
                  {unifiedPayments.filter(p => p.type === 'platform').length}
                </p>
                <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#3B82F6' }}>Platform</p>
              </div>
              <div style={{ padding: '14px', background: '#ECFDF5', borderRadius: '10px', textAlign: 'center', border: '1px solid #A7F3D0' }}>
                <p style={{ margin: 0, fontSize: '20px', fontWeight: '700', color: '#059669' }}>
                  {unifiedPayments.filter(p => p.type === 'bank').length}
                </p>
                <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#059669' }}>Bank Transfer</p>
              </div>
              <div style={{ padding: '14px', background: '#F5F3FF', borderRadius: '10px', textAlign: 'center', border: '1px solid #C4B5FD' }}>
                <p style={{ margin: 0, fontSize: '20px', fontWeight: '700', color: '#7C3AED' }}>
                  {unifiedPayments.length}
                </p>
                <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#7C3AED' }}>Total</p>
              </div>
            </div>

            {/* Unified Table */}
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }} data-testid="unified-payments-table">
                <thead>
                  <tr style={{ borderBottom: '2px solid #E2E8F0' }}>
                    <th style={{ padding: '10px 8px', textAlign: 'left', color: '#64748B', fontWeight: '600' }}>Date</th>
                    <th style={{ padding: '10px 8px', textAlign: 'left', color: '#64748B', fontWeight: '600' }}>Member</th>
                    <th style={{ padding: '10px 8px', textAlign: 'left', color: '#64748B', fontWeight: '600' }}>Case</th>
                    <th style={{ padding: '10px 8px', textAlign: 'right', color: '#64748B', fontWeight: '600' }}>Amount</th>
                    <th style={{ padding: '10px 8px', textAlign: 'center', color: '#64748B', fontWeight: '600' }}>Source</th>
                    <th style={{ padding: '10px 8px', textAlign: 'center', color: '#64748B', fontWeight: '600' }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {unifiedPayments
                    .filter(p => !unifiedCaseFilter || p.case_number === unifiedCaseFilter)
                    .filter(p => !unifiedFilterMember || p.member_name === unifiedFilterMember)
                    .filter(p => filterByTime(p.date, unifiedFilterTime))
                    .map(p => (
                    <tr key={p.id} style={{ borderBottom: '1px solid #F1F5F9' }}>
                      <td style={{ padding: '10px 8px', color: '#334155' }}>
                        {new Date(p.date).toLocaleDateString()}
                      </td>
                      <td style={{ padding: '10px 8px', color: '#334155', fontWeight: '500' }}>
                        {p.member_name}
                      </td>
                      <td style={{ padding: '10px 8px', color: '#334155' }}>
                        {p.case_number ? `#${p.case_number}` : '-'}
                      </td>
                      <td style={{ padding: '10px 8px', textAlign: 'right', fontWeight: '600', color: '#1E293B' }}>
                        {formatCurrency(p.amount, group?.currency)}
                      </td>
                      <td style={{ padding: '10px 8px', textAlign: 'center' }}>
                        <span style={{
                          padding: '3px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '600',
                          background: p.type === 'platform' ? '#DBEAFE' : '#D1FAE5',
                          color: p.type === 'platform' ? '#1D4ED8' : '#065F46'
                        }}>
                          {p.method}
                        </span>
                      </td>
                      <td style={{ padding: '10px 8px', textAlign: 'center' }}>
                        <span style={{
                          padding: '3px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '600',
                          background: p.status === 'completed' || p.status === 'confirmed' ? '#D1FAE5' : '#FEF3C7',
                          color: p.status === 'completed' || p.status === 'confirmed' ? '#065F46' : '#92400E'
                        }}>
                          {p.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {unifiedPayments.length === 0 && (
                <div style={{ textAlign: 'center', padding: '40px', color: '#94A3B8' }}>
                  No unified payment records found
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Compliance Tab */}
      {activeSubTab === 'compliance' && (
        <div data-testid="compliance-panel">
          <div className="card" style={{ padding: '20px', marginBottom: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <div>
                <h3 style={{ margin: '0 0 4px', color: '#1E293B', fontSize: '16px' }}>Payment Compliance</h3>
                <p style={{ margin: 0, color: '#64748B', fontSize: '13px' }}>
                  Track member payment compliance across active cases
                </p>
              </div>
              <select value={complianceCaseFilter} onChange={(e) => setComplianceCaseFilter(e.target.value)} data-testid="compliance-case-filter"
                style={{ padding: '8px 12px', border: '1px solid #CBD5E1', borderRadius: '6px', fontSize: '13px' }}>
                <option value="">All Cases</option>
                {caseNumbers.map(cn => <option key={cn} value={cn}>Case #{cn}</option>)}
              </select>
            </div>

            {complianceData.cases.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#94A3B8' }}>
                No active cases to track compliance
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {complianceData.cases
                  .filter(c => !complianceCaseFilter || c.case_number === complianceCaseFilter)
                  .map(c => (
                  <div key={c.id || c.case_number} style={{ 
                    border: '1px solid #E2E8F0', borderRadius: '12px', overflow: 'hidden', background: '#fff' 
                  }} data-testid={`compliance-case-${c.case_number}`}>
                    {/* Case Header - clickable for deep dive */}
                    <div 
                      onClick={() => setExpandedComplianceCase(expandedComplianceCase === c.case_number ? null : c.case_number)}
                      style={{ 
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center', 
                        padding: '16px', cursor: 'pointer', background: '#FAFAFA',
                        borderBottom: expandedComplianceCase === c.case_number ? '1px solid #E2E8F0' : 'none'
                      }}
                      data-testid={`compliance-expand-${c.case_number}`}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <span style={{ fontWeight: '700', color: '#1E293B', fontSize: '15px' }}>Case #{c.case_number}</span>
                        {c.description && <span style={{ color: '#64748B', fontSize: '13px' }}>— {c.description}</span>}
                        <div style={{ display: 'flex', gap: '10px', fontSize: '13px' }}>
                          <span style={{ color: '#10B981', fontWeight: '600' }}>{c.paid_count} Paid</span>
                          <span style={{ color: '#EF4444', fontWeight: '600' }}>{c.unpaid_count} Unpaid</span>
                          <span style={{ color: '#64748B' }}>/ {c.total_members}</span>
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <span style={{
                          padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: '700',
                          background: c.compliance_pct >= 80 ? '#D1FAE5' : c.compliance_pct >= 50 ? '#FEF3C7' : '#FEE2E2',
                          color: c.compliance_pct >= 80 ? '#065F46' : c.compliance_pct >= 50 ? '#92400E' : '#991B1B'
                        }}>
                          {c.compliance_pct.toFixed(0)}%
                        </span>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#94A3B8" strokeWidth="2"
                          style={{ transform: expandedComplianceCase === c.case_number ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>
                          <polyline points="6 9 12 15 18 9"></polyline>
                        </svg>
                      </div>
                    </div>
                    
                    {/* Progress bar */}
                    <div style={{ width: '100%', height: '4px', background: '#E2E8F0' }}>
                      <div style={{
                        width: `${c.compliance_pct}%`, height: '100%',
                        background: c.compliance_pct >= 80 ? '#10B981' : c.compliance_pct >= 50 ? '#F59E0B' : '#EF4444',
                        transition: 'width 0.4s ease'
                      }}></div>
                    </div>

                    {/* Deep Dive - Expanded View */}
                    {expandedComplianceCase === c.case_number && (
                      <div style={{ padding: '16px' }} data-testid={`compliance-detail-${c.case_number}`}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                          {/* Paid Members */}
                          <div>
                            <h4 style={{ margin: '0 0 10px', fontSize: '13px', color: '#10B981', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"></polyline></svg>
                              Paid ({c.paid_count})
                            </h4>
                            {c.paid_count === 0 ? (
                              <p style={{ color: '#94A3B8', fontSize: '13px', fontStyle: 'italic' }}>No payments yet</p>
                            ) : (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                {(complianceData.members || [])
                                  .filter(m => m.status === 'active' && !c.unpaid_members.find(u => u.id === m.id))
                                  .map(m => (
                                    <div key={m.id} style={{ 
                                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                      padding: '8px 12px', background: '#F0FDF4', borderRadius: '6px', border: '1px solid #BBF7D0'
                                    }}>
                                      <span style={{ fontSize: '13px', color: '#166534', fontWeight: '500' }}>{m.user_name || m.user_email}</span>
                                      <span style={{ fontSize: '11px', color: '#059669', fontWeight: '600', padding: '2px 8px', background: '#D1FAE5', borderRadius: '10px' }}>PAID</span>
                                    </div>
                                  ))
                                }
                              </div>
                            )}
                          </div>
                          {/* Unpaid Members */}
                          <div>
                            <h4 style={{ margin: '0 0 10px', fontSize: '13px', color: '#EF4444', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#EF4444" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                              Unpaid ({c.unpaid_count})
                            </h4>
                            {c.unpaid_members.length === 0 ? (
                              <p style={{ color: '#10B981', fontSize: '13px', fontStyle: 'italic' }}>All members have paid!</p>
                            ) : (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                {c.unpaid_members.map(m => (
                                  <div key={m.id} style={{ 
                                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                    padding: '8px 12px', background: '#FEF2F2', borderRadius: '6px', border: '1px solid #FECACA'
                                  }}>
                                    <span style={{ fontSize: '13px', color: '#991B1B', fontWeight: '500' }}>{m.name}</span>
                                    <span style={{ fontSize: '11px', color: '#DC2626', fontWeight: '600', padding: '2px 8px', background: '#FEE2E2', borderRadius: '10px' }}>UNPAID</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {activeSubTab === 'imported' && (
        <div className="imported-payments-section">
          {/* Import Button and Info */}
          <div className="card" style={{ marginBottom: '20px', padding: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
              <div>
                <h3 style={{ margin: '0 0 8px 0', color: '#374151' }}>📥 Import Historical Payments</h3>
                <p style={{ margin: 0, color: '#6B7280', fontSize: '14px' }}>
                  Import your group's payment history from CSV, Excel, or PDF files
                </p>
              </div>
              <button
                onClick={() => setShowImportModal(true)}
                className="btn btn-primary"
                data-testid="open-import-modal"
                style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
              >
                <span>📤</span> Import File
              </button>
            </div>
          </div>

          {/* Import Batches Summary */}
          {importBatches.length > 0 && (
            <div className="card" style={{ marginBottom: '20px', padding: '20px' }}>
              <h3 style={{ margin: '0 0 16px 0', color: '#374151' }}>📁 Import History</h3>
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '16px' }}>
                <select
                  value={selectedBatchFilter}
                  onChange={(e) => {
                    setSelectedBatchFilter(e.target.value);
                    setTimeout(loadImportedPayments, 100);
                  }}
                  style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #D1D5DB', minWidth: '200px' }}
                >
                  <option value="">All Imports</option>
                  {importBatches.map(batch => (
                    <option key={batch.id} value={batch.id}>
                      {batch.filename} ({batch.total_records} records)
                    </option>
                  ))}
                </select>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '12px' }}>
                {importBatches.map(batch => (
                  <div key={batch.id} style={{
                    background: '#F9FAFB',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    padding: '16px'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div>
                        <p style={{ margin: '0 0 4px 0', fontWeight: '600', color: '#374151', fontSize: '14px' }}>
                          {batch.filename}
                        </p>
                        <p style={{ margin: '0 0 4px 0', color: '#6B7280', fontSize: '12px' }}>
                          {batch.total_records} records • {formatCurrency(batch.total_amount || 0, group?.currency)}
                        </p>
                        <p style={{ margin: 0, color: '#9CA3AF', fontSize: '11px' }}>
                          Imported {new Date(batch.imported_at).toLocaleDateString()} by {batch.imported_by_name}
                        </p>
                      </div>
                      <button
                        onClick={() => handleDeleteBatch(batch.id, batch.filename)}
                        className="btn-delete"
                        style={{ padding: '4px 8px', fontSize: '12px' }}
                        title="Delete this import"
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Imported Payments Table */}
          {loadingImported ? (
            <div className="card" style={{ padding: '40px', textAlign: 'center' }}>
              <p>Loading imported payments...</p>
            </div>
          ) : importedPayments.length === 0 ? (
            <div className="empty-state card">
              <h3>No imported payments yet</h3>
              <p>Click "Import File" to upload your historical payment data</p>
            </div>
          ) : (
            <div className="card" style={{ padding: '20px' }}>
              <div style={{ marginBottom: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3 style={{ margin: 0 }}>Imported Records ({importedPayments.length})</h3>
              </div>
              <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                <table className="data-table" style={{ minWidth: '900px' }} data-testid="imported-payments-table">
                  <thead>
                    <tr>
                      <th style={{ minWidth: '100px', whiteSpace: 'nowrap' }}>Date</th>
                      <th style={{ minWidth: '140px' }}>Member Name</th>
                      <th style={{ minWidth: '160px' }}>Email</th>
                      <th style={{ minWidth: '110px', whiteSpace: 'nowrap' }}>Phone</th>
                      <th style={{ minWidth: '90px', whiteSpace: 'nowrap' }}>Amount</th>
                      <th style={{ minWidth: '100px' }}>Method</th>
                      <th style={{ minWidth: '120px' }}>Reference</th>
                      <th style={{ minWidth: '70px' }}>Source</th>
                    </tr>
                  </thead>
                  <tbody>
                    {importedPayments.map((payment) => (
                      <tr key={payment.id}>
                        <td style={{ whiteSpace: 'nowrap' }}>{payment.payment_date || 'N/A'}</td>
                        <td style={{ fontWeight: '500' }}>{payment.member_name}</td>
                        <td style={{ fontSize: '12px', maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={payment.member_email || ''}>{payment.member_email || '-'}</td>
                        <td style={{ fontSize: '12px', whiteSpace: 'nowrap' }}>{payment.member_phone || '-'}</td>
                        <td style={{ fontWeight: '600', color: '#059669', whiteSpace: 'nowrap' }}>{formatCurrency(payment.amount, group?.currency)}</td>
                        <td>
                          <span style={{ 
                            background: '#E0E7FF', 
                            color: '#3730A3', 
                            padding: '2px 8px', 
                            borderRadius: '4px',
                            fontSize: '11px',
                            whiteSpace: 'nowrap',
                            display: 'inline-block'
                          }}>
                            {payment.payment_method || 'N/A'}
                          </span>
                        </td>
                        <td style={{ fontSize: '12px', color: '#6B7280', maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={payment.reference_number || ''}>{payment.reference_number || '-'}</td>
                        <td>
                          <span style={{ 
                            background: payment.source_type === 'csv' ? '#DCFCE7' : payment.source_type === 'excel' ? '#DBEAFE' : '#FEF3C7',
                            color: payment.source_type === 'csv' ? '#166534' : payment.source_type === 'excel' ? '#1E40AF' : '#92400E',
                            padding: '2px 6px',
                            borderRadius: '4px',
                            fontSize: '10px',
                            textTransform: 'uppercase',
                            whiteSpace: 'nowrap',
                            display: 'inline-block'
                          }}>
                            {payment.source_type}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* M-Pesa Transactions Tab */}
      {activeSubTab === 'mpesa' && (
        <MpesaTransactionsPanel groupId={groupId} group={group} members={members} />
      )}


      {/* Import Modal */}
      {showImportModal && (
        <div className="modal-overlay" onClick={closeImportModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <h2>📥 Import Payment History</h2>
              <button className="close-button" onClick={closeImportModal}>×</button>
            </div>
            
            <div className="modal-body">
              <div style={{ 
                background: '#EFF6FF', 
                border: '1px solid #BFDBFE', 
                borderRadius: '8px', 
                padding: '16px',
                marginBottom: '20px'
              }}>
                <h4 style={{ margin: '0 0 8px 0', color: '#1E40AF' }}>📋 Supported File Formats</h4>
                <ul style={{ margin: 0, paddingLeft: '20px', color: '#374151', fontSize: '14px' }}>
                  <li><strong>CSV</strong> - Comma-separated values</li>
                  <li><strong>Excel</strong> - .xlsx or .xls files</li>
                  <li><strong>PDF</strong> - Tables will be extracted automatically</li>
                </ul>
              </div>

              <div style={{ 
                background: '#FEF3C7', 
                border: '1px solid #FCD34D', 
                borderRadius: '8px', 
                padding: '16px',
                marginBottom: '20px'
              }}>
                <h4 style={{ margin: '0 0 8px 0', color: '#92400E' }}>📝 Expected Columns</h4>
                <p style={{ margin: 0, color: '#78350F', fontSize: '13px' }}>
                  Your file should include columns like: <strong>Name/Member</strong>, <strong>Amount/Payment</strong>, <strong>Date</strong>. 
                  Optional: Email, Phone, Reference, Method.
                </p>
              </div>

              <div className="form-group">
                <label style={{ fontWeight: '600', marginBottom: '8px', display: 'block' }}>Select File</label>
                <input
                  type="file"
                  accept=".csv,.xlsx,.xls,.pdf"
                  onChange={handleFileSelect}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px dashed #D1D5DB',
                    borderRadius: '8px',
                    cursor: 'pointer'
                  }}
                  data-testid="import-file-input"
                />
                {selectedFile && (
                  <p style={{ margin: '8px 0 0 0', color: '#059669', fontSize: '14px' }}>
                    ✓ Selected: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)
                  </p>
                )}
              </div>

              {importResult && (
                <div style={{ 
                  marginTop: '20px',
                  padding: '16px',
                  background: importResult.records_imported > 0 ? '#ECFDF5' : '#FEF2F2',
                  border: `1px solid ${importResult.records_imported > 0 ? '#A7F3D0' : '#FECACA'}`,
                  borderRadius: '8px'
                }}>
                  <h4 style={{ margin: '0 0 8px 0', color: importResult.records_imported > 0 ? '#065F46' : '#991B1B' }}>
                    {importResult.records_imported > 0 ? '✅ Import Results' : '❌ Import Failed'}
                  </h4>
                  {importResult.records_imported > 0 && (
                    <>
                      <p style={{ margin: '4px 0', color: '#374151' }}>
                        <strong>{importResult.records_imported}</strong> records imported
                      </p>
                      <p style={{ margin: '4px 0', color: '#374151' }}>
                        Total amount: <strong>${importResult.total_amount?.toFixed(2)}</strong>
                      </p>
                    </>
                  )}
                  {importResult.error_count > 0 && (
                    <div style={{ marginTop: '12px' }}>
                      <p style={{ margin: '0 0 4px 0', color: '#B91C1C', fontSize: '13px' }}>
                        {importResult.error_count} rows had errors:
                      </p>
                      <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '12px', color: '#7F1D1D' }}>
                        {importResult.errors?.slice(0, 5).map((err, i) => (
                          <li key={i}>{err}</li>
                        ))}
                        {importResult.error_count > 5 && (
                          <li>...and {importResult.error_count - 5} more errors</li>
                        )}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={closeImportModal}>
                Cancel
              </button>
              <button 
                className="btn btn-primary" 
                onClick={handleImport}
                disabled={!selectedFile || importing}
                data-testid="confirm-import-btn"
              >
                {importing ? 'Importing...' : '📥 Import File'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bank Transactions Tab */}
      {activeSubTab === 'bank' && (
        <BankTransactionsPanel
          groupId={groupId}
          bankConnection={bankConnection}
          bankTransactions={bankTransactions}
          bankTxFilter={bankTxFilter}
          setBankTxFilter={setBankTxFilter}
          txSummary={txSummary}
          syncing={syncing}
          loadingBankTx={loadingBankTx}
          onSync={handleSyncTransactions}
          onConfirm={handleConfirmTx}
          onDismiss={handleDismissTx}
          onMatch={(tx) => { setMatchModalTx(tx); setMatchCaseId(''); }}
          onDisconnect={handleDisconnectBank}
          connecting={connecting}
          linkToken={linkToken}
          initPlaidLink={initPlaidLink}
          onPlaidSuccess={handlePlaidSuccess}
          paymentInstructions={paymentInstructions}
          setPaymentInstructions={setPaymentInstructions}
          showPaymentInstructionsModal={showPaymentInstructionsModal}
          setShowPaymentInstructionsModal={setShowPaymentInstructionsModal}
          onSavePaymentInstructions={handleSavePaymentInstructions}
          caseNumbers={caseNumbers}
          bankClaims={bankClaims}
          prepaidSettings={prepaidSettings}
          onTogglePrepaid={handleTogglePrepaid}
          parsingFees={parsingFees}
          formatCurrency={formatCurrency}
          members={members}
          bankFilterCase={bankFilterCase}
          setBankFilterCase={setBankFilterCase}
          bankFilterMember={bankFilterMember}
          setBankFilterMember={setBankFilterMember}
          bankFilterTime={bankFilterTime}
          setBankFilterTime={setBankFilterTime}
          filterByTime={filterByTime}
        />
      )}

      {/* Manual Match Modal */}
      {matchModalTx && (
        <div className="modal-overlay" onClick={() => setMatchModalTx(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '420px' }}>
            <div className="modal-header">
              <h2>Match Transaction to Case</h2>
              <button className="modal-close" onClick={() => setMatchModalTx(null)}>&times;</button>
            </div>
            <div className="modal-body">
              <div style={{ background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: '8px', padding: '12px', marginBottom: '16px' }}>
                <p style={{ margin: 0, fontSize: '13px', color: '#166534' }}>
                  <strong>{formatCurrency(matchModalTx.amount, group?.currency)}</strong> from <strong>{matchModalTx.name || 'Unknown'}</strong>
                </p>
                <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#15803D' }}>
                  {matchModalTx.date} | {matchModalTx.original_description || ''}
                </p>
              </div>
              <div className="form-group">
                <label>Select Case</label>
                <select value={matchCaseId} onChange={e => setMatchCaseId(e.target.value)}
                  data-testid="match-case-select"
                  style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #D1D5DB' }}>
                  <option value="">Choose a case...</option>
                  {bankClaims && bankClaims.map(c => (
                    <option key={c.id} value={c.id}>{c.case_number || c.id?.slice(0,8)} - {c.title || c.reason || 'Case'} {c.amount ? `(${formatCurrency(c.amount, group?.currency)})` : ''}</option>
                  ))}
                </select>
              </div>
              <div style={{ display: 'flex', gap: '10px', marginTop: '16px' }}>
                <button className="btn btn-secondary" onClick={() => setMatchModalTx(null)} style={{ flex: 1 }}>Cancel</button>
                <button className="btn btn-primary" onClick={handleManualMatch} disabled={!matchCaseId} style={{ flex: 1 }} data-testid="confirm-match-btn">
                  Match
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function BankTransactionsPanel({ groupId, bankConnection, bankTransactions, bankTxFilter, setBankTxFilter, txSummary, syncing, loadingBankTx, onSync, onConfirm, onDismiss, onMatch, onDisconnect, connecting, linkToken, initPlaidLink, onPlaidSuccess, paymentInstructions, setPaymentInstructions, showPaymentInstructionsModal, setShowPaymentInstructionsModal, onSavePaymentInstructions, caseNumbers, bankClaims, prepaidSettings, onTogglePrepaid, parsingFees, formatCurrency, members, bankFilterCase, setBankFilterCase, bankFilterMember, setBankFilterMember, bankFilterTime, setBankFilterTime, filterByTime }) {
  
  const PlaidLinkButton = ({ token, onSuccess }) => {
    const receivedRedirectUri = window.location.href.includes('oauth_state_id') ? window.location.href : undefined;
    const { open, ready } = usePlaidLink({
      token,
      onSuccess: (public_token, metadata) => onSuccess(public_token, metadata),
      onExit: (err) => {
        if (err) {
          console.error('[PlaidLink] Exit error:', err);
          toast.error(`Bank connection error: ${err.display_message || err.error_message || 'Please try again'}`);
        }
      },
      receivedRedirectUri
    });
    return (
      <button onClick={() => open()} disabled={!ready || connecting} data-testid="plaid-connect-btn"
        style={{ padding: '12px 28px', borderRadius: '10px', border: 'none', background: '#059669', color: '#fff', fontSize: '15px', fontWeight: '700', cursor: 'pointer' }}>
        {connecting ? 'Connecting...' : 'Connect Bank Account'}
      </button>
    );
  };

  const generateParsingFeeReceipt = (period) => {
    if (!parsingFees?.fees?.length) {
      toast.error('No parsing fee records to generate receipt');
      return;
    }

    const now = new Date();
    let filteredFees = parsingFees.fees;
    let periodLabel = 'All Time';

    if (period === 'month') {
      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
      filteredFees = parsingFees.fees.filter(f => new Date(f.created_at) >= monthStart);
      periodLabel = now.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    } else if (period === 'quarter') {
      const qStart = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1);
      filteredFees = parsingFees.fees.filter(f => new Date(f.created_at) >= qStart);
      const qNum = Math.floor(now.getMonth() / 3) + 1;
      periodLabel = `Q${qNum} ${now.getFullYear()}`;
    }

    if (filteredFees.length === 0) {
      toast.info(`No parsing fees found for ${periodLabel}`);
      return;
    }

    const doc = new jsPDF();
    const pw = doc.internal.pageSize.width;

    // Header
    doc.setFillColor(30, 41, 59);
    doc.rect(0, 0, pw, 45, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(22);
    doc.text('Plaid Parsing Fee Receipt', pw / 2, 20, { align: 'center' });
    doc.setFontSize(11);
    doc.text(`Period: ${periodLabel}`, pw / 2, 30, { align: 'center' });
    doc.setFontSize(9);
    doc.text(`Generated: ${now.toLocaleString()}`, pw / 2, 38, { align: 'center' });

    // Group info
    doc.setTextColor(30, 41, 59);
    doc.setFontSize(13);
    doc.text('Group Details', 14, 58);

    const totalFees = filteredFees.reduce((s, f) => s + (f.fee_amount || 0), 0);
    const totalParsed = filteredFees.reduce((s, f) => s + (f.transaction_amount || 0), 0);

    const feeRatePct = parsingFees.fee_percentage || 1;

    autoTable(doc, {
      startY: 63,
      head: [['Field', 'Value']],
      body: [
        ['Bank', bankConnection?.institution_name || 'N/A'],
        ['Fee Rate', `${feeRatePct}% per confirmed transaction`],
        ['Transactions Billed', filteredFees.length.toString()],
        ['Total Amount Parsed', formatCurrency(totalParsed, selectedGroup?.currency)],
        ['Total Fees Charged', formatCurrency(totalFees, selectedGroup?.currency)],
      ],
      theme: 'striped',
      headStyles: { fillColor: [30, 41, 59] },
      styles: { fontSize: 10 },
      margin: { left: 14, right: 14 }
    });

    // Fee details table
    const detailY = (doc.lastAutoTable?.finalY || 110) + 12;
    doc.setFontSize(13);
    doc.text('Fee Details', 14, detailY);

    const rows = filteredFees.map(f => [
      new Date(f.created_at).toLocaleDateString(),
      f.transaction_id ? f.transaction_id.substring(0, 12) + '...' : 'N/A',
      formatCurrency(f.transaction_amount || 0, selectedGroup?.currency),
      `${(f.fee_percentage || feeRatePct)}%`,
      formatCurrency(f.fee_amount || 0, selectedGroup?.currency),
      (f.status || '').toUpperCase()
    ]);

    autoTable(doc, {
      startY: detailY + 5,
      head: [['Date', 'Transaction', 'Tx Amount', 'Rate', 'Fee', 'Status']],
      body: rows,
      theme: 'striped',
      headStyles: { fillColor: [30, 41, 59] },
      styles: { fontSize: 9 },
      columnStyles: {
        0: { cellWidth: 28 },
        3: { cellWidth: 18, halign: 'center' },
        4: { halign: 'right', fontStyle: 'bold' },
        5: { cellWidth: 25, halign: 'center' }
      },
      margin: { left: 14, right: 14 }
    });

    // Summary box
    const sumY = (doc.lastAutoTable?.finalY || 200) + 15;
    doc.setFillColor(241, 245, 249);
    doc.roundedRect(14, sumY, pw - 28, 30, 3, 3, 'F');
    doc.setFontSize(11);
    doc.setTextColor(30, 41, 59);
    doc.text('Total Fees:', 24, sumY + 12);
    doc.setFontSize(16);
    doc.setFont(undefined, 'bold');
    doc.text(formatCurrency(totalFees, selectedGroup?.currency), pw - 24, sumY + 12, { align: 'right' });
    doc.setFont(undefined, 'normal');
    doc.setFontSize(9);
    doc.setTextColor(100, 116, 139);
    doc.text(`${filteredFees.length} transaction(s) at ${feeRatePct}% fee rate on ${formatCurrency(totalParsed, selectedGroup?.currency)} total parsed`, 24, sumY + 23);

    // Footer
    const pages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pages; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(148, 163, 184);
      doc.text('ChamaLink - Plaid Parsing Fee Receipt', 14, doc.internal.pageSize.height - 10);
      doc.text(`Page ${i} of ${pages}`, pw - 14, doc.internal.pageSize.height - 10, { align: 'right' });
    }

    doc.save(`plaid_parsing_fees_${periodLabel.replace(/\s+/g, '_').toLowerCase()}.pdf`);
    toast.success('Receipt downloaded');
  };

  return (
    <div data-testid="bank-transactions-panel">
      {/* Payment Instructions Section */}
      <div className="card" style={{ padding: '20px', marginBottom: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <h3 style={{ margin: 0, fontSize: '16px', color: '#1E293B' }}>Alternative Payment Methods</h3>
          <button className="btn btn-secondary" onClick={() => setShowPaymentInstructionsModal(true)} data-testid="edit-payment-instructions-btn"
            style={{ padding: '6px 14px', fontSize: '13px' }}>Edit</button>
        </div>
        {(paymentInstructions.zelle_email || paymentInstructions.cashapp_tag) ? (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            {(paymentInstructions.zelle_email || paymentInstructions.zelle_phone) && (
              <div style={{ padding: '14px', borderRadius: '10px', background: '#EDE9FE', border: '1px solid #C4B5FD' }}>
                <p style={{ margin: '0 0 4px', fontSize: '13px', fontWeight: '700', color: '#5B21B6' }}>Zelle</p>
                {paymentInstructions.zelle_name && <p style={{ margin: '0 0 2px', fontSize: '13px', color: '#6D28D9' }}>{paymentInstructions.zelle_name}</p>}
                <p style={{ margin: 0, fontSize: '14px', color: '#4C1D95', fontWeight: '600' }}>
                  {paymentInstructions.zelle_email || paymentInstructions.zelle_phone}
                </p>
              </div>
            )}
            {paymentInstructions.cashapp_tag && (
              <div style={{ padding: '14px', borderRadius: '10px', background: '#ECFDF5', border: '1px solid #A7F3D0' }}>
                <p style={{ margin: '0 0 4px', fontSize: '13px', fontWeight: '700', color: '#065F46' }}>Cash App</p>
                {paymentInstructions.cashapp_name && <p style={{ margin: '0 0 2px', fontSize: '13px', color: '#047857' }}>{paymentInstructions.cashapp_name}</p>}
                <p style={{ margin: 0, fontSize: '14px', color: '#064E3B', fontWeight: '600' }}>{paymentInstructions.cashapp_tag}</p>
              </div>
            )}
          </div>
        ) : (
          <p style={{ color: '#9CA3AF', fontSize: '13px', margin: 0 }}>No payment instructions set. Click Edit to add Zelle/Cash App details.</p>
        )}
        {paymentInstructions.additional_instructions && (
          <p style={{ marginTop: '10px', padding: '10px', background: '#F3F4F6', borderRadius: '8px', fontSize: '13px', color: '#374151' }}>
            {paymentInstructions.additional_instructions}
          </p>
        )}
      </div>

      {/* Bank Connection Section */}
      <div className="card" style={{ padding: '20px', marginBottom: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h3 style={{ margin: 0, fontSize: '16px', color: '#1E293B' }}>Bank Connection</h3>
          {bankConnection && (
            <button className="btn btn-secondary" onClick={onDisconnect} style={{ padding: '6px 14px', fontSize: '12px', color: '#EF4444', borderColor: '#FCA5A5' }} data-testid="disconnect-bank-btn">
              Disconnect
            </button>
          )}
        </div>
        
        {bankConnection ? (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '14px', background: '#F0FDF4', borderRadius: '10px', border: '1px solid #BBF7D0' }}>
              <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: '#22C55E', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <span style={{ color: '#fff', fontSize: '16px' }}>$</span>
              </div>
              <div>
                <p style={{ margin: 0, fontWeight: '600', color: '#166534' }}>{bankConnection.institution_name || 'Connected Bank'}</p>
                <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#15803D' }}>Connected {new Date(bankConnection.connected_at).toLocaleDateString()}</p>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '10px', marginTop: '12px' }}>
              <button className="btn btn-primary" onClick={onSync} disabled={syncing} data-testid="sync-transactions-btn"
                style={{ padding: '10px 20px', background: '#059669' }}>
                {syncing ? 'Syncing...' : 'Sync Transactions'}
              </button>
            </div>
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '30px 20px' }}>
            <p style={{ color: '#6B7280', marginBottom: '16px', fontSize: '14px' }}>
              Connect your group's bank account to automatically detect Zelle, Cash App, and other incoming payments.
            </p>
            {linkToken ? (
              <PlaidLinkButton token={linkToken} onSuccess={onPlaidSuccess} />
            ) : (
              <button className="btn btn-primary" onClick={initPlaidLink} data-testid="init-plaid-btn"
                style={{ padding: '12px 28px', borderRadius: '10px', border: 'none', background: '#059669', color: '#fff', fontSize: '15px', fontWeight: '700', cursor: 'pointer' }}>
                Set Up Bank Connection
              </button>
            )}
          </div>
        )}
      </div>

      {/* Prepaid Service Control */}
      <div className="card" style={{ padding: '20px', marginBottom: '20px' }} data-testid="prepaid-service-control">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '16px', color: '#1E293B' }}>Prepaid Services</h3>
            <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#64748B' }}>
              Allow members to use prepaid auto-payment for case contributions
            </p>
          </div>
          <label style={{ position: 'relative', display: 'inline-block', width: '48px', height: '26px' }}>
            <input
              type="checkbox"
              checked={!prepaidSettings.prepaid_disabled}
              onChange={onTogglePrepaid}
              data-testid="prepaid-toggle-leadership"
              style={{ opacity: 0, width: 0, height: 0 }}
            />
            <span style={{
              position: 'absolute', cursor: 'pointer', top: 0, left: 0, right: 0, bottom: 0,
              backgroundColor: prepaidSettings.prepaid_disabled ? '#CBD5E1' : '#22C55E',
              borderRadius: '26px', transition: '0.3s'
            }}>
              <span style={{
                position: 'absolute', height: '20px', width: '20px', left: prepaidSettings.prepaid_disabled ? '3px' : '25px',
                bottom: '3px', backgroundColor: '#fff', borderRadius: '50%', transition: '0.3s'
              }}></span>
            </span>
          </label>
        </div>
        <div style={{ marginTop: '10px', padding: '10px 14px', borderRadius: '8px', fontSize: '13px',
          background: prepaidSettings.prepaid_disabled ? '#FEF2F2' : '#F0FDF4',
          color: prepaidSettings.prepaid_disabled ? '#991B1B' : '#166534',
          border: `1px solid ${prepaidSettings.prepaid_disabled ? '#FECACA' : '#BBF7D0'}`
        }}>
          {prepaidSettings.prepaid_disabled
            ? 'Prepaid services are disabled. Members cannot use auto-payment from their prepaid balance.'
            : 'Prepaid services are active. Members with sufficient balance will have case payments auto-deducted.'}
        </div>
      </div>

      {/* Plaid Parsing Fee Summary */}
      {bankConnection && parsingFees && (
        <div className="card" style={{ padding: '20px', marginBottom: '20px' }} data-testid="plaid-parsing-fees">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
            <div>
              <h3 style={{ margin: '0 0 4px', fontSize: '16px', color: '#1E293B' }}>Plaid Parsing Fee</h3>
              <p style={{ margin: 0, fontSize: '13px', color: '#64748B' }}>
                {parsingFees.fee_percentage || 1}% fee on the total amount of each confirmed bank transaction match
              </p>
            </div>
            {parsingFees.fees?.length > 0 && (
              <div style={{ display: 'flex', gap: '6px' }}>
                <button
                  onClick={() => generateParsingFeeReceipt('month')}
                  className="btn btn-secondary"
                  style={{ padding: '6px 12px', fontSize: '12px' }}
                  data-testid="download-receipt-month"
                >
                  This Month
                </button>
                <button
                  onClick={() => generateParsingFeeReceipt('quarter')}
                  className="btn btn-secondary"
                  style={{ padding: '6px 12px', fontSize: '12px' }}
                  data-testid="download-receipt-quarter"
                >
                  This Quarter
                </button>
                <button
                  onClick={() => generateParsingFeeReceipt('all')}
                  className="btn btn-primary"
                  style={{ padding: '6px 12px', fontSize: '12px' }}
                  data-testid="download-receipt-all"
                >
                  Download Receipt
                </button>
              </div>
            )}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginBottom: '12px' }}>
            <div style={{ padding: '14px', borderRadius: '10px', background: '#F8FAFC', border: '1px solid #E2E8F0', textAlign: 'center' }}>
              <p style={{ margin: 0, fontSize: '22px', fontWeight: '700', color: '#1E293B' }}>
                {formatCurrency ? formatCurrency(parsingFees.total_fees_charged || 0) : `$${(parsingFees.total_fees_charged || 0).toFixed(2)}`}
              </p>
              <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#64748B' }}>Total Fees Charged</p>
            </div>
            <div style={{ padding: '14px', borderRadius: '10px', background: '#F8FAFC', border: '1px solid #E2E8F0', textAlign: 'center' }}>
              <p style={{ margin: 0, fontSize: '22px', fontWeight: '700', color: '#1E293B' }}>
                {formatCurrency ? formatCurrency(parsingFees.total_amount_parsed || 0) : `$${(parsingFees.total_amount_parsed || 0).toFixed(2)}`}
              </p>
              <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#64748B' }}>Total Amount Parsed</p>
            </div>
            <div style={{ padding: '14px', borderRadius: '10px', background: '#F8FAFC', border: '1px solid #E2E8F0', textAlign: 'center' }}>
              <p style={{ margin: 0, fontSize: '22px', fontWeight: '700', color: '#1E293B' }}>{parsingFees.fees?.length || 0}</p>
              <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#64748B' }}>Transactions Billed</p>
            </div>
          </div>
          {parsingFees.fees?.length > 0 && (
            <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #E2E8F0' }}>
                    <th style={{ padding: '8px', textAlign: 'left', color: '#64748B', fontWeight: '600' }}>Date</th>
                    <th style={{ padding: '8px', textAlign: 'right', color: '#64748B', fontWeight: '600' }}>Tx Amount</th>
                    <th style={{ padding: '8px', textAlign: 'right', color: '#64748B', fontWeight: '600' }}>Fee (1%)</th>
                    <th style={{ padding: '8px', textAlign: 'center', color: '#64748B', fontWeight: '600' }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {parsingFees.fees.map(fee => (
                    <tr key={fee.id} style={{ borderBottom: '1px solid #F1F5F9' }}>
                      <td style={{ padding: '8px', color: '#334155' }}>{new Date(fee.created_at).toLocaleDateString()}</td>
                      <td style={{ padding: '8px', textAlign: 'right', color: '#334155' }}>{formatCurrency(fee.transaction_amount, group?.currency)}</td>
                      <td style={{ padding: '8px', textAlign: 'right', fontWeight: '600', color: '#1E293B' }}>{formatCurrency(fee.fee_amount, group?.currency)}</td>
                      <td style={{ padding: '8px', textAlign: 'center' }}>
                        <span style={{
                          padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: '600',
                          background: fee.status === 'charged' ? '#D1FAE5' : fee.status === 'unbilled' ? '#FEF3C7' : '#FEE2E2',
                          color: fee.status === 'charged' ? '#065F46' : fee.status === 'unbilled' ? '#92400E' : '#991B1B'
                        }}>
                          {fee.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Transaction Summary */}
      {bankConnection && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '20px' }}>
            {[
              { label: 'Unmatched', count: txSummary.unmatched || 0, color: '#EF4444', bg: '#FEF2F2' },
              { label: 'Auto-Matched', count: txSummary.auto_matched || 0, color: '#F59E0B', bg: '#FFFBEB' },
              { label: 'Manually Matched', count: txSummary.manually_matched || 0, color: '#3B82F6', bg: '#EFF6FF' },
              { label: 'Confirmed', count: txSummary.confirmed || 0, color: '#22C55E', bg: '#F0FDF4' },
            ].map(s => (
              <div key={s.label} className="card" style={{ padding: '14px', textAlign: 'center', background: s.bg, border: `1px solid ${s.color}20` }}>
                <p style={{ margin: 0, fontSize: '24px', fontWeight: '700', color: s.color }}>{s.count}</p>
                <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#6B7280' }}>{s.label}</p>
              </div>
            ))}
          </div>

          {/* Filter and Transaction List */}
          <div className="card" style={{ padding: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0, fontSize: '16px', color: '#1E293B' }}>Transactions</h3>
              <div style={{ display: 'flex', gap: '6px' }}>
                {['all', 'unmatched', 'matched', 'confirmed'].map(f => (
                  <button key={f} onClick={() => setBankTxFilter(f)} data-testid={`tx-filter-${f}`}
                    style={{
                      padding: '6px 12px', borderRadius: '6px', border: '1px solid #E5E7EB', fontSize: '12px', fontWeight: '600', cursor: 'pointer',
                      background: bankTxFilter === f ? '#1F2937' : '#fff', color: bankTxFilter === f ? '#fff' : '#374151'
                    }}>
                    {f.charAt(0).toUpperCase() + f.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            {/* Case / Member / Time filters */}
            <div style={{ display: 'flex', gap: '8px', marginBottom: '14px', flexWrap: 'wrap' }} data-testid="bank-tx-filters">
              <select value={bankFilterCase} onChange={(e) => setBankFilterCase(e.target.value)} data-testid="bank-filter-case"
                style={{ padding: '7px 10px', border: '1px solid #CBD5E1', borderRadius: '6px', fontSize: '12px', minWidth: '130px' }}>
                <option value="">All Cases</option>
                {caseNumbers.map(cn => <option key={cn} value={cn}>Case #{cn}</option>)}
              </select>
              <select value={bankFilterMember} onChange={(e) => setBankFilterMember(e.target.value)} data-testid="bank-filter-member"
                style={{ padding: '7px 10px', border: '1px solid #CBD5E1', borderRadius: '6px', fontSize: '12px', minWidth: '140px' }}>
                <option value="">All Members</option>
                {(members || []).filter(m => m.status === 'active').map(m => <option key={m.id} value={m.id}>{m.user_name || m.user_email}</option>)}
              </select>
              <select value={bankFilterTime} onChange={(e) => setBankFilterTime(e.target.value)} data-testid="bank-filter-time"
                style={{ padding: '7px 10px', border: '1px solid #CBD5E1', borderRadius: '6px', fontSize: '12px' }}>
                <option value="alltime">All Time</option>
                <option value="month">This Month</option>
                <option value="quarter">This Quarter</option>
                <option value="annual">This Year</option>
              </select>
              {(bankFilterCase || bankFilterMember || bankFilterTime !== 'alltime') && (
                <button onClick={() => { setBankFilterCase(''); setBankFilterMember(''); setBankFilterTime('alltime'); }}
                  style={{ padding: '7px 10px', border: 'none', background: '#FEE2E2', color: '#DC2626', borderRadius: '6px', fontSize: '12px', cursor: 'pointer', fontWeight: '600' }}>
                  Clear Filters
                </button>
              )}
            </div>

            {loadingBankTx ? (
              <p style={{ textAlign: 'center', color: '#9CA3AF', padding: '30px' }}>Loading transactions...</p>
            ) : bankTransactions.length === 0 ? (
              <p style={{ textAlign: 'center', color: '#9CA3AF', padding: '30px' }}>
                {bankTxFilter === 'all' ? 'No transactions yet. Click "Sync Transactions" to fetch.' : `No ${bankTxFilter} transactions.`}
              </p>
            ) : (() => {
              const filteredBankTx = bankTransactions.filter(tx => {
                if (bankFilterCase && tx.matched_case_id !== bankFilterCase) return false;
                if (bankFilterMember && tx.matched_member_id !== bankFilterMember) return false;
                if (!filterByTime(tx.date, bankFilterTime)) return false;
                return true;
              });
              return filteredBankTx.length === 0 ? (
                <p style={{ textAlign: 'center', color: '#9CA3AF', padding: '30px' }}>No transactions match the selected filters.</p>
              ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: '#F9FAFB', borderBottom: '2px solid #E5E7EB' }}>
                      <th style={{ padding: '10px 12px', textAlign: 'left', fontSize: '12px', color: '#6B7280', fontWeight: '600' }}>Date</th>
                      <th style={{ padding: '10px 12px', textAlign: 'left', fontSize: '12px', color: '#6B7280', fontWeight: '600' }}>Description</th>
                      <th style={{ padding: '10px 12px', textAlign: 'right', fontSize: '12px', color: '#6B7280', fontWeight: '600' }}>Amount</th>
                      <th style={{ padding: '10px 12px', textAlign: 'left', fontSize: '12px', color: '#6B7280', fontWeight: '600' }}>Matched To</th>
                      <th style={{ padding: '10px 12px', textAlign: 'center', fontSize: '12px', color: '#6B7280', fontWeight: '600' }}>Status</th>
                      <th style={{ padding: '10px 12px', textAlign: 'center', fontSize: '12px', color: '#6B7280', fontWeight: '600' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredBankTx.map(tx => (
                      <tr key={tx.id} style={{ borderBottom: '1px solid #F3F4F6' }} data-testid={`bank-tx-${tx.id}`}>
                        <td style={{ padding: '10px 12px', fontSize: '13px', color: '#374151' }}>{tx.date}</td>
                        <td style={{ padding: '10px 12px', fontSize: '13px', color: '#1E293B', maxWidth: '250px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          <span style={{ fontWeight: '500' }}>{tx.name}</span>
                          {tx.original_description && tx.original_description !== tx.name && (
                            <span style={{ display: 'block', fontSize: '11px', color: '#9CA3AF' }}>{tx.original_description}</span>
                          )}
                        </td>
                        <td style={{ padding: '10px 12px', textAlign: 'right', fontSize: '14px', fontWeight: '600', color: tx.is_incoming ? '#059669' : '#374151' }}>
                          {tx.is_incoming ? '+' : ''}{formatCurrency(tx.amount, group?.currency)}
                        </td>
                        <td style={{ padding: '10px 12px', fontSize: '13px', color: '#374151' }}>
                          {tx.matched_case_title ? (
                            <div>
                              <span style={{ fontWeight: '500' }}>{tx.matched_case_title}</span>
                              {tx.matched_member_name && <span style={{ display: 'block', fontSize: '11px', color: '#6B7280' }}>{tx.matched_member_name}</span>}
                              {tx.match_method && (
                                <span style={{ fontSize: '10px', padding: '1px 5px', borderRadius: '3px', background: tx.match_confidence >= 90 ? '#DCFCE7' : tx.match_confidence >= 70 ? '#FEF3C7' : '#FEE2E2', color: tx.match_confidence >= 90 ? '#166534' : tx.match_confidence >= 70 ? '#92400E' : '#991B1B' }}>
                                  {tx.match_method.replace('_', ' ')} ({tx.match_confidence}%)
                                </span>
                              )}
                            </div>
                          ) : <span style={{ color: '#9CA3AF' }}>-</span>}
                        </td>
                        <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                          <span style={{
                            padding: '3px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '600',
                            background: tx.status === 'confirmed' ? '#DCFCE7' : tx.status === 'unmatched' ? '#FEE2E2' : tx.status === 'dismissed' ? '#F3F4F6' : '#FEF3C7',
                            color: tx.status === 'confirmed' ? '#166534' : tx.status === 'unmatched' ? '#991B1B' : tx.status === 'dismissed' ? '#6B7280' : '#92400E'
                          }}>
                            {tx.status === 'auto_matched' ? 'Auto' : tx.status === 'manually_matched' ? 'Manual' : tx.status.charAt(0).toUpperCase() + tx.status.slice(1)}
                          </span>
                        </td>
                        <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                          <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                            {tx.status === 'unmatched' && (
                              <button onClick={() => onMatch(tx)} data-testid={`match-tx-${tx.id}`}
                                style={{ padding: '4px 8px', borderRadius: '4px', border: '1px solid #3B82F6', background: '#EFF6FF', color: '#1D4ED8', fontSize: '11px', cursor: 'pointer', fontWeight: '600' }}>
                                Match
                              </button>
                            )}
                            {(tx.status === 'auto_matched' || tx.status === 'manually_matched') && (
                              <button onClick={() => onConfirm(tx.id)} data-testid={`confirm-tx-${tx.id}`}
                                style={{ padding: '4px 8px', borderRadius: '4px', border: '1px solid #22C55E', background: '#F0FDF4', color: '#166534', fontSize: '11px', cursor: 'pointer', fontWeight: '600' }}>
                                Confirm
                              </button>
                            )}
                            {tx.status !== 'confirmed' && tx.status !== 'dismissed' && (
                              <button onClick={() => onDismiss(tx.id)} data-testid={`dismiss-tx-${tx.id}`}
                                style={{ padding: '4px 8px', borderRadius: '4px', border: '1px solid #E5E7EB', background: '#F9FAFB', color: '#6B7280', fontSize: '11px', cursor: 'pointer' }}>
                                Dismiss
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
            })()}
          </div>
        </>
      )}

      {/* Payment Instructions Modal */}
      {showPaymentInstructionsModal && (
        <div className="modal-overlay" onClick={() => setShowPaymentInstructionsModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '520px' }}>
            <div className="modal-header">
              <h2>Payment Instructions</h2>
              <button className="modal-close" onClick={() => setShowPaymentInstructionsModal(false)}>&times;</button>
            </div>
            <div className="modal-body">
              <p style={{ color: '#6B7280', fontSize: '13px', marginBottom: '16px' }}>
                Members will see these details when making alternative payments. Include the case reference number instructions.
              </p>
              
              <h4 style={{ fontSize: '14px', color: '#5B21B6', marginBottom: '8px' }}>Zelle</h4>
              <div className="form-row" style={{ gap: '10px', marginBottom: '12px' }}>
                <div className="form-group" style={{ flex: 1 }}>
                  <label>Zelle Email</label>
                  <input type="email" value={paymentInstructions.zelle_email || ''} onChange={e => setPaymentInstructions(p => ({...p, zelle_email: e.target.value}))}
                    placeholder="group@email.com" data-testid="zelle-email-input"
                    style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #D1D5DB' }} />
                </div>
                <div className="form-group" style={{ flex: 1 }}>
                  <label>Zelle Phone</label>
                  <input type="tel" value={paymentInstructions.zelle_phone || ''} onChange={e => setPaymentInstructions(p => ({...p, zelle_phone: e.target.value}))}
                    placeholder="(555) 123-4567" style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #D1D5DB' }} />
                </div>
              </div>
              <div className="form-group" style={{ marginBottom: '16px' }}>
                <label>Zelle Recipient Name</label>
                <input value={paymentInstructions.zelle_name || ''} onChange={e => setPaymentInstructions(p => ({...p, zelle_name: e.target.value}))}
                  placeholder="Group Treasury" style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #D1D5DB' }} />
              </div>

              <h4 style={{ fontSize: '14px', color: '#065F46', marginBottom: '8px' }}>Cash App</h4>
              <div className="form-row" style={{ gap: '10px', marginBottom: '12px' }}>
                <div className="form-group" style={{ flex: 1 }}>
                  <label>Cash App Tag</label>
                  <input value={paymentInstructions.cashapp_tag || ''} onChange={e => setPaymentInstructions(p => ({...p, cashapp_tag: e.target.value}))}
                    placeholder="$GroupCashTag" data-testid="cashapp-tag-input"
                    style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #D1D5DB' }} />
                </div>
                <div className="form-group" style={{ flex: 1 }}>
                  <label>Cash App Name</label>
                  <input value={paymentInstructions.cashapp_name || ''} onChange={e => setPaymentInstructions(p => ({...p, cashapp_name: e.target.value}))}
                    placeholder="Group Treasury" style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #D1D5DB' }} />
                </div>
              </div>

              <div className="form-group" style={{ marginBottom: '16px' }}>
                <label>Additional Instructions</label>
                <textarea value={paymentInstructions.additional_instructions || ''} onChange={e => setPaymentInstructions(p => ({...p, additional_instructions: e.target.value}))}
                  rows={3} placeholder="e.g., Always include the case reference number in the payment memo..."
                  style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #D1D5DB', resize: 'vertical' }} />
              </div>

              <div style={{ display: 'flex', gap: '10px' }}>
                <button className="btn btn-secondary" onClick={() => setShowPaymentInstructionsModal(false)} style={{ flex: 1 }}>Cancel</button>
                <button className="btn btn-primary" onClick={onSavePaymentInstructions} style={{ flex: 1 }} data-testid="save-payment-instructions-btn">Save Instructions</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ManualPaymentVerificationTab({ groupId, group, onRefresh }) {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCaseNumber, setSelectedCaseNumber] = useState('');
  const [caseNumbers, setCaseNumbers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [verifying, setVerifying] = useState({});
  const [verificationSubTab, setVerificationSubTab] = useState('individual');
  const [poolPayments, setPoolPayments] = useState([]);
  const [poolLoading, setPoolLoading] = useState(false);
  const [verifyingPool, setVerifyingPool] = useState({});
  const [poolProofPreview, setPoolProofPreview] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [notes, setNotes] = useState('');
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [customAmount, setCustomAmount] = useState('');
  const [showProofModal, setShowProofModal] = useState(false);
  const [selectedProof, setSelectedProof] = useState(null);
  const [verifyingProof, setVerifyingProof] = useState(false);
  const [verifyCaseNumber, setVerifyCaseNumber] = useState('');

  useEffect(() => {
    loadCaseNumbers();
    loadMembers();
  }, [groupId]);

  useEffect(() => {
    loadMembers();
  }, [selectedCaseNumber, searchTerm]);

  useEffect(() => {
    if (verificationSubTab === 'pool') loadPoolPayments();
  }, [verificationSubTab]);

  const loadCaseNumbers = async () => {
    try {
      const response = await axios.get(`${API}/groups/${groupId}/case-numbers`);
      setCaseNumbers(response.data.case_numbers || []);
    } catch (error) {
      console.error('Failed to load case numbers');
    }
  };

  const loadMembers = async () => {
    setLoading(true);
    try {
      let url = selectedCaseNumber 
        ? `${API}/groups/${groupId}/members/pending-verification?case_number=${selectedCaseNumber}`
        : `${API}/groups/${groupId}/members/pending-verification`;
      if (searchTerm) {
        url += (url.includes('?') ? '&' : '?') + `search=${encodeURIComponent(searchTerm)}`;
      }
      const response = await axios.get(url);
      setMembers(response.data.members || []);
    } catch (error) {
      toast.error('Failed to load members');
    } finally {
      setLoading(false);
    }
  };

  const loadPoolPayments = async () => {
    setPoolLoading(true);
    try {
      const response = await axios.get(`${API}/groups/${groupId}/pool-payments`);
      setPoolPayments(response.data);
    } catch (error) {
      toast.error('Failed to load pool payments');
    } finally {
      setPoolLoading(false);
    }
  };

  const handleVerifyPool = async (poolId, paymentMethod = 'cash') => {
    setVerifyingPool(prev => ({ ...prev, [poolId]: true }));
    try {
      const params = new URLSearchParams();
      params.append('payment_method', paymentMethod);
      const response = await axios.post(`${API}/pool-payments/${poolId}/verify?${params.toString()}`);
      toast.success(response.data.message);
      loadPoolPayments();
      if (onRefresh) onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to verify pool payment');
    } finally {
      setVerifyingPool(prev => ({ ...prev, [poolId]: false }));
    }
  };

  const openVerifyModal = (member) => {
    setSelectedMember(member);
    setCustomAmount(group.payment_amount?.toString() || '');
    setPaymentMethod('cash');
    setNotes('');
    setShowVerifyModal(true);
  };

  const confirmPayment = async () => {
    if (!selectedMember) return;
    
    const amount = parseFloat(customAmount) || group.payment_amount;
    if (!amount || amount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    setVerifying({ ...verifying, [selectedMember.id]: true });
    try {
      await axios.post(`${API}/groups/${groupId}/payments/manual-verify`, {
        member_id: selectedMember.id,
        group_id: groupId,
        amount: amount,
        case_number: selectedCaseNumber || null,
        payment_method: paymentMethod,
        notes: notes || null
      });
      
      toast.success(`Payment verified for ${selectedMember.user_name}`);
      setShowVerifyModal(false);
      loadMembers();
      if (onRefresh) onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to verify payment');
    } finally {
      setVerifying({ ...verifying, [selectedMember.id]: false });
    }
  };

  const paidCount = members.filter(m => m.has_paid).length;
  const unpaidCount = members.filter(m => !m.has_paid).length;

  const verifyProofAndPay = async () => {
    if (!selectedProof) return;
    
    setVerifyingProof(true);
    try {
      // Use selected case number, or fall back to proof's case number
      const caseNumberToUse = verifyCaseNumber || selectedProof.case_number || null;
      
      const response = await axios.post(`${API}/proofs/${selectedProof.id}/verify`, null, {
        params: {
          payment_method: paymentMethod || 'cash',
          notes: notes || null,
          case_number: caseNumberToUse
        }
      });
      
      toast.success(`Payment verified for ${response.data.member_name || 'member'}! Amount: ${formatCurrency(response.data.amount, group?.currency)}`);
      setShowProofModal(false);
      setSelectedProof(null);
      setVerifyCaseNumber(''); // Reset case number selection
      loadMembers();
      if (onRefresh) onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to verify proof');
    } finally {
      setVerifyingProof(false);
    }
  };

  return (
    <div className="manual-verification-tab" data-testid="manual-verification-tab">
      <div className="tab-header">
        <h1>Manual Payment Verification</h1>
        <p className="tab-description">Verify cash and non-conventional payments for members</p>
      </div>

      {/* Sub-tabs */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '20px', background: '#F3F4F6', padding: '4px', borderRadius: '10px', width: 'fit-content' }}>
        <button
          onClick={() => setVerificationSubTab('individual')}
          data-testid="verification-subtab-individual"
          style={{
            padding: '8px 20px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontWeight: '600', fontSize: '13px',
            background: verificationSubTab === 'individual' ? '#1F2937' : 'transparent',
            color: verificationSubTab === 'individual' ? 'white' : '#6B7280'
          }}
        >
          Individual Payments
        </button>
        <button
          onClick={() => setVerificationSubTab('pool')}
          data-testid="verification-subtab-pool"
          style={{
            padding: '8px 20px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontWeight: '600', fontSize: '13px',
            background: verificationSubTab === 'pool' ? '#1F2937' : 'transparent',
            color: verificationSubTab === 'pool' ? 'white' : '#6B7280'
          }}
        >
          Pool Payments
        </button>
      </div>

      {verificationSubTab === 'individual' && (
        <>
      <div className="verification-filters card">
        <div className="form-row">
          <div className="form-group" style={{ flex: 2 }}>
            <label>Search by Member Name or Membership #</label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Type to search..."
              data-testid="verification-search-input"
            />
          </div>
          <div className="form-group">
            <label>Filter by Case Number</label>
            <select 
              value={selectedCaseNumber} 
              onChange={(e) => setSelectedCaseNumber(e.target.value)}
              data-testid="case-number-filter"
            >
              <option value="">All Cases</option>
              {caseNumbers.map((caseNum) => (
                <option key={caseNum} value={caseNum}>{caseNum}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="verification-stats">
        <div className="stat-card paid">
          <span className="stat-value">{paidCount}</span>
          <span className="stat-label">Paid</span>
        </div>
        <div className="stat-card unpaid">
          <span className="stat-value">{unpaidCount}</span>
          <span className="stat-label">Pending</span>
        </div>
        <div className="stat-card total">
          <span className="stat-value">{members.length}</span>
          <span className="stat-label">Total Members</span>
        </div>
      </div>

      {loading ? (
        <div className="loading-state card">
          <p>Loading members...</p>
        </div>
      ) : members.length === 0 ? (
        <div className="empty-state card">
          <h3>No members found</h3>
          <p>Add members to the group to start verifying payments</p>
        </div>
      ) : (
        <div className="members-verification-list card">
          <table className="data-table verification-table">
            <thead>
              <tr>
                <th>Membership #</th>
                <th>Member Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Proof</th>
                <th>Status</th>
                <th>Payment Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {members.map((member) => (
                <tr key={member.id} className={member.has_paid ? 'row-paid' : 'row-unpaid'}>
                  <td className="membership-number">{member.membership_number || 'N/A'}</td>
                  <td>{member.user_name}</td>
                  <td>{member.user_email}</td>
                  <td>{member.phone_number}</td>
                  <td>
                    {member.proof_of_payment ? (
                      <button
                        onClick={() => {
                          setSelectedProof(member.proof_of_payment);
                          setShowProofModal(true);
                        }}
                        className="proof-view-btn"
                        data-testid={`view-proof-${member.id}`}
                      >
                        {member.proof_of_payment.file_type === '.pdf' ? '📄' : '🖼️'} View Proof
                      </button>
                    ) : (
                      <span className="no-proof">—</span>
                    )}
                  </td>
                  <td>
                    <span className={`status-badge status-${member.status}`}>
                      {member.status}
                    </span>
                  </td>
                  <td>
                    {member.has_paid ? (
                      <span className="payment-status paid">✓ Paid</span>
                    ) : (
                      <span className="payment-status unpaid">Pending</span>
                    )}
                  </td>
                  <td>
                    {!member.has_paid && (
                      <button
                        onClick={() => openVerifyModal(member)}
                        className="btn btn-primary btn-sm"
                        disabled={verifying[member.id]}
                        data-testid={`verify-payment-${member.id}`}
                      >
                        {verifying[member.id] ? 'Verifying...' : 'Verify Payment'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      </>
      )}

      {/* Pool Payments Sub-tab */}
      {verificationSubTab === 'pool' && (
        <div data-testid="pool-payments-section">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#1F2937' }}>Pool Payment Submissions</h3>
            <button onClick={loadPoolPayments} className="btn btn-secondary" style={{ fontSize: '13px' }} data-testid="refresh-pool-btn">
              Refresh
            </button>
          </div>

          {poolLoading ? (
            <div className="loading-state card"><p>Loading pool payments...</p></div>
          ) : poolPayments.length === 0 ? (
            <div className="empty-state card">
              <h3>No pool payments</h3>
              <p>Pool payments will appear here when members submit proof of payment covering multiple members.</p>
            </div>
          ) : (
            poolPayments.map(pool => (
              <div key={pool.id} className="card" style={{ marginBottom: '16px', padding: '20px' }} data-testid={`pool-card-${pool.id}`}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '14px', paddingBottom: '12px', borderBottom: '1px solid #E5E7EB' }}>
                  <div>
                    <div style={{ fontSize: '15px', fontWeight: '600', color: '#1F2937' }}>
                      Submitted by: {pool.submitter_name} <span style={{ color: '#6B7280', fontWeight: '400', fontSize: '13px' }}>({pool.submitter_membership_number})</span>
                    </div>
                    <div style={{ fontSize: '13px', color: '#6B7280', marginTop: '4px' }}>
                      Case #{pool.case_number} | {new Date(pool.uploaded_at).toLocaleDateString()} {new Date(pool.uploaded_at).toLocaleTimeString()}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: '18px', fontWeight: '700', color: '#059669' }}>{pool.pool_amount?.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}</div>
                    <div style={{ fontSize: '12px', color: '#6B7280' }}>Amount in proof</div>
                  </div>
                </div>
                
                <div style={{ marginBottom: '12px' }}>
                  <div style={{ fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
                    Members Covered ({pool.total_members_covered} total including submitter):
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    <span style={{ padding: '4px 10px', borderRadius: '20px', background: '#DBEAFE', color: '#1E40AF', fontSize: '12px', fontWeight: '500' }}>
                      {pool.submitter_name} (submitter)
                    </span>
                    {(pool.pool_members || []).map(pm => (
                      <span key={pm.member_id} style={{ padding: '4px 10px', borderRadius: '20px', background: '#F3F4F6', color: '#374151', fontSize: '12px', fontWeight: '500' }}>
                        {pm.name} ({pm.membership_number})
                      </span>
                    ))}
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                  <button 
                    onClick={() => setPoolProofPreview(poolProofPreview === pool.id ? null : pool.id)}
                    style={{ background: 'none', border: 'none', color: '#3B82F6', fontSize: '13px', fontWeight: '500', cursor: 'pointer', textDecoration: 'underline', padding: 0 }} 
                    data-testid={`view-pool-proof-${pool.id}`}
                  >
                    {poolProofPreview === pool.id ? 'Hide Proof' : 'View Proof'}
                  </button>
                  <a href={pool.file_url} target="_blank" rel="noopener noreferrer" style={{ color: '#6B7280', fontSize: '12px', textDecoration: 'underline' }}>
                    Open in New Tab
                  </a>
                  <span style={{ padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '600', background: pool.verified ? '#D1FAE5' : '#FEF3C7', color: pool.verified ? '#065F46' : '#92400E' }}>
                    {pool.verified ? 'Verified' : 'Pending Verification'}
                  </span>
                  {pool.notes && <span style={{ fontSize: '12px', color: '#6B7280' }}>Note: {pool.notes}</span>}
                  
                  {!pool.verified && (
                    <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <select
                        id={`pool-method-${pool.id}`}
                        defaultValue="cash"
                        style={{ padding: '6px 10px', borderRadius: '6px', border: '1px solid #D1D5DB', fontSize: '12px' }}
                        data-testid={`pool-payment-method-${pool.id}`}
                      >
                        <option value="cash">Cash</option>
                        <option value="bank_transfer">Bank Transfer</option>
                        <option value="mobile_money">Mobile Money</option>
                        <option value="other">Other</option>
                      </select>
                      <button
                        onClick={() => {
                          const method = document.getElementById(`pool-method-${pool.id}`).value;
                          handleVerifyPool(pool.id, method);
                        }}
                        disabled={verifyingPool[pool.id]}
                        data-testid={`verify-pool-btn-${pool.id}`}
                        style={{
                          padding: '6px 16px', borderRadius: '8px', border: 'none', cursor: 'pointer',
                          background: '#059669', color: 'white', fontWeight: '600', fontSize: '13px',
                          opacity: verifyingPool[pool.id] ? 0.6 : 1
                        }}
                      >
                        {verifyingPool[pool.id] ? 'Verifying...' : `Verify All (${pool.total_members_covered} members)`}
                      </button>
                    </div>
                  )}
                  {pool.verified && pool.verified_by_name && (
                    <span style={{ marginLeft: 'auto', fontSize: '12px', color: '#059669' }}>
                      Verified by {pool.verified_by_name} on {new Date(pool.verified_at).toLocaleDateString()}
                    </span>
                  )}
                </div>

                {/* Proof Image Preview */}
                {poolProofPreview === pool.id && (
                  <div style={{ marginTop: '14px', paddingTop: '14px', borderTop: '1px solid #E5E7EB' }} data-testid={`pool-proof-preview-${pool.id}`}>
                    {pool.file_path?.endsWith('.pdf') ? (
                      <div style={{ textAlign: 'center', padding: '20px', background: '#F9FAFB', borderRadius: '8px' }}>
                        <p style={{ fontSize: '14px', color: '#374151' }}>📄 PDF Document</p>
                        <a href={pool.file_url} target="_blank" rel="noopener noreferrer" className="btn btn-primary" style={{ display: 'inline-block', marginTop: '8px', padding: '8px 20px', background: '#3B82F6', color: 'white', borderRadius: '8px', textDecoration: 'none', fontWeight: '600', fontSize: '13px' }}>
                          Open PDF
                        </a>
                      </div>
                    ) : (
                      <div style={{ textAlign: 'center' }}>
                        <img 
                          src={pool.file_url} 
                          alt="Proof of payment" 
                          style={{ maxWidth: '100%', maxHeight: '500px', borderRadius: '8px', border: '1px solid #E5E7EB', objectFit: 'contain' }}
                          onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'block'; }}
                        />
                        <p style={{ display: 'none', color: '#EF4444', fontSize: '13px', padding: '20px' }}>Unable to load image preview. <a href={pool.file_url} target="_blank" rel="noopener noreferrer" style={{ color: '#3B82F6' }}>Open in new tab</a></p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}

      {/* Verify Payment Modal */}
      {showVerifyModal && selectedMember && (
        <div className="modal-overlay" onClick={() => setShowVerifyModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Verify Payment</h2>
              <button className="modal-close" onClick={() => setShowVerifyModal(false)}>×</button>
            </div>
            
            <div className="verify-modal-body">
              <div className="member-info-card">
                <h3>{selectedMember.user_name}</h3>
                <p className="membership-num">Membership #: {selectedMember.membership_number || 'N/A'}</p>
                <p>{selectedMember.user_email}</p>
                <p>{selectedMember.phone_number}</p>
              </div>

              <div className="form-group">
                <label>Payment Amount *</label>
                <input
                  type="number"
                  value={customAmount}
                  onChange={(e) => setCustomAmount(e.target.value)}
                  placeholder={`Default: ${formatCurrency(group.payment_amount, group?.currency)}`}
                  step="0.01"
                  min="0"
                  data-testid="verify-amount-input"
                />
              </div>

              <div className="form-group">
                <label>Payment Method *</label>
                <select 
                  value={paymentMethod} 
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  data-testid="payment-method-select"
                >
                  <option value="cash">Cash</option>
                  <option value="bank_transfer">Bank Transfer</option>
                  <option value="mobile_money">Mobile Money (M-Pesa, etc.)</option>
                  <option value="other">Other</option>
                </select>
              </div>

              {selectedCaseNumber && (
                <div className="form-group">
                  <label>Case Number</label>
                  <input type="text" value={selectedCaseNumber} disabled />
                </div>
              )}

              <div className="form-group">
                <label>Notes (Optional)</label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Add any notes about this payment..."
                  rows="3"
                  data-testid="verify-notes-input"
                />
              </div>

              <div className="modal-actions">
                <button 
                  className="btn btn-secondary" 
                  onClick={() => setShowVerifyModal(false)}
                >
                  Cancel
                </button>
                <button 
                  className="btn btn-primary"
                  onClick={confirmPayment}
                  disabled={verifying[selectedMember.id]}
                  data-testid="confirm-verify-btn"
                >
                  {verifying[selectedMember.id] ? 'Verifying...' : 'Confirm Payment'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Proof of Payment Viewer Modal */}
      {showProofModal && selectedProof && (
        <div className="modal-overlay" onClick={() => setShowProofModal(false)}>
          <div className="modal-content proof-viewer-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>📋 Proof of Payment</h2>
              <button className="modal-close" onClick={() => setShowProofModal(false)}>×</button>
            </div>
            
            <div className="proof-viewer-body">
              <div className="proof-meta">
                <div className="meta-item">
                  <span className="meta-label">Member:</span>
                  <span className="meta-value">{selectedProof.member_name || 'Unknown'}</span>
                </div>
                <div className="meta-item">
                  <span className="meta-label">Membership #:</span>
                  <span className="meta-value">{selectedProof.membership_number || 'N/A'}</span>
                </div>
                {selectedProof.member_email && selectedProof.member_email !== 'N/A' && (
                  <div className="meta-item">
                    <span className="meta-label">Email:</span>
                    <span className="meta-value">{selectedProof.member_email}</span>
                  </div>
                )}
                <div className="meta-item">
                  <span className="meta-label">Uploaded:</span>
                  <span className="meta-value">{new Date(selectedProof.uploaded_at).toLocaleString()}</span>
                </div>
                <div className="meta-item">
                  <span className="meta-label">File:</span>
                  <span className="meta-value">{selectedProof.file_name}</span>
                </div>
                {selectedProof.case_number && (
                  <div className="meta-item">
                    <span className="meta-label">Case #:</span>
                    <span className="meta-value">{selectedProof.case_number}</span>
                  </div>
                )}
                {selectedProof.notes && (
                  <div className="meta-item notes">
                    <span className="meta-label">Notes:</span>
                    <span className="meta-value">{selectedProof.notes}</span>
                  </div>
                )}
                <div className="meta-item">
                  <span className="meta-label">Status:</span>
                  <span className={`meta-value proof-verified-status ${selectedProof.verified ? 'verified' : 'pending'}`}>
                    {selectedProof.verified ? '✓ Verified' : '⏳ Pending Review'}
                  </span>
                </div>
              </div>
              
              <div className="proof-preview">
                {selectedProof.file_type === '.pdf' ? (
                  <div className="pdf-preview">
                    <p>📄 PDF Document</p>
                    <a 
                      href={selectedProof.file_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="btn btn-primary"
                    >
                      Open PDF in New Tab
                    </a>
                  </div>
                ) : (
                  <img 
                    src={selectedProof.file_url} 
                    alt="Proof of payment" 
                    className="proof-image"
                  />
                )}
              </div>
              
              {/* Verification Section - Only show if not already verified */}
              {!selectedProof.verified && (
                <div className="proof-verify-section">
                  <h4>✅ Verify This Payment</h4>
                  <p className="verify-info">Verifying this proof will automatically create a payment record for the member and send them a confirmation email.</p>
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label>📋 Case Number</label>
                      <select 
                        value={verifyCaseNumber || selectedProof.case_number || ''} 
                        onChange={(e) => setVerifyCaseNumber(e.target.value)}
                        data-testid="verify-case-number-select"
                        style={{
                          background: verifyCaseNumber || selectedProof.case_number ? '#ECFDF5' : '#FEF3C7',
                          borderColor: verifyCaseNumber || selectedProof.case_number ? '#10B981' : '#F59E0B'
                        }}
                      >
                        <option value="">-- Select Case Number --</option>
                        {caseNumbers.map((caseNum) => (
                          <option key={caseNum} value={caseNum}>{caseNum}</option>
                        ))}
                      </select>
                      <small className="form-help" style={{color: '#6B7280', marginTop: '4px', display: 'block'}}>
                        {selectedProof.case_number 
                          ? `Member selected: ${selectedProof.case_number}` 
                          : 'Select the case number this payment is for'}
                      </small>
                    </div>
                  </div>
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label>Payment Method</label>
                      <select 
                        value={paymentMethod} 
                        onChange={(e) => setPaymentMethod(e.target.value)}
                      >
                        <option value="cash">Cash</option>
                        <option value="bank_transfer">Bank Transfer</option>
                        <option value="mobile_money">Mobile Money</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                  </div>
                  
                  <button 
                    className="btn btn-success full-width"
                    onClick={verifyProofAndPay}
                    disabled={verifyingProof}
                    data-testid="verify-proof-btn"
                  >
                    {verifyingProof ? 'Verifying...' : `Verify & Confirm Payment (${formatCurrency(group.payment_amount, group?.currency)})`}
                  </button>
                </div>
              )}
              
              {/* Show verified confirmation for already verified proofs */}
              {selectedProof.verified && (
                <div className="verified-confirmation" style={{
                  background: '#ECFDF5',
                  border: '2px solid #10B981',
                  borderRadius: '12px',
                  padding: '16px',
                  marginBottom: '16px',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '32px', marginBottom: '8px' }}>✅</div>
                  <h4 style={{ margin: '0 0 4px 0', color: '#065F46' }}>Payment Verified</h4>
                  <p style={{ margin: 0, color: '#047857', fontSize: '14px' }}>
                    This proof has been verified and payment recorded.
                  </p>
                </div>
              )}
              
              <div className="proof-actions">
                <button 
                  className="btn btn-primary"
                  onClick={() => setShowProofModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function DisbursementsTab({ disbursements, groupId, onCreateDisbursement, onRefresh, group, members }) {
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [selectedDisbursement, setSelectedDisbursement] = useState(null);
  const [password, setPassword] = useState('');
  const [signing, setSigning] = useState(false);
  
  // Main tab state - 'disbursements' or 'loans'
  const [mainTab, setMainTab] = useState('disbursements');
  const [loanSummary, setLoanSummary] = useState(null);
  const [smsMessage, setSmsMessage] = useState('');
  const [smsRecipientFilter, setSmsRecipientFilter] = useState('all');
  const [sendingSms, setSendingSms] = useState(false);
  const [showSmsCompose, setShowSmsCompose] = useState(false);
  
  // Manual disbursement state
  const [disbursementType, setDisbursementType] = useState('bank'); // 'bank' or 'manual'
  const [manualDisbursements, setManualDisbursements] = useState([]);
  const [showManualForm, setShowManualForm] = useState(false);
  const [showProofUpload, setShowProofUpload] = useState(false);
  const [selectedManualDisbursement, setSelectedManualDisbursement] = useState(null);
  const [manualFormData, setManualFormData] = useState({
    beneficiary_name: '',
    amount: '',
    description: '',
    payment_method: 'cash',
    payment_details: '',
    reason: '',
    notes: ''
  });
  const [uploading, setUploading] = useState(false);
  const [proofFiles, setProofFiles] = useState([]);
  
  // Loan state
  const [loans, setLoans] = useState([]);
  const [loanStats, setLoanStats] = useState(null);
  const [nonMembers, setNonMembers] = useState([]);
  const [showLoanForm, setShowLoanForm] = useState(false);
  const [showLoanDetails, setShowLoanDetails] = useState(false);
  const [selectedLoan, setSelectedLoan] = useState(null);
  const [showLoanSignModal, setShowLoanSignModal] = useState(false);
  const [showRepaymentModal, setShowRepaymentModal] = useState(false);
  const [loanFormData, setLoanFormData] = useState({
    applicant_type: 'member',
    member_id: '',
    non_member_id: '',
    non_member_name: '',
    non_member_email: '',
    non_member_phone: '',
    non_member_address: '',
    non_member_id_number: '',
    amount: '',
    purpose: '',
    term_months: 12,
    interest_rate: 10,
    interest_type: 'simple',
    rate_period: 'monthly',
    repayment_mode: 'interest_only',
    origination_fee_type: 'percentage',
    origination_fee_value: 0,
    disbursement_method: 'manual',
    bank_routing_number: '',
    bank_account_number: '',
    manual_payment_method: 'cash',
    manual_payment_details: '',
    guarantor_ids: [],
    collateral_type: '',
    collateral_description: '',
    collateral_value: '',
    // Default handling settings
    grace_period_days: 7,
    default_action: 'probation',
    late_fee_type: 'none',
    late_fee_value: 0
  });
  const [repaymentData, setRepaymentData] = useState({
    amount: '',
    payment_method: 'cash',
    reference: '',
    notes: ''
  });
  const [overdueLoans, setOverdueLoans] = useState([]);
  
  // Set Terms state (for member-requested loans)
  const [showSetTermsModal, setShowSetTermsModal] = useState(false);
  const [setTermsLoan, setSetTermsLoan] = useState(null);
  const [termsFormData, setTermsFormData] = useState({
    term_months: 12, interest_rate: 10, interest_type: 'simple', rate_period: 'monthly', repayment_mode: 'interest_only',
    origination_fee_type: 'percentage', origination_fee_value: 0,
    disbursement_method: 'manual', manual_payment_method: 'cash', manual_payment_details: '',
    bank_routing_number: '', bank_account_number: '',
    guarantor_ids: [], grace_period_days: 7, default_action: 'probation',
    late_fee_type: 'none', late_fee_value: 0, notes: ''
  });
  const [settingTerms, setSettingTerms] = useState(false);
  
  // Search & filter state
  const [disbSearchQuery, setDisbSearchQuery] = useState('');
  const [disbFilterStartDate, setDisbFilterStartDate] = useState('');
  const [disbFilterEndDate, setDisbFilterEndDate] = useState('');
  
  // Check if group is benevolence (loans not allowed)
  const isBenevolenceGroup = group?.category === 'benevolence';

  // Load manual disbursements
  useEffect(() => {
    if (groupId) {
      loadManualDisbursements();
    }
  }, [groupId]);
  
  // Load loans when on loans tab
  useEffect(() => {
    if (groupId && mainTab === 'loans' && !isBenevolenceGroup) {
      loadLoans();
      loadLoanStats();
      loadNonMembers();
      loadOverdueLoans();
    }
  }, [groupId, mainTab]);

  const loadManualDisbursements = async () => {
    try {
      const response = await axios.get(`${API}/manual-disbursements/${groupId}`);
      setManualDisbursements(response.data);
    } catch (error) {
      console.error('Error loading manual disbursements:', error);
    }
  };
  
  const loadLoans = async () => {
    try {
      const response = await axios.get(`${API}/groups/${groupId}/loans`);
      setLoans(response.data);
      // Also fetch loan summary
      try {
        const summaryRes = await axios.get(`${API}/groups/${groupId}/loans/summary`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
        setLoanSummary(summaryRes.data);
      } catch (e) { console.error('Loan summary fetch error:', e); }
    } catch (error) {
      console.error('Error loading loans:', error);
    }
  };
  
  const loadLoanStats = async () => {
    try {
      const response = await axios.get(`${API}/groups/${groupId}/loan-stats`);
      setLoanStats(response.data);
    } catch (error) {
      console.error('Error loading loan stats:', error);
    }
  };
  
  const loadNonMembers = async () => {
    try {
      const response = await axios.get(`${API}/groups/${groupId}/non-members`);
      setNonMembers(response.data);
    } catch (error) {
      console.error('Error loading non-members:', error);
    }
  };
  
  const loadOverdueLoans = async () => {
    try {
      const response = await axios.get(`${API}/groups/${groupId}/loans/overdue`);
      setOverdueLoans(response.data);
    } catch (error) {
      console.error('Error loading overdue loans:', error);
    }
  };

  const initiateSign = (disbursement) => {
    setSelectedDisbursement(disbursement);
    setPassword('');
    setShowPasswordModal(true);
  };

  const confirmSign = async () => {
    if (!password) {
      toast.error('Please enter your password');
      return;
    }

    setSigning(true);
    try {
      await axios.post(`${API}/disbursements/${selectedDisbursement.id}/sign`, {
        password: password
      });
      toast.success('Disbursement signed successfully');
      setShowPasswordModal(false);
      setPassword('');
      setSelectedDisbursement(null);
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sign disbursement');
    } finally {
      setSigning(false);
    }
  };

  const cancelSign = () => {
    setShowPasswordModal(false);
    setPassword('');
    setSelectedDisbursement(null);
  };

  const handleDirectTransfer = async (disbursementId) => {
    if (!window.confirm('Initiate direct transfer to beneficiary account? This action cannot be undone.')) {
      return;
    }

    setSigning(true);
    try {
      const response = await axios.post(`${API}/disbursements/${disbursementId}/transfer-direct`);
      toast.success(`Transfer successful! Payout ID: ${response.data.payout_id}`);
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to transfer funds');
    } finally {
      setSigning(false);
    }
  };

  // Manual disbursement handlers
  const handleCreateManualDisbursement = async (e) => {
    e.preventDefault();
    
    if (!manualFormData.beneficiary_name || !manualFormData.amount || !manualFormData.reason) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSigning(true);
    try {
      await axios.post(`${API}/manual-disbursements`, {
        group_id: groupId,
        ...manualFormData,
        amount: parseFloat(manualFormData.amount)
      });
      toast.success('Manual disbursement created successfully');
      setShowManualForm(false);
      setManualFormData({
        beneficiary_name: '',
        amount: '',
        description: '',
        payment_method: 'cash',
        payment_details: '',
        reason: '',
        notes: ''
      });
      loadManualDisbursements();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create manual disbursement');
    } finally {
      setSigning(false);
    }
  };

  const handleCompleteManualDisbursement = async (disbursementId) => {
    if (!window.confirm('Mark this disbursement as completed? This indicates payment has been made.')) {
      return;
    }

    try {
      await axios.put(`${API}/manual-disbursements/${disbursementId}/complete`);
      toast.success('Disbursement marked as completed');
      loadManualDisbursements();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete disbursement');
    }
  };

  const handleCancelManualDisbursement = async (disbursementId) => {
    if (!window.confirm('Cancel this disbursement? This action cannot be undone.')) {
      return;
    }

    try {
      await axios.put(`${API}/manual-disbursements/${disbursementId}/cancel`);
      toast.success('Disbursement cancelled');
      loadManualDisbursements();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel disbursement');
    }
  };

  const openProofUpload = (disbursement) => {
    setSelectedManualDisbursement(disbursement);
    setProofFiles([]);
    setShowProofUpload(true);
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter(file => {
      const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'application/pdf', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        toast.error(`${file.name}: Invalid file type. Only JPG, PNG, GIF, WebP, PDF allowed.`);
        return false;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name}: File too large. Max 10MB per file.`);
        return false;
      }
      return true;
    });
    setProofFiles(prev => [...prev, ...validFiles]);
  };

  const removeSelectedFile = (index) => {
    setProofFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleUploadProofs = async () => {
    if (proofFiles.length === 0) {
      toast.error('Please select at least one file');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      proofFiles.forEach(file => {
        formData.append('files', file);
      });

      await axios.post(
        `${API}/manual-disbursements/${selectedManualDisbursement.id}/upload-proof`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      toast.success(`${proofFiles.length} file(s) uploaded successfully`);
      setShowProofUpload(false);
      setProofFiles([]);
      loadManualDisbursements();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload proofs');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteProof = async (disbursementId, proofIndex) => {
    if (!window.confirm('Delete this proof file?')) return;
    
    try {
      await axios.delete(`${API}/manual-disbursements/${disbursementId}/proof/${proofIndex}`);
      toast.success('Proof deleted');
      loadManualDisbursements();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete proof');
    }
  };

  const getPaymentMethodLabel = (method) => {
    const labels = {
      cash: '💵 Cash',
      mobile_money: '📱 Mobile Money',
      check: '📝 Check',
      bank_transfer: '🏦 Bank Transfer',
      other: '📋 Other'
    };
    return labels[method] || method;
  };
  
  // =============== LOAN FUNCTIONS ===============
  
  const calculateLoanPreview = () => {
    const principal = parseFloat(loanFormData.amount) || 0;
    const rate = parseFloat(loanFormData.interest_rate) || 0;
    const term = parseInt(loanFormData.term_months) || 1;
    const feeType = loanFormData.origination_fee_type;
    const feeValue = parseFloat(loanFormData.origination_fee_value) || 0;
    
    let totalInterest = 0;
    let totalRepayment = 0;
    let originationFee = 0;
    
    const rows = computeLoanSchedule(principal, rate, term, loanFormData.interest_type, loanFormData.repayment_mode, loanFormData.rate_period);
    totalInterest = rows.reduce((sum, r) => sum + r.interest, 0);
    totalRepayment = principal + totalInterest;
    
    if (feeType === 'percentage') {
      originationFee = principal * feeValue / 100;
    } else {
      originationFee = feeValue;
    }
    
    const monthlyPayment = rows.length ? rows[0].total : 0;
    const finalPayment = rows.length ? rows[rows.length - 1].total : 0;
    
    return {
      principal: principal.toFixed(2),
      totalInterest: totalInterest.toFixed(2),
      totalRepayment: totalRepayment.toFixed(2),
      originationFee: originationFee.toFixed(2),
      monthlyPayment: monthlyPayment.toFixed(2),
      finalPayment: finalPayment.toFixed(2),
      netDisbursement: (principal - originationFee).toFixed(2)
    };
  };
  
  const handleCreateLoan = async (e) => {
    e.preventDefault();
    
    if (!loanFormData.amount || !loanFormData.purpose) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    if (loanFormData.applicant_type === 'member' && !loanFormData.member_id) {
      toast.error('Please select a member');
      return;
    }
    
    if (loanFormData.applicant_type === 'non_member' && !loanFormData.non_member_id && !loanFormData.non_member_name) {
      toast.error('Please select or enter non-member details');
      return;
    }
    
    // Validate at least 1 guarantor is selected
    if (!loanFormData.guarantor_ids || loanFormData.guarantor_ids.length === 0 || !loanFormData.guarantor_ids[0]) {
      toast.error('Please select at least one guarantor (Primary Guarantor is required)');
      return;
    }
    
    setSigning(true);
    try {
      const payload = {
        group_id: groupId,
        applicant_type: loanFormData.applicant_type,
        member_id: loanFormData.applicant_type === 'member' ? loanFormData.member_id : null,
        non_member_id: loanFormData.applicant_type === 'non_member' && loanFormData.non_member_id ? loanFormData.non_member_id : null,
        non_member_name: loanFormData.applicant_type === 'non_member' && !loanFormData.non_member_id ? loanFormData.non_member_name : null,
        non_member_email: loanFormData.applicant_type === 'non_member' && !loanFormData.non_member_id ? loanFormData.non_member_email : null,
        non_member_phone: loanFormData.applicant_type === 'non_member' && !loanFormData.non_member_id ? loanFormData.non_member_phone : null,
        non_member_address: loanFormData.applicant_type === 'non_member' && !loanFormData.non_member_id ? loanFormData.non_member_address : null,
        non_member_id_number: loanFormData.applicant_type === 'non_member' && !loanFormData.non_member_id ? loanFormData.non_member_id_number : null,
        amount: parseFloat(loanFormData.amount),
        purpose: loanFormData.purpose,
        term_months: parseInt(loanFormData.term_months),
        interest_rate: parseFloat(loanFormData.interest_rate),
        interest_type: loanFormData.interest_type,
        repayment_mode: loanFormData.repayment_mode || 'interest_only',
        rate_period: loanFormData.interest_type === 'fixed_amount' ? null : (loanFormData.rate_period || 'monthly'),
        origination_fee_type: loanFormData.origination_fee_type,
        origination_fee_value: parseFloat(loanFormData.origination_fee_value),
        disbursement_method: loanFormData.disbursement_method,
        bank_routing_number: loanFormData.disbursement_method === 'stripe' ? loanFormData.bank_routing_number : null,
        bank_account_number: loanFormData.disbursement_method === 'stripe' ? loanFormData.bank_account_number : null,
        manual_payment_method: loanFormData.disbursement_method === 'manual' ? loanFormData.manual_payment_method : null,
        manual_payment_details: loanFormData.disbursement_method === 'manual' ? loanFormData.manual_payment_details : null,
        guarantor_ids: loanFormData.guarantor_ids,
        collateral: loanFormData.collateral_type ? {
          type: loanFormData.collateral_type,
          description: loanFormData.collateral_description,
          estimated_value: parseFloat(loanFormData.collateral_value) || 0
        } : null,
        // Default handling settings
        grace_period_days: parseInt(loanFormData.grace_period_days) || 7,
        default_action: loanFormData.default_action,
        late_fee_type: loanFormData.late_fee_type,
        late_fee_value: parseFloat(loanFormData.late_fee_value) || 0
      };
      
      await axios.post(`${API}/groups/${groupId}/loans`, payload);
      toast.success('Loan application created successfully!');
      setShowLoanForm(false);
      resetLoanForm();
      loadLoans();
      loadLoanStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create loan');
    } finally {
      setSigning(false);
    }
  };
  
  const resetLoanForm = () => {
    setLoanFormData({
      applicant_type: 'member',
      member_id: '',
      non_member_id: '',
      non_member_name: '',
      non_member_email: '',
      non_member_phone: '',
      non_member_address: '',
      non_member_id_number: '',
      amount: '',
      purpose: '',
      term_months: 12,
      interest_rate: 10,
      interest_type: 'simple',
      rate_period: 'monthly',
      repayment_mode: 'interest_only',
      origination_fee_type: 'percentage',
      origination_fee_value: 1,
      disbursement_method: 'manual',
      bank_routing_number: '',
      bank_account_number: '',
      manual_payment_method: 'cash',
      manual_payment_details: '',
      guarantor_ids: [],
      collateral_type: '',
      collateral_description: '',
      collateral_value: '',
      grace_period_days: 7,
      default_action: 'probation',
      late_fee_type: 'none',
      late_fee_value: 0
    });
  };
  
  const handleSignLoan = async () => {
    if (!password) {
      toast.error('Please enter your password');
      return;
    }
    
    setSigning(true);
    try {
      const response = await axios.post(`${API}/groups/${groupId}/loans/${selectedLoan.id}/sign`, {
        password: password
      });
      toast.success(response.data.message);
      setShowLoanSignModal(false);
      setPassword('');
      loadLoans();
      loadLoanStats();
      
      // Refresh the loan details if viewing
      if (showLoanDetails) {
        const updated = await axios.get(`${API}/groups/${groupId}/loans/${selectedLoan.id}`);
        setSelectedLoan(updated.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sign loan');
    } finally {
      setSigning(false);
    }
  };
  
  const handleDisburseLoan = async (loanId) => {
    if (!window.confirm('Disburse this loan? This action cannot be undone.')) {
      return;
    }
    
    setSigning(true);
    try {
      await axios.post(`${API}/groups/${groupId}/loans/${loanId}/disburse`);
      toast.success('Loan disbursed successfully!');
      loadLoans();
      loadLoanStats();
      
      // Refresh loan details
      if (showLoanDetails && selectedLoan?.id === loanId) {
        const updated = await axios.get(`${API}/groups/${groupId}/loans/${loanId}`);
        setSelectedLoan(updated.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to disburse loan');
    } finally {
      setSigning(false);
    }
  };
  
  const handleRecordRepayment = async () => {
    if (!repaymentData.amount || parseFloat(repaymentData.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    
    setSigning(true);
    try {
      await axios.post(`${API}/groups/${groupId}/loans/${selectedLoan.id}/repayment`, {
        amount: parseFloat(repaymentData.amount),
        payment_method: repaymentData.payment_method,
        reference: repaymentData.reference,
        notes: repaymentData.notes
      });
      toast.success('Repayment recorded successfully!');
      setShowRepaymentModal(false);
      setRepaymentData({ amount: '', payment_method: 'cash', reference: '', notes: '' });
      loadLoans();
      loadLoanStats();
      
      // Refresh loan details
      const updated = await axios.get(`${API}/groups/${groupId}/loans/${selectedLoan.id}`);
      setSelectedLoan(updated.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record repayment');
    } finally {
      setSigning(false);
    }
  };
  
  const handleCancelLoan = async (loanId) => {
    if (!window.confirm('Cancel this loan application? This action cannot be undone.')) {
      return;
    }
    
    try {
      await axios.post(`${API}/groups/${groupId}/loans/${loanId}/cancel`);
      toast.success('Loan cancelled');
      loadLoans();
      loadLoanStats();
      setShowLoanDetails(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel loan');
    }
  };
  
  const openSetTermsModal = (loan) => {
    setSetTermsLoan(loan);
    setTermsFormData({
      term_months: loan.term_months || 12,
      interest_rate: 10, interest_type: 'simple', rate_period: 'monthly', repayment_mode: 'interest_only',
      origination_fee_type: 'percentage', origination_fee_value: 0,
      disbursement_method: 'manual', manual_payment_method: 'cash', manual_payment_details: '',
      bank_routing_number: '', bank_account_number: '',
      guarantor_ids: [], grace_period_days: 7, default_action: 'probation',
      late_fee_type: 'none', late_fee_value: 0, notes: ''
    });
    setShowSetTermsModal(true);
  };

  const handleSetTerms = async () => {
    if (!setTermsLoan) return;
    setSettingTerms(true);
    try {
      await axios.put(`${API}/groups/${groupId}/loans/${setTermsLoan.id}/set-terms`, termsFormData);
      toast.success('Loan terms set. Waiting for member to accept or decline.');
      setShowSetTermsModal(false);
      setSetTermsLoan(null);
      loadLoans();
      loadLoanStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to set loan terms');
    } finally {
      setSettingTerms(false);
    }
  };

  const toggleTermsGuarantor = (memberId) => {
    setTermsFormData(prev => {
      const current = prev.guarantor_ids || [];
      if (current.includes(memberId)) {
        return { ...prev, guarantor_ids: current.filter(id => id !== memberId) };
      }
      return { ...prev, guarantor_ids: [...current, memberId] };
    });
  };

  const viewLoanDetails = async (loan) => {
    try {
      const response = await axios.get(`${API}/groups/${groupId}/loans/${loan.id}`);
      setSelectedLoan(response.data);
      setShowLoanDetails(true);
    } catch (error) {
      toast.error('Failed to load loan details');
    }
  };
  
  const getLoanStatusColor = (status) => {
    const colors = {
      requested: { bg: '#E0E7FF', text: '#4338CA' },
      terms_set: { bg: '#FDE68A', text: '#78350F' },
      pending: { bg: '#FEF3C7', text: '#92400E' },
      approved: { bg: '#DBEAFE', text: '#1E40AF' },
      active: { bg: '#D1FAE5', text: '#065F46' },
      disbursed: { bg: '#D1FAE5', text: '#065F46' },
      completed: { bg: '#E0E7FF', text: '#3730A3' },
      declined: { bg: '#FEE2E2', text: '#991B1B' },
      defaulted: { bg: '#FEE2E2', text: '#DC2626' },
      cancelled: { bg: '#F3F4F6', text: '#6B7280' }
    };
    return colors[status] || colors.pending;
  };
  
  const toggleGuarantor = (memberId) => {
    setLoanFormData(prev => {
      const current = prev.guarantor_ids || [];
      if (current.includes(memberId)) {
        return { ...prev, guarantor_ids: current.filter(id => id !== memberId) };
      } else {
        return { ...prev, guarantor_ids: [...current, memberId] };
      }
    });
  };
  
  // =============== DEFAULT HANDLING FUNCTIONS ===============
  
  const handleCheckDefaults = async () => {
    if (!window.confirm('Run default check? This will mark overdue loans and apply penalties based on loan settings.')) {
      return;
    }
    
    setSigning(true);
    try {
      const response = await axios.post(`${API}/groups/${groupId}/loans/check-defaults`);
      const results = response.data.results;
      toast.success(`Default check complete: ${results.loans_checked} loans checked, ${results.overdue_found} overdue found, ${results.defaults_applied} actions applied`);
      loadLoans();
      loadLoanStats();
      loadOverdueLoans();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to check defaults');
    } finally {
      setSigning(false);
    }
  };

  const handleSendReminders = async () => {
    try {
      const response = await axios.post(`${API}/cron/loan-repayment-reminders?days_before=3`);
      const r = response.data.results;
      toast.success(`Reminders: ${r.reminders_sent} sent, ${r.already_reminded} already reminded, ${r.loans_checked} loans checked`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reminders');
    }
  };

  const handleSendSms = async () => {
    if (!smsMessage.trim()) { toast.error('Please enter a message'); return; }
    setSendingSms(true);
    try {
      const res = await axios.post(`${API}/groups/${groupId}/sms/send`, {
        message: smsMessage.trim(),
        recipient_filter: smsRecipientFilter
      }, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      toast.success(res.data.message);
      setSmsMessage('');
      setShowSmsCompose(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send SMS');
    } finally {
      setSendingSms(false);
    }
  };
  
  const handleWaiveLateFees = async (loanId) => {
    if (!window.confirm('Waive all late fees for this loan?')) {
      return;
    }
    
    try {
      const response = await axios.post(`${API}/groups/${groupId}/loans/${loanId}/waive-late-fees`);
      toast.success(response.data.message);
      loadLoans();
      loadOverdueLoans();
      
      if (showLoanDetails && selectedLoan?.id === loanId) {
        const updated = await axios.get(`${API}/groups/${groupId}/loans/${loanId}`);
        setSelectedLoan(updated.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to waive late fees');
    }
  };
  
  const handleRestoreMember = async (loanId) => {
    if (!window.confirm('Restore this member to active status?')) {
      return;
    }
    
    try {
      const response = await axios.post(`${API}/groups/${groupId}/loans/${loanId}/restore-member`);
      toast.success(response.data.message);
      loadLoans();
      loadOverdueLoans();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to restore member');
    }
  };

  return (
    <>
    <div className="disbursements-tab" data-testid="disbursements-tab">
      <div className="tab-header">
        <h1>{mainTab === 'loans' ? 'Loans' : 'Disbursements'}</h1>
        <div className="tab-header-actions">
          {mainTab === 'disbursements' && (
            <>
              {disbursementType === 'bank' ? (
                <button onClick={onCreateDisbursement} className="btn btn-primary" data-testid="create-disbursement-btn">
                  + Bank Transfer Disbursement
                </button>
              ) : (
                <button onClick={() => setShowManualForm(true)} className="btn btn-primary" data-testid="create-manual-disbursement-btn">
                  + Manual Disbursement
                </button>
              )}
            </>
          )}
          {mainTab === 'loans' && !isBenevolenceGroup && (
            <button onClick={() => setShowLoanForm(true)} className="btn btn-primary" data-testid="create-loan-btn">
              + New Loan Application
            </button>
          )}
        </div>
      </div>
      
      {/* Main Tab Toggle - Disbursements vs Loans */}
      <div style={{ 
        display: 'flex', 
        gap: '0', 
        marginBottom: '20px',
        borderBottom: '2px solid #E5E7EB',
        paddingBottom: '0'
      }}>
        <button
          onClick={() => setMainTab('disbursements')}
          style={{
            padding: '12px 24px',
            border: 'none',
            background: mainTab === 'disbursements' ? '#3B82F6' : 'transparent',
            color: mainTab === 'disbursements' ? 'white' : '#6B7280',
            borderRadius: '8px 8px 0 0',
            cursor: 'pointer',
            fontWeight: '600',
            fontSize: '14px',
            transition: 'all 0.2s'
          }}
          data-testid="main-tab-disbursements"
        >
          💸 Disbursements
        </button>
        {!isBenevolenceGroup && (
          <button
            onClick={() => setMainTab('loans')}
            style={{
              padding: '12px 24px',
              border: 'none',
              background: mainTab === 'loans' ? '#8B5CF6' : 'transparent',
              color: mainTab === 'loans' ? 'white' : '#6B7280',
              borderRadius: '8px 8px 0 0',
              cursor: 'pointer',
              fontWeight: '600',
              fontSize: '14px',
              transition: 'all 0.2s'
            }}
            data-testid="main-tab-loans"
          >
            🏦 Loans {loanStats?.active_loans > 0 && <span style={{ background: 'white', color: '#8B5CF6', padding: '2px 8px', borderRadius: '10px', fontSize: '12px', marginLeft: '6px' }}>{loanStats.active_loans}</span>}
          </button>
        )}
      </div>
      
      {/* Search & Time Frame Filters */}
      <div data-testid="disbursements-search-filters" style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div style={{ flex: '1 1 220px' }}>
          <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', color: '#6B7280', marginBottom: '4px' }}>
            {mainTab === 'loans' ? 'Search by loan # or applicant name' : 'Search by name or description'}
          </label>
          <input
            type="text"
            value={disbSearchQuery}
            onChange={(e) => setDisbSearchQuery(e.target.value)}
            placeholder="Type to search..."
            data-testid="disbursements-search-input"
            style={{ width: '100%', padding: '8px 12px', border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '14px' }}
          />
        </div>
        <div style={{ flex: '0 0 160px' }}>
          <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', color: '#6B7280', marginBottom: '4px' }}>From</label>
          <input
            type="date"
            value={disbFilterStartDate}
            onChange={(e) => setDisbFilterStartDate(e.target.value)}
            data-testid="disbursements-start-date"
            style={{ width: '100%', padding: '8px 12px', border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '14px' }}
          />
        </div>
        <div style={{ flex: '0 0 160px' }}>
          <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', color: '#6B7280', marginBottom: '4px' }}>To</label>
          <input
            type="date"
            value={disbFilterEndDate}
            onChange={(e) => setDisbFilterEndDate(e.target.value)}
            data-testid="disbursements-end-date"
            style={{ width: '100%', padding: '8px 12px', border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '14px' }}
          />
        </div>
        {(disbSearchQuery || disbFilterStartDate || disbFilterEndDate) && (
          <button
            onClick={() => { setDisbSearchQuery(''); setDisbFilterStartDate(''); setDisbFilterEndDate(''); }}
            data-testid="disbursements-clear-search"
            style={{ padding: '8px 16px', border: '1px solid #D1D5DB', borderRadius: '8px', background: '#F9FAFB', cursor: 'pointer', fontSize: '13px', color: '#374151', whiteSpace: 'nowrap' }}
          >
            Clear Filters
          </button>
        )}
      </div>

      {/* LOANS TAB CONTENT */}
      {mainTab === 'loans' && !isBenevolenceGroup && (
        <div className="loans-content">
          {/* Loan Stats */}
          {loanStats && (
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', 
              gap: '12px', 
              marginBottom: '24px' 
            }}>
              <div style={{ background: '#F3F4F6', padding: '16px', borderRadius: '12px', textAlign: 'center' }}>
                <div style={{ fontSize: '24px', fontWeight: '700', color: '#1F2937' }}>{loanStats.total_loans}</div>
                <div style={{ color: '#6B7280', fontSize: '13px' }}>Total Loans</div>
              </div>
              <div style={{ background: '#FEF3C7', padding: '16px', borderRadius: '12px', textAlign: 'center' }}>
                <div style={{ fontSize: '24px', fontWeight: '700', color: '#92400E' }}>{loanStats.pending_loans}</div>
                <div style={{ color: '#92400E', fontSize: '13px' }}>Pending</div>
              </div>
              <div style={{ background: '#D1FAE5', padding: '16px', borderRadius: '12px', textAlign: 'center' }}>
                <div style={{ fontSize: '24px', fontWeight: '700', color: '#065F46' }}>{loanStats.active_loans}</div>
                <div style={{ color: '#065F46', fontSize: '13px' }}>Active</div>
              </div>
              <div style={{ background: '#DBEAFE', padding: '16px', borderRadius: '12px', textAlign: 'center' }}>
                <div style={{ fontSize: '24px', fontWeight: '700', color: '#1E40AF' }}>{formatCurrency(loanStats.total_disbursed, group?.currency)}</div>
                <div style={{ color: '#1E40AF', fontSize: '13px' }}>Disbursed</div>
              </div>
              <div style={{ background: '#E0E7FF', padding: '16px', borderRadius: '12px', textAlign: 'center' }}>
                <div style={{ fontSize: '24px', fontWeight: '700', color: '#3730A3' }}>{formatCurrency(loanStats.total_outstanding, group?.currency)}</div>
                <div style={{ color: '#3730A3', fontSize: '13px' }}>Outstanding</div>
              </div>
            </div>
          )}
          
          {/* Overdue Loans Alert */}
          {overdueLoans.length > 0 && (
            <div style={{ 
              background: '#FEE2E2', 
              border: '1px solid #FECACA',
              borderRadius: '12px', 
              padding: '16px', 
              marginBottom: '20px' 
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <h4 style={{ margin: 0, color: '#DC2626' }}>Overdue Loans ({overdueLoans.length})</h4>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    onClick={handleSendReminders}
                    className="btn btn-secondary btn-sm"
                    data-testid="send-reminders-btn"
                    style={{ background: '#FEF3C7', color: '#92400E', border: '1px solid #F59E0B' }}
                  >
                    Send Reminders
                  </button>
                  <button
                    onClick={() => { setSmsRecipientFilter('loanees'); setShowSmsCompose(!showSmsCompose); }}
                    className="btn btn-sm"
                    data-testid="sms-loanees-btn"
                    style={{ background: '#DBEAFE', color: '#1E40AF', border: '1px solid #3B82F6' }}
                  >
                    SMS Loanees
                  </button>
                  <button
                    onClick={handleCheckDefaults}
                    className="btn btn-danger btn-sm"
                    disabled={signing}
                    data-testid="check-defaults-btn"
                  >
                    {signing ? 'Checking...' : 'Run Default Check'}
                  </button>
                </div>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {overdueLoans.slice(0, 5).map(loan => (
                  <div 
                    key={loan.id}
                    onClick={() => viewLoanDetails(loan)}
                    style={{
                      background: 'white',
                      padding: '10px 14px',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '4px',
                      minWidth: '160px'
                    }}
                  >
                    <span style={{ fontWeight: '600', fontSize: '13px' }}>{loan.loan_number}</span>
                    <span style={{ fontSize: '12px', color: '#6B7280' }}>{loan.applicant_name}</span>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px' }}>
                      <span style={{ color: '#DC2626' }}>{loan.overdue_payments} overdue</span>
                      <span style={{ fontWeight: '500' }}>{formatCurrency(loan.overdue_amount, group?.currency)}</span>
                    </div>
                    {loan.total_late_fees > 0 && (
                      <span style={{ fontSize: '11px', color: '#9CA3AF' }}>
                        Late fees: {formatCurrency(loan.total_late_fees, group?.currency)}
                      </span>
                    )}
                  </div>
                ))}
                {overdueLoans.length > 5 && (
                  <div style={{ 
                    background: 'white', 
                    padding: '10px 14px', 
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    color: '#6B7280',
                    fontSize: '13px'
                  }}>
                    +{overdueLoans.length - 5} more
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* SMS Compose Panel */}
          {showSmsCompose && (
            <div style={{ background: '#EFF6FF', borderRadius: '12px', padding: '20px', marginBottom: '20px', border: '1px solid #BFDBFE' }} data-testid="sms-compose-panel">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <h4 style={{ margin: 0, color: '#1E40AF' }}>Send SMS Notification</h4>
                <button onClick={() => setShowSmsCompose(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6B7280', fontSize: '18px' }}>&times;</button>
              </div>
              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', fontWeight: 600, fontSize: '13px', color: '#374151', marginBottom: '4px' }}>Recipients</label>
                <select
                  value={smsRecipientFilter}
                  onChange={(e) => setSmsRecipientFilter(e.target.value)}
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '14px' }}
                  data-testid="sms-recipient-filter"
                >
                  <option value="all">All Members</option>
                  <option value="active">Active Members Only</option>
                  <option value="loanees">Active Loanees Only</option>
                </select>
              </div>
              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', fontWeight: 600, fontSize: '13px', color: '#374151', marginBottom: '4px' }}>Message</label>
                <textarea
                  value={smsMessage}
                  onChange={(e) => setSmsMessage(e.target.value)}
                  rows={3}
                  maxLength={320}
                  placeholder="Type your SMS message..."
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '14px', resize: 'vertical', boxSizing: 'border-box' }}
                  data-testid="sms-message-input"
                />
                <small style={{ color: '#9CA3AF', fontSize: '12px' }}>{smsMessage.length}/320 characters (SMS limit: 160 per part)</small>
              </div>
              <button
                onClick={handleSendSms}
                disabled={sendingSms || !smsMessage.trim()}
                className="btn btn-primary btn-sm"
                data-testid="send-sms-btn"
                style={{ background: '#2563EB', color: 'white', border: 'none', padding: '10px 24px', borderRadius: '8px', cursor: 'pointer' }}
              >
                {sendingSms ? 'Sending...' : 'Send SMS'}
              </button>
            </div>
          )}

          {/* Loanee Summary Table */}
          {loanSummary && loanSummary.loanees.length > 0 && (
            <div style={{ background: 'white', borderRadius: '12px', padding: '20px', marginBottom: '20px', border: '1px solid #E5E7EB' }} data-testid="loan-summary-section">
              <h3 style={{ margin: '0 0 16px', fontSize: '16px', fontWeight: 700, color: '#1F2937' }}>Loan Portfolio Summary</h3>
              
              {/* Summary Totals */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px', marginBottom: '16px' }}>
                {[
                  { label: 'Total Principal', value: formatCurrency(loanSummary.totals.total_principal, loanSummary.currency), color: '#3B82F6' },
                  { label: 'Interest Paid', value: formatCurrency(loanSummary.totals.total_interest_paid, loanSummary.currency), color: '#10B981' },
                  { label: 'Principal Paid', value: formatCurrency(loanSummary.totals.total_principal_paid, loanSummary.currency), color: '#059669' },
                  { label: 'Outstanding Principal', value: formatCurrency(loanSummary.totals.total_outstanding_principal, loanSummary.currency), color: '#F59E0B' },
                  { label: 'Outstanding Interest', value: formatCurrency(loanSummary.totals.total_outstanding_interest, loanSummary.currency), color: '#EF4444' },
                ].map((item, i) => (
                  <div key={i} style={{ background: '#F9FAFB', borderRadius: '8px', padding: '12px', textAlign: 'center' }}>
                    <div style={{ fontSize: '12px', color: '#6B7280', marginBottom: '4px' }}>{item.label}</div>
                    <div style={{ fontSize: '16px', fontWeight: 700, color: item.color }}>{item.value}</div>
                  </div>
                ))}
              </div>
              
              {/* Loanee Table */}
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                  <thead>
                    <tr style={{ background: '#F3F4F6' }}>
                      <th style={{ padding: '10px 8px', textAlign: 'left', fontWeight: 600, color: '#374151' }}>Loanee</th>
                      <th style={{ padding: '10px 8px', textAlign: 'left', fontWeight: 600, color: '#374151' }}>Loan #</th>
                      <th style={{ padding: '10px 8px', textAlign: 'right', fontWeight: 600, color: '#374151' }}>Principal</th>
                      <th style={{ padding: '10px 8px', textAlign: 'center', fontWeight: 600, color: '#374151' }}>Interest Type</th>
                      <th style={{ padding: '10px 8px', textAlign: 'center', fontWeight: 600, color: '#374151' }}>Repayment Mode</th>
                      <th style={{ padding: '10px 8px', textAlign: 'right', fontWeight: 600, color: '#374151' }}>Interest Paid</th>
                      <th style={{ padding: '10px 8px', textAlign: 'right', fontWeight: 600, color: '#374151' }}>Principal Paid</th>
                      <th style={{ padding: '10px 8px', textAlign: 'right', fontWeight: 600, color: '#374151' }}>Outstanding</th>
                      <th style={{ padding: '10px 8px', textAlign: 'center', fontWeight: 600, color: '#374151' }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loanSummary.loanees.map((loanee, idx) => (
                      <tr key={idx} style={{ borderBottom: '1px solid #E5E7EB' }}>
                        <td style={{ padding: '10px 8px', fontWeight: 500 }}>{loanee.applicant_name}</td>
                        <td style={{ padding: '10px 8px', color: '#6B7280' }}>{loanee.loan_number}</td>
                        <td style={{ padding: '10px 8px', textAlign: 'right' }}>{formatCurrency(loanee.principal, loanSummary.currency)}</td>
                        <td style={{ padding: '10px 8px', textAlign: 'center' }}>
                          <span style={{ background: '#EEF2FF', color: '#4F46E5', padding: '2px 8px', borderRadius: '12px', fontSize: '11px' }}>
                            {describeLoanRate(loanee, getCurrencySymbol(loanSummary.currency))}
                          </span>
                        </td>
                        <td style={{ padding: '10px 8px', textAlign: 'center' }}>
                          <span style={{ background: loanee.repayment_mode === 'interest_only' ? '#FEF3C7' : '#D1FAE5', color: loanee.repayment_mode === 'interest_only' ? '#92400E' : '#065F46', padding: '2px 8px', borderRadius: '12px', fontSize: '11px' }}>
                            {loanee.repayment_mode === 'interest_only' ? 'Interest Only' : 'P + I'}
                          </span>
                        </td>
                        <td style={{ padding: '10px 8px', textAlign: 'right', color: '#10B981' }}>{formatCurrency(loanee.interest_paid, loanSummary.currency)}</td>
                        <td style={{ padding: '10px 8px', textAlign: 'right', color: '#059669' }}>{formatCurrency(loanee.principal_paid, loanSummary.currency)}</td>
                        <td style={{ padding: '10px 8px', textAlign: 'right', fontWeight: 600, color: '#EF4444' }}>{formatCurrency(loanee.outstanding_principal, loanSummary.currency)}</td>
                        <td style={{ padding: '10px 8px', textAlign: 'center' }}>
                          <span style={{ 
                            padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: 600,
                            background: loanee.status === 'active' ? '#D1FAE5' : loanee.status === 'completed' ? '#DBEAFE' : loanee.status === 'defaulted' ? '#FEE2E2' : '#F3F4F6',
                            color: loanee.status === 'active' ? '#065F46' : loanee.status === 'completed' ? '#1E40AF' : loanee.status === 'defaulted' ? '#991B1B' : '#374151'
                          }}>
                            {loanee.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Loans List */}
          {(() => {
            const q = disbSearchQuery.toLowerCase().trim();
            const filteredLoans = loans.filter(loan => {
              if (q && !(
                (loan.loan_number || '').toLowerCase().includes(q) ||
                (loan.applicant_name || '').toLowerCase().includes(q)
              )) return false;
              if (disbFilterStartDate && new Date(loan.created_at) < new Date(disbFilterStartDate)) return false;
              if (disbFilterEndDate && new Date(loan.created_at) > new Date(disbFilterEndDate + 'T23:59:59')) return false;
              return true;
            });
            if (loans.length === 0) return (
            <div className="empty-state card" style={{ textAlign: 'center', padding: '40px' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>🏦</div>
              <h3>No Loans Yet</h3>
              <p style={{ color: '#6B7280' }}>Create your first loan application to get started</p>
              <button 
                onClick={() => setShowLoanForm(true)} 
                className="btn btn-primary"
                style={{ marginTop: '16px' }}
              >
                + New Loan Application
              </button>
            </div>
          );
            if (filteredLoans.length === 0) return (
              <p style={{ textAlign: 'center', color: '#9CA3AF', padding: '20px' }}>No loans match the current filters</p>
            );
            return (
            <div style={{ display: 'grid', gap: '16px' }}>
              {filteredLoans.map((loan) => {
                const statusColor = getLoanStatusColor(loan.status);
                return (
                  <div 
                    key={loan.id} 
                    className="card"
                    style={{ 
                      padding: '20px',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      border: '1px solid #E5E7EB'
                    }}
                    onClick={() => viewLoanDetails(loan)}
                    data-testid={`loan-card-${loan.id}`}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                      <div>
                        <span style={{ fontWeight: '600', color: '#6B7280', fontSize: '13px' }}>{loan.loan_number}</span>
                        <h3 style={{ margin: '4px 0', fontSize: '18px' }}>{loan.applicant_name}</h3>
                        <span style={{ 
                          background: loan.applicant_type === 'member' ? '#DBEAFE' : '#FEF3C7',
                          color: loan.applicant_type === 'member' ? '#1E40AF' : '#92400E',
                          padding: '2px 8px',
                          borderRadius: '4px',
                          fontSize: '11px',
                          textTransform: 'capitalize'
                        }}>
                          {loan.applicant_type === 'member' ? '👤 Member' : '👥 Non-Member'}
                        </span>
                      </div>
                      <span style={{
                        background: statusColor.bg,
                        color: statusColor.text,
                        padding: '4px 12px',
                        borderRadius: '6px',
                        fontSize: '13px',
                        fontWeight: '500',
                        textTransform: 'capitalize'
                      }}>
                        {loan.status === 'terms_set' ? 'Terms Set' : loan.status}
                      </span>
                    </div>
                    
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '12px', marginBottom: '12px' }}>
                      <div>
                        <div style={{ color: '#6B7280', fontSize: '12px' }}>Amount</div>
                        <div style={{ fontWeight: '600', fontSize: '16px' }}>{formatCurrency(loan.amount, group?.currency)}</div>
                      </div>
                      <div>
                        <div style={{ color: '#6B7280', fontSize: '12px' }}>Interest</div>
                        <div style={{ fontWeight: '500' }}>{describeLoanRate(loan, getCurrencySymbol(group?.currency))}</div>
                      </div>
                      <div>
                        <div style={{ color: '#6B7280', fontSize: '12px' }}>Term</div>
                        <div style={{ fontWeight: '500' }}>{loan.term_months} months</div>
                      </div>
                      <div>
                        <div style={{ color: '#6B7280', fontSize: '12px' }}>Repaid</div>
                        <div style={{ fontWeight: '500', color: '#10B981' }}>{formatCurrency(loan.amount_repaid || 0, group?.currency)}</div>
                      </div>
                    </div>
                    
                    {/* Member-requested loan - Set Terms */}
                    {loan.status === 'requested' && (
                      <div style={{ background: '#EEF2FF', padding: '12px', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div>
                          <span style={{ fontWeight: '500', fontSize: '13px', color: '#4338CA' }}>Member Request</span>
                          <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#6B7280' }}>Review and set loan terms for this member request.</p>
                        </div>
                        <button
                          onClick={(e) => { e.stopPropagation(); openSetTermsModal(loan); }}
                          className="btn btn-primary btn-sm"
                          data-testid={`set-terms-${loan.id}`}
                          style={{ background: '#4338CA', whiteSpace: 'nowrap' }}
                        >
                          Set Terms
                        </button>
                      </div>
                    )}

                    {/* Terms set - awaiting member response */}
                    {loan.status === 'terms_set' && (
                      <div style={{ background: '#FEF3C7', padding: '12px', borderRadius: '8px' }}>
                        <span style={{ fontWeight: '500', fontSize: '13px', color: '#92400E' }}>Awaiting Member Response</span>
                        <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#78350F' }}>Terms have been sent. Waiting for the member to accept or decline.</p>
                      </div>
                    )}

                    {/* Declined by member */}
                    {loan.status === 'declined' && (
                      <div style={{ background: '#FEE2E2', padding: '12px', borderRadius: '8px' }}>
                        <span style={{ fontWeight: '500', fontSize: '13px', color: '#991B1B' }}>Declined by Member</span>
                        <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#7F1D1D' }}>The member declined the loan offer.</p>
                      </div>
                    )}

                    {/* Signatures for pending loans */}
                    {loan.status === 'pending' && (
                      <div style={{ 
                        background: '#F9FAFB', 
                        padding: '12px', 
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                      }}>
                        <div>
                          <span style={{ fontWeight: '500', fontSize: '13px' }}>Signatures: </span>
                          <span>{loan.signed_by?.length || 0}/3 required</span>
                          <div style={{ display: 'flex', gap: '4px', marginTop: '4px' }}>
                            {[1, 2, 3].map(num => (
                              <span 
                                key={num}
                                style={{
                                  width: '20px',
                                  height: '20px',
                                  borderRadius: '50%',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '12px',
                                  background: (loan.signed_by?.length || 0) >= num ? '#10B981' : '#E5E7EB',
                                  color: (loan.signed_by?.length || 0) >= num ? 'white' : '#9CA3AF'
                                }}
                              >
                                {(loan.signed_by?.length || 0) >= num ? '✓' : num}
                              </span>
                            ))}
                          </div>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedLoan(loan);
                            setShowLoanSignModal(true);
                          }}
                          className="btn btn-primary btn-sm"
                          data-testid={`sign-loan-${loan.id}`}
                        >
                          🔒 Sign
                        </button>
                      </div>
                    )}
                    
                    {/* Disburse button for approved loans */}
                    {loan.status === 'approved' && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDisburseLoan(loan.id);
                        }}
                        className="btn btn-primary full-width"
                        style={{ marginTop: '12px' }}
                        data-testid={`disburse-loan-${loan.id}`}
                      >
                        💸 Disburse Loan
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
            );
          })()}
        </div>
      )}
      
      {/* DISBURSEMENTS TAB CONTENT */}
      {mainTab === 'disbursements' && (
        <>

      {/* Disbursement Type Toggle */}
      <div className="disbursement-type-toggle" style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
        <button 
          className={`btn ${disbursementType === 'bank' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setDisbursementType('bank')}
          data-testid="bank-disbursement-tab"
        >
          🏦 Bank Transfer
        </button>
        <button 
          className={`btn ${disbursementType === 'manual' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setDisbursementType('manual')}
          data-testid="manual-disbursement-tab"
        >
          💵 Manual / Other
        </button>
      </div>

      {/* Bank Transfer Disbursements */}
      {disbursementType === 'bank' && (
        <>
          {(() => {
            const q = disbSearchQuery.toLowerCase().trim();
            const filteredDisb = disbursements.filter(d => {
              if (q && !(
                (d.beneficiary_name || '').toLowerCase().includes(q) ||
                (d.description || '').toLowerCase().includes(q)
              )) return false;
              if (disbFilterStartDate && new Date(d.created_at) < new Date(disbFilterStartDate)) return false;
              if (disbFilterEndDate && new Date(d.created_at) > new Date(disbFilterEndDate + 'T23:59:59')) return false;
              return true;
            });
            if (disbursements.length === 0) return (
            <div className="empty-state card">
              <h3>No bank disbursements yet</h3>
              <p>Create a bank transfer disbursement request to get started</p>
            </div>
            );
            if (filteredDisb.length === 0) return (
              <p style={{ textAlign: 'center', color: '#9CA3AF', padding: '20px' }}>No bank disbursements match the current filters</p>
            );
            return (
            <div className="disbursements-grid">
              {filteredDisb.map((disbursement) => (
                <div key={disbursement.id} className="disbursement-card card">
                  <div className="disbursement-header">
                    <h3>{formatCurrency(disbursement.amount, group?.currency)}</h3>
                    <span className={`status-badge status-${disbursement.status}`}>
                      {disbursement.status}
                    </span>
                  </div>
                  
                  <div className="disbursement-details">
                    <p><strong>Beneficiary:</strong> {disbursement.beneficiary_name || 'Not specified'}</p>
                    <p><strong>Description:</strong> {disbursement.description}</p>
                    <p><strong>Bank Account:</strong> ****{disbursement.bank_account_last4 || '****'}</p>
                    <p><strong>Routing:</strong> ****{disbursement.bank_routing_number?.slice(-4) || '****'}</p>
                  </div>
                  
                  <div className="disbursement-signatures">
                    <p><strong>Signatures:</strong> {disbursement.signed_by?.length || 0} / 3 required</p>
                    <div className="signature-indicators">
                      {[1, 2, 3].map(num => (
                        <span 
                          key={num} 
                          className={`signature-dot ${(disbursement.signed_by?.length || 0) >= num ? 'signed' : 'pending'}`}
                          title={`Signature ${num}`}
                        >
                          {(disbursement.signed_by?.length || 0) >= num ? '✓' : '○'}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  {disbursement.status === 'pending' && (
                    <button 
                      onClick={() => initiateSign(disbursement)}
                      className="btn btn-secondary full-width mt-2"
                      data-testid={`sign-disbursement-${disbursement.id}`}
                    >
                      🔒 Sign Disbursement
                    </button>
                  )}
                  
                  {disbursement.status === 'approved' && (
                    <>
                      <div className="approved-banner">
                        ✓ Approved - Ready for Transfer
                      </div>
                      <button 
                        onClick={() => handleDirectTransfer(disbursement.id)}
                        className="btn btn-primary full-width mt-2"
                        data-testid={`transfer-disbursement-${disbursement.id}`}
                      >
                        💸 Transfer to Beneficiary
                      </button>
                    </>
                  )}
                  
                  {disbursement.status === 'transferred' && (
                    <div className="transferred-banner">
                      ✓ Transferred
                      {disbursement.payout_id && (
                        <div className="payout-id-small">
                          Payout: {disbursement.payout_id.substring(0, 20)}...
                        </div>
                      )}
                    </div>
                  )}
                  
                  {disbursement.status === 'processing' && (
                    <div className="processing-banner">
                      ⏳ Processing Transfer...
                    </div>
                  )}
                </div>
              ))}
            </div>
            );
          })()}
        </>
      )}

      {/* Manual Disbursements */}
      {disbursementType === 'manual' && (
        <>
          {(() => {
            const q = disbSearchQuery.toLowerCase().trim();
            const filteredManual = manualDisbursements.filter(d => {
              if (q && !(
                (d.beneficiary_name || '').toLowerCase().includes(q) ||
                (d.reason || '').toLowerCase().includes(q) ||
                (d.description || '').toLowerCase().includes(q)
              )) return false;
              if (disbFilterStartDate && new Date(d.created_at) < new Date(disbFilterStartDate)) return false;
              if (disbFilterEndDate && new Date(d.created_at) > new Date(disbFilterEndDate + 'T23:59:59')) return false;
              return true;
            });
            if (manualDisbursements.length === 0) return (
            <div className="empty-state card">
              <h3>No manual disbursements yet</h3>
              <p>Create a manual disbursement for beneficiaries without bank accounts</p>
              <p className="text-muted" style={{ marginTop: '8px', fontSize: '13px' }}>
                Use this for cash payments, mobile money, checks, or other payment methods
              </p>
            </div>
            );
            if (filteredManual.length === 0) return (
              <p style={{ textAlign: 'center', color: '#9CA3AF', padding: '20px' }}>No manual disbursements match the current filters</p>
            );
            return (
            <div className="disbursements-grid">
              {filteredManual.map((disbursement) => (
                <div key={disbursement.id} className="disbursement-card card">
                  <div className="disbursement-header">
                    <h3>{formatCurrency(disbursement.amount, group?.currency)}</h3>
                    <span className={`status-badge status-${disbursement.status}`}>
                      {disbursement.status}
                    </span>
                  </div>
                  
                  <div className="disbursement-details">
                    <p><strong>Beneficiary:</strong> {disbursement.beneficiary_name}</p>
                    <p><strong>Method:</strong> {getPaymentMethodLabel(disbursement.payment_method)}</p>
                    {disbursement.payment_details && (
                      <p><strong>Details:</strong> {disbursement.payment_details}</p>
                    )}
                    <p><strong>Reason:</strong> {disbursement.reason}</p>
                    {disbursement.description && (
                      <p><strong>Description:</strong> {disbursement.description}</p>
                    )}
                    <p className="text-muted" style={{ fontSize: '12px', marginTop: '8px' }}>
                      Created by {disbursement.created_by_name} on {new Date(disbursement.created_at).toLocaleDateString()}
                    </p>
                  </div>

                  {/* Proof Files Section */}
                  {disbursement.proof_files && disbursement.proof_files.length > 0 && (
                    <div className="proof-files-section" style={{ marginTop: '12px', padding: '10px', background: '#f8fafc', borderRadius: '8px' }}>
                      <p style={{ fontWeight: '600', fontSize: '13px', marginBottom: '8px' }}>📎 Proof of Disbursement ({disbursement.proof_files.length})</p>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                        {disbursement.proof_files.map((file, index) => (
                          <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '4px', background: '#e2e8f0', padding: '4px 8px', borderRadius: '4px', fontSize: '12px' }}>
                            <a href={`${API}${file}`} target="_blank" rel="noopener noreferrer" style={{ color: '#3B82F6' }}>
                              {file.includes('.pdf') ? '📄' : '🖼️'} File {index + 1}
                            </a>
                            {disbursement.status !== 'completed' && (
                              <button 
                                onClick={() => handleDeleteProof(disbursement.id, index)}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#EF4444', fontSize: '14px', padding: '0 4px' }}
                                title="Delete proof"
                              >
                                ×
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Action Buttons */}
                  {disbursement.status === 'pending' && (
                    <div style={{ display: 'flex', gap: '8px', marginTop: '12px', flexWrap: 'wrap' }}>
                      <button 
                        onClick={() => handleCompleteManualDisbursement(disbursement.id)}
                        className="btn btn-primary"
                        style={{ flex: 1, minWidth: '120px' }}
                        data-testid={`complete-manual-${disbursement.id}`}
                      >
                        ✓ Mark Completed
                      </button>
                      <button 
                        onClick={() => openProofUpload(disbursement)}
                        className="btn btn-secondary"
                        style={{ flex: 1, minWidth: '120px' }}
                        data-testid={`upload-proof-${disbursement.id}`}
                      >
                        📎 Upload Proof
                      </button>
                      <button 
                        onClick={() => handleCancelManualDisbursement(disbursement.id)}
                        className="btn btn-danger"
                        style={{ flex: 1, minWidth: '120px' }}
                        data-testid={`cancel-manual-${disbursement.id}`}
                      >
                        ✕ Cancel
                      </button>
                    </div>
                  )}
                  
                  {disbursement.status === 'completed' && (
                    <div className="transferred-banner" style={{ marginTop: '12px' }}>
                      ✓ Completed
                      {disbursement.completed_by_name && (
                        <div style={{ fontSize: '12px', opacity: '0.8' }}>
                          by {disbursement.completed_by_name} on {new Date(disbursement.completed_at).toLocaleDateString()}
                        </div>
                      )}
                      {/* Allow uploading more proofs even after completion */}
                      <button 
                        onClick={() => openProofUpload(disbursement)}
                        className="btn btn-secondary"
                        style={{ marginTop: '8px', fontSize: '12px' }}
                      >
                        📎 Add More Proofs
                      </button>
                    </div>
                  )}
                  
                  {disbursement.status === 'cancelled' && (
                    <div className="cancelled-banner" style={{ marginTop: '12px', background: '#FEE2E2', color: '#DC2626', padding: '12px', borderRadius: '8px', textAlign: 'center' }}>
                      ✕ Cancelled
                    </div>
                  )}
                </div>
              ))}
            </div>
            );
          })()}
        </>
      )}

      {/* Password Confirmation Modal for Bank Transfers */}
      {showPasswordModal && (
        <div className="modal-overlay" onClick={cancelSign}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>🔒 Confirm Your Signature</h2>
              <button className="close-button" onClick={cancelSign}>×</button>
            </div>
            
            <div className="password-confirm-content">
              <div className="disbursement-summary">
                <h3>Disbursement Details</h3>
                <p><strong>Amount:</strong> {formatCurrency(selectedDisbursement?.amount, group?.currency)}</p>
                <p><strong>Beneficiary:</strong> {selectedDisbursement?.beneficiary_name}</p>
                <p><strong>Description:</strong> {selectedDisbursement?.description}</p>
              </div>

              <div className="security-notice">
                <p>⚠️ For security purposes, please enter your password to confirm your signature on this disbursement.</p>
              </div>

              <div className="form-group">
                <label>Your Password *</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && confirmSign()}
                  placeholder="Enter your password"
                  autoFocus
                  className="password-input"
                />
              </div>

              <div className="modal-actions">
                <button 
                  onClick={cancelSign} 
                  className="btn btn-secondary"
                  disabled={signing}
                >
                  Cancel
                </button>
                <button 
                  onClick={confirmSign} 
                  className="btn btn-primary"
                  disabled={signing || !password}
                >
                  {signing ? 'Signing...' : 'Confirm & Sign'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Manual Disbursement Form Modal */}
      {showManualForm && (
        <div className="modal-overlay" onClick={() => setShowManualForm(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h2>💵 Create Manual Disbursement</h2>
              <button className="close-button" onClick={() => setShowManualForm(false)}>×</button>
            </div>
            
            <form onSubmit={handleCreateManualDisbursement}>
              <div className="form-group">
                <label>Beneficiary Name *</label>
                <input
                  type="text"
                  value={manualFormData.beneficiary_name}
                  onChange={(e) => setManualFormData({ ...manualFormData, beneficiary_name: e.target.value })}
                  placeholder="Enter beneficiary name"
                  required
                  data-testid="manual-beneficiary-input"
                />
              </div>

              <div className="form-group">
                <label>Amount ($) *</label>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={manualFormData.amount}
                  onChange={(e) => setManualFormData({ ...manualFormData, amount: e.target.value })}
                  placeholder="0.00"
                  required
                  data-testid="manual-amount-input"
                />
              </div>

              <div className="form-group">
                <label>Payment Method *</label>
                <select
                  value={manualFormData.payment_method}
                  onChange={(e) => setManualFormData({ ...manualFormData, payment_method: e.target.value })}
                  required
                  data-testid="manual-payment-method"
                >
                  <option value="cash">💵 Cash</option>
                  <option value="mobile_money">📱 Mobile Money</option>
                  <option value="check">📝 Check</option>
                  <option value="bank_transfer">🏦 Bank Transfer (Manual)</option>
                  <option value="other">📋 Other</option>
                </select>
              </div>

              {manualFormData.payment_method === 'mobile_money' && (
                <div className="form-group">
                  <label>Phone Number</label>
                  <input
                    type="text"
                    value={manualFormData.payment_details}
                    onChange={(e) => setManualFormData({ ...manualFormData, payment_details: e.target.value })}
                    placeholder="e.g., +254712345678"
                    data-testid="manual-phone-input"
                  />
                </div>
              )}

              {manualFormData.payment_method === 'check' && (
                <div className="form-group">
                  <label>Check Number</label>
                  <input
                    type="text"
                    value={manualFormData.payment_details}
                    onChange={(e) => setManualFormData({ ...manualFormData, payment_details: e.target.value })}
                    placeholder="e.g., Check #1234"
                    data-testid="manual-check-input"
                  />
                </div>
              )}

              {(manualFormData.payment_method === 'other' || manualFormData.payment_method === 'bank_transfer') && (
                <div className="form-group">
                  <label>Payment Details</label>
                  <input
                    type="text"
                    value={manualFormData.payment_details}
                    onChange={(e) => setManualFormData({ ...manualFormData, payment_details: e.target.value })}
                    placeholder="Enter payment details"
                    data-testid="manual-details-input"
                  />
                </div>
              )}

              <div className="form-group">
                <label>Reason for Disbursement *</label>
                <input
                  type="text"
                  value={manualFormData.reason}
                  onChange={(e) => setManualFormData({ ...manualFormData, reason: e.target.value })}
                  placeholder="e.g., Medical assistance, Emergency fund, etc."
                  required
                  data-testid="manual-reason-input"
                />
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={manualFormData.description}
                  onChange={(e) => setManualFormData({ ...manualFormData, description: e.target.value })}
                  placeholder="Additional details about this disbursement"
                  rows={3}
                  data-testid="manual-description-input"
                />
              </div>

              <div className="form-group">
                <label>Notes (Internal)</label>
                <textarea
                  value={manualFormData.notes}
                  onChange={(e) => setManualFormData({ ...manualFormData, notes: e.target.value })}
                  placeholder="Internal notes for leadership"
                  rows={2}
                  data-testid="manual-notes-input"
                />
              </div>

              <div className="modal-actions" style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
                <button 
                  type="button"
                  onClick={() => setShowManualForm(false)} 
                  className="btn btn-secondary"
                  style={{ flex: 1 }}
                >
                  Cancel
                </button>
                <button 
                  type="submit"
                  className="btn btn-primary"
                  disabled={signing}
                  style={{ flex: 1 }}
                  data-testid="submit-manual-disbursement"
                >
                  {signing ? 'Creating...' : 'Create Disbursement'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Proof Upload Modal */}
      {showProofUpload && selectedManualDisbursement && (
        <div className="modal-overlay" onClick={() => setShowProofUpload(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h2>📎 Upload Proof of Disbursement</h2>
              <button className="close-button" onClick={() => setShowProofUpload(false)}>×</button>
            </div>
            
            <div style={{ marginBottom: '16px' }}>
              <p><strong>Disbursement:</strong> {formatCurrency(selectedManualDisbursement.amount, group?.currency)} to {selectedManualDisbursement.beneficiary_name}</p>
              <p style={{ color: '#64748b', fontSize: '13px' }}>Upload receipts, photos, or documents as proof of payment</p>
            </div>

            <div 
              className="file-drop-zone"
              style={{
                border: '2px dashed #CBD5E1',
                borderRadius: '12px',
                padding: '24px',
                textAlign: 'center',
                background: '#F8FAFC',
                cursor: 'pointer',
                marginBottom: '16px'
              }}
              onClick={() => document.getElementById('proof-file-input').click()}
            >
              <input
                id="proof-file-input"
                type="file"
                multiple
                accept="image/*,.pdf"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
                data-testid="proof-file-input"
              />
              <div style={{ fontSize: '32px', marginBottom: '8px' }}>📁</div>
              <p style={{ fontWeight: '600', marginBottom: '4px' }}>Click to select files</p>
              <p style={{ color: '#64748b', fontSize: '13px' }}>JPG, PNG, GIF, WebP, PDF • Max 10MB per file</p>
            </div>

            {/* Selected Files Preview */}
            {proofFiles.length > 0 && (
              <div style={{ marginBottom: '16px' }}>
                <p style={{ fontWeight: '600', marginBottom: '8px' }}>Selected Files ({proofFiles.length})</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {proofFiles.map((file, index) => (
                    <div 
                      key={index}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '8px 12px',
                        background: '#E2E8F0',
                        borderRadius: '8px',
                        fontSize: '13px'
                      }}
                    >
                      <span>
                        {file.type.includes('pdf') ? '📄' : '🖼️'} {file.name}
                        <span style={{ color: '#64748b', marginLeft: '8px' }}>
                          ({(file.size / 1024 / 1024).toFixed(2)} MB)
                        </span>
                      </span>
                      <button
                        onClick={() => removeSelectedFile(index)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#EF4444', fontSize: '16px' }}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Existing Proofs */}
            {selectedManualDisbursement.proof_files && selectedManualDisbursement.proof_files.length > 0 && (
              <div style={{ marginBottom: '16px', padding: '12px', background: '#F0FDF4', borderRadius: '8px' }}>
                <p style={{ fontWeight: '600', marginBottom: '8px', color: '#166534' }}>
                  ✓ Already Uploaded ({selectedManualDisbursement.proof_files.length})
                </p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {selectedManualDisbursement.proof_files.map((file, index) => (
                    <a 
                      key={index}
                      href={`${API}${file}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        padding: '4px 8px',
                        background: '#DCFCE7',
                        borderRadius: '4px',
                        fontSize: '12px',
                        color: '#166534',
                        textDecoration: 'none'
                      }}
                    >
                      {file.includes('.pdf') ? '📄' : '🖼️'} File {index + 1}
                    </a>
                  ))}
                </div>
              </div>
            )}

            <div className="modal-actions" style={{ display: 'flex', gap: '12px' }}>
              <button 
                onClick={() => setShowProofUpload(false)} 
                className="btn btn-secondary"
                style={{ flex: 1 }}
              >
                Cancel
              </button>
              <button 
                onClick={handleUploadProofs}
                className="btn btn-primary"
                disabled={uploading || proofFiles.length === 0}
                style={{ flex: 1 }}
                data-testid="upload-proofs-btn"
              >
                {uploading ? 'Uploading...' : `Upload ${proofFiles.length} File(s)`}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Close disbursements tab content */}
      </>
      )}
      
      {/* ============= LOAN MODALS ============= */}
      
      {/* Loan Application Form Modal */}
      {showLoanForm && (
        <div className="modal-overlay" onClick={() => setShowLoanForm(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '700px', maxHeight: '90vh', overflow: 'auto' }}>
            <div className="modal-header">
              <h2>🏦 New Loan Application</h2>
              <button className="close-button" onClick={() => setShowLoanForm(false)}>×</button>
            </div>
            
            <form onSubmit={handleCreateLoan}>
              {/* Applicant Type */}
              <div className="form-group">
                <label>Applicant Type *</label>
                <div style={{ display: 'flex', gap: '12px' }}>
                  <button
                    type="button"
                    onClick={() => setLoanFormData({ ...loanFormData, applicant_type: 'member', non_member_id: '', non_member_name: '' })}
                    style={{
                      flex: 1,
                      padding: '12px',
                      border: loanFormData.applicant_type === 'member' ? '2px solid #3B82F6' : '1px solid #E5E7EB',
                      borderRadius: '8px',
                      background: loanFormData.applicant_type === 'member' ? '#EFF6FF' : 'white',
                      cursor: 'pointer'
                    }}
                  >
                    👤 Group Member
                  </button>
                  <button
                    type="button"
                    onClick={() => setLoanFormData({ ...loanFormData, applicant_type: 'non_member', member_id: '' })}
                    style={{
                      flex: 1,
                      padding: '12px',
                      border: loanFormData.applicant_type === 'non_member' ? '2px solid #8B5CF6' : '1px solid #E5E7EB',
                      borderRadius: '8px',
                      background: loanFormData.applicant_type === 'non_member' ? '#F5F3FF' : 'white',
                      cursor: 'pointer'
                    }}
                  >
                    👥 Non-Member
                  </button>
                </div>
              </div>
              
              {/* Member Selection */}
              {loanFormData.applicant_type === 'member' && (
                <div className="form-group">
                  <label>Select Member *</label>
                  <select
                    value={loanFormData.member_id}
                    onChange={(e) => setLoanFormData({ ...loanFormData, member_id: e.target.value })}
                    required
                  >
                    <option value="">-- Select a Member --</option>
                    {members?.filter(m => m.status === 'active').map((member) => (
                      <option key={member.id} value={member.id}>
                        {member.user_name} ({member.user_email})
                      </option>
                    ))}
                  </select>
                </div>
              )}
              
              {/* Non-Member Selection/Entry */}
              {loanFormData.applicant_type === 'non_member' && (
                <>
                  {nonMembers.length > 0 && (
                    <div className="form-group">
                      <label>Select Existing Non-Member (or enter new below)</label>
                      <select
                        value={loanFormData.non_member_id}
                        onChange={(e) => setLoanFormData({ ...loanFormData, non_member_id: e.target.value, non_member_name: '' })}
                      >
                        <option value="">-- New Non-Member --</option>
                        {nonMembers.map((nm) => (
                          <option key={nm.id} value={nm.id}>
                            {nm.name} ({nm.phone})
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                  
                  {!loanFormData.non_member_id && (
                    <div style={{ background: '#F9FAFB', padding: '16px', borderRadius: '8px', marginBottom: '16px' }}>
                      <h4 style={{ margin: '0 0 12px 0', fontSize: '14px' }}>New Non-Member Details</h4>
                      <div className="form-group">
                        <label>Full Name *</label>
                        <input
                          type="text"
                          value={loanFormData.non_member_name}
                          onChange={(e) => setLoanFormData({ ...loanFormData, non_member_name: e.target.value })}
                          placeholder="Enter full name"
                          required={!loanFormData.non_member_id}
                        />
                      </div>
                      <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                        <div className="form-group">
                          <label>Phone *</label>
                          <input
                            type="text"
                            value={loanFormData.non_member_phone}
                            onChange={(e) => setLoanFormData({ ...loanFormData, non_member_phone: e.target.value })}
                            placeholder="e.g., +254712345678"
                            required={!loanFormData.non_member_id}
                          />
                        </div>
                        <div className="form-group">
                          <label>Email</label>
                          <input
                            type="email"
                            value={loanFormData.non_member_email}
                            onChange={(e) => setLoanFormData({ ...loanFormData, non_member_email: e.target.value })}
                            placeholder="Optional"
                          />
                        </div>
                      </div>
                      <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                        <div className="form-group">
                          <label>ID/Passport Number</label>
                          <input
                            type="text"
                            value={loanFormData.non_member_id_number}
                            onChange={(e) => setLoanFormData({ ...loanFormData, non_member_id_number: e.target.value })}
                            placeholder="Optional"
                          />
                        </div>
                        <div className="form-group">
                          <label>Address</label>
                          <input
                            type="text"
                            value={loanFormData.non_member_address}
                            onChange={(e) => setLoanFormData({ ...loanFormData, non_member_address: e.target.value })}
                            placeholder="Optional"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
              
              {/* Loan Details */}
              <div style={{ borderTop: '1px solid #E5E7EB', paddingTop: '16px', marginTop: '8px' }}>
                <h4 style={{ margin: '0 0 12px 0' }}>Loan Details</h4>
                
                <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div className="form-group">
                    <label>Loan Amount ($) *</label>
                    <input
                      type="number"
                      step="0.01"
                      min="1"
                      value={loanFormData.amount}
                      onChange={(e) => setLoanFormData({ ...loanFormData, amount: e.target.value })}
                      placeholder="0.00"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Term (Months) *</label>
                    <input
                      type="number"
                      min="1"
                      max="120"
                      value={loanFormData.term_months}
                      onChange={(e) => setLoanFormData({ ...loanFormData, term_months: e.target.value })}
                      required
                    />
                  </div>
                </div>
                
                <div className="form-group">
                  <label>Purpose *</label>
                  <textarea
                    value={loanFormData.purpose}
                    onChange={(e) => setLoanFormData({ ...loanFormData, purpose: e.target.value })}
                    placeholder="Describe the purpose of this loan"
                    rows={2}
                    required
                  />
                </div>
                
                <LoanInterestFields data={loanFormData} onChange={setLoanFormData} currencySymbol={getCurrencySymbol(group?.currency)} prefix="loan" />
                
                <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div className="form-group">
                    <label>Origination Fee Type</label>
                    <select
                      value={loanFormData.origination_fee_type}
                      onChange={(e) => setLoanFormData({ ...loanFormData, origination_fee_type: e.target.value })}
                    >
                      <option value="percentage">Percentage (%)</option>
                      <option value="flat">Flat Amount ({getCurrencySymbol(group?.currency)})</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>
                      {loanFormData.origination_fee_type === 'percentage' ? 'Fee Percentage (e.g., 1 = 1%)' : `Fee Amount (${getCurrencySymbol(group?.currency)})`}
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      value={loanFormData.origination_fee_value}
                      onChange={(e) => setLoanFormData({ ...loanFormData, origination_fee_value: e.target.value })}
                      placeholder={loanFormData.origination_fee_type === 'percentage' ? 'e.g., 1 for 1%' : 'e.g., 50'}
                    />
                  </div>
                </div>
              </div>
              
              {/* Disbursement Method */}
              <div style={{ borderTop: '1px solid #E5E7EB', paddingTop: '16px', marginTop: '8px' }}>
                <h4 style={{ margin: '0 0 12px 0' }}>Disbursement Method</h4>
                
                <div className="form-group">
                  <label>Method *</label>
                  <div style={{ display: 'flex', gap: '12px' }}>
                    <button
                      type="button"
                      onClick={() => setLoanFormData({ ...loanFormData, disbursement_method: 'stripe' })}
                      style={{
                        flex: 1,
                        padding: '12px',
                        border: loanFormData.disbursement_method === 'stripe' ? '2px solid #10B981' : '1px solid #E5E7EB',
                        borderRadius: '8px',
                        background: loanFormData.disbursement_method === 'stripe' ? '#ECFDF5' : 'white',
                        cursor: 'pointer'
                      }}
                    >
                      💳 Stripe Bank Transfer
                    </button>
                    <button
                      type="button"
                      onClick={() => setLoanFormData({ ...loanFormData, disbursement_method: 'manual' })}
                      style={{
                        flex: 1,
                        padding: '12px',
                        border: loanFormData.disbursement_method === 'manual' ? '2px solid #F59E0B' : '1px solid #E5E7EB',
                        borderRadius: '8px',
                        background: loanFormData.disbursement_method === 'manual' ? '#FFFBEB' : 'white',
                        cursor: 'pointer'
                      }}
                    >
                      💵 Manual / Cash
                    </button>
                  </div>
                </div>
                
                {loanFormData.disbursement_method === 'stripe' && (
                  <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                    <div className="form-group">
                      <label>Bank Routing Number</label>
                      <input
                        type="text"
                        value={loanFormData.bank_routing_number}
                        onChange={(e) => setLoanFormData({ ...loanFormData, bank_routing_number: e.target.value })}
                        placeholder="9 digits"
                      />
                    </div>
                    <div className="form-group">
                      <label>Bank Account Number</label>
                      <input
                        type="text"
                        value={loanFormData.bank_account_number}
                        onChange={(e) => setLoanFormData({ ...loanFormData, bank_account_number: e.target.value })}
                        placeholder="Account number"
                      />
                    </div>
                  </div>
                )}
                
                {loanFormData.disbursement_method === 'manual' && (
                  <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                    <div className="form-group">
                      <label>Payment Method</label>
                      <select
                        value={loanFormData.manual_payment_method}
                        onChange={(e) => setLoanFormData({ ...loanFormData, manual_payment_method: e.target.value })}
                      >
                        <option value="cash">💵 Cash</option>
                        <option value="mobile_money">📱 Mobile Money</option>
                        <option value="check">📝 Check</option>
                        <option value="bank_transfer">🏦 Bank Transfer</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Payment Details</label>
                      <input
                        type="text"
                        value={loanFormData.manual_payment_details}
                        onChange={(e) => setLoanFormData({ ...loanFormData, manual_payment_details: e.target.value })}
                        placeholder="Phone, check #, etc."
                      />
                    </div>
                  </div>
                )}
              </div>
              
              {/* Guarantors */}
              <div style={{ borderTop: '1px solid #E5E7EB', paddingTop: '16px', marginTop: '8px' }}>
                <h4 style={{ margin: '0 0 12px 0' }}>🤝 Guarantors</h4>
                <p style={{ color: '#6B7280', fontSize: '13px', marginBottom: '12px' }}>
                  Select group members to act as guarantors. <strong>1 guarantor is required</strong>, 2 additional are optional.
                </p>
                
                <div style={{ display: 'grid', gap: '12px' }}>
                  {/* Primary Guarantor (Required) */}
                  <div className="form-group">
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      Primary Guarantor <span style={{ color: '#DC2626' }}>*</span>
                      <span style={{ background: '#FEE2E2', color: '#DC2626', padding: '2px 8px', borderRadius: '4px', fontSize: '11px' }}>Required</span>
                    </label>
                    <select
                      value={loanFormData.guarantor_ids?.[0] || ''}
                      onChange={(e) => {
                        const newIds = [...(loanFormData.guarantor_ids || [])];
                        newIds[0] = e.target.value;
                        setLoanFormData({ ...loanFormData, guarantor_ids: newIds.filter(id => id) });
                      }}
                      required
                      style={{ borderColor: !loanFormData.guarantor_ids?.[0] ? '#FCA5A5' : undefined }}
                    >
                      <option value="">-- Select Primary Guarantor --</option>
                      {members?.filter(m => 
                        m.status === 'active' && 
                        m.id !== loanFormData.member_id &&
                        m.id !== loanFormData.guarantor_ids?.[1] &&
                        m.id !== loanFormData.guarantor_ids?.[2]
                      ).map((member) => (
                        <option key={member.id} value={member.id}>
                          {member.user_name} ({member.user_email})
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  {/* Secondary Guarantor (Optional) */}
                  <div className="form-group">
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      Secondary Guarantor
                      <span style={{ background: '#E5E7EB', color: '#6B7280', padding: '2px 8px', borderRadius: '4px', fontSize: '11px' }}>Optional</span>
                    </label>
                    <select
                      value={loanFormData.guarantor_ids?.[1] || ''}
                      onChange={(e) => {
                        const newIds = [...(loanFormData.guarantor_ids || [])];
                        newIds[1] = e.target.value;
                        setLoanFormData({ ...loanFormData, guarantor_ids: newIds.filter(id => id) });
                      }}
                    >
                      <option value="">-- None --</option>
                      {members?.filter(m => 
                        m.status === 'active' && 
                        m.id !== loanFormData.member_id &&
                        m.id !== loanFormData.guarantor_ids?.[0] &&
                        m.id !== loanFormData.guarantor_ids?.[2]
                      ).map((member) => (
                        <option key={member.id} value={member.id}>
                          {member.user_name} ({member.user_email})
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  {/* Tertiary Guarantor (Optional) */}
                  <div className="form-group">
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      Third Guarantor
                      <span style={{ background: '#E5E7EB', color: '#6B7280', padding: '2px 8px', borderRadius: '4px', fontSize: '11px' }}>Optional</span>
                    </label>
                    <select
                      value={loanFormData.guarantor_ids?.[2] || ''}
                      onChange={(e) => {
                        const newIds = [...(loanFormData.guarantor_ids || [])];
                        newIds[2] = e.target.value;
                        setLoanFormData({ ...loanFormData, guarantor_ids: newIds.filter(id => id) });
                      }}
                    >
                      <option value="">-- None --</option>
                      {members?.filter(m => 
                        m.status === 'active' && 
                        m.id !== loanFormData.member_id &&
                        m.id !== loanFormData.guarantor_ids?.[0] &&
                        m.id !== loanFormData.guarantor_ids?.[1]
                      ).map((member) => (
                        <option key={member.id} value={member.id}>
                          {member.user_name} ({member.user_email})
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
              
              {/* Collateral */}
              <div style={{ borderTop: '1px solid #E5E7EB', paddingTop: '16px', marginTop: '8px' }}>
                <h4 style={{ margin: '0 0 12px 0' }}>Collateral (Optional)</h4>
                
                <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div className="form-group">
                    <label>Collateral Type</label>
                    <select
                      value={loanFormData.collateral_type}
                      onChange={(e) => setLoanFormData({ ...loanFormData, collateral_type: e.target.value })}
                    >
                      <option value="">-- None --</option>
                      <option value="property">Property/Real Estate</option>
                      <option value="vehicle">Vehicle</option>
                      <option value="equipment">Equipment</option>
                      <option value="savings">Savings/Deposit</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Estimated Value ($)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={loanFormData.collateral_value}
                      onChange={(e) => setLoanFormData({ ...loanFormData, collateral_value: e.target.value })}
                      placeholder="0.00"
                      disabled={!loanFormData.collateral_type}
                    />
                  </div>
                </div>
                
                {loanFormData.collateral_type && (
                  <div className="form-group">
                    <label>Collateral Description</label>
                    <textarea
                      value={loanFormData.collateral_description}
                      onChange={(e) => setLoanFormData({ ...loanFormData, collateral_description: e.target.value })}
                      placeholder="Describe the collateral in detail"
                      rows={2}
                    />
                  </div>
                )}
              </div>
              
              {/* Default Handling Settings */}
              <div style={{ borderTop: '1px solid #E5E7EB', paddingTop: '16px', marginTop: '8px' }}>
                <h4 style={{ margin: '0 0 12px 0', color: '#DC2626' }}>⚠️ Default Handling (Optional)</h4>
                <p style={{ color: '#6B7280', fontSize: '13px', marginBottom: '12px' }}>
                  Configure what happens if loan payments become overdue
                </p>
                
                <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div className="form-group">
                    <label>Grace Period (Days)</label>
                    <input
                      type="number"
                      min="0"
                      max="90"
                      value={loanFormData.grace_period_days}
                      onChange={(e) => setLoanFormData({ ...loanFormData, grace_period_days: e.target.value })}
                    />
                    <span style={{ fontSize: '11px', color: '#6B7280' }}>Days after due date before marking overdue</span>
                  </div>
                  <div className="form-group">
                    <label>Default Action (for Members)</label>
                    <select
                      value={loanFormData.default_action}
                      onChange={(e) => setLoanFormData({ ...loanFormData, default_action: e.target.value })}
                    >
                      <option value="none">No Action</option>
                      <option value="probation">Place on Probation</option>
                      <option value="inactive">Mark as Inactive</option>
                      <option value="penalty">Penalty Only (Late Fee)</option>
                    </select>
                  </div>
                </div>
                
                <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div className="form-group">
                    <label>Late Fee Type</label>
                    <select
                      value={loanFormData.late_fee_type}
                      onChange={(e) => setLoanFormData({ ...loanFormData, late_fee_type: e.target.value })}
                    >
                      <option value="none">No Late Fee</option>
                      <option value="percentage">Percentage of Payment</option>
                      <option value="flat">Flat Amount</option>
                    </select>
                  </div>
                  {loanFormData.late_fee_type !== 'none' && (
                    <div className="form-group">
                      <label>Late Fee {loanFormData.late_fee_type === 'percentage' ? '(%)' : '($)'}</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={loanFormData.late_fee_value}
                        onChange={(e) => setLoanFormData({ ...loanFormData, late_fee_value: e.target.value })}
                        placeholder={loanFormData.late_fee_type === 'percentage' ? 'e.g., 5' : 'e.g., 25'}
                      />
                    </div>
                  )}
                </div>
              </div>
              
              {/* Loan Preview */}
              {loanFormData.amount && (
                <div style={{ 
                  background: '#F0FDF4', 
                  border: '1px solid #86EFAC',
                  borderRadius: '12px', 
                  padding: '16px', 
                  marginTop: '16px'
                }}>
                  <h4 style={{ margin: '0 0 12px 0', color: '#166534' }}>📊 Loan Preview</h4>
                  {(() => {
                    const preview = calculateLoanPreview();
                    return (
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', fontSize: '14px' }}>
                        <div>
                          <div style={{ color: '#6B7280' }}>Principal</div>
                          <div style={{ fontWeight: '600' }}>{formatCurrency(parseFloat(preview.principal), group?.currency)}</div>
                        </div>
                        <div>
                          <div style={{ color: '#6B7280' }}>Total Interest</div>
                          <div style={{ fontWeight: '600' }}>{formatCurrency(parseFloat(preview.totalInterest), group?.currency)}</div>
                        </div>
                        <div>
                          <div style={{ color: '#6B7280' }}>Origination Fee</div>
                          <div style={{ fontWeight: '600' }}>{formatCurrency(parseFloat(preview.originationFee), group?.currency)}</div>
                        </div>
                        <div>
                          <div style={{ color: '#6B7280' }}>Total Repayment</div>
                          <div style={{ fontWeight: '600', color: '#DC2626' }} data-testid="loan-preview-total">{formatCurrency(parseFloat(preview.totalRepayment), group?.currency)}</div>
                        </div>
                        <div>
                          <div style={{ color: '#6B7280' }}>Monthly Payment</div>
                          <div style={{ fontWeight: '600' }} data-testid="loan-preview-monthly">{formatCurrency(parseFloat(preview.monthlyPayment), group?.currency)}</div>
                        </div>
                        <div>
                          <div style={{ color: '#6B7280' }}>Final Payment</div>
                          <div style={{ fontWeight: '600' }} data-testid="loan-preview-final">{formatCurrency(parseFloat(preview.finalPayment), group?.currency)}</div>
                        </div>
                        <div>
                          <div style={{ color: '#6B7280' }}>Net Disbursement</div>
                          <div style={{ fontWeight: '600', color: '#10B981' }}>{formatCurrency(parseFloat(preview.netDisbursement), group?.currency)}</div>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}
              
              <div className="modal-actions" style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
                <button 
                  type="button"
                  onClick={() => setShowLoanForm(false)} 
                  className="btn btn-secondary"
                  style={{ flex: 1 }}
                >
                  Cancel
                </button>
                <button 
                  type="submit"
                  className="btn btn-primary"
                  disabled={signing}
                  style={{ flex: 1 }}
                  data-testid="submit-loan-application"
                >
                  {signing ? 'Creating...' : 'Create Loan Application'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {/* Loan Sign Modal */}
      {/* Set Terms Modal (for member-requested loans) */}
      {showSetTermsModal && setTermsLoan && (
        <div className="modal-overlay" onClick={() => setShowSetTermsModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '640px', maxHeight: '90vh', overflow: 'auto' }}>
            <div className="modal-header">
              <h2>Set Loan Terms - {setTermsLoan.loan_number}</h2>
              <button className="close-button" onClick={() => setShowSetTermsModal(false)}>×</button>
            </div>
            
            <div style={{ background: '#F0F9FF', padding: '14px', borderRadius: '8px', marginBottom: '16px' }}>
              <p style={{ margin: 0, fontSize: '14px' }}><strong>Member:</strong> {setTermsLoan.applicant_name}</p>
              <p style={{ margin: '4px 0 0', fontSize: '14px' }}><strong>Requested Amount:</strong> {formatCurrency(setTermsLoan.amount, group?.currency)}</p>
              <p style={{ margin: '4px 0 0', fontSize: '14px' }}><strong>Requested Term:</strong> {setTermsLoan.term_months} months</p>
              <p style={{ margin: '4px 0 0', fontSize: '14px' }}><strong>Purpose:</strong> {setTermsLoan.purpose}</p>
              {setTermsLoan.physical_address && <p style={{ margin: '4px 0 0', fontSize: '14px' }}><strong>Physical Address:</strong> {setTermsLoan.physical_address}</p>}
              {setTermsLoan.mailing_address && <p style={{ margin: '4px 0 0', fontSize: '14px' }}><strong>Mailing Address:</strong> {setTermsLoan.mailing_address}</p>}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
              <div className="form-group">
                <label>Term (months) *</label>
                <input type="number" value={termsFormData.term_months} onChange={e => setTermsFormData({...termsFormData, term_months: parseInt(e.target.value) || 1})} min="1" style={{ width: '100%', padding: '8px 10px', border: '1px solid #D1D5DB', borderRadius: '8px' }} data-testid="terms-term-input" />
              </div>
              <LoanInterestFields data={termsFormData} onChange={setTermsFormData} currencySymbol={getCurrencySymbol(group?.currency)} prefix="terms" />
              <LoanSchedulePreview
                rows={computeLoanSchedule(parseFloat(setTermsLoan?.amount) || 0, parseFloat(termsFormData.interest_rate) || 0, parseInt(termsFormData.term_months) || 1, termsFormData.interest_type, termsFormData.repayment_mode, termsFormData.rate_period)}
                fmt={(v) => formatCurrency(v, group?.currency)}
              />
              <div className="form-group">
                <label>Disbursement Method</label>
                <select value={termsFormData.disbursement_method} onChange={e => setTermsFormData({...termsFormData, disbursement_method: e.target.value})} style={{ width: '100%', padding: '8px 10px', border: '1px solid #D1D5DB', borderRadius: '8px' }} data-testid="terms-disbursement-method">
                  <option value="manual">Manual</option>
                  <option value="stripe">Stripe</option>
                </select>
              </div>
              <div className="form-group">
                <label>Origination Fee Type</label>
                <select value={termsFormData.origination_fee_type} onChange={e => setTermsFormData({...termsFormData, origination_fee_type: e.target.value})} style={{ width: '100%', padding: '8px 10px', border: '1px solid #D1D5DB', borderRadius: '8px' }}>
                  <option value="percentage">Percentage</option>
                  <option value="flat">Flat Amount</option>
                </select>
              </div>
              <div className="form-group">
                <label>Origination Fee Value</label>
                <input type="number" step="0.01" value={termsFormData.origination_fee_value} onChange={e => setTermsFormData({...termsFormData, origination_fee_value: parseFloat(e.target.value) || 0})} min="0" style={{ width: '100%', padding: '8px 10px', border: '1px solid #D1D5DB', borderRadius: '8px' }} />
              </div>
              <div className="form-group">
                <label>Grace Period (days)</label>
                <input type="number" value={termsFormData.grace_period_days} onChange={e => setTermsFormData({...termsFormData, grace_period_days: parseInt(e.target.value) || 7})} min="0" style={{ width: '100%', padding: '8px 10px', border: '1px solid #D1D5DB', borderRadius: '8px' }} />
              </div>
              <div className="form-group">
                <label>Default Action</label>
                <select value={termsFormData.default_action} onChange={e => setTermsFormData({...termsFormData, default_action: e.target.value})} style={{ width: '100%', padding: '8px 10px', border: '1px solid #D1D5DB', borderRadius: '8px' }}>
                  <option value="none">None</option>
                  <option value="probation">Probation</option>
                  <option value="inactive">Inactive</option>
                  <option value="penalty">Penalty</option>
                </select>
              </div>
              <div className="form-group">
                <label>Late Fee Type</label>
                <select value={termsFormData.late_fee_type} onChange={e => setTermsFormData({...termsFormData, late_fee_type: e.target.value})} style={{ width: '100%', padding: '8px 10px', border: '1px solid #D1D5DB', borderRadius: '8px' }}>
                  <option value="none">None</option>
                  <option value="percentage">Percentage</option>
                  <option value="flat">Flat Amount</option>
                </select>
              </div>
              {termsFormData.late_fee_type !== 'none' && (
                <div className="form-group">
                  <label>Late Fee Value</label>
                  <input type="number" step="0.01" value={termsFormData.late_fee_value} onChange={e => setTermsFormData({...termsFormData, late_fee_value: parseFloat(e.target.value) || 0})} min="0" style={{ width: '100%', padding: '8px 10px', border: '1px solid #D1D5DB', borderRadius: '8px' }} />
                </div>
              )}
            </div>

            {/* Guarantors */}
            <div style={{ marginTop: '14px' }}>
              <label style={{ fontWeight: '600', fontSize: '14px', display: 'block', marginBottom: '8px' }}>Select Guarantors</label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '8px', maxHeight: '150px', overflow: 'auto' }}>
                {members.filter(m => m.id !== setTermsLoan.member_id && m.status === 'active').map(m => (
                  <label key={m.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 10px', border: `1px solid ${termsFormData.guarantor_ids.includes(m.id) ? '#6366F1' : '#E5E7EB'}`, borderRadius: '8px', cursor: 'pointer', fontSize: '13px', background: termsFormData.guarantor_ids.includes(m.id) ? '#EEF2FF' : 'white' }}>
                    <input type="checkbox" checked={termsFormData.guarantor_ids.includes(m.id)} onChange={() => toggleTermsGuarantor(m.id)} />
                    {m.user_name}
                  </label>
                ))}
              </div>
            </div>

            {/* Notes */}
            <div className="form-group" style={{ marginTop: '14px' }}>
              <label>Notes for Member (optional)</label>
              <textarea value={termsFormData.notes} onChange={e => setTermsFormData({...termsFormData, notes: e.target.value})} placeholder="Any special conditions or notes..." rows={2} style={{ width: '100%', padding: '8px 10px', border: '1px solid #D1D5DB', borderRadius: '8px', resize: 'vertical' }} data-testid="terms-notes-input" />
            </div>

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '18px' }}>
              <button className="btn btn-secondary" onClick={() => setShowSetTermsModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSetTerms} disabled={settingTerms} data-testid="submit-set-terms-btn" style={{ background: '#4338CA' }}>
                {settingTerms ? 'Setting Terms...' : 'Set Terms & Send to Member'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showLoanSignModal && selectedLoan && (
        <div className="modal-overlay" onClick={() => setShowLoanSignModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '450px' }}>
            <div className="modal-header">
              <h2>🔒 Sign Loan Approval</h2>
              <button className="close-button" onClick={() => setShowLoanSignModal(false)}>×</button>
            </div>
            
            <div style={{ marginBottom: '16px' }}>
              <div style={{ background: '#F3F4F6', padding: '16px', borderRadius: '8px', marginBottom: '16px' }}>
                <p><strong>Loan:</strong> {selectedLoan.loan_number}</p>
                <p><strong>Applicant:</strong> {selectedLoan.applicant_name}</p>
                <p><strong>Amount:</strong> {formatCurrency(selectedLoan.amount, group?.currency)}</p>
                <p><strong>Term:</strong> {selectedLoan.term_months} months @ {selectedLoan.interest_rate}%</p>
              </div>
              
              <div style={{ 
                background: '#FEF3C7', 
                padding: '12px', 
                borderRadius: '8px',
                marginBottom: '16px',
                fontSize: '13px',
                color: '#92400E'
              }}>
                ⚠️ By signing, you approve this loan application. 3 signatures are required for final approval.
              </div>
              
              <div className="form-group">
                <label>Your Password *</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSignLoan()}
                  placeholder="Enter your password to sign"
                  autoFocus
                />
              </div>
            </div>
            
            <div className="modal-actions" style={{ display: 'flex', gap: '12px' }}>
              <button 
                onClick={() => { setShowLoanSignModal(false); setPassword(''); }} 
                className="btn btn-secondary"
                style={{ flex: 1 }}
              >
                Cancel
              </button>
              <button 
                onClick={handleSignLoan}
                className="btn btn-primary"
                disabled={signing || !password}
                style={{ flex: 1 }}
              >
                {signing ? 'Signing...' : 'Sign & Approve'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Loan Details Modal */}
      {showLoanDetails && selectedLoan && (
        <div className="modal-overlay" onClick={() => setShowLoanDetails(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '700px', maxHeight: '90vh', overflow: 'auto' }}>
            <div className="modal-header">
              <h2>🏦 Loan Details - {selectedLoan.loan_number}</h2>
              <button className="close-button" onClick={() => setShowLoanDetails(false)}>×</button>
            </div>
            
            <div style={{ padding: '0 0 20px 0' }}>
              {/* Status Badge */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <span style={{
                  background: getLoanStatusColor(selectedLoan.status).bg,
                  color: getLoanStatusColor(selectedLoan.status).text,
                  padding: '6px 16px',
                  borderRadius: '20px',
                  fontWeight: '600',
                  textTransform: 'capitalize'
                }}>
                  {selectedLoan.status}
                </span>
                <span style={{ color: '#6B7280', fontSize: '13px' }}>
                  Created: {new Date(selectedLoan.created_at).toLocaleDateString()}
                </span>
              </div>
              
              {/* Applicant Info */}
              <div style={{ background: '#F9FAFB', padding: '16px', borderRadius: '12px', marginBottom: '16px' }}>
                <h4 style={{ margin: '0 0 12px 0' }}>👤 Applicant</h4>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div>
                    <div style={{ color: '#6B7280', fontSize: '12px' }}>Name</div>
                    <div style={{ fontWeight: '500' }}>{selectedLoan.applicant_name}</div>
                  </div>
                  <div>
                    <div style={{ color: '#6B7280', fontSize: '12px' }}>Type</div>
                    <div style={{ fontWeight: '500', textTransform: 'capitalize' }}>{selectedLoan.applicant_type?.replace('_', '-')}</div>
                  </div>
                  {selectedLoan.applicant_email && (
                    <div>
                      <div style={{ color: '#6B7280', fontSize: '12px' }}>Email</div>
                      <div>{selectedLoan.applicant_email}</div>
                    </div>
                  )}
                  {selectedLoan.applicant_phone && (
                    <div>
                      <div style={{ color: '#6B7280', fontSize: '12px' }}>Phone</div>
                      <div>{selectedLoan.applicant_phone}</div>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Loan Terms */}
              <div style={{ background: '#F9FAFB', padding: '16px', borderRadius: '12px', marginBottom: '16px' }}>
                <h4 style={{ margin: '0 0 12px 0' }}>💰 Loan Terms</h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
                  <div>
                    <div style={{ color: '#6B7280', fontSize: '12px' }}>Principal</div>
                    <div style={{ fontWeight: '600', fontSize: '18px' }}>{formatCurrency(selectedLoan.amount, group?.currency)}</div>
                  </div>
                  <div>
                    <div style={{ color: '#6B7280', fontSize: '12px' }}>Interest Rate</div>
                    <div style={{ fontWeight: '500' }}>
                      {describeLoanRate(selectedLoan, getCurrencySymbol(group?.currency))}
                    </div>
                  </div>
                  <div>
                    <div style={{ color: '#6B7280', fontSize: '12px' }}>Repayment Mode</div>
                    <div style={{ fontWeight: '500' }}>{selectedLoan.repayment_mode === 'interest_only' ? 'Interest Only' : 'Principal + Interest'}</div>
                  </div>
                  <div>
                    <div style={{ color: '#6B7280', fontSize: '12px' }}>Term</div>
                    <div style={{ fontWeight: '500' }}>{selectedLoan.term_months} months</div>
                  </div>
                  <div>
                    <div style={{ color: '#6B7280', fontSize: '12px' }}>Total Interest</div>
                    <div style={{ fontWeight: '500' }}>{formatCurrency(selectedLoan.total_interest, group?.currency)}</div>
                  </div>
                  <div>
                    <div style={{ color: '#6B7280', fontSize: '12px' }}>Origination Fee</div>
                    <div style={{ fontWeight: '500' }}>{formatCurrency(selectedLoan.origination_fee_amount, group?.currency)}</div>
                  </div>
                  <div>
                    <div style={{ color: '#6B7280', fontSize: '12px' }}>Total Repayment</div>
                    <div style={{ fontWeight: '600', color: '#DC2626' }}>{formatCurrency(selectedLoan.total_repayment, group?.currency)}</div>
                  </div>
                </div>
                <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #E5E7EB' }}>
                  <div style={{ color: '#6B7280', fontSize: '12px' }}>Purpose</div>
                  <div>{selectedLoan.purpose}</div>
                </div>
              </div>
              
              {/* Repayment Progress (for active/completed loans) */}
              {['active', 'disbursed', 'completed'].includes(selectedLoan.status) && (
                <div style={{ background: '#F0FDF4', padding: '16px', borderRadius: '12px', marginBottom: '16px' }}>
                  <h4 style={{ margin: '0 0 12px 0', color: '#166534' }}>📊 Repayment Progress</h4>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <span>Repaid: {formatCurrency(selectedLoan.amount_repaid || 0, group?.currency)}</span>
                    <span>Remaining: {formatCurrency((selectedLoan.total_repayment || 0) - (selectedLoan.amount_repaid || 0), group?.currency)}</span>
                  </div>
                  <div style={{ background: '#E5E7EB', borderRadius: '10px', height: '10px', overflow: 'hidden' }}>
                    <div style={{
                      background: '#10B981',
                      height: '100%',
                      width: `${Math.min(100, ((selectedLoan.amount_repaid || 0) / (selectedLoan.total_repayment || 1)) * 100)}%`,
                      transition: 'width 0.3s'
                    }} />
                  </div>
                  {selectedLoan.next_payment_date && (
                    <div style={{ marginTop: '12px', fontSize: '13px', color: '#6B7280' }}>
                      Next Payment Due: <strong>{selectedLoan.next_payment_date}</strong>
                    </div>
                  )}
                </div>
              )}
              
              {/* Signatures (for pending loans) */}
              {selectedLoan.status === 'pending' && (
                <div style={{ background: '#FEF3C7', padding: '16px', borderRadius: '12px', marginBottom: '16px' }}>
                  <h4 style={{ margin: '0 0 12px 0', color: '#92400E' }}>✍️ Approval Signatures ({selectedLoan.signed_by?.length || 0}/3)</h4>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    {[1, 2, 3].map(num => (
                      <div key={num} style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: (selectedLoan.signed_by?.length || 0) >= num ? '#10B981' : '#E5E7EB',
                        color: (selectedLoan.signed_by?.length || 0) >= num ? 'white' : '#9CA3AF',
                        fontSize: '18px'
                      }}>
                        {(selectedLoan.signed_by?.length || 0) >= num ? '✓' : num}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Guarantors */}
              {selectedLoan.guarantors && selectedLoan.guarantors.length > 0 && (
                <div style={{ background: '#F9FAFB', padding: '16px', borderRadius: '12px', marginBottom: '16px' }}>
                  <h4 style={{ margin: '0 0 12px 0' }}>🤝 Guarantors</h4>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {selectedLoan.guarantors.map((g) => (
                      <span key={g.id} style={{
                        background: g.status === 'confirmed' ? '#D1FAE5' : '#FEF3C7',
                        color: g.status === 'confirmed' ? '#065F46' : '#92400E',
                        padding: '6px 12px',
                        borderRadius: '20px',
                        fontSize: '13px'
                      }}>
                        {g.member_name}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Collateral */}
              {selectedLoan.collateral && selectedLoan.collateral.length > 0 && (
                <div style={{ background: '#F9FAFB', padding: '16px', borderRadius: '12px', marginBottom: '16px' }}>
                  <h4 style={{ margin: '0 0 12px 0' }}>🔐 Collateral</h4>
                  {selectedLoan.collateral.map((c) => (
                    <div key={c.id}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                        <div>
                          <div style={{ color: '#6B7280', fontSize: '12px' }}>Type</div>
                          <div style={{ textTransform: 'capitalize' }}>{c.type}</div>
                        </div>
                        <div>
                          <div style={{ color: '#6B7280', fontSize: '12px' }}>Estimated Value</div>
                          <div>{formatCurrency(c.estimated_value, group?.currency)}</div>
                        </div>
                      </div>
                      {c.description && (
                        <div style={{ marginTop: '8px' }}>
                          <div style={{ color: '#6B7280', fontSize: '12px' }}>Description</div>
                          <div>{c.description}</div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
              
              {/* Repayment Schedule */}
              {selectedLoan.repayment_schedule && selectedLoan.repayment_schedule.length > 0 && (
                <div style={{ marginBottom: '16px' }}>
                  <h4 style={{ margin: '0 0 12px 0' }}>📅 Repayment Schedule</h4>
                  <div style={{ maxHeight: '200px', overflow: 'auto', border: '1px solid #E5E7EB', borderRadius: '8px' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                      <thead style={{ background: '#F3F4F6', position: 'sticky', top: 0 }}>
                        <tr>
                          <th style={{ padding: '10px', textAlign: 'left' }}>#</th>
                          <th style={{ padding: '10px', textAlign: 'left' }}>Due Date</th>
                          <th style={{ padding: '10px', textAlign: 'right' }}>Amount</th>
                          <th style={{ padding: '10px', textAlign: 'center' }}>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedLoan.repayment_schedule.map((entry, idx) => (
                          <tr key={idx} style={{ borderTop: '1px solid #E5E7EB' }}>
                            <td style={{ padding: '10px' }}>{entry.month || idx + 1}</td>
                            <td style={{ padding: '10px' }}>{entry.due_date}</td>
                            <td style={{ padding: '10px', textAlign: 'right' }}>{formatCurrency(entry.total_amount, group?.currency)}</td>
                            <td style={{ padding: '10px', textAlign: 'center' }}>
                              <span style={{
                                background: entry.status === 'paid' ? '#D1FAE5' : entry.status === 'overdue' ? '#FEE2E2' : '#F3F4F6',
                                color: entry.status === 'paid' ? '#065F46' : entry.status === 'overdue' ? '#DC2626' : '#6B7280',
                                padding: '2px 8px',
                                borderRadius: '4px',
                                fontSize: '11px',
                                textTransform: 'capitalize'
                              }}>
                                {entry.status}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              
              {/* Action Buttons */}
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                {selectedLoan.status === 'pending' && (
                  <>
                    <button
                      onClick={() => {
                        setShowLoanDetails(false);
                        setShowLoanSignModal(true);
                      }}
                      className="btn btn-primary"
                    >
                      🔒 Sign Loan
                    </button>
                    <button
                      onClick={() => handleCancelLoan(selectedLoan.id)}
                      className="btn btn-danger"
                    >
                      ✕ Cancel Loan
                    </button>
                  </>
                )}
                {selectedLoan.status === 'approved' && (
                  <button
                    onClick={() => handleDisburseLoan(selectedLoan.id)}
                    className="btn btn-primary"
                    disabled={signing}
                  >
                    💸 Disburse Loan
                  </button>
                )}
                {['active', 'disbursed'].includes(selectedLoan.status) && (
                  <>
                    <button
                      onClick={() => setShowRepaymentModal(true)}
                      className="btn btn-primary"
                    >
                      💵 Record Repayment
                    </button>
                    {selectedLoan.total_late_fees > 0 && (
                      <button
                        onClick={() => handleWaiveLateFees(selectedLoan.id)}
                        className="btn btn-secondary"
                        title="Waive all late fees"
                      >
                        🏷️ Waive Fees ({formatCurrency(selectedLoan.total_late_fees, group?.currency)})
                      </button>
                    )}
                    {selectedLoan.applicant_type === 'member' && selectedLoan.overdue_payments > 0 && (
                      <button
                        onClick={() => handleRestoreMember(selectedLoan.id)}
                        className="btn btn-success"
                        title="Restore member to active status"
                      >
                        ✅ Restore Member
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Repayment Modal */}
      {showRepaymentModal && selectedLoan && (
        <div className="modal-overlay" onClick={() => setShowRepaymentModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '450px' }}>
            <div className="modal-header">
              <h2>💵 Record Repayment</h2>
              <button className="close-button" onClick={() => setShowRepaymentModal(false)}>×</button>
            </div>
            
            <div style={{ marginBottom: '16px' }}>
              <div style={{ background: '#F3F4F6', padding: '12px', borderRadius: '8px', marginBottom: '16px' }}>
                <p><strong>Loan:</strong> {selectedLoan.loan_number}</p>
                <p><strong>Outstanding:</strong> {formatCurrency((selectedLoan.total_repayment || 0) - (selectedLoan.amount_repaid || 0), group?.currency)}</p>
              </div>
              
              <div className="form-group">
                <label>Amount ({getCurrencySymbol(group?.currency)}) *</label>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={repaymentData.amount}
                  onChange={(e) => setRepaymentData({ ...repaymentData, amount: e.target.value })}
                  placeholder="0.00"
                  required
                />
              </div>
              
              <div className="form-group">
                <label>Payment Method</label>
                <select
                  value={repaymentData.payment_method}
                  onChange={(e) => setRepaymentData({ ...repaymentData, payment_method: e.target.value })}
                >
                  <option value="cash">💵 Cash</option>
                  <option value="bank_transfer">🏦 Bank Transfer</option>
                  <option value="mobile_money">📱 Mobile Money</option>
                  <option value="stripe">💳 Stripe</option>
                  <option value="wallet">👛 Wallet</option>
                </select>
              </div>
              
              <div className="form-group">
                <label>Reference (Optional)</label>
                <input
                  type="text"
                  value={repaymentData.reference}
                  onChange={(e) => setRepaymentData({ ...repaymentData, reference: e.target.value })}
                  placeholder="Receipt #, transaction ID, etc."
                />
              </div>
              
              <div className="form-group">
                <label>Notes (Optional)</label>
                <textarea
                  value={repaymentData.notes}
                  onChange={(e) => setRepaymentData({ ...repaymentData, notes: e.target.value })}
                  placeholder="Additional notes"
                  rows={2}
                />
              </div>
            </div>
            
            <div className="modal-actions" style={{ display: 'flex', gap: '12px' }}>
              <button 
                onClick={() => setShowRepaymentModal(false)} 
                className="btn btn-secondary"
                style={{ flex: 1 }}
              >
                Cancel
              </button>
              <button 
                onClick={handleRecordRepayment}
                className="btn btn-primary"
                disabled={signing || !repaymentData.amount}
                style={{ flex: 1 }}
              >
                {signing ? 'Recording...' : 'Record Repayment'}
              </button>
            </div>
          </div>
        </div>
      )}
      
    </div>
    </>
  );
}

function MeetingsTab({ meetings, groupId, onCreateMeeting, onRefresh }) {
  return (
    <div className="meetings-tab" data-testid="meetings-tab">
      <div className="tab-header">
        <h1>Meetings</h1>
        <button onClick={onCreateMeeting} className="btn btn-primary" data-testid="create-meeting-btn">
          + Schedule Meeting
        </button>
      </div>

      {meetings.length === 0 ? (
        <div className="empty-state card">
          <h3>No meetings scheduled</h3>
          <p>Schedule your first meeting to keep members informed</p>
        </div>
      ) : (
        <div className="meetings-grid">
          {meetings.map((meeting) => (
            <div key={meeting.id} className="meeting-card card">
              <div className="meeting-date">
                📅 {new Date(meeting.date).toLocaleDateString()}
                {meeting.time && <span className="meeting-time"> • ⏰ {meeting.time}</span>}
              </div>
              <h3>{meeting.title}</h3>
              {meeting.description && <p className="meeting-description">{meeting.description}</p>}
              {meeting.location && (
                <div className="meeting-location">
                  <span className="location-icon">📍</span>
                  <span>{meeting.location}</span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function NotificationsTab({ groupId }) {
  const [reminderMessage, setReminderMessage] = useState('');
  const [broadcastSubject, setBroadcastSubject] = useState('');
  const [broadcastMessage, setBroadcastMessage] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Deadline settings
  const [deadlineDate, setDeadlineDate] = useState('');
  const [deadlineTime, setDeadlineTime] = useState('');
  const [inactiveWindowDays, setInactiveWindowDays] = useState(90);
  const [probationDays, setProbationDays] = useState(30);
  const [penaltyAmount, setPenaltyAmount] = useState(0);
  const [caseNumber, setCaseNumber] = useState('');

  const sendPaymentReminder = async () => {
    if (!deadlineDate || !deadlineTime) {
      toast.error('Please set deadline date and time');
      return;
    }

    setLoading(true);
    try {
      const deadlineDateTime = `${deadlineDate}T${deadlineTime}:00`;
      
      await axios.post(`${API}/notifications/payment-reminder`, {
        group_id: groupId,
        message: reminderMessage || undefined,
        deadline: deadlineDateTime,
        missed_action: 'inactive',
        inactive_window_days: inactiveWindowDays,
        probation_days: probationDays,
        penalty_amount: penaltyAmount,
        case_number: caseNumber || undefined
      });
      toast.success('Payment reminders sent with deadline');
      setReminderMessage('');
      setDeadlineDate('');
      setDeadlineTime('');
    } catch (error) {
      toast.error('Failed to send reminders');
    } finally {
      setLoading(false);
    }
  };

  const sendBroadcast = async () => {
    if (!broadcastSubject || !broadcastMessage) {
      toast.error('Please fill in all fields');
      return;
    }

    if (!groupId) {
      toast.error('No group selected');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/notifications/broadcast`, {
        group_id: groupId,
        subject: broadcastSubject,
        message: broadcastMessage
      });
      toast.success('Broadcast sent successfully');
      setBroadcastSubject('');
      setBroadcastMessage('');
    } catch (error) {
      console.error('Broadcast error:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to send broadcast';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="notifications-tab" data-testid="notifications-tab">
      <h1>Notifications</h1>

      <div className="notification-section card">
        <h2>Payment Reminders with Deadline</h2>
        <p className="section-description">Send payment reminders to all members with automatic enforcement</p>
        
        <div className="form-group">
          <label>Custom Message (Optional)</label>
          <textarea
            value={reminderMessage}
            onChange={(e) => setReminderMessage(e.target.value)}
            placeholder="Add a custom message or leave blank for default"
            data-testid="reminder-message-input"
          />
        </div>

        <div className="form-group">
          <label>Case Number (Optional)</label>
          <input
            type="text"
            value={caseNumber}
            onChange={(e) => setCaseNumber(e.target.value)}
            placeholder="e.g., CASE-2025-001"
            data-testid="case-number-input"
          />
          <small className="form-hint">
            Track this payment reminder with a case number for reporting
          </small>
        </div>

        <div className="deadline-section">
          <h3>📅 Payment Deadline</h3>
          <div className="form-row">
            <div className="form-group">
              <label>Deadline Date *</label>
              <input
                type="date"
                value={deadlineDate}
                onChange={(e) => setDeadlineDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                required
                data-testid="deadline-date-input"
              />
            </div>
            <div className="form-group">
              <label>Deadline Time *</label>
              <input
                type="time"
                value={deadlineTime}
                onChange={(e) => setDeadlineTime(e.target.value)}
                required
                data-testid="deadline-time-input"
              />
            </div>
          </div>
        </div>

        <div className="actions-section">
          <h3>Enforcement Settings</h3>
          <small className="form-hint" style={{ display: 'block', marginBottom: '16px', color: '#6B7280' }}>
            When the grace period (deadline) passes, the member is automatically marked <strong>Inactive</strong>.
            If they pay during the inactive window, they enter <strong>Probation</strong>. After probation ends, they return to <strong>Active</strong>.
          </small>

          <div className="form-row">
            <div className="form-group">
              <label>Inactive Window (Days)</label>
              <input
                type="number"
                value={inactiveWindowDays}
                onChange={(e) => setInactiveWindowDays(parseInt(e.target.value) || 90)}
                min="1"
                max="365"
                placeholder="e.g., 90"
                data-testid="inactive-window-days-input"
              />
              <small className="form-hint">
                Members have {inactiveWindowDays} days to pay while Inactive. After this, they are Suspended.
              </small>
            </div>
            <div className="form-group">
              <label>Probation Period (Days)</label>
              <input
                type="number"
                value={probationDays}
                onChange={(e) => setProbationDays(parseInt(e.target.value) || 30)}
                min="1"
                max="365"
                placeholder="e.g., 30"
                data-testid="notice-probation-days-input"
              />
              <small className="form-hint">
                After paying, members serve {probationDays} days probation before returning to Active.
              </small>
            </div>
          </div>

          <div className="form-group">
            <label>Penalty Amount ($)</label>
            <input
              type="number"
              value={penaltyAmount}
              onChange={(e) => setPenaltyAmount(parseFloat(e.target.value) || 0)}
              min="0"
              step="0.01"
              placeholder="0.00 (optional)"
              data-testid="penalty-amount-input"
            />
            <small className="form-hint">
              {penaltyAmount > 0 ? `${formatCurrency(penaltyAmount, group?.currency)} penalty added for late payment` : 'No penalty fee (optional)'}
            </small>
          </div>
        </div>

        <div className="action-summary">
          <h4>Flow Summary:</h4>
          <ul>
            <li>Deadline: {deadlineDate ? `${deadlineDate} at ${deadlineTime}` : 'Not set'}</li>
            <li>Grace period countdown starts immediately when notice is sent</li>
            <li>Missed deadline → Inactive (automatic)</li>
            <li>Inactive window: {inactiveWindowDays} days to pay</li>
            {penaltyAmount > 0 && <li>Late penalty: ${penaltyAmount.toFixed(2)}</li>}
            <li>After payment → {probationDays}-day probation → Active</li>
            <li>No payment within {inactiveWindowDays} days → Suspended</li>
          </ul>
        </div>

        <button 
          onClick={sendPaymentReminder} 
          className="btn btn-primary btn-large" 
          disabled={loading}
          data-testid="send-reminder-btn"
        >
          {loading ? 'Sending...' : '🔔 Send Payment Reminder with Deadline'}
        </button>
      </div>

      <div className="notification-section card mt-3">
        <h2>Broadcast Message</h2>
        <p className="section-description">Send an email to all group members</p>
        <div className="form-group">
          <label>Subject</label>
          <input
            type="text"
            value={broadcastSubject}
            onChange={(e) => setBroadcastSubject(e.target.value)}
            placeholder="Email subject"
            data-testid="broadcast-subject-input"
          />
        </div>
        <div className="form-group">
          <label>Message</label>
          <textarea
            value={broadcastMessage}
            onChange={(e) => setBroadcastMessage(e.target.value)}
            placeholder="Your message to members"
            data-testid="broadcast-message-input"
          />
        </div>
        <button 
          onClick={sendBroadcast} 
          className="btn btn-primary" 
          disabled={loading}
          data-testid="send-broadcast-btn"
        >
          {loading ? 'Sending...' : '📧 Send Broadcast'}
        </button>
      </div>
    </div>
  );
}

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

function StripeCheckoutForm({ groupId, onSuccess, onCancel, hasExisting }) {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setProcessing(true);

    try {
      const { error, setupIntent } = await stripe.confirmSetup({
        elements,
        confirmParams: { return_url: window.location.href },
        redirect: 'if_required'
      });

      if (error) {
        toast.error(error.message);
      } else if (setupIntent && setupIntent.status === 'succeeded') {
        const token = localStorage.getItem('token');
        await axios.post(`${API}/groups/${groupId}/confirm-payment-method`, {
          setup_intent_id: setupIntent.id
        }, { headers: { 'Authorization': `Bearer ${token}` } });
        onSuccess();
      }
    } catch (err) {
      toast.error('Failed to update payment method');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} style={{ marginTop: '20px', padding: '20px', border: '1px solid #E5E7EB', borderRadius: '10px', background: '#F9FAFB' }}>
      <h3 style={{ margin: '0 0 14px', fontSize: '15px', color: '#1E293B' }}>
        {hasExisting ? 'Update Payment Method' : 'Add Payment Method'}
      </h3>
      <div data-testid="stripe-card-element" style={{ background: '#fff', borderRadius: '8px', border: '1px solid #D1D5DB', padding: '12px' }}>
        <PaymentElement />
      </div>
      <div style={{ display: 'flex', gap: '10px', marginTop: '14px' }}>
        <button type="button" className="btn btn-secondary" onClick={onCancel} style={{ padding: '8px 16px', fontSize: '14px' }}>
          Cancel
        </button>
        <button type="submit" className="btn btn-primary" disabled={!stripe || processing} data-testid="confirm-card-update-btn" style={{ padding: '8px 16px', fontSize: '14px' }}>
          {processing ? 'Saving...' : 'Confirm'}
        </button>
      </div>
    </form>
  );
}

function StripePaymentForm({ clientSecret, groupId, onSuccess, onCancel, hasExisting }) {
  const options = { clientSecret, appearance: { theme: 'stripe' } };
  return (
    <Elements stripe={stripePromise} options={options}>
      <StripeCheckoutForm groupId={groupId} onSuccess={onSuccess} onCancel={onCancel} hasExisting={hasExisting} />
    </Elements>
  );
}

function SubscriptionTab({ groupId, subscription, onRefresh }) {
  const [tiers, setTiers] = useState(null);
  const [selectedTier, setSelectedTier] = useState('');
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [loading, setLoading] = useState(false);
  const [showUpdateCard, setShowUpdateCard] = useState(false);
  const [clientSecret, setClientSecret] = useState(null);

  useEffect(() => {
    loadTiers();
  }, []);

  const loadTiers = async () => {
    try {
      const response = await axios.get(`${API}/subscriptions/tiers`);
      setTiers(response.data);
    } catch (error) {
      toast.error('Failed to load subscription tiers');
    }
  };

  const handleSubscribe = async () => {
    if (!selectedTier) {
      toast.error('Please select a tier');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/subscriptions/checkout`, {
        group_id: groupId,
        tier: selectedTier,
        billing_cycle: billingCycle
      }, {
        headers: {
          'origin': window.location.origin
        }
      });

      window.location.href = response.data.url;
    } catch (error) {
      toast.error('Failed to initiate subscription');
      setLoading(false);
    }
  };

  const handleOpenUpdateCard = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/groups/${groupId}/update-payment-method`, {}, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setClientSecret(res.data.client_secret);
      setShowUpdateCard(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to set up card update');
    }
  };

  const handleCardSuccess = () => {
    toast.success('Payment method updated successfully');
    setShowUpdateCard(false);
    setClientSecret(null);
    onRefresh();
  };

  if (!subscription || !tiers) {
    return <div>Loading...</div>;
  }

  const getStatusBadge = (status) => {
    const badges = {
      'trial': { class: 'status-pending', text: 'Trial' },
      'active': { class: 'status-active', text: 'Active' },
      'expired': { class: 'status-inactive', text: 'Expired' },
      'cancelled': { class: 'status-inactive', text: 'Cancelled' }
    };
    return badges[status] || badges['trial'];
  };

  const statusBadge = getStatusBadge(subscription.subscription_status);

  return (
    <div className="subscription-tab" data-testid="subscription-tab">
      <h1>Subscription Management</h1>

      <div className="subscription-status-card card">
        <div className="status-header-row">
          <h2>Current Subscription</h2>
          <span className={`status-badge ${statusBadge.class}`}>{statusBadge.text}</span>
        </div>
        
        <div className="subscription-details">
          <div className="detail-row">
            <span>Current Plan:</span>
            <strong>{tiers[subscription.current_tier]?.name}</strong>
          </div>
          <div className="detail-row">
            <span>Member Count:</span>
            <strong>{subscription.member_count}</strong>
          </div>
          {subscription.subscription_end_date && (
            <>
              <div className="detail-row">
                <span>Valid Until:</span>
                <strong>{new Date(subscription.subscription_end_date).toLocaleDateString()}</strong>
              </div>
              <div className="detail-row">
                <span>Days Remaining:</span>
                <strong style={{ color: (() => {
                  const days = Math.ceil((new Date(subscription.subscription_end_date) - new Date()) / (1000*60*60*24));
                  return days <= 7 ? '#DC2626' : days <= 14 ? '#F59E0B' : '#10B981';
                })() }}>
                  {(() => {
                    const days = Math.ceil((new Date(subscription.subscription_end_date) - new Date()) / (1000*60*60*24));
                    return days > 0 ? `${days} days` : 'Expired';
                  })()}
                </strong>
              </div>
            </>
          )}
          {subscription.needs_upgrade && (
            <div className="upgrade-warning">
              Warning: Your group has exceeded the member limit for your current tier. Please upgrade to continue adding members.
            </div>
          )}
        </div>
        
        {/* M-Pesa Quick Pay for KES Groups */}
        {subscription.currency === 'KES' && (
          <div style={{
            background: '#F0FFF4', border: '1px solid #48BB78', borderRadius: '10px',
            padding: '16px', marginTop: '16px'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '15px', color: '#276749' }}>M-Pesa Subscription Payment</h3>
                <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#48BB78' }}>
                  Pay {formatCurrency(subscription.billing_cycle === 'annual' ? subscription.annual_price : subscription.monthly_price, 'KES')} ({subscription.billing_cycle}) via M-Pesa
                </p>
              </div>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <button
                  className="btn btn-primary"
                  data-testid="mpesa-quick-pay-btn"
                  style={{ background: '#48BB78', border: 'none', padding: '8px 16px', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px' }}
                  onClick={async () => {
                    const phone = subscription.group_phone_number || prompt('Enter M-Pesa phone number (254...)');
                    if (!phone) return;
                    try {
                      const token = localStorage.getItem('token');
                      const res = await axios.post(`${API}/mpesa/subscription-stk-push`, 
                        { group_id: groupId, phone_number: phone },
                        { headers: { Authorization: `Bearer ${token}` } }
                      );
                      toast.success(`STK Push sent! Check your phone. Amount: ${formatCurrency(res.data.amount, 'KES')}`);
                    } catch (err) {
                      toast.error(err.response?.data?.detail || 'Failed to initiate M-Pesa payment');
                    }
                  }}
                >
                  Pay via M-Pesa
                </button>
                <label
                  className="btn btn-secondary"
                  data-testid="upload-proof-btn"
                  style={{ padding: '8px 16px', fontSize: '13px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
                >
                  Upload Proof
                  <input type="file" accept="image/*,.pdf" style={{ display: 'none' }} onChange={async (e) => {
                    const file = e.target.files[0];
                    if (!file) return;
                    const code = prompt('Enter M-Pesa transaction code (optional):') || '';
                    const fd = new FormData();
                    fd.append('file', file);
                    fd.append('group_id', groupId);
                    fd.append('mpesa_code', code);
                    try {
                      const token = localStorage.getItem('token');
                      await axios.post(`${API}/mpesa/subscription-upload`, fd, {
                        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
                      });
                      toast.success('Payment proof uploaded! Admin will verify shortly.');
                    } catch (err) {
                      toast.error(err.response?.data?.detail || 'Upload failed');
                    }
                  }} />
                </label>
              </div>
            </div>
            {subscription.group_phone_number && (
              <p style={{ margin: '8px 0 0', fontSize: '12px', color: '#6B7280' }}>
                Designated phone: {subscription.group_phone_number}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Payment Method on File (non-KES groups only) */}
      {subscription.currency !== 'KES' && (
      <div className="card" style={{ padding: '24px', marginBottom: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h2 style={{ margin: '0 0 8px', fontSize: '18px', color: '#1E293B' }}>Payment Method</h2>
            {subscription.has_payment_method ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{ background: '#F3F4F6', borderRadius: '8px', padding: '10px 16px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ fontSize: '18px' }}>
                    {subscription.payment_method_type === 'us_bank_account' ? '🏦' : '💳'}
                  </span>
                  <div>
                    <p style={{ margin: 0, fontWeight: '600', fontSize: '15px', color: '#1E293B', letterSpacing: '1px' }} data-testid="payment-method-display">
                      {subscription.payment_method_type === 'us_bank_account' 
                        ? `${subscription.payment_method_brand || 'Bank'} •••• ${subscription.payment_method_last4 || '••••'}`
                        : `${(subscription.payment_method_brand || 'Card').charAt(0).toUpperCase() + (subscription.payment_method_brand || 'card').slice(1)} •••• •••• •••• ${subscription.payment_method_last4 || '••••'}`
                      }
                    </p>
                    <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#6B7280' }}>
                      {subscription.is_trial ? 'Will be charged when trial ends' : 'Auto-charge enabled'}
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <p style={{ color: '#9CA3AF', fontSize: '14px', margin: 0 }} data-testid="no-payment-method">
                No payment method on file. Add a card or bank account to enable auto-billing.
              </p>
            )}
          </div>
          <button 
            className="btn btn-secondary" 
            onClick={handleOpenUpdateCard} 
            data-testid="change-payment-method-btn"
            style={{ padding: '8px 18px', fontSize: '14px' }}
          >
            {subscription.has_payment_method ? 'Change Payment Method' : 'Add Card / Bank'}
          </button>
        </div>
        
        {/* Update Card Form */}
        {showUpdateCard && clientSecret && (
          <StripePaymentForm
            clientSecret={clientSecret}
            groupId={groupId}
            onSuccess={handleCardSuccess}
            onCancel={() => { setShowUpdateCard(false); setClientSecret(null); }}
            hasExisting={subscription.has_payment_method}
          />
        )}
      </div>
      )}

      <div className="pricing-section">
        <h2>Select a Plan</h2>
        
        <div className="billing-toggle">
          <button 
            className={billingCycle === 'monthly' ? 'active' : ''}
            onClick={() => setBillingCycle('monthly')}
            data-testid="billing-monthly"
          >
            Monthly
          </button>
          <button 
            className={billingCycle === 'annual' ? 'active' : ''}
            onClick={() => setBillingCycle('annual')}
            data-testid="billing-annual"
          >
            Annual <span className="save-badge">Save 10%</span>
          </button>
        </div>

        <div className="pricing-grid">
          {Object.entries(tiers).filter(([key]) => key !== 'custom').map(([key, tier]) => (
            <div 
              key={key}
              className={`pricing-card card ${selectedTier === key ? 'selected' : ''} ${subscription.current_tier === key ? 'current' : ''}`}
              onClick={() => setSelectedTier(key)}
              data-testid={`tier-${key}`}
            >
              {subscription.current_tier === key && (
                <div className="current-plan-badge">Current Plan</div>
              )}
              <h3>{tier.name}</h3>
              <div className="price">
                <span className="amount">
                  {formatCurrency(billingCycle === 'annual' ? tier.annual_price : tier.monthly_price, selectedGroup?.currency)}
                </span>
                <span className="period">/{billingCycle === 'annual' ? 'year' : 'month'}</span>
              </div>
              {billingCycle === 'annual' && (
                <div className="savings">
                  Save {formatCurrency(tier.monthly_price * 12 - tier.annual_price, selectedGroup?.currency)}/year
                </div>
              )}
              <div className="tier-features">
                <div className="feature">✓ Up to {tier.max_members} members</div>
                <div className="feature">✓ Full platform access</div>
                <div className="feature">✓ Payment processing</div>
                <div className="feature">✓ Analytics & reports</div>
                <div className="feature">✓ Email notifications</div>
              </div>
            </div>
          ))}
          <div 
            className={`pricing-card card ${subscription.current_tier === 'custom' ? 'current' : ''}`}
            data-testid="tier-custom"
            style={{ opacity: subscription.current_tier === 'custom' ? 1 : 0.85 }}
          >
            {subscription.current_tier === 'custom' && (
              <div className="current-plan-badge">Current Plan</div>
            )}
            <h3>1500+ Members</h3>
            {subscription.current_tier === 'custom' && subscription.custom_pricing ? (
              <div className="price">
                <span className="amount">
                  ${billingCycle === 'annual' ? (subscription.custom_pricing.annual_price || 0).toFixed(2) : (subscription.custom_pricing.monthly_price || 0).toFixed(2)}
                </span>
                <span className="period">/{billingCycle === 'annual' ? 'year' : 'month'}</span>
              </div>
            ) : (
              <div className="price">
                <span className="amount" style={{ fontSize: '20px' }}>Custom Pricing</span>
              </div>
            )}
            <div className="tier-features">
              <div className="feature">✓ Unlimited members</div>
              <div className="feature">✓ Full platform access</div>
              <div className="feature">✓ Custom pricing by admin</div>
              <div className="feature">✓ Priority support</div>
              <div className="feature" style={{ color: '#6B7280', fontStyle: 'italic' }}>Contact admin for pricing</div>
            </div>
          </div>
        </div>

        <div className="subscription-actions">
          <button 
            onClick={handleSubscribe}
            className="btn btn-primary btn-large"
            disabled={loading || !selectedTier}
            data-testid="subscribe-btn"
          >
            {loading ? 'Processing...' : selectedTier === subscription.current_tier ? 'Renew Subscription' : 'Subscribe Now'}
          </button>
        </div>
      </div>
    </div>
  );
}



function AccountsTab({ groupId, group: initialGroup, onRefresh }) {
  const [group, setGroup] = useState(initialGroup);
  const [connectStatus, setConnectStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showBankForm, setShowBankForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [routingValidation, setRoutingValidation] = useState(null);
  const [validatingRouting, setValidatingRouting] = useState(false);
  const [formData, setFormData] = useState({
    account_holder_name: '',
    bank_name: '',
    routing_number: '',
    account_number: '',
    account_number_confirm: '',
    account_type: 'checking'
  });

  // Validate routing number when it changes
  const validateRoutingNumber = async (routingNumber, bankName = '') => {
    if (routingNumber.length !== 9) {
      setRoutingValidation(null);
      return;
    }
    
    setValidatingRouting(true);
    try {
      const response = await axios.get(`${API}/validate-routing/${routingNumber}`, {
        params: { bank_name: bankName || undefined }
      });
      setRoutingValidation(response.data);
      
      // Auto-fill bank name if found and field is empty
      if (response.data.bank_name && !bankName) {
        setFormData(prev => ({ ...prev, bank_name: response.data.bank_name }));
      }
    } catch (error) {
      setRoutingValidation({ valid: false, error: 'Failed to validate routing number' });
    } finally {
      setValidatingRouting(false);
    }
  };

  const handleRoutingChange = (value) => {
    const cleanValue = value.replace(/\D/g, '').slice(0, 9);
    setFormData({ ...formData, routing_number: cleanValue });
    
    // Validate when 9 digits entered
    if (cleanValue.length === 9) {
      validateRoutingNumber(cleanValue, formData.bank_name);
    } else {
      setRoutingValidation(null);
    }
  };

  const handleBankNameChange = (value) => {
    setFormData({ ...formData, bank_name: value });
    
    // Re-validate if routing number is complete
    if (formData.routing_number.length === 9) {
      validateRoutingNumber(formData.routing_number, value);
    }
  };

  useEffect(() => {
    loadAllData();
  }, [groupId]);

  useEffect(() => {
    if (initialGroup) {
      setGroup(initialGroup);
    }
  }, [initialGroup]);

  const loadAllData = async () => {
    setLoading(true);
    try {
      const [groupRes, statusRes] = await Promise.all([
        axios.get(`${API}/groups/${groupId}`),
        axios.get(`${API}/groups/${groupId}/connect/status`)
      ]);
      
      setGroup(groupRes.data);
      setConnectStatus(statusRes.data);
    } catch (error) {
      console.error('Failed to load account data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSetupBankAccount = async (e) => {
    e.preventDefault();
    
    // Validation
    if (formData.account_number !== formData.account_number_confirm) {
      toast.error('Account numbers do not match');
      return;
    }
    
    if (formData.routing_number.length !== 9) {
      toast.error('Routing number must be exactly 9 digits');
      return;
    }
    
    if (formData.account_number.length < 4) {
      toast.error('Invalid account number');
      return;
    }

    if (!formData.account_holder_name.trim()) {
      toast.error('Account holder name is required');
      return;
    }

    if (!formData.bank_name.trim()) {
      toast.error('Bank name is required');
      return;
    }
    
    setSaving(true);
    try {
      const response = await axios.post(`${API}/groups/${groupId}/connect/setup-with-bank`, {
        account_holder_name: formData.account_holder_name,
        bank_name: formData.bank_name,
        routing_number: formData.routing_number,
        account_number: formData.account_number,
        account_type: formData.account_type
      });
      
      // Show success message
      toast.success(response.data.message || 'Bank account set up successfully!');
      
      // Show warning if bank name doesn't match routing number
      if (response.data.warning) {
        setTimeout(() => toast.warning(response.data.warning, { duration: 8000 }), 500);
      }
      
      // Show verified bank name if available
      if (response.data.verified_bank_name) {
        setTimeout(() => toast.info(`Bank verified: ${response.data.verified_bank_name}`, { duration: 5000 }), 1000);
      }
      
      setShowBankForm(false);
      setRoutingValidation(null);
      setFormData({
        account_holder_name: '',
        bank_name: '',
        routing_number: '',
        account_number: '',
        account_number_confirm: '',
        account_type: 'checking'
      });
      loadAllData();
      if (onRefresh) onRefresh();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to set up bank account';
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="accounts-tab" data-testid="accounts-tab">
        <div className="loading-state">Loading account information...</div>
      </div>
    );
  }

  const platformFee = group?.platform_fee_percentage || 4.0;
  const hasBankDetails = group?.bank_account_configured && group?.bank_account_last4;
  const hasStripeConnect = group?.connected_account_id && connectStatus?.status === 'active';
  const isAccountSetup = hasBankDetails || hasStripeConnect;
  const hasConnectedAccount = group?.connected_account_id;

  return (
    <div className="accounts-tab" data-testid="accounts-tab">
      <div className="tab-header">
        <h1>🏦 Bank Account Setup</h1>
        <p>Connect your bank account to enable disbursements to beneficiaries</p>
      </div>

      {/* Account Status Overview */}
      {!isAccountSetup && !showBankForm && (
        <div className="setup-prompt-card card">
          <div className="setup-icon">🏦</div>
          <h2>Set Up Your Payment Account</h2>
          <p>Connect your bank account to start receiving member contributions directly.</p>
          
          <div className="benefits-grid">
            <div className="benefit-item">
              <span className="benefit-icon">✅</span>
              <div>
                <strong>Direct Deposits</strong>
                <p>Member payments go straight to your account</p>
              </div>
            </div>
            <div className="benefit-item">
              <span className="benefit-icon">💸</span>
              <div>
                <strong>Secure Disbursements</strong>
                <p>3-signatory approval for all payouts</p>
              </div>
            </div>
            <div className="benefit-item">
              <span className="benefit-icon">📊</span>
              <div>
                <strong>Real-time Tracking</strong>
                <p>Monitor all transactions in one place</p>
              </div>
            </div>
            <div className="benefit-item">
              <span className="benefit-icon">🔒</span>
              <div>
                <strong>Secure Processing</strong>
                <p>Powered by Stripe's trusted infrastructure</p>
              </div>
            </div>
          </div>

          <button 
            onClick={() => setShowBankForm(true)}
            className="btn btn-primary btn-large"
            data-testid="setup-bank-btn"
          >
            🚀 Set Up Bank Account
          </button>
        </div>
      )}

      {/* Bank Account Setup Form */}
      {showBankForm && (
        <div className="bank-setup-form card">
          <div className="form-header">
            <h2>{hasConnectedAccount ? '✏️ Update Bank Account' : '🏦 Connect Your Bank Account'}</h2>
            <p>Enter your bank details to enable direct payouts from member contributions</p>
          </div>
          
          <form onSubmit={handleSetupBankAccount}>
            <div className="form-row">
              <div className="form-group">
                <label>Account Holder Name *</label>
                <input
                  type="text"
                  value={formData.account_holder_name}
                  onChange={(e) => setFormData({ ...formData, account_holder_name: e.target.value })}
                  required
                  placeholder="Enter name on bank account"
                  data-testid="account-holder-name-input"
                />
                <small className="form-hint">Name exactly as it appears on your bank account</small>
              </div>
              
              <div className="form-group">
                <label>Bank Name *</label>
                <input
                  type="text"
                  value={formData.bank_name}
                  onChange={(e) => handleBankNameChange(e.target.value)}
                  required
                  placeholder="e.g., Chase, Bank of America"
                  data-testid="bank-name-input"
                  style={{
                    borderColor: routingValidation?.match === false ? '#F59E0B' : 
                                 routingValidation?.match === true ? '#10B981' : undefined
                  }}
                />
                {routingValidation?.bank_name && routingValidation.match === false && (
                  <small className="form-warning" style={{ color: '#F59E0B', display: 'block', marginTop: '4px' }}>
                    ⚠️ Routing number is registered to: <strong>{routingValidation.bank_name}</strong>
                  </small>
                )}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Routing Number *</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type="text"
                    value={formData.routing_number}
                    onChange={(e) => handleRoutingChange(e.target.value)}
                    required
                    placeholder="9-digit routing number"
                    maxLength="9"
                    data-testid="routing-number-input"
                    style={{
                      borderColor: routingValidation?.valid === false ? '#EF4444' : 
                                   routingValidation?.valid === true ? '#10B981' : undefined,
                      paddingRight: '40px'
                    }}
                  />
                  {validatingRouting && (
                    <span style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', color: '#6B7280' }}>
                      ⏳
                    </span>
                  )}
                  {!validatingRouting && routingValidation?.valid === true && (
                    <span style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', color: '#10B981' }}>
                      ✓
                    </span>
                  )}
                  {!validatingRouting && routingValidation?.valid === false && (
                    <span style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', color: '#EF4444' }}>
                      ✗
                    </span>
                  )}
                </div>
                {routingValidation?.valid === false && (
                  <small className="form-error" style={{ color: '#EF4444', display: 'block', marginTop: '4px' }}>
                    ❌ {routingValidation.error}
                  </small>
                )}
                {routingValidation?.valid === true && routingValidation?.bank_name && (
                  <small className="form-success" style={{ color: '#10B981', display: 'block', marginTop: '4px' }}>
                    ✅ Verified: {routingValidation.bank_name}
                  </small>
                )}
                {routingValidation?.valid === true && !routingValidation?.bank_name && (
                  <small className="form-warning" style={{ color: '#F59E0B', display: 'block', marginTop: '4px' }}>
                    ⚠️ Valid routing number, but bank not in database. Please verify manually.
                  </small>
                )}
                <small className="form-hint">9-digit ACH routing number (found on checks)</small>
              </div>
              
              <div className="form-group">
                <label>Account Type *</label>
                <select
                  value={formData.account_type}
                  onChange={(e) => setFormData({ ...formData, account_type: e.target.value })}
                  data-testid="account-type-select"
                >
                  <option value="checking">Checking</option>
                  <option value="savings">Savings</option>
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Account Number *</label>
                <input
                  type="text"
                  value={formData.account_number}
                  onChange={(e) => setFormData({ ...formData, account_number: e.target.value.replace(/\D/g, '') })}
                  required
                  placeholder="Enter account number"
                  data-testid="account-number-input"
                />
              </div>
              
              <div className="form-group">
                <label>Confirm Account Number *</label>
                <input
                  type="text"
                  value={formData.account_number_confirm}
                  onChange={(e) => setFormData({ ...formData, account_number_confirm: e.target.value.replace(/\D/g, '') })}
                  required
                  placeholder="Re-enter account number"
                  data-testid="account-number-confirm-input"
                />
                {formData.account_number && formData.account_number_confirm && 
                 formData.account_number !== formData.account_number_confirm && (
                  <small className="error-text">Account numbers do not match</small>
                )}
              </div>
            </div>

            <div className="security-notice">
              <span className="security-icon">🔒</span>
              <p>Your bank information is encrypted and securely processed by Stripe. We never store your full account number - only the last 4 digits for verification.</p>
            </div>

            <div className="form-actions">
              <button 
                type="button" 
                className="btn btn-secondary"
                onClick={() => setShowBankForm(false)}
                disabled={saving}
              >
                Cancel
              </button>
              <button 
                type="submit" 
                className="btn btn-primary"
                disabled={saving || (formData.account_number && formData.account_number_confirm && formData.account_number !== formData.account_number_confirm)}
                data-testid="save-bank-btn"
              >
                {saving ? 'Setting up...' : hasConnectedAccount ? 'Update Bank Account' : 'Connect Bank Account'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Active Account Overview */}
      {isAccountSetup && !showBankForm && (
        <>
          {/* Connected Bank Card */}
          <div className="connected-account-card card">
            <div className="account-status-header">
              <div className="status-indicator active">
                <span className="status-icon">✅</span>
                <div>
                  <h3>Bank Account Connected</h3>
                  <p>Your account is set up and ready to receive payments</p>
                </div>
              </div>
              <button 
                className="btn btn-secondary btn-sm"
                onClick={() => setShowBankForm(true)}
              >
                ✏️ Update
              </button>
            </div>

            <div className="bank-details">
              <div className="detail-row">
                <span className="detail-label">Account Holder:</span>
                <span className="detail-value">{group.bank_account_name || 'N/A'}</span>
              </div>
              {group.bank_name && (
                <div className="detail-row">
                  <span className="detail-label">Bank:</span>
                  <span className="detail-value">{group.bank_name}</span>
                </div>
              )}
              <div className="detail-row">
                <span className="detail-label">Account:</span>
                <span className="detail-value">****{group.bank_account_last4}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Routing:</span>
                <span className="detail-value">****{group.bank_routing_number?.slice(-4)}</span>
              </div>
              {group.bank_account_type && (
                <div className="detail-row">
                  <span className="detail-label">Type:</span>
                  <span className="detail-value" style={{textTransform: 'capitalize'}}>{group.bank_account_type}</span>
                </div>
              )}
            </div>
          </div>

          {/* Account Balance - Read Only */}
          {hasStripeConnect && (
            <div className="balance-card card">
              <h2>💰 Account Balance</h2>
              
              <div className="balance-display">
                <div className="balance-item available">
                  <div className="balance-label">Available Balance</div>
                  <div className="balance-amount">{formatCurrency(connectStatus?.available_balance || 0, group?.currency)}</div>
                  <div className="balance-hint">Available for disbursements</div>
                </div>
                <div className="balance-item pending">
                  <div className="balance-label">Pending Balance</div>
                  <div className="balance-amount">{formatCurrency(connectStatus?.pending_balance || 0, group?.currency)}</div>
                  <div className="balance-hint">Processing (2-7 days)</div>
                </div>
              </div>

              <div className="disbursement-notice">
                <span className="notice-icon">ℹ️</span>
                <p>To make a payout to a beneficiary, go to the <strong>Disbursements</strong> tab. All payouts require approval from 3 signatories for security.</p>
              </div>
            </div>
          )}

          {/* Notice for accounts without Stripe Connect */}
          {!hasStripeConnect && (
            <div className="stripe-connect-notice card">
              <div className="notice-icon">💡</div>
              <h3>Complete Setup</h3>
              <p>Your bank details are saved. Click "Update" above to complete Stripe Connect setup and enable disbursements.</p>
            </div>
          )}
        </>
      )}

      {/* Fee Structure Information */}
      <div className="fee-structure card">
        <h2>💵 Fee Structure</h2>
        <div className="fee-breakdown">
          <div className="fee-item">
            <span className="fee-label">Platform Fee:</span>
            <span className="fee-value">{platformFee}%</span>
          </div>
          <div className="fee-item">
            <span className="fee-label">Stripe Processing:</span>
            <span className="fee-value">{group?.currency === 'KES' ? 'N/A (M-Pesa)' : '2.9% + $0.30'}</span>
          </div>
        </div>
        
        <div className="fee-example">
          <h3>Example for {formatCurrency(group?.payment_amount || 50, group?.currency)} contribution:</h3>
          <p>Platform Fee ({platformFee}%): <strong>{formatCurrency((group?.payment_amount || 50) * (platformFee / 100), group?.currency)}</strong></p>
          <p className="total-highlight">Total charged to member: <strong>{formatCurrency((group?.payment_amount || 50) * (1 + platformFee / 100), group?.currency)}</strong></p>
        </div>
      </div>

      {/* How It Works */}
      <div className="how-it-works card">
        <h2>📖 How Disbursements Work</h2>
        <div className="steps-grid">
          <div className="step-item">
            <div className="step-number">1</div>
            <div className="step-content">
              <h4>Members Pay</h4>
              <p>Members make contributions via secure Stripe checkout</p>
            </div>
          </div>
          <div className="step-item">
            <div className="step-number">2</div>
            <div className="step-content">
              <h4>Funds Processed</h4>
              <p>Payments are processed and {platformFee}% platform fee is deducted</p>
            </div>
          </div>
          <div className="step-item">
            <div className="step-number">3</div>
            <div className="step-content">
              <h4>Create Disbursement</h4>
              <p>Leadership initiates payout to beneficiary</p>
            </div>
          </div>
          <div className="step-item">
            <div className="step-number">4</div>
            <div className="step-content">
              <h4>3-Signatory Approval</h4>
              <p>3 signatories must approve before funds are released</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function AnalyticsTab({ groupId, groupName, group }) {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeframe, setTimeframe] = useState('all');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [showCustomRange, setShowCustomRange] = useState(false);
  
  const COLORS = ['#27AE60', '#E74C3C', '#F39C12', '#3498DB', '#9B59B6'];

  useEffect(() => {
    loadAnalytics();
  }, [groupId, timeframe]);

  const loadAnalytics = async (customStart = null, customEnd = null) => {
    setLoading(true);
    try {
      let url = `${API}/groups/${groupId}/analytics`;
      const params = new URLSearchParams();
      
      if (customStart && customEnd) {
        params.append('start_date', customStart);
        params.append('end_date', customEnd);
      } else if (timeframe !== 'custom') {
        params.append('timeframe', timeframe);
      }
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      
      const response = await axios.get(url);
      setAnalytics(response.data);
    } catch (error) {
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const handleTimeframeChange = (newTimeframe) => {
    if (newTimeframe === 'custom') {
      setShowCustomRange(true);
      setTimeframe('custom');
    } else {
      setShowCustomRange(false);
      setTimeframe(newTimeframe);
    }
  };

  const applyCustomRange = () => {
    if (customStartDate && customEndDate) {
      loadAnalytics(customStartDate, customEndDate);
    } else {
      toast.error('Please select both start and end dates');
    }
  };

  const generatePDF = () => {
    if (!analytics) return;
    
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    
    // Title
    doc.setFontSize(20);
    doc.setTextColor(39, 174, 96);
    doc.text(`${groupName} - Analytics Report`, pageWidth / 2, 20, { align: 'center' });
    
    // Subtitle with timeframe
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(`Period: ${analytics.timeframe_label || 'All Time'}`, pageWidth / 2, 28, { align: 'center' });
    doc.text(`Generated: ${new Date().toLocaleString()}`, pageWidth / 2, 34, { align: 'center' });
    
    // Summary Section
    doc.setFontSize(14);
    doc.setTextColor(0);
    doc.text('Summary', 14, 40);
    
    const summaryData = [
      ['Total Members', analytics.total_members],
      ['Active Members', analytics.active_members],
      ['Inactive Members', analytics.inactive_members],
      ['Probation Members', analytics.probation_members],
      ['Total Payments', analytics.total_payments],
      ['Total Amount Collected', formatCurrency(analytics.total_amount, group?.currency)],
      ['Average Payment', formatCurrency(analytics.average_payment, group?.currency)],
      ['Total Disbursements', analytics.total_disbursements],
      ['Pending Disbursements', analytics.pending_disbursements],
      ['Approved Disbursements', analytics.approved_disbursements],
      ['Total Disbursed', formatCurrency(analytics.total_disbursed, group?.currency)],
      ['Net Balance', formatCurrency(analytics.net_balance, group?.currency)]
    ];
    
    autoTable(doc, {
      startY: 45,
      head: [['Metric', 'Value']],
      body: summaryData,
      theme: 'striped',
      headStyles: { fillColor: [39, 174, 96] },
      margin: { left: 14, right: 14 }
    });
    
    // Payment Trends Section
    if (analytics.payment_trends && analytics.payment_trends.length > 0) {
      let finalY = 45;
      // Get the final Y position from previous table
      if (doc.lastAutoTable && doc.lastAutoTable.finalY) {
        finalY = doc.lastAutoTable.finalY + 10;
      } else {
        finalY = 100; // Fallback position
      }
      doc.setFontSize(14);
      doc.text('Payment Trends', 14, finalY);
      
      const trendsData = analytics.payment_trends.map(trend => [
        trend.month,
        trend.count,
        formatCurrency(trend.amount, group?.currency)
      ]);
      
      autoTable(doc, {
        startY: finalY + 5,
        head: [['Month', 'Payment Count', 'Total Amount']],
        body: trendsData,
        theme: 'striped',
        headStyles: { fillColor: [39, 174, 96] },
        margin: { left: 14, right: 14 }
      });
    }
    
    // Footer
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(150);
      doc.text(
        `Page ${i} of ${pageCount} | ChamaLink Analytics`,
        pageWidth / 2,
        doc.internal.pageSize.height - 10,
        { align: 'center' }
      );
    }
    
    doc.save(`${groupName}-Analytics-${new Date().toISOString().split('T')[0]}.pdf`);
    toast.success('PDF downloaded successfully');
  };

  if (loading) {
    return <div>Loading analytics...</div>;
  }

  if (!analytics) {
    return <div>No analytics data available</div>;
  }

  const memberData = [
    { name: 'Active', value: analytics.active_members, color: '#27AE60' },
    { name: 'Inactive', value: analytics.inactive_members, color: '#E74C3C' },
    { name: 'Probation', value: analytics.probation_members, color: '#F39C12' }
  ];

  return (
    <div className="analytics-tab">
      <div className="analytics-header">
        <div className="header-left">
          <h1>Analytics Dashboard</h1>
          {analytics.timeframe_label && (
            <span className="timeframe-badge">{analytics.timeframe_label}</span>
          )}
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button className="btn btn-primary" onClick={generatePDF} data-testid="download-pdf-btn">
            Download PDF Report
          </button>
          <button 
            className="btn btn-secondary" 
            data-testid="download-analytics-pdf-btn"
            style={{ background: '#059669', color: '#fff', border: 'none' }}
            onClick={async () => {
              try {
                const token = localStorage.getItem('token');
                const response = await axios.get(`${API}/groups/${groupId}/analytics/export-pdf`, {
                  headers: { Authorization: `Bearer ${token}` },
                  responseType: 'blob'
                });
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const a = document.createElement('a');
                a.href = url;
                a.download = `analytics_${group?.name || 'report'}.pdf`;
                a.click();
                toast.success('Analytics PDF downloaded!');
              } catch (err) {
                toast.error('Failed to download PDF');
              }
            }}
          >
            Branded PDF
          </button>
          <button 
            className="btn btn-secondary" 
            data-testid="email-report-btn"
            style={{ background: '#7C3AED', color: '#fff', border: 'none' }}
            onClick={async () => {
              if (!window.confirm('Send analytics summary email to all active members?')) return;
              try {
                const token = localStorage.getItem('token');
                const res = await axios.post(`${API}/groups/${groupId}/analytics/email-report`, {}, { headers: { Authorization: `Bearer ${token}` } });
                toast.success(res.data.message);
              } catch (err) {
                toast.error(err.response?.data?.detail || 'Failed to send emails');
              }
            }}
          >
            Email Members
          </button>
        </div>
      </div>

      {/* Timeframe Filter */}
      <div className="timeframe-filter card">
        <div className="filter-label">
          <span className="filter-icon">🗓️</span>
          <span>Filter by Period:</span>
        </div>
        <div className="timeframe-buttons">
          <button 
            className={`timeframe-btn ${timeframe === 'today' ? 'active' : ''}`}
            onClick={() => handleTimeframeChange('today')}
          >
            Today
          </button>
          <button 
            className={`timeframe-btn ${timeframe === 'week' ? 'active' : ''}`}
            onClick={() => handleTimeframeChange('week')}
          >
            Last 7 Days
          </button>
          <button 
            className={`timeframe-btn ${timeframe === 'month' ? 'active' : ''}`}
            onClick={() => handleTimeframeChange('month')}
          >
            Last 30 Days
          </button>
          <button 
            className={`timeframe-btn ${timeframe === 'quarter' ? 'active' : ''}`}
            onClick={() => handleTimeframeChange('quarter')}
          >
            Last 90 Days
          </button>
          <button 
            className={`timeframe-btn ${timeframe === 'year' ? 'active' : ''}`}
            onClick={() => handleTimeframeChange('year')}
          >
            Last 12 Months
          </button>
          <button 
            className={`timeframe-btn ${timeframe === 'ytd' ? 'active' : ''}`}
            onClick={() => handleTimeframeChange('ytd')}
          >
            Year to Date
          </button>
          <button 
            className={`timeframe-btn ${timeframe === 'all' ? 'active' : ''}`}
            onClick={() => handleTimeframeChange('all')}
          >
            All Time
          </button>
          <button 
            className={`timeframe-btn ${timeframe === 'custom' ? 'active' : ''}`}
            onClick={() => handleTimeframeChange('custom')}
          >
            Custom Range
          </button>
        </div>
        
        {showCustomRange && (
          <div className="custom-range-picker">
            <div className="date-inputs">
              <div className="date-field">
                <label>Start Date</label>
                <input
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  data-testid="analytics-start-date"
                />
              </div>
              <span className="date-separator">to</span>
              <div className="date-field">
                <label>End Date</label>
                <input
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  data-testid="analytics-end-date"
                />
              </div>
            </div>
            <button 
              className="btn btn-primary btn-sm"
              onClick={applyCustomRange}
              data-testid="apply-custom-range"
            >
              Apply
            </button>
          </div>
        )}
      </div>

      {/* Key Metrics */}
      <div className="metrics-grid">
        <div className="metric-card card">
          <div className="metric-icon">👥</div>
          <div className="metric-content">
            <h3>{analytics.total_members}</h3>
            <p>Total Members</p>
          </div>
        </div>
        
        <div className="metric-card card">
          <div className="metric-icon">💰</div>
          <div className="metric-content">
            <h3>{formatCurrency(analytics.total_amount, group?.currency)}</h3>
            <p>Total Collected</p>
          </div>
        </div>
        
        <div className="metric-card card">
          <div className="metric-icon">📊</div>
          <div className="metric-content">
            <h3>{analytics.total_payments}</h3>
            <p>Total Payments</p>
          </div>
        </div>
        
        <div className="metric-card card">
          <div className="metric-icon">💸</div>
          <div className="metric-content">
            <h3>{formatCurrency(analytics.net_balance, group?.currency)}</h3>
            <p>Net Balance</p>
          </div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="charts-grid">
        {/* Member Status Distribution */}
        <div className="chart-card card">
          <h2>Member Status Distribution</h2>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={memberData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({name, value}) => `${name}: ${value}`}
                outerRadius={100}
                fill="#8884d8"
                dataKey="value"
              >
                {memberData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Payment Trends */}
        {analytics.payment_trends && analytics.payment_trends.length > 0 && (
          <div className="chart-card card">
            <h2>Payment Trends</h2>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={analytics.payment_trends}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="amount" stroke="#27AE60" strokeWidth={2} name="Amount ($)" />
                <Line type="monotone" dataKey="count" stroke="#3498DB" strokeWidth={2} name="Count" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Disbursement Summary */}
      <div className="disbursement-summary card">
        <h2>Disbursement Summary</h2>
        <div className="summary-grid">
          <div className="summary-item">
            <span className="summary-label">Total Disbursements:</span>
            <span className="summary-value">{analytics.total_disbursements}</span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Pending:</span>
            <span className="summary-value warning">{analytics.pending_disbursements}</span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Approved:</span>
            <span className="summary-value success">{analytics.approved_disbursements}</span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Total Disbursed:</span>
            <span className="summary-value">{formatCurrency(analytics.total_disbursed, group?.currency)}</span>
          </div>
        </div>
      </div>

      {/* Financial Overview */}
      <div className="financial-overview card">
        <h2>Financial Overview</h2>
        <div className="financial-details">
          <div className="financial-row">
            <span>Total Collected:</span>
            <strong className="positive">{formatCurrency(analytics.total_amount, group?.currency)}</strong>
          </div>
          <div className="financial-row">
            <span>Total Disbursed:</span>
            <strong className="negative">-{formatCurrency(analytics.total_disbursed, group?.currency)}</strong>
          </div>
          <div className="financial-row total">
            <span>Net Balance:</span>
            <strong className={analytics.net_balance >= 0 ? 'positive' : 'negative'}>
              {formatCurrency(analytics.net_balance, group?.currency)}
            </strong>
          </div>
          <div className="financial-row">
            <span>Average Payment:</span>
            <strong>{formatCurrency(analytics.average_payment, group?.currency)}</strong>
          </div>
        </div>
      </div>
    </div>
  );
}

function AccessControlTab({ groupId, group, currentUserId, canWrite, onRefresh }) {
  const [leaders, setLeaders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [activeSubTab, setActiveSubTab] = useState('permissions');
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditFilter, setAuditFilter] = useState('all');
  const [auditStartDate, setAuditStartDate] = useState('');
  const [auditEndDate, setAuditEndDate] = useState('');

  const FEATURE_LABELS = {
    'members': 'Members',
    'payments': 'Payments',
    'manual-verification': 'Manual Verification',
    'disbursements': 'Disbursements',
    'meetings': 'Meetings',
    'claims': 'Claims',
    'notifications': 'Notifications',
    'accounts': 'Bank Account',
    'analytics': 'Analytics',
    'support': 'Support',
    'group-settings': 'Group Settings',
    'subscription': 'Subscription',
    'access-control': 'Access Control'
  };

  const FEATURES = Object.keys(FEATURE_LABELS);

  useEffect(() => {
    loadLeaders();
  }, [groupId]);

  useEffect(() => {
    if (activeSubTab === 'audit-log') loadAuditLogs();
  }, [activeSubTab, auditFilter, auditStartDate, auditEndDate]);

  const loadLeaders = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/groups/${groupId}/leader-access`);
      setLeaders(response.data);
    } catch (error) {
      toast.error('Failed to load access settings');
    } finally {
      setLoading(false);
    }
  };

  const loadAuditLogs = async () => {
    setAuditLoading(true);
    try {
      const params = new URLSearchParams();
      if (auditFilter !== 'all') params.append('event_type', auditFilter);
      if (auditStartDate) params.append('start_date', auditStartDate);
      if (auditEndDate) params.append('end_date', auditEndDate);
      const response = await axios.get(`${API}/groups/${groupId}/audit-logs?${params.toString()}`);
      setAuditLogs(response.data);
    } catch (error) {
      toast.error('Failed to load audit logs');
    } finally {
      setAuditLoading(false);
    }
  };

  const handlePermissionChange = async (leaderId, feature, level) => {
    const leader = leaders.find(l => l.leader_id === leaderId);
    if (!leader || leader.is_founder) return;

    const updatedPermissions = { ...leader.permissions, [feature]: level };
    setSaving(prev => ({ ...prev, [leaderId]: true }));
    try {
      await axios.put(`${API}/groups/${groupId}/leader-access/${leaderId}`, {
        permissions: updatedPermissions
      });
      setLeaders(prev => prev.map(l =>
        l.leader_id === leaderId ? { ...l, permissions: updatedPermissions } : l
      ));
      toast.success(`Access updated for ${leader.name}`);
      if (onRefresh) onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update access');
    } finally {
      setSaving(prev => ({ ...prev, [leaderId]: false }));
    }
  };

  const handleBulkChange = async (leaderId, level) => {
    const leader = leaders.find(l => l.leader_id === leaderId);
    if (!leader || leader.is_founder) return;

    const updatedPermissions = {};
    FEATURES.forEach(f => { updatedPermissions[f] = level; });
    setSaving(prev => ({ ...prev, [leaderId]: true }));
    try {
      await axios.put(`${API}/groups/${groupId}/leader-access/${leaderId}`, {
        permissions: updatedPermissions
      });
      setLeaders(prev => prev.map(l =>
        l.leader_id === leaderId ? { ...l, permissions: updatedPermissions } : l
      ));
      toast.success(`All permissions set to "${level}" for ${leader.name}`);
      if (onRefresh) onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update access');
    } finally {
      setSaving(prev => ({ ...prev, [leaderId]: false }));
    }
  };

  const accessLevelColors = {
    'read-write': { bg: '#D1FAE5', color: '#065F46', label: 'Read & Write' },
    'read-only': { bg: '#DBEAFE', color: '#1E40AF', label: 'Read Only' },
    'deny': { bg: '#FEE2E2', color: '#991B1B', label: 'Denied' }
  };

  const eventTypeLabels = {
    'login': { icon: '🔑', label: 'Login', color: '#6366F1' },
    'access_control_change': { icon: '🔐', label: 'Access Change', color: '#F59E0B' },
    'loan_approval': { icon: '📝', label: 'Loan Approval', color: '#10B981' },
    'disbursement_approval': { icon: '💸', label: 'Disbursement', color: '#3B82F6' }
  };

  if (loading) return <div className="loading-state card"><p>Loading access settings...</p></div>;

  const otherLeaders = leaders.filter(l => !l.is_founder);
  const founder = leaders.find(l => l.is_founder);

  return (
    <div className="access-control-tab" data-testid="access-control-tab">
      <div className="tab-header">
        <h1>🔐 Access Control</h1>
        <p className="tab-description">Manage feature access and view activity logs</p>
      </div>

      {/* Sub-tabs */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '20px', background: '#F3F4F6', padding: '4px', borderRadius: '10px', width: 'fit-content' }}>
        <button
          onClick={() => setActiveSubTab('permissions')}
          data-testid="access-subtab-permissions"
          style={{
            padding: '8px 20px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontWeight: '600', fontSize: '13px',
            background: activeSubTab === 'permissions' ? '#1F2937' : 'transparent',
            color: activeSubTab === 'permissions' ? 'white' : '#6B7280'
          }}
        >
          Permissions
        </button>
        <button
          onClick={() => setActiveSubTab('audit-log')}
          data-testid="access-subtab-audit-log"
          style={{
            padding: '8px 20px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontWeight: '600', fontSize: '13px',
            background: activeSubTab === 'audit-log' ? '#1F2937' : 'transparent',
            color: activeSubTab === 'audit-log' ? 'white' : '#6B7280'
          }}
        >
          Audit Log
        </button>
      </div>

      {activeSubTab === 'permissions' && (
        <>
          {founder && (
            <div className="card" style={{ marginBottom: '20px', padding: '16px', background: '#F0FDF4', border: '1px solid #BBF7D0' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{ fontSize: '20px' }}>👑</span>
                <div>
                  <strong style={{ color: '#166534' }}>{founder.name}</strong>
                  <span style={{ color: '#6B7280', marginLeft: '8px', fontSize: '13px' }}>{founder.email}</span>
                </div>
                <span style={{ marginLeft: 'auto', background: '#166534', color: 'white', padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: '600' }}>
                  Founding Leader - Full Access
                </span>
              </div>
            </div>
          )}

          {otherLeaders.length === 0 ? (
            <div className="empty-state card">
              <h3>No other leaders</h3>
              <p>When additional leaders are confirmed, you can manage their access here.</p>
            </div>
          ) : (
            otherLeaders.map(leader => (
              <div key={leader.leader_id} className="card" style={{ marginBottom: '16px', padding: '20px' }} data-testid={`leader-access-card-${leader.leader_id}`}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', paddingBottom: '12px', borderBottom: '1px solid #E5E7EB' }}>
                  <div>
                    <strong style={{ fontSize: '16px' }}>{leader.name}</strong>
                    <span style={{ color: '#6B7280', marginLeft: '8px', fontSize: '13px' }}>{leader.email}</span>
                  </div>
                  {canWrite && (
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button onClick={() => handleBulkChange(leader.leader_id, 'read-write')} disabled={saving[leader.leader_id]} style={{ padding: '4px 12px', borderRadius: '6px', border: '1px solid #10B981', background: '#D1FAE5', color: '#065F46', cursor: 'pointer', fontSize: '12px', fontWeight: '600' }} data-testid={`bulk-rw-${leader.leader_id}`}>
                        Grant All
                      </button>
                      <button onClick={() => handleBulkChange(leader.leader_id, 'read-only')} disabled={saving[leader.leader_id]} style={{ padding: '4px 12px', borderRadius: '6px', border: '1px solid #3B82F6', background: '#DBEAFE', color: '#1E40AF', cursor: 'pointer', fontSize: '12px', fontWeight: '600' }} data-testid={`bulk-ro-${leader.leader_id}`}>
                        Read Only All
                      </button>
                      <button onClick={() => handleBulkChange(leader.leader_id, 'deny')} disabled={saving[leader.leader_id]} style={{ padding: '4px 12px', borderRadius: '6px', border: '1px solid #EF4444', background: '#FEE2E2', color: '#991B1B', cursor: 'pointer', fontSize: '12px', fontWeight: '600' }} data-testid={`bulk-deny-${leader.leader_id}`}>
                        Deny All
                      </button>
                    </div>
                  )}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '10px' }}>
                  {FEATURES.map(feature => {
                    const current = leader.permissions?.[feature] || 'deny';
                    const style = accessLevelColors[current];
                    return (
                      <div key={feature} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', borderRadius: '8px', background: '#F9FAFB', border: '1px solid #E5E7EB' }}>
                        <span style={{ fontSize: '13px', fontWeight: '500', color: '#374151' }}>{FEATURE_LABELS[feature]}</span>
                        {canWrite ? (
                          <select
                            value={current}
                            onChange={(e) => handlePermissionChange(leader.leader_id, feature, e.target.value)}
                            disabled={saving[leader.leader_id]}
                            style={{ padding: '4px 8px', borderRadius: '6px', border: `1px solid ${style.color}`, background: style.bg, color: style.color, fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}
                            data-testid={`access-${leader.leader_id}-${feature}`}
                          >
                            <option value="read-write">Read & Write</option>
                            <option value="read-only">Read Only</option>
                            <option value="deny">Denied</option>
                          </select>
                        ) : (
                          <span style={{ padding: '4px 10px', borderRadius: '6px', background: style.bg, color: style.color, fontSize: '12px', fontWeight: '600' }}>
                            {style.label}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </>
      )}

      {activeSubTab === 'audit-log' && (
        <div data-testid="audit-log-section">
          <div className="card" style={{ padding: '16px', marginBottom: '16px' }}>
            <div className="form-row" style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div className="form-group" style={{ minWidth: '180px' }}>
                <label>Event Type</label>
                <select value={auditFilter} onChange={(e) => setAuditFilter(e.target.value)} data-testid="audit-filter-type">
                  <option value="all">All Events</option>
                  <option value="login">Logins</option>
                  <option value="access_control_change">Access Changes</option>
                  <option value="loan_approval">Loan Approvals</option>
                  <option value="disbursement_approval">Disbursement Approvals</option>
                </select>
              </div>
              <div className="form-group" style={{ minWidth: '160px' }}>
                <label>From Date</label>
                <input type="date" value={auditStartDate} onChange={(e) => setAuditStartDate(e.target.value)} data-testid="audit-start-date" />
              </div>
              <div className="form-group" style={{ minWidth: '160px' }}>
                <label>To Date</label>
                <input type="date" value={auditEndDate} onChange={(e) => setAuditEndDate(e.target.value)} data-testid="audit-end-date" />
              </div>
              <button onClick={loadAuditLogs} className="btn btn-secondary" style={{ height: '38px' }} data-testid="audit-refresh-btn">
                Refresh
              </button>
            </div>
          </div>

          {auditLoading ? (
            <div className="loading-state card"><p>Loading audit logs...</p></div>
          ) : auditLogs.length === 0 ? (
            <div className="empty-state card">
              <h3>No audit logs yet</h3>
              <p>Activity logs will appear here as leaders interact with the system.</p>
            </div>
          ) : (
            <div className="card" style={{ padding: '0', overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }} data-testid="audit-log-table">
                <thead>
                  <tr style={{ background: '#F9FAFB', borderBottom: '2px solid #E5E7EB' }}>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6B7280', textTransform: 'uppercase' }}>Event</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6B7280', textTransform: 'uppercase' }}>User</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6B7280', textTransform: 'uppercase' }}>Description</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6B7280', textTransform: 'uppercase' }}>Date & Time</th>
                  </tr>
                </thead>
                <tbody>
                  {auditLogs.map((log) => {
                    const typeInfo = eventTypeLabels[log.event_type] || { icon: '📋', label: log.event_type, color: '#6B7280' };
                    return (
                      <tr key={log.id} style={{ borderBottom: '1px solid #F3F4F6' }} data-testid={`audit-row-${log.id}`}>
                        <td style={{ padding: '12px 16px' }}>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '3px 10px', borderRadius: '20px', background: `${typeInfo.color}15`, color: typeInfo.color, fontSize: '12px', fontWeight: '600' }}>
                            {typeInfo.icon} {typeInfo.label}
                          </span>
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          <div style={{ fontSize: '14px', fontWeight: '500', color: '#1F2937' }}>{log.user_name}</div>
                          <div style={{ fontSize: '12px', color: '#9CA3AF' }}>{log.user_email}</div>
                        </td>
                        <td style={{ padding: '12px 16px', fontSize: '13px', color: '#374151', maxWidth: '400px' }}>
                          {log.description}
                        </td>
                        <td style={{ padding: '12px 16px', fontSize: '13px', color: '#6B7280', whiteSpace: 'nowrap' }}>
                          {new Date(log.created_at).toLocaleDateString()} {new Date(log.created_at).toLocaleTimeString()}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function GroupSettingsTab({ group, onUpdate }) {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    payment_amount: '',
    payment_frequency: 'monthly',
    payment_criteria_days: '',
    inactive_window_days: 90,
    probation_days: 30,
    penalty_amount: 0,
    custom_payment_date: '',
    reminder_days_before: 3,
    reminders_enabled: true
  });
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (group) {
      setFormData({
        name: group.name || '',
        description: group.description || '',
        payment_amount: group.payment_amount || '',
        payment_frequency: group.payment_frequency || 'monthly',
        payment_criteria_days: group.payment_criteria_days || '',
        inactive_window_days: group.inactive_window_days || 90,
        probation_days: group.probation_days || 30,
        penalty_amount: group.penalty_amount || 0,
        custom_payment_date: group.custom_payment_date ? new Date(group.custom_payment_date).toISOString().split('T')[0] : '',
        reminder_days_before: group.reminder_days_before || 3,
        reminders_enabled: group.reminders_enabled !== false
      });
    }
  }, [group]);

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    setHasChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Build update payload with only changed fields
      const updates = {};
      if (formData.name !== group.name) updates.name = formData.name;
      if (formData.description !== group.description) updates.description = formData.description;
      if (parseFloat(formData.payment_amount) !== group.payment_amount) {
        updates.payment_amount = parseFloat(formData.payment_amount);
      }
      if (formData.payment_frequency !== group.payment_frequency) {
        updates.payment_frequency = formData.payment_frequency;
      }
      if (parseInt(formData.payment_criteria_days) !== group.payment_criteria_days) {
        updates.payment_criteria_days = parseInt(formData.payment_criteria_days);
      }
      if (parseInt(formData.inactive_window_days) !== (group.inactive_window_days || 90)) {
        updates.inactive_window_days = parseInt(formData.inactive_window_days);
      }
      if (parseInt(formData.probation_days) !== (group.probation_days || 30)) {
        updates.probation_days = parseInt(formData.probation_days);
      }
      if (parseFloat(formData.penalty_amount) !== (group.penalty_amount || 0)) {
        updates.penalty_amount = parseFloat(formData.penalty_amount);
      }
      if (formData.custom_payment_date && formData.payment_frequency === 'custom') {
        updates.custom_payment_date = new Date(formData.custom_payment_date).toISOString();
      }
      if (formData.reminders_enabled !== (group.reminders_enabled !== false)) {
        updates.reminders_enabled = formData.reminders_enabled;
      }
      if (parseInt(formData.reminder_days_before) !== (group.reminder_days_before || 3)) {
        updates.reminder_days_before = parseInt(formData.reminder_days_before);
      }

      if (Object.keys(updates).length === 0) {
        toast.info('No changes to save');
        setSaving(false);
        return;
      }

      await axios.put(`${API}/groups/${group.id}`, updates);
      toast.success('Group settings updated successfully');
      setHasChanges(false);
      onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update group settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="group-settings-tab">
      <div className="settings-header">
        <h1>⚙️ Group Settings</h1>
        <p>Manage your group configuration and payment settings</p>
      </div>

      <div className="settings-section card">
        <h2>Basic Information</h2>
        
        <div className="form-group">
          <label>Group Name *</label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => handleChange('name', e.target.value)}
            className="form-control"
            placeholder="Group name"
          />
        </div>

        <div className="form-group">
          <label>Description</label>
          <textarea
            value={formData.description}
            onChange={(e) => handleChange('description', e.target.value)}
            className="form-control"
            placeholder="Group description"
            rows="3"
          />
        </div>
      </div>

      <div className="settings-section card">
        <h2>Payment Settings</h2>
        
        <div className="form-group">
          <label>Payment Amount ($) *</label>
          <input
            type="number"
            step="0.01"
            min="0"
            value={formData.payment_amount}
            onChange={(e) => handleChange('payment_amount', e.target.value)}
            className="form-control"
            placeholder="50.00"
          />
          <small className="form-hint">Amount each member needs to contribute</small>
        </div>

        <div className="form-group">
          <label>Payment Frequency *</label>
          <select
            value={formData.payment_frequency}
            onChange={(e) => handleChange('payment_frequency', e.target.value)}
            className="form-control"
          >
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
            <option value="quarterly">Quarterly</option>
            <option value="on_demand">On Demand (As Needed)</option>
            <option value="custom">Custom Date</option>
          </select>
          <small className="form-hint">How often members should make payments</small>
        </div>

        {formData.payment_frequency === 'custom' && (
          <div className="form-group">
            <label>Custom Payment Date *</label>
            <input
              type="date"
              value={formData.custom_payment_date}
              onChange={(e) => handleChange('custom_payment_date', e.target.value)}
              className="form-control"
            />
            <small className="form-hint">Specific date for custom payment schedule</small>
          </div>
        )}

        <div className="form-group">
          <label>Inactivity Threshold (Days) *</label>
          <input
            type="number"
            min="1"
            value={formData.payment_criteria_days}
            onChange={(e) => handleChange('payment_criteria_days', e.target.value)}
            className="form-control"
            placeholder="30"
          />
          <small className="form-hint">Days without payment before member is marked inactive</small>
        </div>
      </div>

      <div className="settings-section card" style={{ marginBottom: '24px' }}>
        <h2>Payment Reminders</h2>
        <small className="form-hint" style={{ display: 'block', marginBottom: '16px', color: '#6B7280' }}>
          Automated reminders are sent before payment due dates. {group?.currency === 'KES' ? 'KES groups also receive M-Pesa STK push prompts.' : 'Members receive email reminders.'}
        </small>
        
        <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          <div className="form-group">
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input
                type="checkbox"
                checked={formData.reminders_enabled}
                onChange={(e) => handleChange('reminders_enabled', e.target.checked)}
                data-testid="reminders-enabled-checkbox"
                style={{ width: '18px', height: '18px', accentColor: '#10B981' }}
              />
              Enable Automated Reminders
            </label>
            <small className="form-hint">Send payment reminders before due dates</small>
          </div>
          <div className="form-group">
            <label>Remind Days Before Due Date</label>
            <input
              type="number"
              value={formData.reminder_days_before}
              onChange={(e) => handleChange('reminder_days_before', Math.max(1, Math.min(14, parseInt(e.target.value) || 3)))}
              min="1"
              max="14"
              className="form-control"
              disabled={!formData.reminders_enabled}
              data-testid="reminder-days-input"
            />
            <small className="form-hint">Members get reminded {formData.reminder_days_before} day(s) before each payment deadline</small>
          </div>
        </div>
      </div>

      <div className="settings-section card">
        <h2>Enforcement Settings</h2>
        <small className="form-hint" style={{ display: 'block', marginBottom: '16px', color: '#6B7280' }}>
          When the payment deadline passes, the member is automatically marked <strong>Inactive</strong>.
          If they pay during the inactive window, they enter <strong>Probation</strong>. After probation ends, they return to <strong>Active</strong>.
        </small>
        
        <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          <div className="form-group">
            <label>Inactive Window (Days)</label>
            <input
              type="number"
              value={formData.inactive_window_days}
              onChange={(e) => handleChange('inactive_window_days', parseInt(e.target.value) || 90)}
              min="1"
              max="365"
              className="form-control"
              placeholder="90"
              data-testid="settings-inactive-window-days"
            />
            <small className="form-hint">
              Members have {formData.inactive_window_days} days to pay while Inactive. After this, they are Suspended.
            </small>
          </div>
          <div className="form-group">
            <label>Probation Period (Days)</label>
            <input
              type="number"
              value={formData.probation_days}
              onChange={(e) => handleChange('probation_days', parseInt(e.target.value) || 30)}
              min="1"
              max="365"
              className="form-control"
              placeholder="30"
              data-testid="settings-probation-days"
            />
            <small className="form-hint">
              After paying, members serve {formData.probation_days} days probation before returning to Active.
            </small>
          </div>
        </div>

        <div className="form-group">
          <label>Penalty Amount ($)</label>
          <input
            type="number"
            value={formData.penalty_amount}
            onChange={(e) => handleChange('penalty_amount', parseFloat(e.target.value) || 0)}
            min="0"
            step="0.01"
            className="form-control"
            placeholder="0.00 (optional)"
            data-testid="settings-penalty-amount"
          />
          <small className="form-hint">
            {formData.penalty_amount > 0 ? `$${parseFloat(formData.penalty_amount).toFixed(2)} penalty added for late payment` : 'No penalty fee (optional)'}
          </small>
        </div>

        <div style={{ background: '#F0F9FF', border: '1px solid #BAE6FD', borderRadius: '10px', padding: '16px', marginTop: '12px' }}>
          <h4 style={{ margin: '0 0 8px', fontSize: '14px', color: '#0369A1' }}>Enforcement Flow Summary</h4>
          <ul style={{ margin: 0, paddingLeft: '18px', fontSize: '13px', color: '#0C4A6E', lineHeight: '1.8' }}>
            <li>Missed deadline &rarr; Inactive (automatic)</li>
            <li>Inactive window: {formData.inactive_window_days} days to pay</li>
            {formData.penalty_amount > 0 && <li>Late penalty: ${parseFloat(formData.penalty_amount).toFixed(2)}</li>}
            <li>After payment &rarr; {formData.probation_days}-day probation &rarr; Active</li>
            <li>No payment within {formData.inactive_window_days} days &rarr; Suspended</li>
          </ul>
        </div>
      </div>

      <div className="payment-examples card">
        <h3>Payment Schedule Examples</h3>
        <div className="examples-grid">
          <div className="example-item">
            <strong>Weekly:</strong>
            <p>Payments due every 7 days</p>
          </div>
          <div className="example-item">
            <strong>Monthly:</strong>
            <p>Payments due every 30 days</p>
          </div>
          <div className="example-item">
            <strong>Quarterly:</strong>
            <p>Payments due every 90 days</p>
          </div>
          <div className="example-item">
            <strong>On Demand:</strong>
            <p>Members contribute when need arises - no automatic schedule</p>
          </div>
          <div className="example-item">
            <strong>Custom Date:</strong>
            <p>Payment due on specific date: {formData.custom_payment_date || 'Not set'}</p>
          </div>
        </div>
      </div>

      <div className="settings-actions">
        <button
          className="btn btn-secondary"
          onClick={onUpdate}
          disabled={saving}
        >
          Cancel Changes
        </button>
        <button
          className="btn btn-primary"
          onClick={handleSave}
          disabled={saving || !hasChanges}
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>
    </div>
  );
}

function Modal({ type, onClose, onSuccess, groupId, members, group, user, initialFormData, onPaymentSetup }) {
  const [formData, setFormData] = useState(initialFormData || {});
  const [loading, setLoading] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importResults, setImportResults] = useState(null);

  // Check if payment details are valid for create-group
  const isPaymentValid = () => {
    if (type !== 'create-group') return true;
    
    // KES groups skip card/bank payment (use M-Pesa instead)
    if (formData.currency === 'KES') {
      return true; // No card requirement for KES groups
    }
    
    const paymentType = formData.payment_method_type || 'card';
    
    if (paymentType === 'card') {
      return (
        formData.card_number?.length >= 15 &&
        formData.card_exp_month &&
        formData.card_exp_year &&
        formData.card_cvv?.length >= 3
      );
    } else if (paymentType === 'us_bank_account') {
      return (
        formData.ach_routing?.length === 9 &&
        formData.ach_account?.length >= 4 &&
        formData.ach_account === formData.ach_account_confirm
      );
    }
    return false;
  };

  // Check if basic group info is valid
  const isBasicInfoValid = () => {
    if (type !== 'create-group') return true;
    return (
      formData.name?.trim() &&
      formData.description?.trim() &&
      formData.category &&
      parseFloat(formData.payment_amount) > 0
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (type === 'create-group') {
        // Validate payment details
        if (!isPaymentValid()) {
          toast.error('Please fill in all required payment details');
          setLoading(false);
          return;
        }
        
        const response = await axios.post(`${API}/groups/create-with-subscription`, {
          name: formData.name,
          description: formData.description,
          category: formData.category,
          payment_amount: parseFloat(formData.payment_amount) || 0,
          payment_frequency: formData.payment_frequency || 'monthly',
          language: formData.language || 'en',
          currency: formData.currency || 'USD',
          subscription_tier: formData.subscription_tier || 'tier1',
          billing_cycle: formData.billing_cycle || 'monthly',
          payment_method_type: formData.currency === 'KES' ? 'mpesa' : (formData.payment_method_type || 'card'),
          group_phone_number: formData.group_phone_number || null,
          // Payment details for Stripe (non-KES only)
          card_number: formData.card_number,
          card_exp_month: formData.card_exp_month,
          card_exp_year: formData.card_exp_year,
          card_cvv: formData.card_cvv,
          ach_routing: formData.ach_routing,
          ach_account: formData.ach_account
        });
        
        // Show inline payment method form (only for non-KES groups with Stripe)
        if (response.data.setup_intent_client_secret && onPaymentSetup) {
          onPaymentSetup({
            groupId: response.data.group.id,
            groupName: response.data.group.name,
            clientSecret: response.data.setup_intent_client_secret
          });
        }
        
        const isKES = (formData.currency || 'USD') === 'KES';
        toast.success(isKES 
          ? 'Group created with 14-day trial! Subscription payments will be via M-Pesa.' 
          : 'Group created with 14-day trial! Add your payment method below.');
      } else if (type === 'add-member') {
        await axios.post(`${API}/members`, {
          user_email: formData.email || null,
          user_name: formData.name,
          phone_number: formData.phone,
          group_id: groupId,
          membership_number: formData.membership_number || null
        });
        toast.success(formData.email ? 'Member added successfully. Invitation email sent.' : 'Member added successfully.');
      } else if (type === 'edit-member') {
        // Edit existing member
        await axios.put(`${API}/members/${formData.member_id}`, {
          user_email: formData.email || null,
          user_name: formData.name,
          phone_number: formData.phone,
          group_id: groupId,
          membership_number: formData.membership_number || null
        });
        toast.success('Member updated successfully');
      } else if (type === 'import-members') {
        // Import members from file
        if (!importFile) {
          toast.error('Please select a file to import');
          setLoading(false);
          return;
        }
        
        const formDataUpload = new FormData();
        formDataUpload.append('file', importFile);
        
        // First do a preview to check for duplicates
        const autoAssign = formData.autoAssignNumbers ? '&auto_assign=true' : '';
        const previewRes = await axios.post(`${API}/groups/${groupId}/members/import?mode=preview${autoAssign}`, formDataUpload, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        const preview = previewRes.data;
        
        if (preview.duplicates && preview.duplicates.length > 0) {
          // Show preview with duplicates - store for overwrite decision
          setImportResults({
            ...preview,
            _hasPreview: true,
            _file: importFile
          });
          setLoading(false);
          return;
        }
        
        // No duplicates - proceed with normal import
        const formDataUpload2 = new FormData();
        formDataUpload2.append('file', importFile);
        const response = await axios.post(`${API}/groups/${groupId}/members/import?mode=skip${autoAssign}`, formDataUpload2, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        setImportResults(response.data);
        
        if (response.data.success > 0) {
          toast.success(`Successfully imported ${response.data.success} members`);
        }
        if (response.data.failed > 0) {
          toast.warning(`${response.data.failed} members failed to import`);
        }
        
        // Don't close modal immediately so user can see results
        setLoading(false);
        return;
      } else if (type === 'create-disbursement') {
        // Validate bank account numbers match
        if (formData.bank_account_number !== formData.bank_account_number_confirm) {
          toast.error('Bank account numbers do not match');
          setLoading(false);
          return;
        }
        
        // Validate routing number
        if (!formData.bank_routing_number || formData.bank_routing_number.length !== 9) {
          toast.error('Bank routing number must be exactly 9 digits');
          setLoading(false);
          return;
        }
        
        // Validate account number
        if (!formData.bank_account_number || formData.bank_account_number.length < 8) {
          toast.error('Bank account number must be at least 8 digits');
          setLoading(false);
          return;
        }
        
        // Validate signatories
        const signatoryIds = [formData.signatory_1, formData.signatory_2, formData.signatory_3].filter(Boolean);
        if (signatoryIds.length !== 3) {
          toast.error('Please select exactly 3 signatories');
          setLoading(false);
          return;
        }
        
        // Check for duplicate signatories
        if (new Set(signatoryIds).size !== 3) {
          toast.error('All 3 signatories must be different');
          setLoading(false);
          return;
        }
        
        await axios.post(`${API}/disbursements`, {
          group_id: groupId,
          amount: parseFloat(formData.amount),
          description: formData.description,
          beneficiary_id: formData.beneficiary_id,
          bank_routing_number: formData.bank_routing_number,
          bank_account_number: formData.bank_account_number,
          signatory_ids: signatoryIds
        });
        toast.success('Disbursement created and pending 3 signatures');
      } else if (type === 'create-meeting') {
        await axios.post(`${API}/meetings`, {
          group_id: groupId,
          title: formData.title,
          description: formData.description,
          date: new Date(formData.date).toISOString(),
          location: formData.location || '',
          send_notification: formData.send_notification !== false
        });
        toast.success('Meeting scheduled' + (formData.send_notification !== false ? ' and notifications sent' : ''));
      }

      onSuccess();
    } catch (error) {
      // Handle validation errors from backend
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        // If it's an array of validation errors
        if (Array.isArray(detail)) {
          const errorMessages = detail.map(err => err.msg).join(', ');
          toast.error(`Validation error: ${errorMessages}`);
        } else {
          // If it's a simple string
          toast.error(detail);
        }
      } else {
        toast.error('Operation failed');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>
            {type === 'create-group' && 'Create New Group'}
            {type === 'add-member' && 'Add Member'}
            {type === 'edit-member' && 'Edit Member'}
            {type === 'import-members' && 'Import Members'}
            {type === 'create-disbursement' && 'Create Disbursement'}
            {type === 'create-meeting' && 'Schedule Meeting'}
          </h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleSubmit}>
          {type === 'create-group' && (
            <>
              <div className="form-group">
                <label>{t(formData.language, 'groupName')}</label>
                <input
                  type="text"
                  value={formData.name || ''}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  data-testid="group-name-input"
                />
              </div>
              <div className="form-group">
                <label>{t(formData.language, 'description')}</label>
                <textarea
                  value={formData.description || ''}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  required
                  data-testid="group-description-input"
                />
              </div>
              <div className="form-group">
                <label>{t(formData.language, 'groupCategory')}</label>
                <select
                  value={formData.category || ''}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  required
                  data-testid="group-category-select"
                  style={{
                    borderColor: !formData.category ? '#F59E0B' : '#10B981'
                  }}
                >
                  <option value="">{t(formData.language, 'selectCategory')}</option>
                  <option value="benevolence">{t(formData.language, 'benevolence')}</option>
                  <option value="table_banking">{t(formData.language, 'tableBanking')}</option>
                  <option value="sacco">{t(formData.language, 'sacco')}</option>
                  <option value="self_help">{t(formData.language, 'selfHelp')}</option>
                  <option value="investment">{t(formData.language, 'investment')}</option>
                  <option value="savings">{t(formData.language, 'savings')}</option>
                  <option value="community">{t(formData.language, 'community')}</option>
                  <option value="empowerment">{t(formData.language, 'empowerment')}</option>
                </select>
                <small className="form-hint">{t(formData.language, 'categoryHint')}</small>
              </div>
              <div className="form-group">
                <label>{t(formData.language, 'language')}</label>
                <select
                  value={formData.language || 'en'}
                  onChange={(e) => setFormData({ ...formData, language: e.target.value })}
                  required
                  data-testid="language-select"
                  style={{
                    borderColor: formData.language ? '#10B981' : '#D1D5DB'
                  }}
                >
                  <optgroup label="Primary">
                    <option value="en">English</option>
                    <option value="es">Spanish (Espanol)</option>
                    <option value="fr">French (Francais)</option>
                    <option value="pt">Portuguese (Portugues)</option>
                  </optgroup>
                  <optgroup label="African Languages">
                    <option value="sw">Swahili (Kiswahili)</option>
                    <option value="am">Amharic</option>
                    <option value="ha">Hausa</option>
                    <option value="yo">Yoruba</option>
                    <option value="ig">Igbo</option>
                    <option value="zu">Zulu (isiZulu)</option>
                  </optgroup>
                  <optgroup label="Asian Languages">
                    <option value="zh">Chinese (Mandarin)</option>
                    <option value="hi">Hindi</option>
                    <option value="tl">Filipino (Tagalog)</option>
                    <option value="id">Indonesian (Bahasa)</option>
                    <option value="ms">Malay (Bahasa Melayu)</option>
                  </optgroup>
                  <optgroup label="Other">
                    <option value="ar">Arabic</option>
                    <option value="de">German (Deutsch)</option>
                    <option value="it">Italian (Italiano)</option>
                    <option value="ja">Japanese</option>
                    <option value="ko">Korean</option>
                    <option value="ru">Russian</option>
                  </optgroup>
                </select>
                <small className="form-hint">{t(formData.language, 'languageHint')}</small>
              </div>
              <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div className="form-group">
                  <label>{t(formData.language, 'currency')}</label>
                  <select
                    value={formData.currency || 'USD'}
                    onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                    required
                    data-testid="currency-select"
                  >
                    <optgroup label="Major Currencies">
                      <option value="USD">🇺🇸 USD - US Dollar ($)</option>
                      <option value="EUR">🇪🇺 EUR - Euro (€)</option>
                      <option value="GBP">🇬🇧 GBP - British Pound (£)</option>
                    </optgroup>
                    <optgroup label="African Currencies">
                      <option value="KES">🇰🇪 KES - Kenyan Shilling (KSh)</option>
                      <option value="NGN">🇳🇬 NGN - Nigerian Naira (₦)</option>
                      <option value="ZAR">🇿🇦 ZAR - South African Rand (R)</option>
                      <option value="GHS">🇬🇭 GHS - Ghanaian Cedi (₵)</option>
                      <option value="UGX">🇺🇬 UGX - Ugandan Shilling (USh)</option>
                      <option value="TZS">🇹🇿 TZS - Tanzanian Shilling (TSh)</option>
                      <option value="RWF">🇷🇼 RWF - Rwandan Franc (FRw)</option>
                      <option value="ETB">🇪🇹 ETB - Ethiopian Birr (Br)</option>
                      <option value="EGP">🇪🇬 EGP - Egyptian Pound (E£)</option>
                      <option value="MAD">🇲🇦 MAD - Moroccan Dirham (DH)</option>
                    </optgroup>
                    <optgroup label="Asian Currencies">
                      <option value="INR">🇮🇳 INR - Indian Rupee (₹)</option>
                      <option value="PHP">🇵🇭 PHP - Philippine Peso (₱)</option>
                      <option value="IDR">🇮🇩 IDR - Indonesian Rupiah (Rp)</option>
                      <option value="MYR">🇲🇾 MYR - Malaysian Ringgit (RM)</option>
                      <option value="SGD">🇸🇬 SGD - Singapore Dollar (S$)</option>
                    </optgroup>
                    <optgroup label="Other Currencies">
                      <option value="CAD">🇨🇦 CAD - Canadian Dollar (C$)</option>
                      <option value="AUD">🇦🇺 AUD - Australian Dollar (A$)</option>
                      <option value="NZD">🇳🇿 NZD - New Zealand Dollar (NZ$)</option>
                      <option value="BRL">🇧🇷 BRL - Brazilian Real (R$)</option>
                      <option value="MXN">🇲🇽 MXN - Mexican Peso (MX$)</option>
                    </optgroup>
                  </select>
                </div>
                <div className="form-group">
                  <label>{t(formData.language, 'paymentAmount')} ({formData.currency || 'USD'})</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.payment_amount || ''}
                    onChange={(e) => setFormData({ ...formData, payment_amount: e.target.value })}
                    required
                    data-testid="payment-amount-input"
                  />
                </div>
              </div>
              <div className="form-group">
                <label>{t(formData.language, 'paymentFrequency')}</label>
                <select
                  value={formData.payment_frequency || 'monthly'}
                  onChange={(e) => setFormData({ ...formData, payment_frequency: e.target.value })}
                  data-testid="payment-frequency-select"
                >
                  <option value="weekly">{t(formData.language, 'weekly')}</option>
                  <option value="monthly">{t(formData.language, 'monthly')}</option>
                  <option value="quarterly">{t(formData.language, 'quarterly')}</option>
                  <option value="on_demand">{t(formData.language, 'onDemand')}</option>
                </select>
              </div>
              
              {/* Subscription Section */}
              <div className="subscription-section">
                <h3>{t(formData.language, 'subscriptionPlan')}</h3>
                <p className="section-help">{t(formData.language, 'subscriptionHint')}</p>
                
                <div className="form-group">
                  <label>{t(formData.language, 'subscriptionTier')}</label>
                  <select
                    value={formData.subscription_tier || 'tier1'}
                    onChange={(e) => setFormData({ ...formData, subscription_tier: e.target.value })}
                    data-testid="subscription-tier-select"
                  >
                    <option value="tier1">0-250 {t(formData.language, 'members')} - $29.99/{t(formData.language, 'monthly').toLowerCase()}</option>
                    <option value="tier2">251-500 {t(formData.language, 'members')} - $39.99/{t(formData.language, 'monthly').toLowerCase()}</option>
                    <option value="tier3">501-750 {t(formData.language, 'members')} - $49.99/{t(formData.language, 'monthly').toLowerCase()}</option>
                    <option value="tier4">751-1000 {t(formData.language, 'members')} - $59.99/{t(formData.language, 'monthly').toLowerCase()}</option>
                    <option value="tier5">1001-1500 {t(formData.language, 'members')} - $69.99/{t(formData.language, 'monthly').toLowerCase()}</option>
                  </select>
                  <small className="form-help">{t(formData.language, 'tierAutoUpgrade')}</small>
                </div>
                
                <div className="form-group">
                  <label>{t(formData.language, 'billingCycle')}</label>
                  <div className="billing-options">
                    <label className={`billing-option ${formData.billing_cycle === 'monthly' ? 'selected' : ''}`}>
                      <input
                        type="radio"
                        name="billing_cycle"
                        value="monthly"
                        checked={formData.billing_cycle === 'monthly' || !formData.billing_cycle}
                        onChange={(e) => setFormData({ ...formData, billing_cycle: e.target.value })}
                      />
                      <div className="option-content">
                        <span className="option-label">{t(formData.language, 'monthly')}</span>
                        <span className="option-price">{t(formData.language, 'billedMonthly')}</span>
                      </div>
                    </label>
                    <label className={`billing-option ${formData.billing_cycle === 'annual' ? 'selected' : ''}`}>
                      <input
                        type="radio"
                        name="billing_cycle"
                        value="annual"
                        checked={formData.billing_cycle === 'annual'}
                        onChange={(e) => setFormData({ ...formData, billing_cycle: e.target.value })}
                      />
                      <div className="option-content">
                        <span className="option-label">{t(formData.language, 'annual')}</span>
                        <span className="option-price">{t(formData.language, 'save10')}</span>
                        <span className="option-badge">{t(formData.language, 'bestValue')}</span>
                      </div>
                    </label>
                  </div>
                </div>
                
                <div className="form-group">
                  <label>{t(formData.language, 'paymentMethod')}</label>
                  
                  {/* KES groups: M-Pesa only, no card/bank required */}
                  {formData.currency === 'KES' ? (
                    <div style={{
                      background: '#F0FFF4',
                      border: '1px solid #48BB78',
                      borderRadius: '12px',
                      padding: '20px',
                      marginTop: '8px'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
                        <span style={{ fontSize: '24px' }}>📱</span>
                        <div>
                          <strong style={{ color: '#276749', fontSize: '15px' }}>M-Pesa Subscription Billing</strong>
                          <p style={{ color: '#48BB78', fontSize: '13px', margin: '2px 0 0 0' }}>
                            Platform subscription fees are paid via M-Pesa. No card required.
                          </p>
                        </div>
                      </div>
                      <div className="form-group" style={{ marginBottom: '0' }}>
                        <label style={{ color: '#276749', fontWeight: '600', fontSize: '13px' }}>Group M-Pesa Phone Number *</label>
                        <input
                          type="tel"
                          value={formData.group_phone_number || ''}
                          onChange={(e) => setFormData({ ...formData, group_phone_number: e.target.value })}
                          placeholder="e.g. 0712345678 or 254712345678"
                          required
                          data-testid="group-phone-input"
                          style={{ marginTop: '4px' }}
                        />
                        <small style={{ color: '#6B7280', fontSize: '12px' }}>This phone will receive STK push prompts for subscription payments</small>
                      </div>
                    </div>
                  ) : (
                  <div className="payment-method-options">
                    <label className={`payment-option ${formData.payment_method_type === 'card' || !formData.payment_method_type ? 'selected' : ''}`}>
                      <input
                        type="radio"
                        name="payment_method_type"
                        value="card"
                        checked={formData.payment_method_type === 'card' || !formData.payment_method_type}
                        onChange={(e) => setFormData({ ...formData, payment_method_type: e.target.value })}
                      />
                      <span className="payment-icon">💳</span>
                      <span className="payment-label">{t(formData.language, 'creditCard')}</span>
                    </label>
                    <label className={`payment-option ${formData.payment_method_type === 'us_bank_account' ? 'selected' : ''}`}>
                      <input
                        type="radio"
                        name="payment_method_type"
                        value="us_bank_account"
                        checked={formData.payment_method_type === 'us_bank_account'}
                        onChange={(e) => setFormData({ ...formData, payment_method_type: e.target.value })}
                      />
                      <span className="payment-icon">🏦</span>
                      <span className="payment-label">{t(formData.language, 'bankAccount')}</span>
                    </label>
                  </div>
                  )}
                </div>
                
                {/* Card Payment Details (non-KES only) */}
                {formData.currency !== 'KES' && (formData.payment_method_type === 'card' || !formData.payment_method_type) && (
                  <div className="payment-details-section" style={{
                    background: '#F8FAFC',
                    border: '1px solid #E2E8F0',
                    borderRadius: '12px',
                    padding: '20px',
                    marginTop: '16px'
                  }}>
                    <h4 style={{ margin: '0 0 16px 0', color: '#334155', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      💳 Card Details <span style={{ color: '#EF4444', fontSize: '12px' }}>*Required for billing</span>
                    </h4>
                    <div className="form-group">
                      <label>Card Number *</label>
                      <input
                        type="text"
                        value={formData.card_number || ''}
                        onChange={(e) => setFormData({ ...formData, card_number: e.target.value.replace(/\D/g, '').slice(0, 16) })}
                        placeholder="1234 5678 9012 3456"
                        maxLength="16"
                        required
                        data-testid="card-number-input"
                      />
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                      <div className="form-group">
                        <label>Expiry Month *</label>
                        <select
                          value={formData.card_exp_month || ''}
                          onChange={(e) => setFormData({ ...formData, card_exp_month: e.target.value })}
                          required
                          data-testid="card-exp-month-select"
                        >
                          <option value="">MM</option>
                          {[...Array(12)].map((_, i) => (
                            <option key={i+1} value={String(i+1).padStart(2, '0')}>{String(i+1).padStart(2, '0')}</option>
                          ))}
                        </select>
                      </div>
                      <div className="form-group">
                        <label>Expiry Year *</label>
                        <select
                          value={formData.card_exp_year || ''}
                          onChange={(e) => setFormData({ ...formData, card_exp_year: e.target.value })}
                          required
                          data-testid="card-exp-year-select"
                        >
                          <option value="">YYYY</option>
                          {[...Array(10)].map((_, i) => {
                            const year = new Date().getFullYear() + i;
                            return <option key={year} value={year}>{year}</option>;
                          })}
                        </select>
                      </div>
                      <div className="form-group">
                        <label>CVV *</label>
                        <input
                          type="text"
                          value={formData.card_cvv || ''}
                          onChange={(e) => setFormData({ ...formData, card_cvv: e.target.value.replace(/\D/g, '').slice(0, 4) })}
                          placeholder="123"
                          maxLength="4"
                          required
                          data-testid="card-cvv-input"
                        />
                      </div>
                    </div>
                    <div style={{ 
                      background: '#ECFDF5', 
                      border: '1px solid #10B981', 
                      borderRadius: '8px', 
                      padding: '10px', 
                      marginTop: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      fontSize: '13px'
                    }}>
                      <span>🔒</span>
                      <span style={{ color: '#065F46' }}>Your card is securely processed via Stripe. We never store your full card details.</span>
                    </div>
                  </div>
                )}
                
                {/* Bank Account (ACH) Payment Details (non-KES only) */}
                {formData.currency !== 'KES' && formData.payment_method_type === 'us_bank_account' && (
                  <div className="payment-details-section" style={{
                    background: '#F0F9FF',
                    border: '1px solid #BAE6FD',
                    borderRadius: '12px',
                    padding: '20px',
                    marginTop: '16px'
                  }}>
                    <h4 style={{ margin: '0 0 16px 0', color: '#0369A1', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      🏦 Bank Account Details <span style={{ color: '#EF4444', fontSize: '12px' }}>*Required for billing</span>
                    </h4>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                      <div className="form-group">
                        <label>Routing Number * (9 digits)</label>
                        <input
                          type="text"
                          value={formData.ach_routing || ''}
                          onChange={(e) => setFormData({ ...formData, ach_routing: e.target.value.replace(/\D/g, '').slice(0, 9) })}
                          placeholder="9-digit routing number"
                          maxLength="9"
                          required
                          data-testid="ach-routing-input"
                        />
                        <small style={{ color: '#6B7280', fontSize: '12px' }}>Found on your checks</small>
                      </div>
                      <div className="form-group">
                        <label>Account Number *</label>
                        <input
                          type="text"
                          value={formData.ach_account || ''}
                          onChange={(e) => setFormData({ ...formData, ach_account: e.target.value.replace(/\D/g, '') })}
                          placeholder="Account number"
                          required
                          data-testid="ach-account-input"
                        />
                      </div>
                    </div>
                    <div className="form-group" style={{ marginTop: '12px' }}>
                      <label>Confirm Account Number *</label>
                      <input
                        type="text"
                        value={formData.ach_account_confirm || ''}
                        onChange={(e) => setFormData({ ...formData, ach_account_confirm: e.target.value.replace(/\D/g, '') })}
                        placeholder="Re-enter account number"
                        required
                        data-testid="ach-account-confirm-input"
                        style={{
                          borderColor: formData.ach_account && formData.ach_account_confirm && 
                                       formData.ach_account !== formData.ach_account_confirm ? '#EF4444' : undefined
                        }}
                      />
                      {formData.ach_account && formData.ach_account_confirm && 
                       formData.ach_account !== formData.ach_account_confirm && (
                        <small style={{ color: '#EF4444' }}>Account numbers do not match</small>
                      )}
                    </div>
                    <div style={{ 
                      background: '#ECFDF5', 
                      border: '1px solid #10B981', 
                      borderRadius: '8px', 
                      padding: '10px', 
                      marginTop: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      fontSize: '13px'
                    }}>
                      <span>🔒</span>
                      <span style={{ color: '#065F46' }}>Your bank details are securely processed via Stripe ACH. We never store your full account number.</span>
                    </div>
                  </div>
                )}
                
                <div className="trial-notice">
                  <span className="trial-icon">🎉</span>
                  <div className="trial-content">
                    <strong>14-Day Free Trial</strong>
                    <p>Try all features free for 14 days. You won't be charged until the trial ends.</p>
                  </div>
                </div>
              </div>
            </>
          )}

          {type === 'add-member' && (
            <>
              <div className="form-group">
                <label>Full Name *</label>
                <input
                  type="text"
                  value={formData.name || ''}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  data-testid="member-name-input"
                />
              </div>
              <div className="form-group">
                <label>Phone Number *</label>
                <input
                  type="tel"
                  value={formData.phone || ''}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  required
                  placeholder="+254..."
                  data-testid="member-phone-input"
                />
              </div>
              <div className="form-group">
                <label>Email Address (optional)</label>
                <input
                  type="email"
                  value={formData.email || ''}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  data-testid="member-email-input"
                  placeholder="Required for login account"
                />
                <small style={{ color: '#9CA3AF', fontSize: '12px' }}>Invitation email sent only if provided</small>
              </div>
              <div className="form-group">
                <label>Membership Number {formData._nextMembershipNumber ? '' : '(Optional)'}</label>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <input
                    type="text"
                    value={formData.membership_number || ''}
                    onChange={(e) => setFormData({ ...formData, membership_number: e.target.value })}
                    placeholder={formData._nextMembershipNumber || 'e.g. M-001, 2024-001'}
                    data-testid="member-membership-number-input"
                    style={{ flex: 1 }}
                  />
                  {formData._nextMembershipNumber && !formData.membership_number && (
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, membership_number: formData._nextMembershipNumber })}
                      style={{
                        padding: '6px 12px',
                        background: '#10B981',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        whiteSpace: 'nowrap'
                      }}
                      data-testid="auto-fill-membership-btn"
                    >
                      Use {formData._nextMembershipNumber}
                    </button>
                  )}
                </div>
                <small className="form-help">
                  {formData._nextMembershipNumber 
                    ? `Next auto-generated: ${formData._nextMembershipNumber} (based on existing pattern)`
                    : 'Enter the first membership number to start auto-generation for future members'}
                </small>
              </div>
            </>
          )}

          {type === 'edit-member' && (
            <>
              <div className="form-group">
                <label>Full Name</label>
                <input
                  type="text"
                  value={formData.name || ''}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  data-testid="edit-member-name-input"
                />
              </div>
              <div className="form-group">
                <label>Email Address</label>
                <input
                  type="email"
                  value={formData.email || ''}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  data-testid="edit-member-email-input"
                />
              </div>
              <div className="form-group">
                <label>Phone Number</label>
                <input
                  type="tel"
                  value={formData.phone || ''}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  required
                  placeholder="+254..."
                  data-testid="edit-member-phone-input"
                />
              </div>
              <div className="form-group">
                <label>Membership Number (Optional)</label>
                <input
                  type="text"
                  value={formData.membership_number || ''}
                  onChange={(e) => setFormData({ ...formData, membership_number: e.target.value })}
                  placeholder="e.g. M-001, 2024-001"
                  data-testid="edit-member-membership-number-input"
                />
                <small className="form-help">Optional unique identifier for this member</small>
              </div>
            </>
          )}

          {type === 'import-members' && (
            <>
              <div className="import-info">
                <p>Upload a CSV or Excel file with member information.</p>
                <div className="import-requirements">
                  <h4>Required columns:</h4>
                  <ul>
                    <li><strong>name</strong> - Member's full name</li>
                    <li><strong>email</strong> - Member's email address</li>
                    <li><strong>phone</strong> - Member's phone number</li>
                  </ul>
                  <h4>Optional columns:</h4>
                  <ul>
                    <li><strong>membership_number</strong> - Unique identifier</li>
                  </ul>
                </div>
                <button 
                  type="button"
                  onClick={() => {
                    const csvContent = 'name,email,phone,membership_number\nJohn Doe,john@example.com,+1234567890,MEM001\nJane Smith,jane@example.com,+0987654321,MEM002';
                    const blob = new Blob([csvContent], { type: 'text/csv' });
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = 'member_import_template.csv';
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    window.URL.revokeObjectURL(url);
                  }}
                  className="btn btn-secondary btn-sm download-template-btn"
                  data-testid="download-template-btn"
                >
                  📥 Download CSV Template
                </button>
              </div>
              
              <div className="form-group">
                <label>Select File (CSV or Excel)</label>
                <div className="file-upload-wrapper">
                  <input
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    onChange={(e) => setImportFile(e.target.files[0])}
                    required
                    data-testid="import-file-input"
                    id="import-file-input"
                  />
                  {importFile && (
                    <div className="selected-file">
                      <span className="file-icon">📄</span>
                      <span className="file-name">{importFile.name}</span>
                      <span className="file-size">({(importFile.size / 1024).toFixed(1)} KB)</span>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="form-group" style={{ marginTop: '8px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontWeight: 500, fontSize: '14px' }}>
                  <input
                    type="checkbox"
                    checked={formData.autoAssignNumbers || false}
                    onChange={(e) => setFormData({ ...formData, autoAssignNumbers: e.target.checked })}
                    data-testid="auto-assign-checkbox"
                    style={{ width: '18px', height: '18px', accentColor: '#10B981' }}
                  />
                  Auto-assign membership numbers
                </label>
                <small className="form-help" style={{ marginLeft: '26px' }}>
                  Generates sequential numbers for imported members without one (e.g., CL-003, CL-004, ...) based on existing pattern
                </small>
              </div>
              
              {importResults && (
                <div className="import-results">
                  {importResults._hasPreview ? (
                    <>
                      <h4>Import Preview</h4>
                      <div className="results-summary">
                        <div className="result-item success">
                          <span className="result-label">New Members:</span>
                          <span className="result-value">{importResults.new_entries?.length || 0}</span>
                        </div>
                        <div className="result-item" style={{ color: '#F59E0B' }}>
                          <span className="result-label">Duplicates Found:</span>
                          <span className="result-value" style={{ color: '#F59E0B', fontWeight: '700' }}>{importResults.duplicates?.length || 0}</span>
                        </div>
                        {importResults.invalid?.length > 0 && (
                          <div className="result-item failed">
                            <span className="result-label">Invalid Rows:</span>
                            <span className="result-value">{importResults.invalid.length}</span>
                          </div>
                        )}
                      </div>
                      
                      {importResults.duplicates?.length > 0 && (
                        <div style={{ margin: '16px 0', padding: '12px', background: '#FEF3C7', border: '1px solid #F59E0B', borderRadius: '8px' }}>
                          <p style={{ margin: '0 0 8px', fontWeight: '600', color: '#92400E', fontSize: '14px' }}>Duplicate Members Found</p>
                          <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                            {importResults.duplicates.map((d, idx) => (
                              <div key={idx} style={{ padding: '8px', borderBottom: '1px solid #FCD34D', fontSize: '13px' }}>
                                <p style={{ margin: 0, fontWeight: '600' }}>{d.name} ({d.email})</p>
                                <p style={{ margin: '2px 0 0', color: '#92400E' }}>
                                  Existing: {d.existing?.name} &middot; Phone: {d.existing?.phone || 'N/A'}
                                </p>
                              </div>
                            ))}
                          </div>
                          <p style={{ margin: '8px 0 0', fontSize: '12px', color: '#92400E' }}>
                            Choose an action for these duplicate entries:
                          </p>
                        </div>
                      )}
                      
                      <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                        <button 
                          type="button"
                          onClick={async () => {
                            setLoading(true);
                            try {
                              const fd = new FormData();
                              fd.append('file', importResults._file);
                              const autoAssignParam = formData.autoAssignNumbers ? '&auto_assign=true' : '';
                              const res = await axios.post(`${API}/groups/${groupId}/members/import?mode=overwrite${autoAssignParam}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                              setImportResults(res.data);
                              if (res.data.success > 0 || res.data.updated > 0) toast.success(`Imported ${res.data.success || 0} new, updated ${res.data.updated || 0} existing`);
                              onSuccess();
                            } catch (err) {
                              toast.error('Import failed');
                            } finally { setLoading(false); }
                          }}
                          className="btn btn-primary"
                          style={{ flex: 1 }}
                          data-testid="import-overwrite-btn"
                        >
                          Overwrite Duplicates
                        </button>
                        <button 
                          type="button"
                          onClick={async () => {
                            setLoading(true);
                            try {
                              const fd = new FormData();
                              fd.append('file', importResults._file);
                              const autoAssignParam = formData.autoAssignNumbers ? '&auto_assign=true' : '';
                              const res = await axios.post(`${API}/groups/${groupId}/members/import?mode=skip${autoAssignParam}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                              setImportResults(res.data);
                              if (res.data.success > 0) toast.success(`Imported ${res.data.success} new members (duplicates skipped)`);
                              onSuccess();
                            } catch (err) {
                              toast.error('Import failed');
                            } finally { setLoading(false); }
                          }}
                          className="btn btn-secondary"
                          style={{ flex: 1 }}
                          data-testid="import-skip-btn"
                        >
                          Skip Duplicates
                        </button>
                      </div>
                      <button 
                        type="button"
                        onClick={() => { setImportResults(null); setImportFile(null); }}
                        className="btn btn-secondary full-width mt-2"
                        style={{ fontSize: '13px' }}
                        data-testid="import-edit-btn"
                      >
                        Cancel & Edit File
                      </button>
                    </>
                  ) : (
                    <>
                      <h4>Import Results</h4>
                      <div className="results-summary">
                        <div className="result-item success">
                          <span className="result-label">Successful:</span>
                          <span className="result-value">{importResults.success || 0}</span>
                        </div>
                        {(importResults.updated || 0) > 0 && (
                          <div className="result-item" style={{ color: '#3B82F6' }}>
                            <span className="result-label">Updated:</span>
                            <span className="result-value" style={{ color: '#3B82F6' }}>{importResults.updated}</span>
                          </div>
                        )}
                        <div className="result-item failed">
                          <span className="result-label">Failed:</span>
                          <span className="result-value">{importResults.failed || 0}</span>
                        </div>
                        <div className="result-item total">
                          <span className="result-label">Total:</span>
                          <span className="result-value">{importResults.total || 0}</span>
                        </div>
                      </div>
                      
                      {importResults.errors && importResults.errors.length > 0 && (
                        <div className="errors-list">
                          <h5>Errors:</h5>
                          <ul>
                            {importResults.errors.slice(0, 10).map((error, idx) => (
                              <li key={idx} className="error-item">{error}</li>
                            ))}
                            {importResults.errors.length > 10 && (
                              <li className="error-item">...and {importResults.errors.length - 10} more errors</li>
                            )}
                          </ul>
                        </div>
                      )}
                      
                      <button 
                        type="button"
                        onClick={onSuccess}
                        className="btn btn-primary full-width mt-2"
                        data-testid="import-done-btn"
                      >
                        Done
                      </button>
                    </>
                  )}
                </div>
              )}
            </>
          )}

          {type === 'create-disbursement' && (
            <>
              <div className="form-group">
                <label>Beneficiary *</label>
                <select
                  value={formData.beneficiary_id || ''}
                  onChange={(e) => setFormData({ ...formData, beneficiary_id: e.target.value })}
                  required
                  data-testid="disbursement-beneficiary-select"
                >
                  <option value="">Select a member</option>
                  {members.map((member) => (
                    <option key={member.id} value={member.id}>
                      {member.user_name} - {member.user_email}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="form-group">
                <label>Amount ($) *</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.amount || ''}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  required
                  placeholder="0.00"
                  data-testid="disbursement-amount-input"
                />
              </div>
              
              <div className="form-group">
                <label>Description *</label>
                <textarea
                  value={formData.description || ''}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  required
                  placeholder="Purpose of disbursement"
                  data-testid="disbursement-description-input"
                />
              </div>
              
              <div className="form-section-header">
                <h3>Bank Details</h3>
                <p className="form-hint">Bank account where funds will be transferred</p>
              </div>
              
              <div className="form-group">
                <label>Bank Routing Number *</label>
                <input
                  type="text"
                  value={formData.bank_routing_number || ''}
                  onChange={(e) => setFormData({ ...formData, bank_routing_number: e.target.value.replace(/\D/g, '').slice(0, 9) })}
                  required
                  placeholder="9 digits"
                  maxLength="9"
                  pattern="[0-9]{9}"
                  data-testid="disbursement-routing-input"
                />
                <small className="form-hint">9-digit routing number</small>
              </div>
              
              <div className="form-group">
                <label>Bank Account Number *</label>
                <input
                  type="text"
                  value={formData.bank_account_number || ''}
                  onChange={(e) => setFormData({ ...formData, bank_account_number: e.target.value.replace(/\D/g, '') })}
                  required
                  placeholder="Account number"
                  data-testid="disbursement-account-input"
                />
              </div>
              
              <div className="form-group">
                <label>Confirm Bank Account Number *</label>
                <input
                  type="text"
                  value={formData.bank_account_number_confirm || ''}
                  onChange={(e) => setFormData({ ...formData, bank_account_number_confirm: e.target.value.replace(/\D/g, '') })}
                  required
                  placeholder="Re-enter account number"
                  data-testid="disbursement-account-confirm-input"
                />
                {formData.bank_account_number && formData.bank_account_number_confirm && 
                 formData.bank_account_number !== formData.bank_account_number_confirm && (
                  <small className="error-text">Account numbers do not match</small>
                )}
              </div>
              
              <div className="form-section-header">
                <h3>Required Signatories</h3>
                <p className="form-hint">Select exactly 3 leadership members to approve this disbursement</p>
              </div>
              
              <div className="form-group">
                <label>Signatory 1 *</label>
                <select
                  value={formData.signatory_1 || ''}
                  onChange={(e) => setFormData({ ...formData, signatory_1: e.target.value })}
                  required
                  data-testid="disbursement-signatory-1"
                >
                  <option value="">Select leadership member</option>
                  {group && group.leadership_ids && group.leadership_ids.map(leaderId => {
                    const leaderUser = members.find(m => m.user_id === leaderId);
                    return leaderUser ? (
                      <option key={leaderId} value={leaderId}>
                        {leaderUser.user_name}
                      </option>
                    ) : (
                      <option key={leaderId} value={leaderId}>
                        Leadership Member {leaderId.slice(0, 8)}
                      </option>
                    );
                  })}
                </select>
              </div>
              
              <div className="form-group">
                <label>Signatory 2 *</label>
                <select
                  value={formData.signatory_2 || ''}
                  onChange={(e) => setFormData({ ...formData, signatory_2: e.target.value })}
                  required
                  data-testid="disbursement-signatory-2"
                >
                  <option value="">Select leadership member</option>
                  {group && group.leadership_ids && group.leadership_ids
                    .filter(id => id !== formData.signatory_1)
                    .map(leaderId => {
                      const leaderUser = members.find(m => m.user_id === leaderId);
                      return leaderUser ? (
                        <option key={leaderId} value={leaderId}>
                          {leaderUser.user_name}
                        </option>
                      ) : (
                        <option key={leaderId} value={leaderId}>
                          Leadership Member {leaderId.slice(0, 8)}
                        </option>
                      );
                    })}
                </select>
              </div>
              
              <div className="form-group">
                <label>Signatory 3 *</label>
                <select
                  value={formData.signatory_3 || ''}
                  onChange={(e) => setFormData({ ...formData, signatory_3: e.target.value })}
                  required
                  data-testid="disbursement-signatory-3"
                >
                  <option value="">Select leadership member</option>
                  {group && group.leadership_ids && group.leadership_ids
                    .filter(id => id !== formData.signatory_1 && id !== formData.signatory_2)
                    .map(leaderId => {
                      const leaderUser = members.find(m => m.user_id === leaderId);
                      return leaderUser ? (
                        <option key={leaderId} value={leaderId}>
                          {leaderUser.user_name}
                        </option>
                      ) : (
                        <option key={leaderId} value={leaderId}>
                          Leadership Member {leaderId.slice(0, 8)}
                        </option>
                      );
                    })}
                </select>
              </div>
            </>
          )}

          {type === 'create-meeting' && (
            <>
              <div className="form-group">
                <label>Title</label>
                <input
                  type="text"
                  value={formData.title || ''}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  data-testid="meeting-title-input"
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={formData.description || ''}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  required
                  data-testid="meeting-description-input"
                />
              </div>
              <div className="form-group">
                <label>Date & Time</label>
                <input
                  type="datetime-local"
                  value={formData.date || ''}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  required
                  data-testid="meeting-date-input"
                />
              </div>
              <div className="form-group">
                <label>Location</label>
                <input
                  type="text"
                  value={formData.location || ''}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  placeholder="e.g. Conference Room A, Zoom link, Community Hall"
                  data-testid="meeting-location-input"
                />
                <small className="form-help">Physical location or online meeting link</small>
              </div>
              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.send_notification !== false}
                    onChange={(e) => setFormData({ ...formData, send_notification: e.target.checked })}
                    data-testid="meeting-notification-checkbox"
                  />
                  <span className="checkbox-text">Send email notification to all members</span>
                </label>
              </div>
            </>
          )}

          {/* Show submit button for all types except import-members when results are shown */}
          {!(type === 'import-members' && importResults) && (
            <button 
              type="submit" 
              className="btn btn-primary full-width" 
              disabled={loading || (type === 'create-group' && (!isBasicInfoValid() || !isPaymentValid()))}
              data-testid="modal-submit-btn"
              style={{
                opacity: (type === 'create-group' && (!isBasicInfoValid() || !isPaymentValid())) ? 0.5 : 1,
                cursor: (type === 'create-group' && (!isBasicInfoValid() || !isPaymentValid())) ? 'not-allowed' : 'pointer'
              }}
            >
              {loading ? 'Saving...' : (type === 'edit-member' ? 'Update Member' : (type === 'import-members' ? 'Import Members' : (type === 'create-group' ? t(formData.language, 'createGroup') : 'Submit')))}
            </button>
          )}
          {type === 'create-group' && (!isBasicInfoValid() || !isPaymentValid()) && (
            <p style={{ textAlign: 'center', color: '#EF4444', fontSize: '13px', marginTop: '8px' }}>
              {!isBasicInfoValid() ? '⚠️ Please fill in group name, description, and payment amount' : '⚠️ Please fill in all payment details to continue'}
            </p>
          )}
        </form>
      </div>
    </div>
  );
}
